import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, Input, Output, Injectable, Pipe, Directive, Optional, Inject, InjectionToken, forwardRef, HostListener, ViewChild, Injector, TemplateRef, HostBinding, ElementRef, ContentChild } from '@angular/core';
import { faPlus, faMinus, faArrowRight, faChevronUp, faCheck, faTimes, faTrashAlt, faSearch, faInfoCircle, faStar, faHandPointer, faAngleDown, faThumbtack, faAngleRight, faAngleDoubleLeft, faAngleDoubleRight, faBars, faCalendarAlt, faCaretDown, faChevronLeft, faChevronRight, faCalendar, faFileUpload, faFileAlt, faExclamationTriangle, faExclamationCircle, faColumns, faSyncAlt, faFilter, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { animate, style, trigger, state, transition, keyframes } from '@angular/animations';
import { Subject, BehaviorSubject, ReplaySubject, timer, fromEvent, takeUntil as takeUntil$1, merge, defer } from 'rxjs';
import * as i2$2 from '@angular/common';
import { NgClass, NgStyle, AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { FaIconComponent, FaLayersComponent, FaLayersCounterComponent } from '@fortawesome/angular-fontawesome';
import * as i1$2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormControl, NG_VALIDATORS, Validators, FormGroup, FormControl, UntypedFormGroup, UntypedFormArray, FormArray } from '@angular/forms';
import { startWith, distinctUntilChanged, switchMap, debounce, finalize, takeUntil, throttleTime, tap, filter, take, map, debounceTime } from 'rxjs/operators';
import { DateTime } from 'luxon';
import * as i2 from '@angular/router';
import { NavigationStart, RouterLink, NavigationEnd } from '@angular/router';
import { hasModifierKey } from '@angular/cdk/keycodes';
import * as i1 from '@angular/cdk/overlay';
import { OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import * as i3 from '@angular/cdk/portal';
import { ComponentPortal, BasePortalOutlet, CdkPortalOutlet, PortalModule, TemplatePortal, PortalInjector, DomPortalOutlet } from '@angular/cdk/portal';
import * as i1$1 from '@angular/platform-browser';
import * as i2$1 from '@angular/cdk/a11y';
import { A11yModule, CdkTrapFocus } from '@angular/cdk/a11y';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';
import * as i1$3 from '@angular/cdk/layout';

const collapseTiming = '225ms cubic-bezier(0.4, 0.0, 0.2, 1)';
const popTiming = '100ms cubic-bezier(0, 0, 0.2, 1)';
const collapseAnimation = animate(collapseTiming);
const collapseHiddenStyle = style({ paddingTop: 0, paddingBottom: 0, height: '0', visibility: 'hidden', overflow: 'hidden' });
const collapseVisibleStyle = style({ paddingTop: '*', paddingBottom: '*', height: '*', visibility: 'visible', overflow: 'hidden' });
const trvCollapseVoidAnimation = trigger('trvCollapseVoidAnimation', [
    state('void', collapseHiddenStyle),
    state('*', collapseVisibleStyle),
    transition('* <=> void', collapseAnimation)
]);
const trvCollapseStateAnimation = trigger('trvCollapseStateAnimation', [
    state('collapsed, void', collapseHiddenStyle),
    state('expanded', collapseVisibleStyle),
    transition('expanded <=> void, expanded <=> collapsed', collapseAnimation)
]);
const trvFadeAnimation = trigger('trvFadeAnimation', [
    transition(':enter', [style({ opacity: 0 }), animate('0.25s ease-out', style({ opacity: 1 }))]),
    transition(':leave', [style({ opacity: 1 }), animate('0.25s ease-in', style({ opacity: 0 }))])
]);
const trvPopAnimation = trigger('trvPopAnimation', [
    state('initial, void, hidden', style({ opacity: 0, transform: 'scale(0)' })),
    transition('* => visible', [
        style({ opacity: 0, transform: 'scale(0)' }),
        animate(popTiming, style({ transform: 'none', opacity: 1 }))
    ]),
    transition('visible => hidden', [
        animate(popTiming, style({ opacity: 0, transform: 'scale(0)' }))
    ])
]);
const trvPopAltAnimation = trigger('trvPopAltAnimation', [
    transition(':enter', [
        animate('200ms ease-in-out', keyframes([
            style({ transform: 'scale(0)' }),
            style({ transform: 'scale(1.1)' }),
            style({ transform: 'scale(1)' }),
        ]))
    ]),
    transition(':leave', [
        animate('150ms ease-in-out', keyframes([
            style({ transform: 'scale(1)' }),
            style({ transform: 'scale(0)' })
        ]))
    ]),
]);

class TrvAccordionComponent {
    closeOthersOnExpand;
    destroyOnCollapse = true;
    hasAccordionChildren;
    collapsePanelsWhen;
    collapseChanged = new EventEmitter();
    ngUnsubsribe = new Subject();
    _accordionPanels = [];
    faPlus = faPlus;
    faMinus = faMinus;
    constructor() { }
    ngOnChanges(changes) {
        if (changes?.collapsePanelsWhen?.currentValue) {
            for (const panel of this._accordionPanels) {
                panel.collapse();
            }
        }
    }
    ngOnDestroy() {
        this.ngUnsubsribe.next();
        this.ngUnsubsribe.complete();
    }
    addPanel(panel) {
        this._accordionPanels.push(panel);
    }
    togglePanel(toggledPanel) {
        if (this.closeOthersOnExpand && !toggledPanel.isExpanded()) {
            for (const panel of this._accordionPanels) {
                if (panel !== toggledPanel) {
                    panel.collapse();
                }
            }
            toggledPanel.expand();
        }
        else if (!toggledPanel.isExpanded()) {
            toggledPanel.expand();
        }
        else {
            toggledPanel.collapse();
        }
        this.collapseChanged.emit({ id: toggledPanel.id || 'Id not set', expanded: toggledPanel.isExpanded() });
    }
    togglePanelById(panelId) {
        const foundPanel = this._accordionPanels.find(p => p.id === panelId);
        if (foundPanel) {
            this.togglePanel(foundPanel);
        }
    }
    isPanelOpen(panelId) {
        const foundPanel = this._accordionPanels.find(p => p.id === panelId);
        return foundPanel && foundPanel.isExpanded();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvAccordionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvAccordionComponent, isStandalone: true, selector: "trv-accordion", inputs: { closeOthersOnExpand: "closeOthersOnExpand", destroyOnCollapse: "destroyOnCollapse", hasAccordionChildren: "hasAccordionChildren", collapsePanelsWhen: "collapsePanelsWhen" }, outputs: { collapseChanged: "collapseChanged" }, exportAs: ["trvAccordion"], usesOnChanges: true, ngImport: i0, template: "<div class=\"accordion\"\r\n     [ngClass]=\"{ 'has-accordion-children': hasAccordionChildren }\">\r\n  <ng-content></ng-content>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.accordion{box-shadow:0 0 6px #00000014;border:1px solid rgba(0,0,0,.125);display:block}.accordion ::ng-deep trv-accordion-panel:not(:first-child) .accordion-header-button{border-top:1px solid rgba(0,0,0,.125)}.accordion ::ng-deep trv-accordion-panel.info .accordion-header-button{background-color:#1e5c88;color:#fff}.accordion ::ng-deep trv-accordion-panel.info .accordion-header-button:hover,.accordion ::ng-deep trv-accordion-panel.info .accordion-header-button:focus{background-color:#1e5c88;color:#fff}.accordion ::ng-deep trv-accordion-panel.info .accordion-header-button:not(.collapsed){background-color:#1e5c88;color:#fff}.accordion ::ng-deep trv-accordion-panel.danger .accordion-header .accordion-header-button{background-color:#870000;color:#fff}.accordion ::ng-deep trv-accordion-panel.danger .accordion-header .accordion-header-button:hover,.accordion ::ng-deep trv-accordion-panel.danger .accordion-header .accordion-header-button:focus{background-color:#870000;color:#fff}.accordion ::ng-deep trv-accordion-panel.danger .accordion-header .accordion-header-button:not(.collapsed){background-color:#870000;color:#fff}.accordion.has-accordion-children ::ng-deep .test{color:red}.accordion.has-accordion-children ::ng-deep trv-accordion-panel .accordion-body-content{background:#e7f0f8}.accordion.has-accordion-children ::ng-deep .accordion{box-shadow:none;border:none}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-header-button{border-top:1px solid rgba(0,0,0,.125);padding-left:2rem;background:transparent}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-body-content .accordion .accordion-header-button{padding-left:4rem}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-body-content .accordion .accordion-body-content .accordion .accordion-header-button{padding-left:6rem}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-header-button{padding-left:8rem}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-header-button{padding-left:10rem}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-header-button{padding-left:12rem}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-header-button{padding-left:14rem}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvAccordionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-accordion', exportAs: 'trvAccordion', changeDetection: ChangeDetectionStrategy.OnPush, imports: [NgClass], template: "<div class=\"accordion\"\r\n     [ngClass]=\"{ 'has-accordion-children': hasAccordionChildren }\">\r\n  <ng-content></ng-content>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.accordion{box-shadow:0 0 6px #00000014;border:1px solid rgba(0,0,0,.125);display:block}.accordion ::ng-deep trv-accordion-panel:not(:first-child) .accordion-header-button{border-top:1px solid rgba(0,0,0,.125)}.accordion ::ng-deep trv-accordion-panel.info .accordion-header-button{background-color:#1e5c88;color:#fff}.accordion ::ng-deep trv-accordion-panel.info .accordion-header-button:hover,.accordion ::ng-deep trv-accordion-panel.info .accordion-header-button:focus{background-color:#1e5c88;color:#fff}.accordion ::ng-deep trv-accordion-panel.info .accordion-header-button:not(.collapsed){background-color:#1e5c88;color:#fff}.accordion ::ng-deep trv-accordion-panel.danger .accordion-header .accordion-header-button{background-color:#870000;color:#fff}.accordion ::ng-deep trv-accordion-panel.danger .accordion-header .accordion-header-button:hover,.accordion ::ng-deep trv-accordion-panel.danger .accordion-header .accordion-header-button:focus{background-color:#870000;color:#fff}.accordion ::ng-deep trv-accordion-panel.danger .accordion-header .accordion-header-button:not(.collapsed){background-color:#870000;color:#fff}.accordion.has-accordion-children ::ng-deep .test{color:red}.accordion.has-accordion-children ::ng-deep trv-accordion-panel .accordion-body-content{background:#e7f0f8}.accordion.has-accordion-children ::ng-deep .accordion{box-shadow:none;border:none}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-header-button{border-top:1px solid rgba(0,0,0,.125);padding-left:2rem;background:transparent}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-body-content .accordion .accordion-header-button{padding-left:4rem}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-body-content .accordion .accordion-body-content .accordion .accordion-header-button{padding-left:6rem}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-header-button{padding-left:8rem}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-header-button{padding-left:10rem}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-header-button{padding-left:12rem}.accordion.has-accordion-children ::ng-deep .accordion trv-accordion-panel .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-body-content .accordion .accordion-header-button{padding-left:14rem}\n"] }]
        }], ctorParameters: () => [], propDecorators: { closeOthersOnExpand: [{
                type: Input
            }], destroyOnCollapse: [{
                type: Input
            }], hasAccordionChildren: [{
                type: Input
            }], collapsePanelsWhen: [{
                type: Input
            }], collapseChanged: [{
                type: Output
            }] } });

class TrvAccordionPanelComponent {
    accordion;
    cdr;
    header;
    badge;
    startExpanded;
    disabled;
    id;
    isLink;
    customLeftIcon;
    headerIcon;
    hideLeftIcon;
    _isExpanded;
    _openedOnce;
    faPlus = faPlus;
    faMinus = faMinus;
    faArrowRight = faArrowRight;
    state;
    constructor(accordion, cdr) {
        this.accordion = accordion;
        this.cdr = cdr;
        this.accordion.addPanel(this);
    }
    ngOnInit() {
        this._isExpanded = this.startExpanded || false;
        this._openedOnce = this._isExpanded;
    }
    collapse() {
        this._isExpanded = false;
        this.cdr.detectChanges();
    }
    expand() {
        this._isExpanded = true;
        this._openedOnce = true;
        this.cdr.detectChanges();
    }
    toggle() {
        this.accordion.togglePanel(this);
    }
    isExpanded() {
        return this._isExpanded;
    }
    openedOnce() {
        return this._openedOnce;
    }
    shouldRenderContent() {
        if (this.isLink) {
            return false;
        }
        if (this.accordion.destroyOnCollapse) {
            return this._isExpanded ? true : false;
        }
        else {
            return this._openedOnce ? true : false;
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvAccordionPanelComponent, deps: [{ token: TrvAccordionComponent }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvAccordionPanelComponent, isStandalone: true, selector: "trv-accordion-panel", inputs: { header: "header", badge: "badge", startExpanded: "startExpanded", disabled: "disabled", id: "id", isLink: "isLink", customLeftIcon: "customLeftIcon", headerIcon: "headerIcon", hideLeftIcon: "hideLeftIcon" }, ngImport: i0, template: "<ng-container>\r\n  <div class=\"accordion-header\">\r\n    <button (click)=\"toggle()\"\r\n            [disabled]=\"disabled\"\r\n            [ngClass]=\"{'collapsed' : isLink ? true : !isExpanded()}\"\r\n            class=\"btn btn-link accordion-header-button\">\r\n      <div class=\"title-area\">\r\n        <div class=\"icon-area\">\r\n          @if (!hideLeftIcon) {\r\n          <fa-icon [icon]=\"customLeftIcon ? customLeftIcon : isLink ? faArrowRight : isExpanded() ? faMinus : faPlus\">\r\n          </fa-icon>\r\n          }\r\n        </div>\r\n        @if (header) {\r\n        <div class=\"text-area\">\r\n          @if (headerIcon) {\r\n          <fa-icon [icon]=\"headerIcon\"\r\n                   class=\"me-2\"></fa-icon>\r\n          }\r\n          {{header}}\r\n        </div>\r\n        }\r\n        @if (!header) {\r\n        <div class=\"content-area\">\r\n          <ng-content select=\"[trv-accordion-header]\"></ng-content>\r\n        </div>\r\n        }\r\n      </div>\r\n      <div class=\"badge-area\">\r\n        {{badge}}\r\n        <ng-content select=\"[trv-accordion-badge]\"></ng-content>\r\n      </div>\r\n    </button>\r\n  </div>\r\n  @if (shouldRenderContent()) {\r\n  <div [@trvCollapseStateAnimation]=\"isLink ? 'collapsed' : isExpanded() ? 'expanded' : 'collapsed'\"\r\n       class=\"accordion-body\">\r\n    <div class=\"accordion-body-content\">\r\n      <ng-content select=\"[trv-accordion-content]\"></ng-content>\r\n    </div>\r\n  </div>\r\n  }\r\n</ng-container>", styles: [":root{--bs-border-color: #ced4da}.accordion-header button{width:100%;display:flex;text-decoration:none;border-radius:0;background-color:#fff;text-align:left;position:relative}.accordion-header button .title-area{display:flex;flex:1;align-items:center;font-weight:600}.accordion-header button .title-area .icon-area{width:25px;height:25px;display:flex;align-items:center;justify-content:center;border-radius:100%;background-color:transparent;transition:background-color .25s ease}.accordion-header button .title-area .text-area{margin-left:10px}.accordion-header button .title-area .content-area{margin-left:10px;width:100%}.accordion-header button:hover,.accordion-header button:focus{color:#103249}.accordion-header button:hover .title-area .icon-area,.accordion-header button:focus .title-area .icon-area{background:#adb5bdb3}.accordion-header button:not(.collapsed){background-color:#e7f0f8;color:#212529}.accordion-header button:not(.collapsed):hover .icon-area{background-color:#adb5bdb3}.accordion-body{overflow:hidden;display:flex}.accordion-body .accordion-body-content{flex:1 1 auto;background-color:#fff}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }], animations: [trvCollapseStateAnimation], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvAccordionPanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-accordion-panel', changeDetection: ChangeDetectionStrategy.OnPush, animations: [trvCollapseStateAnimation], imports: [NgClass, FaIconComponent], template: "<ng-container>\r\n  <div class=\"accordion-header\">\r\n    <button (click)=\"toggle()\"\r\n            [disabled]=\"disabled\"\r\n            [ngClass]=\"{'collapsed' : isLink ? true : !isExpanded()}\"\r\n            class=\"btn btn-link accordion-header-button\">\r\n      <div class=\"title-area\">\r\n        <div class=\"icon-area\">\r\n          @if (!hideLeftIcon) {\r\n          <fa-icon [icon]=\"customLeftIcon ? customLeftIcon : isLink ? faArrowRight : isExpanded() ? faMinus : faPlus\">\r\n          </fa-icon>\r\n          }\r\n        </div>\r\n        @if (header) {\r\n        <div class=\"text-area\">\r\n          @if (headerIcon) {\r\n          <fa-icon [icon]=\"headerIcon\"\r\n                   class=\"me-2\"></fa-icon>\r\n          }\r\n          {{header}}\r\n        </div>\r\n        }\r\n        @if (!header) {\r\n        <div class=\"content-area\">\r\n          <ng-content select=\"[trv-accordion-header]\"></ng-content>\r\n        </div>\r\n        }\r\n      </div>\r\n      <div class=\"badge-area\">\r\n        {{badge}}\r\n        <ng-content select=\"[trv-accordion-badge]\"></ng-content>\r\n      </div>\r\n    </button>\r\n  </div>\r\n  @if (shouldRenderContent()) {\r\n  <div [@trvCollapseStateAnimation]=\"isLink ? 'collapsed' : isExpanded() ? 'expanded' : 'collapsed'\"\r\n       class=\"accordion-body\">\r\n    <div class=\"accordion-body-content\">\r\n      <ng-content select=\"[trv-accordion-content]\"></ng-content>\r\n    </div>\r\n  </div>\r\n  }\r\n</ng-container>", styles: [":root{--bs-border-color: #ced4da}.accordion-header button{width:100%;display:flex;text-decoration:none;border-radius:0;background-color:#fff;text-align:left;position:relative}.accordion-header button .title-area{display:flex;flex:1;align-items:center;font-weight:600}.accordion-header button .title-area .icon-area{width:25px;height:25px;display:flex;align-items:center;justify-content:center;border-radius:100%;background-color:transparent;transition:background-color .25s ease}.accordion-header button .title-area .text-area{margin-left:10px}.accordion-header button .title-area .content-area{margin-left:10px;width:100%}.accordion-header button:hover,.accordion-header button:focus{color:#103249}.accordion-header button:hover .title-area .icon-area,.accordion-header button:focus .title-area .icon-area{background:#adb5bdb3}.accordion-header button:not(.collapsed){background-color:#e7f0f8;color:#212529}.accordion-header button:not(.collapsed):hover .icon-area{background-color:#adb5bdb3}.accordion-body{overflow:hidden;display:flex}.accordion-body .accordion-body-content{flex:1 1 auto;background-color:#fff}\n"] }]
        }], ctorParameters: () => [{ type: TrvAccordionComponent }, { type: i0.ChangeDetectorRef }], propDecorators: { header: [{
                type: Input
            }], badge: [{
                type: Input
            }], startExpanded: [{
                type: Input
            }], disabled: [{
                type: Input
            }], id: [{
                type: Input
            }], isLink: [{
                type: Input
            }], customLeftIcon: [{
                type: Input
            }], headerIcon: [{
                type: Input
            }], hideLeftIcon: [{
                type: Input
            }] } });

const TrvAccordionBundle = [TrvAccordionComponent, TrvAccordionPanelComponent];

/*
  This is a service to wrap creation of DateTime-objects and make sure we always create dates with swedish timezone.
  It also contains other utility-functions involving dates.
*/
class TrvDateService {
    dateNow;
    today;
    yesterday;
    monthNames;
    monthNamesShort;
    weekdays;
    weekdaysShort;
    constructor() {
        this.dateNow = this.now();
        this.today = this.dateNow.startOf('day');
        this.yesterday = this.dateNow.minus({ days: 1 }).startOf('day');
        this.buildMonthNames();
        this.buildWeekdays();
    }
    now() {
        return DateTime.now();
    }
    fromISO(date) {
        if (!date) {
            return null;
        }
        const dt = DateTime.fromISO(date);
        return dt;
    }
    fromObject(obj) {
        if (!obj) {
            return null;
        }
        return DateTime.fromObject(obj);
    }
    getMonthName(monthNr) {
        return this.monthNames[monthNr - 1];
    }
    getMonthNames() {
        return this.monthNames.slice(0);
    }
    getWeekdayMin(weekday) {
        return this.weekdaysShort[weekday - 1];
    }
    getWeekdaysMin() {
        return this.weekdaysShort.slice(0);
    }
    isToday(dateTime) {
        return dateTime.hasSame(this.today, 'day');
    }
    isYesterday(dateTime) {
        return dateTime.hasSame(this.yesterday, 'day');
    }
    getDiff(dateTimeA, dateTimeB) {
        if (!dateTimeA || !dateTimeB) {
            return null;
        }
        const diff = dateTimeB.diff(dateTimeA, ['days', 'hours', 'minutes', 'seconds']).toObject();
        return {
            days: diff.days,
            hours: diff.hours,
            minutes: diff.minutes,
        };
    }
    getBounds(dateStrings) {
        if (!dateStrings?.length) {
            return null;
        }
        const dates = dateStrings.map((ds) => new Date(ds));
        let minDate = dates[0];
        let minDateString = dateStrings[0];
        let maxDate = dates[0];
        let maxDateString = dateStrings[0];
        for (let i = 1; i < dates.length; i++) {
            if (dates[i] < minDate) {
                minDate = dates[i];
                minDateString = dateStrings[i];
            }
            else if (dates[i] > maxDate) {
                maxDate = dates[i];
                maxDateString = dateStrings[i];
            }
        }
        return { start: minDateString, end: maxDateString };
    }
    getTotalTimeFromDateRange(dateRange) {
        if (!dateRange || !dateRange.from || !dateRange.to) {
            return null;
        }
        const dateTimeFrom = this.fromISO(dateRange.from);
        const dateTimeTo = this.fromISO(dateRange.to);
        const diff = dateTimeTo.diff(dateTimeFrom, ['days', 'hours', 'minutes', 'seconds']).toObject();
        return { days: diff.days, hours: diff.hours, minutes: diff.minutes };
    }
    getTotalTimeFromDateRangeList(dateRangeList) {
        if (!dateRangeList) {
            return null;
        }
        let totalTimeMinutes = 0;
        dateRangeList.forEach((dateRange) => {
            if (!dateRange || !dateRange.from || !dateRange.to) {
                console.warn('a daterange is missing values');
                return;
            }
            totalTimeMinutes += Math.floor(this.fromISO(dateRange.to).diff(this.fromISO(dateRange.from), 'minutes').minutes);
        });
        return {
            days: Math.floor(totalTimeMinutes / (60 * 24)),
            hours: Math.floor(totalTimeMinutes / 60) % 24,
            minutes: Math.floor(totalTimeMinutes % 60),
        };
    }
    convertDateStringsToDateTimes(dateStrings) {
        const dateTimes = [];
        for (let i = 0; i < dateStrings?.length; i++) {
            const dt = this.fromISO(dateStrings[i]);
            if (dt.isValid) {
                dateTimes.push(dt);
            }
        }
        return dateTimes;
    }
    convertDateTimesToDateStrings(dateTimes) {
        const dateStrings = [];
        for (let i = 0; i < dateTimes.length; i++) {
            if (dateTimes[i].isValid) {
                dateStrings.push(dateTimes[i].toISODate());
            }
        }
        return dateStrings;
    }
    stripTime(dateTime) {
        return dateTime.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    }
    buildMonthNames() {
        let month = DateTime.now().startOf('year');
        const endOfYear = month.endOf('year');
        this.monthNames = [];
        this.monthNamesShort = [];
        while (month < endOfYear) {
            this.monthNames.push(month.monthLong);
            this.monthNamesShort.push(month.monthShort);
            month = month.plus({ month: 1 });
        }
    }
    buildWeekdays() {
        let day = DateTime.now().startOf('week');
        const endOfWeek = day.endOf('week');
        this.weekdays = [];
        this.weekdaysShort = [];
        while (day < endOfWeek) {
            this.weekdays.push(day.weekdayLong);
            this.weekdaysShort.push(day.weekdayLong.slice(0, 2));
            day = day.plus({ day: 1 });
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

/*
    utilityfunctions for our calendar components
*/
class TrvCalendarService {
    dateService;
    // weekdayDeviations: TrvWeekdayDeviation[];
    detectChange = new EventEmitter();
    today;
    todayIsoString;
    constructor(dateService) {
        this.dateService = dateService;
    }
    splitArray(arr, size) {
        const groups = [];
        for (let i = 0; i < arr.length; i += size) {
            groups.push(arr.slice(i, i + size));
        }
        return groups;
    }
    getPeriodInitState(bounds, visibleMonthsCount) {
        const period = {
            months: this.createMonthsArray(bounds.from, bounds.to)
        };
        const currentDT = this.dateService.now();
        const visibleMonths = visibleMonthsCount || 3;
        if (currentDT >= bounds.from && currentDT <= bounds.to) {
            for (let i = 0; i < period.months.length; i++) {
                if (period.months[i].hasSame(currentDT, 'month')) {
                    period.startmonth = period.months[i];
                    if (i + (visibleMonths - 1) < period.months.length) {
                        period.endmonth = period.months[i + (visibleMonths - 1)];
                    }
                    else {
                        period.endmonth = period.months[period.months.length - 1];
                    }
                }
            }
        }
        else {
            period.startmonth = period.months[0];
            period.endmonth = period.months.length > (visibleMonths - 1) ? period.months[(visibleMonths - 1)] :
                period.months[period.months.length - 1];
        }
        return period;
    }
    // getPeriodBySelectedDateTimes(dateTimes: DateTime[], bounds?: CalendarBoundsLuxon): CalendarPeriod {
    //   const selectedBounds = this.dateService.getBounds(dateTimes);
    //   return this.getPeriodByFirstAndLastDate(selectedBounds.start, selectedBounds.end, bounds);
    // }
    getPeriodBySelectedDates(dateStrings, bounds) {
        const selectedBounds = this.dateService.getBounds(dateStrings);
        return this.getPeriodByFirstAndLastDate(selectedBounds.start, selectedBounds.end, bounds);
    }
    getPeriodByFirstAndLastDate(first, last, bounds) {
        const months = this.createMonthsArray(this.dateService.fromISO(first), this.dateService.fromISO(last));
        const period = {
            startmonth: months[0],
            endmonth: months[months.length - 1]
        };
        bounds = bounds || {
            from: period.startmonth,
            to: period.endmonth
        };
        period.months = this.createMonthsArray(bounds.from, bounds.to);
        return period;
    }
    createMonthsArray(beginDT, endDT) {
        let startOfCurrentMonth = beginDT.startOf('month');
        const startOfEndMonth = endDT.startOf('month');
        const months = [];
        while (startOfCurrentMonth <= startOfEndMonth) {
            months.push(startOfCurrentMonth);
            startOfCurrentMonth = startOfCurrentMonth.plus({ month: 1 });
        }
        return months;
    }
    buildPeriod(startmonth, endmonth, selectedDates, weekdayDeviations, minDate, maxDate, isDateSelectableFunction, specificCalendarDayFunction, propertyName) {
        this.today = this.dateService.now().startOf('day');
        this.todayIsoString = this.today.toISO({ suppressMilliseconds: true });
        const months = [];
        let month = startmonth;
        while (month <= endmonth) {
            months.push(this.buildMonth(month, selectedDates, weekdayDeviations, minDate, maxDate, isDateSelectableFunction, specificCalendarDayFunction, propertyName));
            month = month.plus({ month: 1 });
        }
        return months;
    }
    buildMonth(month, selectedDates, weekdayDeviations, minDateString, maxDateString, isDateSelectableFunction, specificCalendarDayFunction, propertyName) {
        const minDate = minDateString ? this.dateService.fromISO(minDateString).startOf('day').toJSDate() : null;
        const maxDate = maxDateString ? this.dateService.fromISO(maxDateString).startOf('day').toJSDate() : null;
        selectedDates = selectedDates || [];
        weekdayDeviations = weekdayDeviations || [];
        const weeks = [];
        let done = false;
        let count = 0;
        let date = month.startOf('week'); //get the monday of the week
        let monthIndex = date.month;
        while (!done) {
            const week = {
                number: date.weekNumber,
                isCurrentWeek: date.hasSame(this.dateService.now(), 'week'),
                days: this.buildWeek(date, month, selectedDates, weekdayDeviations, minDate, maxDate, isDateSelectableFunction, specificCalendarDayFunction, propertyName)
            };
            weeks.push(week);
            date = date.plus({ week: 1 });
            done = count++ > 2 && monthIndex !== date.month;
            monthIndex = date.month;
        }
        return {
            month: month,
            weekdays: this.buildWeekdays(),
            weeks: weeks
        };
    }
    buildWeekdays() {
        return this.dateService.getWeekdaysMin().map(weekday => {
            return { name: weekday };
        });
    }
    buildWeek(dateTime, month, selectedDates, weekdayDeviations, minDate, maxDate, isDateSelectableFunction, specificCalendarDayFunction, propertyName) {
        const days = [];
        for (let i = 0; i < 7; i++) {
            const day = {
                id: dateTime.toISODate(),
                number: dateTime.day,
                isCurrentMonth: dateTime.month === month.month,
                isOutsideAvailableInterval: !this.isSelectable(dateTime.toJSDate(), minDate, maxDate),
                dateTime: dateTime,
                isoString: dateTime.toISO({ suppressMilliseconds: true })
            };
            day.isToday = day.isoString === this.todayIsoString;
            const selected = this.checkIfSelected(day.isoString, selectedDates) && day.isCurrentMonth;
            if (propertyName) {
                day[propertyName] = selected;
            }
            else {
                day.selected = selected;
            }
            day.trafficDay = this.getTrafficDay(day, weekdayDeviations);
            if (specificCalendarDayFunction) {
                specificCalendarDayFunction(day);
            }
            if (isDateSelectableFunction) {
                day.isOutsideAvailableInterval = day.isOutsideAvailableInterval || !isDateSelectableFunction(day);
            }
            days.push(day);
            dateTime = dateTime.plus({ day: 1 });
        }
        return days;
    }
    togglePropertyDay(day, selectedDates, propertyName, externalConditionFunction, callback) {
        if (!day.isCurrentMonth || day.isOutsideAvailableInterval ||
            (externalConditionFunction && !externalConditionFunction(day))) {
            return;
        }
        day[propertyName] = !day[propertyName];
        this.addOrRemoveFromSelected(day, selectedDates, propertyName);
        if (callback) {
            callback();
        }
    }
    togglePropertyWeek(week, selectedDates, propertyName, externalConditionFunction, callback) {
        let allTrue = true;
        const daysToToggle = [];
        for (let i = 0; i < week.days.length; i++) {
            const day = week.days[i];
            if (day.isCurrentMonth && !day.isOutsideAvailableInterval &&
                (!externalConditionFunction || externalConditionFunction(day))) {
                if (!day[propertyName]) {
                    allTrue = false;
                }
                daysToToggle.push(day);
            }
        }
        this.togglePropertyDaysInList(daysToToggle, !allTrue, selectedDates, propertyName, callback);
    }
    togglePropertyWeekday(month, weekday, selectedDates, propertyName, externalConditionFunction, callback) {
        let allTrue = true;
        const daysToToggle = [];
        for (let i = 0; i < month.weeks.length; i++) {
            const week = month.weeks[i];
            for (let j = 0; j < week.days.length; j++) {
                const day = week.days[j];
                if (day.isCurrentMonth && !day.isOutsideAvailableInterval && this.dateService.getWeekdayMin(day.dateTime.weekday) === weekday.name &&
                    (!externalConditionFunction || externalConditionFunction(day))) {
                    if (!day[propertyName]) {
                        allTrue = false;
                    }
                    daysToToggle.push(day);
                }
            }
        }
        this.togglePropertyDaysInList(daysToToggle, !allTrue, selectedDates, propertyName, callback);
    }
    togglePropertyMonth(month, selectedDates, propertyName, externalConditionFunction, callback) {
        let allTrue = true;
        const daysToToggle = [];
        for (let i = 0; i < month.weeks.length; i++) {
            const week = month.weeks[i];
            for (let j = 0; j < week.days.length; j++) {
                const day = week.days[j];
                if (day.isCurrentMonth && !day.isOutsideAvailableInterval &&
                    (!externalConditionFunction || externalConditionFunction(day))) {
                    if (!day[propertyName]) {
                        allTrue = false;
                    }
                    daysToToggle.push(day);
                }
            }
        }
        this.togglePropertyDaysInList(daysToToggle, !allTrue, selectedDates, propertyName, callback);
    }
    togglePropertyWeekdayAll(months, weekday, selectedDates, propertyName, externalConditionFunction, callback) {
        let allTrue = true;
        const daysToToggle = [];
        for (let i = 0; i < months.length; i++) {
            const month = months[i];
            for (let j = 0; j < month.weeks.length; j++) {
                const week = month.weeks[j];
                for (let k = 0; k < week.days.length; k++) {
                    const day = week.days[k];
                    if (day.isCurrentMonth && !day.isOutsideAvailableInterval && this.dateService.getWeekdayMin(day.dateTime.weekday) === weekday.name &&
                        (!externalConditionFunction || externalConditionFunction(day))) {
                        if (!day[propertyName]) {
                            allTrue = false;
                        }
                        daysToToggle.push(day);
                    }
                }
            }
        }
        this.togglePropertyDaysInList(daysToToggle, !allTrue, selectedDates, propertyName, callback);
    }
    togglePropertyTrafficDayAll(months, trafficDay, selectedDates, propertyName, externalConditionFunction, callback) {
        let allTrue = true;
        const daysToToggle = [];
        for (let i = 0; i < months.length; i++) {
            const month = months[i];
            for (let j = 0; j < month.weeks.length; j++) {
                const week = month.weeks[j];
                for (let k = 0; k < week.days.length; k++) {
                    const day = week.days[k];
                    if (day.isCurrentMonth && !day.isOutsideAvailableInterval && day.trafficDay.toLowerCase() === trafficDay &&
                        (!externalConditionFunction || externalConditionFunction(day))) {
                        if (!day[propertyName]) {
                            allTrue = false;
                        }
                        daysToToggle.push(day);
                    }
                }
            }
        }
        this.togglePropertyDaysInList(daysToToggle, !allTrue, selectedDates, propertyName, callback);
    }
    togglePropertyAllDays(months, selectedDates, propertyName, externalConditionFunction, callback) {
        let allTrue = true;
        const daysToToggle = [];
        for (let i = 0; i < months.length; i++) {
            const month = months[i];
            for (let j = 0; j < month.weeks.length; j++) {
                const week = month.weeks[j];
                for (let k = 0; k < week.days.length; k++) {
                    const day = week.days[k];
                    if (day.isCurrentMonth && !day.isOutsideAvailableInterval &&
                        (!externalConditionFunction || externalConditionFunction(day))) {
                        if (!day[propertyName]) {
                            allTrue = false;
                        }
                        daysToToggle.push(day);
                    }
                }
            }
        }
        this.togglePropertyDaysInList(daysToToggle, !allTrue, selectedDates, propertyName, callback);
    }
    togglePropertyDaysInList(list, shouldSelect, selectedDates, propertyName, callback) {
        for (let i = 0; i < list.length; i++) {
            list[i][propertyName] = shouldSelect;
            this.addOrRemoveFromSelected(list[i], selectedDates, propertyName);
        }
        if (list.length && callback) {
            callback();
        }
    }
    addOrRemoveFromSelected(day, selectedDates, propertyName) {
        if (day[propertyName]) {
            if (this.dateIndex(day.isoString, selectedDates) < 0) {
                selectedDates.push(day.isoString);
            }
        }
        else {
            const index = this.dateIndex(day.isoString, selectedDates);
            if (index >= 0) {
                selectedDates.splice(index, 1);
            }
        }
    }
    dateIndex(dateIsoString, selectedDates) {
        for (let i = 0; i < selectedDates.length; i++) {
            if (dateIsoString === selectedDates[i]) {
                return i;
            }
        }
        return -1;
    }
    isSelectable(date, minDate, maxDate) {
        if ((minDate && date < minDate) || (maxDate && date > maxDate)) {
            return false;
        }
        else {
            return true;
        }
    }
    getTrafficDay(day, weekdayDeviations) {
        for (let i = 0; i < weekdayDeviations.length; i++) {
            if (day.isoString === weekdayDeviations[i].isoString) {
                day.hasDeviation = true;
                return weekdayDeviations[i].weekday;
            }
            else {
                day.hasDeviation = false;
            }
        }
        return this.dateService.getWeekdayMin(day.dateTime.weekday);
    }
    checkIfSelected(dateIsoString, selectedDates) {
        for (let i = 0; i < selectedDates.length; i++) {
            if (dateIsoString === selectedDates[i]) {
                return true;
            }
        }
        return false;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvCalendarService, deps: [{ token: TrvDateService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvCalendarService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvCalendarService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: TrvDateService }], propDecorators: { detectChange: [{
                type: Output
            }] } });

class TrvUtilityService {
    constructor() { }
    escapeRegExp(str) {
        //escapes any special character that needs to be escaped in a RegExp
        return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
    }
    removeHtml(str) {
        return str.replace(/<\/?[^>]+(>|$)/g, '');
    }
    findPropByKeys(keys, object) {
        const keysArray = keys.split('.');
        keysArray.forEach(key => {
            object = object[key];
        });
        return object;
    }
    findOffset(obj) {
        const rect = obj.getBoundingClientRect();
        const offsetTop = rect.top + document.body.scrollTop;
        const offsetLeft = rect.left + document.body.scrollLeft;
        return {
            top: offsetTop,
            left: offsetLeft,
            bottom: offsetTop + obj.offsetHeight,
            right: offsetLeft + obj.offsetWidth
        };
    }
    detectChanges(changeDetectorRef) {
        if (!changeDetectorRef.destroyed) {
            changeDetectorRef.detectChanges();
        }
    }
    markForCheck(changeDetectorRef) {
        if (!changeDetectorRef.destroyed) {
            changeDetectorRef.markForCheck();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvUtilityService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvUtilityService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvUtilityService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class TrvDateRangeShortPipe {
    dateService;
    constructor(dateService) {
        this.dateService = dateService;
    }
    transform(dateRange) {
        if (!dateRange) {
            return undefined;
        }
        const dr = dateRange;
        const f = dr.from || dr.beginDate || dr.start;
        const t = dr.to || dr.endDate || dr.end;
        const from = f ? this.dateService.fromISO(f).toFormat('yyyy-MM-dd') : 'Obestämd';
        const to = t ? this.dateService.fromISO(t).toFormat('yyyy-MM-dd') : 'Tillsvidare';
        return from + ' – ' + to;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateRangeShortPipe, deps: [{ token: TrvDateService }], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.1", ngImport: i0, type: TrvDateRangeShortPipe, isStandalone: true, name: "trvDateRangeShort" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateRangeShortPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'trvDateRangeShort',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: TrvDateService }] });

class TrvCapitalizePipe {
    transform(input) {
        if (!input) {
            return undefined;
        }
        return input.charAt(0).toUpperCase() + input.slice(1);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvCapitalizePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.1", ngImport: i0, type: TrvCapitalizePipe, isStandalone: true, name: "trvCapitalize" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvCapitalizePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'trvCapitalize',
                    standalone: true
                }]
        }] });

// @dynamic
class TrvLoadingDirective {
    renderer;
    elementRef;
    cdr;
    trvUtilityService;
    defaults;
    isLoading = false;
    container;
    showAfterDelay;
    destroy$ = new Subject();
    isLoading$ = new BehaviorSubject(this.isLoading);
    delay$ = new ReplaySubject(1);
    loadingElement;
    fadeOutAnimationTimeout;
    constructor(renderer, elementRef, cdr, trvUtilityService, defaults) {
        this.renderer = renderer;
        this.elementRef = elementRef;
        this.cdr = cdr;
        this.trvUtilityService = trvUtilityService;
        this.defaults = defaults;
    }
    ngOnInit() {
        this.showAfterDelay = this.showAfterDelay ?? this.defaults.showAfterDelay;
    }
    ngAfterViewInit() {
        this.loadingElement = this.getHostElement();
        // debugger;
        this.delay$.pipe(startWith(this.showAfterDelay), distinctUntilChanged(), switchMap(delay => delay === 0 ? this.isLoading$ : this.isLoading$.pipe(debounce(loading => timer(loading ? delay : 0)))), finalize(() => this.clearFadeOutAnimationTimeout()), takeUntil(this.destroy$)).subscribe(loading => {
            if (loading === true) {
                this.clearFadeOutAnimationTimeout();
                this.showLoading();
            }
            else {
                this.hideLoading();
            }
            this.isLoading = loading;
            this.trvUtilityService.detectChanges(this.cdr);
        });
    }
    ngOnChanges(changes) {
        const { isLoading, showAfterDelay } = changes;
        if (isLoading) {
            this.isLoading$.next(this.isLoading);
        }
        if (showAfterDelay) {
            this.delay$.next(this.showAfterDelay);
        }
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.hideLoading(true);
    }
    getHostElement() {
        let element;
        if (this.container) {
            const elements = document.querySelectorAll(this.container);
            if (elements && elements.length) {
                element = elements.item(elements.length - 1);
            }
        }
        element = element || this.elementRef.nativeElement;
        if (!element.clientHeight) {
            const childContainer = this.findChildContainer(element);
            if (childContainer) {
                return childContainer;
            }
        }
        return element;
    }
    findChildContainer(element) {
        const children = element.childNodes;
        if (!children) {
            return null;
        }
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            if (child && child.clientHeight) {
                return child;
            }
            const grandChild = this.findChildContainer(child);
            if (grandChild && grandChild.clientHeight) {
                return grandChild;
            }
        }
        return null;
    }
    showLoading() {
        this.clearFadeOutAnimationTimeout();
        this.renderer.addClass(this.loadingElement, 'loading');
    }
    hideLoading(skipFade) {
        if (!this.loadingElement) {
            return;
        }
        this.renderer.addClass(this.loadingElement, 'loading-hidden');
        const removeClasses = () => {
            this.renderer.removeClass(this.loadingElement, 'loading');
            this.renderer.removeClass(this.loadingElement, 'loading-hidden');
        };
        if (skipFade) {
            removeClasses();
        }
        else {
            this.fadeOutAnimationTimeout = setTimeout(() => {
                removeClasses();
            }, 100);
        }
    }
    clearFadeOutAnimationTimeout() {
        clearTimeout(this.fadeOutAnimationTimeout);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvLoadingDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: TrvUtilityService }, { token: TRV_LOADING_DEFAULT_OPTIONS, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvLoadingDirective, isStandalone: true, selector: "[trv-loading]", inputs: { isLoading: ["trv-loading", "isLoading"], container: "container", showAfterDelay: "showAfterDelay" }, usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvLoadingDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[trv-loading]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: TrvUtilityService }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [TRV_LOADING_DEFAULT_OPTIONS]
                }] }], propDecorators: { isLoading: [{
                type: Input,
                args: ['trv-loading']
            }], container: [{
                type: Input
            }], showAfterDelay: [{
                type: Input
            }] } });
/** Injection token to be used to override the default options */
const TRV_LOADING_DEFAULT_OPTIONS = new InjectionToken('trv-loading-default-options', {
    providedIn: 'root',
    factory: () => ({ showAfterDelay: 0 })
});

class TrvCalendarComponent {
    calendarService;
    dateService;
    changeDetectorRef;
    trvUtilityService;
    ngUnsubscribe = new Subject();
    legend;
    readState;
    selectedDatesRead;
    selectedClass;
    selectedIcon;
    toggleDateCondition;
    markDayFunction;
    reloadSubject;
    labels;
    bounds;
    visibleBoundsOnInit;
    visibleMonthCountOnInit;
    selectedPeriodDescriptionLoading;
    selectedPeriodDescription;
    calendarLoadingChange = new EventEmitter();
    _weekdayDeviations;
    get weekdayDeviations() {
        return this._weekdayDeviations;
    }
    set weekdayDeviations(value) {
        this._weekdayDeviations = value;
        if (value && this.period) {
            this.createCalendar();
        }
    }
    boundsDT;
    selectedDates;
    calendarWeekdays;
    period;
    months;
    disabled;
    changeMonths = { start: null, end: null };
    isFormControl;
    propagateChange = () => { };
    propagateTouched = () => { };
    constructor(calendarService, dateService, changeDetectorRef, trvUtilityService) {
        this.calendarService = calendarService;
        this.dateService = dateService;
        this.changeDetectorRef = changeDetectorRef;
        this.trvUtilityService = trvUtilityService;
    }
    ngOnInit() {
        this.selectedClass = this.selectedClass || 'selected';
        this.toggleDateCondition = this.toggleDateCondition || (() => true);
        this.labels = this.labels || this.defaultLabels();
        this.calendarService.detectChange.pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => this.trvUtilityService.detectChanges(this.changeDetectorRef));
        if (this.reloadSubject) {
            this.reloadSubject.pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(() => this.initCalendar());
        }
        setTimeout(() => {
            if (this.readState && !this.isFormControl) {
                this.initCalendar(this.selectedDatesRead);
            }
        });
    }
    writeValue(value) {
        this.isFormControl = true;
        this.initCalendar(value);
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) {
        this.propagateTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this.trvUtilityService.detectChanges(this.changeDetectorRef);
    }
    onDayClick(day) {
        if (day.customClick) {
            day.customClick(day);
        }
        else {
            this.togglePropertyDay(day);
        }
    }
    onShowMonthClick(month) {
        if (!this.changeMonths.start) {
            this.changeMonths.start = month;
        }
        else if (!this.changeMonths.end) {
            this.changeMonths.end = month;
            if (this.changeMonths.start > this.changeMonths.end) {
                const start = this.changeMonths.start;
                this.changeMonths.start = this.changeMonths.end;
                this.changeMonths.end = start;
            }
            this.period.startmonth = this.changeMonths.start;
            this.period.endmonth = this.changeMonths.end;
            this.changeMonths = { start: null, end: null };
            this.createCalendar();
        }
        this.trvUtilityService.detectChanges(this.changeDetectorRef);
    }
    showSelectedInCalendar() {
        if (this.selectedDates.length) {
            this.period = this.calendarService.getPeriodBySelectedDates(this.selectedDates, this.boundsDT);
            this.createCalendar();
        }
    }
    togglePropertyDay(day) {
        if (this.readState || this.disabled) {
            return;
        }
        this.calendarService.togglePropertyDay(day, this.selectedDates, 'selected', this.toggleDateCondition.bind(this), this.onDatesToggled.bind(this));
    }
    togglePropertyWeek(week) {
        if (this.readState || this.disabled) {
            return;
        }
        this.calendarService.togglePropertyWeek(week, this.selectedDates, 'selected', this.toggleDateCondition.bind(this), this.onDatesToggled.bind(this));
    }
    togglePropertyWeekday(month, weekday) {
        if (this.readState || this.disabled) {
            return;
        }
        this.calendarService.togglePropertyWeekday(month, weekday, this.selectedDates, 'selected', this.toggleDateCondition.bind(this), this.onDatesToggled.bind(this));
    }
    togglePropertyMonth(month) {
        if (this.readState || this.disabled) {
            return;
        }
        this.calendarService.togglePropertyMonth(month, this.selectedDates, 'selected', this.toggleDateCondition.bind(this), this.onDatesToggled.bind(this));
    }
    togglePropertyWeekdayAll(weekday) {
        if (this.readState || this.disabled) {
            return;
        }
        this.calendarService.togglePropertyWeekdayAll(this.months, weekday, this.selectedDates, 'selected', this.toggleDateCondition.bind(this), this.onDatesToggled.bind(this));
    }
    togglePropertyTrafficDayAll(trafficDay) {
        if (this.readState || this.disabled) {
            return;
        }
        this.calendarService.togglePropertyTrafficDayAll(this.months, trafficDay, this.selectedDates, 'selected', this.toggleDateCondition.bind(this), this.onDatesToggled.bind(this));
    }
    togglePropertyAllDays() {
        if (this.readState || this.disabled) {
            return;
        }
        this.calendarService.togglePropertyAllDays(this.months, this.selectedDates, 'selected', this.toggleDateCondition.bind(this), this.onDatesToggled.bind(this));
    }
    isMonthActive(month) {
        const isBetween = month >= this.period.startmonth && month <= this.period.endmonth;
        return isBetween;
    }
    monthId(month) {
        return 'month-' + month.year + '-' + month.month;
    }
    onDatesToggled() {
        this.trvUtilityService.detectChanges(this.changeDetectorRef);
        this.propagateTouched();
        this.propagateChange(this.selectedDates);
    }
    initCalendar(selectedDates) {
        this.selectedDates = selectedDates || [];
        this.boundsDT = this.bounds ? {
            from: this.dateService.fromISO(this.bounds.from),
            to: this.dateService.fromISO(this.bounds.to)
        } : null;
        if (this.visibleBoundsOnInit && this.boundsDT) {
            this.period = this.calendarService.getPeriodByFirstAndLastDate(this.visibleBoundsOnInit.from, this.visibleBoundsOnInit.to, this.boundsDT);
        }
        else if (this.readState && this.bounds) {
            this.period = this.calendarService.getPeriodByFirstAndLastDate(this.bounds.from, this.bounds.to);
        }
        else if (this.selectedDates.length) {
            if (this.readState) {
                this.period = this.calendarService.getPeriodBySelectedDates(this.selectedDates);
            }
            else {
                this.period = this.calendarService.getPeriodBySelectedDates(this.selectedDates, this.boundsDT);
            }
        }
        else {
            this.period = this.calendarService.getPeriodInitState(this.boundsDT, this.visibleMonthCountOnInit);
        }
        if (!this.calendarWeekdays) {
            this.calendarWeekdays = this.calendarService.buildWeekdays();
        }
        this.createCalendar();
    }
    createCalendar() {
        this.months = this.calendarService.buildPeriod(this.period.startmonth, this.period.endmonth, this.selectedDates, this.weekdayDeviations, this.readState ? null : this.bounds?.from, //mindate
        this.readState ? null : this.bounds?.to, //maxdate
        this.toggleDateCondition, (day) => {
            // something
            if (!this.readState) {
                day.selectable = true;
            }
            if (this.markDayFunction) {
                this.markDayFunction(day);
            }
        });
        this.trvUtilityService.detectChanges(this.changeDetectorRef);
    }
    defaultLabels() {
        return {
            filterMonthsHeader: 'Visa',
            toggleDaysHeader: 'Markera',
            toggleWeekdays: 'Veckodagar',
            toggleTrafficDays: 'Trafikeringsdagar',
            toggleAllDays: 'Alla dagar',
            selectionHeader: 'Urval',
            selectionShow: 'Visa urval',
            weekShort: 'v.',
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvCalendarComponent, deps: [{ token: TrvCalendarService }, { token: TrvDateService }, { token: i0.ChangeDetectorRef }, { token: TrvUtilityService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvCalendarComponent, isStandalone: true, selector: "trv-calendar", inputs: { legend: "legend", readState: "readState", selectedDatesRead: "selectedDatesRead", selectedClass: "selectedClass", selectedIcon: "selectedIcon", toggleDateCondition: "toggleDateCondition", markDayFunction: "markDayFunction", reloadSubject: "reloadSubject", labels: "labels", bounds: "bounds", visibleBoundsOnInit: "visibleBoundsOnInit", visibleMonthCountOnInit: "visibleMonthCountOnInit", selectedPeriodDescriptionLoading: "selectedPeriodDescriptionLoading", selectedPeriodDescription: "selectedPeriodDescription", weekdayDeviations: "weekdayDeviations" }, outputs: { calendarLoadingChange: "calendarLoadingChange" }, providers: [{
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TrvCalendarComponent),
                multi: true
            }], ngImport: i0, template: "<div class=\"calendar-wrap\"\r\n     [ngClass]=\"{ 'calendar-read-state': readState }\">\r\n  <div class=\"calendar-area\">\r\n    <div class=\"clearfix d-flex flex-column flex-md-row\"\r\n         [ngClass]=\"{ 'row' : readState }\">\r\n      @if (months && !readState) {\r\n      <div class=\"calendar-filter\">\r\n        <div class=\"calendar-filter-group\">\r\n          <h4>{{labels.filterMonthsHeader}}</h4>\r\n          <div class=\"calendar-filter-button-group calendar-filter-months\">\r\n            @for (month of period.months; track month) {\r\n            <button id=\"{{monthId(month)}}\"\r\n                    type=\"button\"\r\n                    class=\"btn btn-month\"\r\n                    [ngClass]=\"{ 'active': isMonthActive(month), \r\n\t\t\t\t\t\t\t\t\t\t\t\t 'change-active': month === changeMonths.start }\"\r\n                    (click)=\"onShowMonthClick(month)\">\r\n              {{month.toFormat(\"MMM yy'\") | trvCapitalize}}\r\n            </button>\r\n            }\r\n          </div>\r\n        </div>\r\n        <div class=\"calendar-filter-group\">\r\n          <h4>{{labels.toggleDaysHeader}}</h4>\r\n          <div class=\"calendar-filter-button-group\">\r\n            <h5>{{labels.toggleWeekdays}}</h5>\r\n            @for (weekday of calendarWeekdays; track weekday) {\r\n            <button id=\"weekday-{{weekday.name}}\"\r\n                    type=\"button\"\r\n                    class=\"btn\"\r\n                    (click)=\"togglePropertyWeekdayAll(weekday)\">\r\n              {{weekday.name | trvCapitalize}}\r\n            </button>\r\n            }\r\n          </div>\r\n          @if (weekdayDeviations?.length) {\r\n          <div class=\"calendar-filter-button-group\">\r\n            <h5>{{labels.toggleTrafficDays}}</h5>\r\n            @for (trafficday of calendarWeekdays; track trafficday) {\r\n            <button id=\"trafficday-{{trafficday.name}}\"\r\n                    type=\"button\"\r\n                    class=\"btn\"\r\n                    (click)=\"togglePropertyTrafficDayAll(trafficday.name)\">\r\n              {{trafficday.name | trvCapitalize}}\r\n            </button>\r\n            }\r\n          </div>\r\n          }\r\n          <div class=\"calendar-filter-button-group block-buttons pt-3\">\r\n            <button type=\"button\"\r\n                    id=\"calendar-all-days-button\"\r\n                    class=\"btn btn-block\"\r\n                    (click)=\"togglePropertyAllDays()\">\r\n              {{labels.toggleAllDays}}\r\n            </button>\r\n          </div>\r\n        </div>\r\n        <div class=\"calendar-filter-group\"\r\n             [trv-loading]=\"selectedPeriodDescriptionLoading | async\">\r\n          <h4>{{labels.selectionHeader}}</h4>\r\n          <div class=\"calendar-filter-button-group block-buttons\">\r\n            @if (selectedPeriodDescription) {\r\n\r\n            <h5>{{{ from: selectedPeriodDescription.beginDate, to: selectedPeriodDescription.endDate } |\r\n              trvDateRangeShort }}</h5>\r\n            <p>{{selectedPeriodDescription.description}}</p>\r\n\r\n            }\r\n            <button type=\"button\"\r\n                    class=\"btn btn-block\"\r\n                    (click)=\"showSelectedInCalendar()\">\r\n              {{labels.selectionShow}}\r\n            </button>\r\n          </div>\r\n          @if (selectedPeriodDescription?.errorMessage) {\r\n          <div class=\"alert alert-danger\"\r\n               role=\"alert\">{{selectedPeriodDescription?.errorMessage}}</div>\r\n          }\r\n        </div>\r\n      </div>\r\n      }\r\n      <div>\r\n        @if (legend && months) {\r\n        <div class=\"legend-area\">\r\n          <ul class=\"list-unstyled list-inline\">\r\n            @for (legendObj of legend; track legendObj) {\r\n            <li>\r\n              <span class=\"{{legendObj.class}}\"\r\n                    [ngStyle]=\"legendObj.styles ? legendObj.styles : null\">\r\n                @if (legendObj.icon) {\r\n                <fa-icon [icon]=\"legendObj.icon\"></fa-icon>\r\n                }\r\n              </span>\r\n              {{legendObj.label}}\r\n            </li>\r\n            }\r\n          </ul>\r\n        </div>\r\n        }\r\n        <div class=\"calendar-items-wrap\">\r\n          @for (monthCalendar of months; track monthCalendar) {\r\n          <div class=\"calendar-items\">\r\n            <div class=\"calendar-item\">\r\n              <table>\r\n                <caption>\r\n                  <a role=\"button\"\r\n                     tabindex=\"0\"\r\n                     (click)=\"togglePropertyMonth(monthCalendar)\"\r\n                     (keyup.space)=\"togglePropertyMonth(monthCalendar)\">\r\n                    <span>{{monthCalendar.month.monthShort | trvCapitalize}}</span>\r\n                    {{monthCalendar.month.year}}\r\n                  </a>\r\n                </caption>\r\n                <thead>\r\n                  <tr>\r\n                    <th scope=\"col\">{{labels.weekShort}}</th>\r\n                    @for (weekday of monthCalendar.weekdays; track weekday) {\r\n                    <th>\r\n                      <a role=\"button\"\r\n                         tabindex=\"0\"\r\n                         (click)=\"togglePropertyWeekday(monthCalendar, weekday)\"\r\n                         (keyup.space)=\"togglePropertyWeekday(monthCalendar, weekday)\">{{weekday.name |\r\n                        trvCapitalize}}</a>\r\n                    </th>\r\n                    }\r\n                  </tr>\r\n                </thead>\r\n                <tbody>\r\n                  @for (week of monthCalendar.weeks; track week) {\r\n                  <tr>\r\n                    <th scope=\"row\"\r\n                        [ngClass]=\"{ 'current-week' : week.isCurrentWeek }\">\r\n                      <a role=\"button\"\r\n                         tabindex=\"0\"\r\n                         (click)=\"togglePropertyWeek(week)\"\r\n                         (keyup.space)=\"togglePropertyWeek(week)\">{{week.number}}</a>\r\n                    </th>\r\n\r\n                    @for (day of week.days; track day) {\r\n                    <td [ngClass]=\"[\r\n                            !day.isCurrentMonth || day.isOutsideAvailableInterval ? 'other-month' : '',\r\n                            day.isToday ? 'current-day': '',\r\n                            day.selected ? selectedClass: '',\r\n                            day.customClass || '',\r\n                            !!day.customClick ? 'has-custom-click': '',\r\n                            !day.selectable ? 'not-selectable': ''\r\n                          ]\">\r\n                      <a (click)=\"onDayClick(day)\"\r\n                         (keyup.enter)=\"onDayClick(day)\"\r\n                         (keyup.space)=\"onDayClick(day)\"\r\n                         id=\"day-{{day.id}}\"\r\n                         [attr.tabindex]=\"!day.isCurrentMonth || day.isOutsideAvailableInterval || !day.selectable ? -1 : 0\"\r\n                         [ngStyle]=\"day.customStyleFunction ? day.customStyleFunction(day): null\">\r\n                        {{day.number}}\r\n                        @if (day.hasDeviation) {\r\n                        <span class=\"traffic-day marker bottom\">{{day.trafficDay}}</span>\r\n                        }\r\n                        @if (day.isCurrentMonth) {\r\n\r\n                        @for (mark of day.customMarkers; track mark) {\r\n                        <span class=\"marker\"\r\n                              [ngClass]=\"[mark.class || '', mark.placement || '']\"\r\n                              [ngStyle]=\"mark.styles ? mark.styles : null\">\r\n                          @if (mark.icon) {\r\n                          <fa-icon [icon]=\"mark.icon\"></fa-icon>\r\n                          }\r\n                          {{mark.label}}\r\n                        </span>\r\n                        }\r\n\r\n                        }\r\n                        <span class=\"marker top-right\">\r\n                          @if (day.selected && selectedIcon) {\r\n                          <fa-icon [icon]=\"selectedIcon\">\r\n                          </fa-icon>\r\n                          }\r\n                        </span>\r\n                      </a>\r\n                    </td>\r\n                    }\r\n                  </tr>\r\n                  }\r\n                </tbody>\r\n              </table>\r\n            </div>\r\n          </div>\r\n          }\r\n        </div>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}:host{display:block}.calendar-wrap{position:relative}.calendar-wrap .calendar-area{display:table;width:auto;position:relative}.calendar-wrap .calendar-area .legend-area{padding:0 1rem}.calendar-wrap .calendar-area .legend-area>ul>li{padding-right:1.25rem;font-size:.9rem}.calendar-wrap .calendar-area .legend-area ul li span{width:20px;height:20px;float:left;position:relative;text-align:center;margin-right:.5rem;box-shadow:0 1px 2px #666}.calendar-wrap .calendar-area .calendar-items-wrap{display:flex;flex-wrap:wrap}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items{float:left;padding:1rem;margin-bottom:1rem}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item{background:#fff;box-shadow:0 1px 8px #999}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item caption{text-align:center;padding:0;background:#1e5c88;caption-side:top;margin:-.4em -.4em 0}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item caption a{color:#fff;display:inline-block;text-decoration:none}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item caption a:hover{cursor:pointer;background:#696059}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item caption a{padding:.4em}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item caption a>span{font-weight:600}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item th,.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td{font-size:.9rem}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item th a{padding:.3rem .4rem}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td{padding:0;background:#f5f4f2}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a{padding:.8rem;position:relative}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a .marker{position:absolute;font-size:.6rem;line-height:1;padding:1px 2px;background-color:transparent}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a .marker.bottom{text-align:center;bottom:0;left:0;right:0}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a .marker.bottom-left{text-align:left;bottom:0;left:0}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a .marker.bottom-right{text-align:right;bottom:0;right:0}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a .marker.top-left{top:0;left:0;text-align:left}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a .marker.top-right{top:0;right:0;text-align:right}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.other-month{background:#fff;visibility:visible;color:#928b81;border:none}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.other-month a:hover{cursor:auto;box-shadow:none}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.disabled{background:#e1ded9}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.disabled span,.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.disabled em{color:#999}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.disabled:hover,.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.disabled:focus{cursor:not-allowed}.calendar-wrap .calendar-area .calendar-filter{min-width:240px;max-width:240px;margin-right:10px;padding-right:20px;border-right:1px solid rgba(0,0,0,.1)}@media (max-width: 575.98px){.calendar-wrap .calendar-area .calendar-filter{min-width:auto;max-width:none;padding-right:0;margin-right:0;border-right:none;border-bottom:1px solid rgba(0,0,0,.1);padding-bottom:20px;margin-bottom:10px}}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-group{margin-top:1rem}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-group h4{font-size:1rem;font-weight:600}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-group h5{font-size:.8rem;color:#696059}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-group p{font-size:.8rem}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-group:not(:last-child){border-bottom:1px solid rgba(0,0,0,.1);padding-bottom:1rem}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group{margin-top:1rem}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn{box-sizing:border-box;margin:.5%;color:#1e5c88;background:#fff;padding:.3rem;font-size:.7rem;width:13.28%;border:1px solid #999999;box-shadow:0 .1px .2px #999;border-radius:2px;text-decoration:none}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.btn-month{width:24%}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.change-active{border-color:#60b3ee;box-shadow:inset 0 0 0 2px #1e5c88}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn:hover{background-color:#e9ecef}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn:active:focus{color:#1e5c88;background:none}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.active{background:#1e5c88;color:#fff}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.active:active,.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.active:focus{color:#fff}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.active:hover,.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.active:focus{background-color:#1e5c8880;box-shadow:inset 0 0 0 2px #1e5c88}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group.block-buttons .btn{margin:0;width:100%}.calendar-read-state .calendar-area .calendar-items-wrap>.calendar-items>.calendar-item>table>caption{background:#696059}.calendar-read-state .calendar-area .calendar-items-wrap>.calendar-items>.calendar-item td{border:1px solid #ccc}.calendar-read-state .calendar-area .calendar-items-wrap>.calendar-items>.calendar-item td:not(.has-custom-click)>a:hover,.calendar-read-state .calendar-area .calendar-items-wrap>.calendar-items>.calendar-item th>a:hover,.calendar-read-state .calendar-area .calendar-items-wrap>.calendar-items>.calendar-item caption>a:hover{cursor:auto;box-shadow:none;background:inherit}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: TrvLoadingDirective, selector: "[trv-loading]", inputs: ["trv-loading", "container", "showAfterDelay"] }, { kind: "directive", type: NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "pipe", type: AsyncPipe, name: "async" }, { kind: "pipe", type: TrvCapitalizePipe, name: "trvCapitalize" }, { kind: "pipe", type: TrvDateRangeShortPipe, name: "trvDateRangeShort" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvCalendarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-calendar', changeDetection: ChangeDetectionStrategy.OnPush, providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TrvCalendarComponent),
                            multi: true
                        }], imports: [NgClass, TrvLoadingDirective, NgStyle, FaIconComponent, AsyncPipe, TrvCapitalizePipe, TrvDateRangeShortPipe], template: "<div class=\"calendar-wrap\"\r\n     [ngClass]=\"{ 'calendar-read-state': readState }\">\r\n  <div class=\"calendar-area\">\r\n    <div class=\"clearfix d-flex flex-column flex-md-row\"\r\n         [ngClass]=\"{ 'row' : readState }\">\r\n      @if (months && !readState) {\r\n      <div class=\"calendar-filter\">\r\n        <div class=\"calendar-filter-group\">\r\n          <h4>{{labels.filterMonthsHeader}}</h4>\r\n          <div class=\"calendar-filter-button-group calendar-filter-months\">\r\n            @for (month of period.months; track month) {\r\n            <button id=\"{{monthId(month)}}\"\r\n                    type=\"button\"\r\n                    class=\"btn btn-month\"\r\n                    [ngClass]=\"{ 'active': isMonthActive(month), \r\n\t\t\t\t\t\t\t\t\t\t\t\t 'change-active': month === changeMonths.start }\"\r\n                    (click)=\"onShowMonthClick(month)\">\r\n              {{month.toFormat(\"MMM yy'\") | trvCapitalize}}\r\n            </button>\r\n            }\r\n          </div>\r\n        </div>\r\n        <div class=\"calendar-filter-group\">\r\n          <h4>{{labels.toggleDaysHeader}}</h4>\r\n          <div class=\"calendar-filter-button-group\">\r\n            <h5>{{labels.toggleWeekdays}}</h5>\r\n            @for (weekday of calendarWeekdays; track weekday) {\r\n            <button id=\"weekday-{{weekday.name}}\"\r\n                    type=\"button\"\r\n                    class=\"btn\"\r\n                    (click)=\"togglePropertyWeekdayAll(weekday)\">\r\n              {{weekday.name | trvCapitalize}}\r\n            </button>\r\n            }\r\n          </div>\r\n          @if (weekdayDeviations?.length) {\r\n          <div class=\"calendar-filter-button-group\">\r\n            <h5>{{labels.toggleTrafficDays}}</h5>\r\n            @for (trafficday of calendarWeekdays; track trafficday) {\r\n            <button id=\"trafficday-{{trafficday.name}}\"\r\n                    type=\"button\"\r\n                    class=\"btn\"\r\n                    (click)=\"togglePropertyTrafficDayAll(trafficday.name)\">\r\n              {{trafficday.name | trvCapitalize}}\r\n            </button>\r\n            }\r\n          </div>\r\n          }\r\n          <div class=\"calendar-filter-button-group block-buttons pt-3\">\r\n            <button type=\"button\"\r\n                    id=\"calendar-all-days-button\"\r\n                    class=\"btn btn-block\"\r\n                    (click)=\"togglePropertyAllDays()\">\r\n              {{labels.toggleAllDays}}\r\n            </button>\r\n          </div>\r\n        </div>\r\n        <div class=\"calendar-filter-group\"\r\n             [trv-loading]=\"selectedPeriodDescriptionLoading | async\">\r\n          <h4>{{labels.selectionHeader}}</h4>\r\n          <div class=\"calendar-filter-button-group block-buttons\">\r\n            @if (selectedPeriodDescription) {\r\n\r\n            <h5>{{{ from: selectedPeriodDescription.beginDate, to: selectedPeriodDescription.endDate } |\r\n              trvDateRangeShort }}</h5>\r\n            <p>{{selectedPeriodDescription.description}}</p>\r\n\r\n            }\r\n            <button type=\"button\"\r\n                    class=\"btn btn-block\"\r\n                    (click)=\"showSelectedInCalendar()\">\r\n              {{labels.selectionShow}}\r\n            </button>\r\n          </div>\r\n          @if (selectedPeriodDescription?.errorMessage) {\r\n          <div class=\"alert alert-danger\"\r\n               role=\"alert\">{{selectedPeriodDescription?.errorMessage}}</div>\r\n          }\r\n        </div>\r\n      </div>\r\n      }\r\n      <div>\r\n        @if (legend && months) {\r\n        <div class=\"legend-area\">\r\n          <ul class=\"list-unstyled list-inline\">\r\n            @for (legendObj of legend; track legendObj) {\r\n            <li>\r\n              <span class=\"{{legendObj.class}}\"\r\n                    [ngStyle]=\"legendObj.styles ? legendObj.styles : null\">\r\n                @if (legendObj.icon) {\r\n                <fa-icon [icon]=\"legendObj.icon\"></fa-icon>\r\n                }\r\n              </span>\r\n              {{legendObj.label}}\r\n            </li>\r\n            }\r\n          </ul>\r\n        </div>\r\n        }\r\n        <div class=\"calendar-items-wrap\">\r\n          @for (monthCalendar of months; track monthCalendar) {\r\n          <div class=\"calendar-items\">\r\n            <div class=\"calendar-item\">\r\n              <table>\r\n                <caption>\r\n                  <a role=\"button\"\r\n                     tabindex=\"0\"\r\n                     (click)=\"togglePropertyMonth(monthCalendar)\"\r\n                     (keyup.space)=\"togglePropertyMonth(monthCalendar)\">\r\n                    <span>{{monthCalendar.month.monthShort | trvCapitalize}}</span>\r\n                    {{monthCalendar.month.year}}\r\n                  </a>\r\n                </caption>\r\n                <thead>\r\n                  <tr>\r\n                    <th scope=\"col\">{{labels.weekShort}}</th>\r\n                    @for (weekday of monthCalendar.weekdays; track weekday) {\r\n                    <th>\r\n                      <a role=\"button\"\r\n                         tabindex=\"0\"\r\n                         (click)=\"togglePropertyWeekday(monthCalendar, weekday)\"\r\n                         (keyup.space)=\"togglePropertyWeekday(monthCalendar, weekday)\">{{weekday.name |\r\n                        trvCapitalize}}</a>\r\n                    </th>\r\n                    }\r\n                  </tr>\r\n                </thead>\r\n                <tbody>\r\n                  @for (week of monthCalendar.weeks; track week) {\r\n                  <tr>\r\n                    <th scope=\"row\"\r\n                        [ngClass]=\"{ 'current-week' : week.isCurrentWeek }\">\r\n                      <a role=\"button\"\r\n                         tabindex=\"0\"\r\n                         (click)=\"togglePropertyWeek(week)\"\r\n                         (keyup.space)=\"togglePropertyWeek(week)\">{{week.number}}</a>\r\n                    </th>\r\n\r\n                    @for (day of week.days; track day) {\r\n                    <td [ngClass]=\"[\r\n                            !day.isCurrentMonth || day.isOutsideAvailableInterval ? 'other-month' : '',\r\n                            day.isToday ? 'current-day': '',\r\n                            day.selected ? selectedClass: '',\r\n                            day.customClass || '',\r\n                            !!day.customClick ? 'has-custom-click': '',\r\n                            !day.selectable ? 'not-selectable': ''\r\n                          ]\">\r\n                      <a (click)=\"onDayClick(day)\"\r\n                         (keyup.enter)=\"onDayClick(day)\"\r\n                         (keyup.space)=\"onDayClick(day)\"\r\n                         id=\"day-{{day.id}}\"\r\n                         [attr.tabindex]=\"!day.isCurrentMonth || day.isOutsideAvailableInterval || !day.selectable ? -1 : 0\"\r\n                         [ngStyle]=\"day.customStyleFunction ? day.customStyleFunction(day): null\">\r\n                        {{day.number}}\r\n                        @if (day.hasDeviation) {\r\n                        <span class=\"traffic-day marker bottom\">{{day.trafficDay}}</span>\r\n                        }\r\n                        @if (day.isCurrentMonth) {\r\n\r\n                        @for (mark of day.customMarkers; track mark) {\r\n                        <span class=\"marker\"\r\n                              [ngClass]=\"[mark.class || '', mark.placement || '']\"\r\n                              [ngStyle]=\"mark.styles ? mark.styles : null\">\r\n                          @if (mark.icon) {\r\n                          <fa-icon [icon]=\"mark.icon\"></fa-icon>\r\n                          }\r\n                          {{mark.label}}\r\n                        </span>\r\n                        }\r\n\r\n                        }\r\n                        <span class=\"marker top-right\">\r\n                          @if (day.selected && selectedIcon) {\r\n                          <fa-icon [icon]=\"selectedIcon\">\r\n                          </fa-icon>\r\n                          }\r\n                        </span>\r\n                      </a>\r\n                    </td>\r\n                    }\r\n                  </tr>\r\n                  }\r\n                </tbody>\r\n              </table>\r\n            </div>\r\n          </div>\r\n          }\r\n        </div>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}:host{display:block}.calendar-wrap{position:relative}.calendar-wrap .calendar-area{display:table;width:auto;position:relative}.calendar-wrap .calendar-area .legend-area{padding:0 1rem}.calendar-wrap .calendar-area .legend-area>ul>li{padding-right:1.25rem;font-size:.9rem}.calendar-wrap .calendar-area .legend-area ul li span{width:20px;height:20px;float:left;position:relative;text-align:center;margin-right:.5rem;box-shadow:0 1px 2px #666}.calendar-wrap .calendar-area .calendar-items-wrap{display:flex;flex-wrap:wrap}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items{float:left;padding:1rem;margin-bottom:1rem}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item{background:#fff;box-shadow:0 1px 8px #999}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item caption{text-align:center;padding:0;background:#1e5c88;caption-side:top;margin:-.4em -.4em 0}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item caption a{color:#fff;display:inline-block;text-decoration:none}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item caption a:hover{cursor:pointer;background:#696059}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item caption a{padding:.4em}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item caption a>span{font-weight:600}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item th,.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td{font-size:.9rem}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item th a{padding:.3rem .4rem}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td{padding:0;background:#f5f4f2}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a{padding:.8rem;position:relative}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a .marker{position:absolute;font-size:.6rem;line-height:1;padding:1px 2px;background-color:transparent}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a .marker.bottom{text-align:center;bottom:0;left:0;right:0}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a .marker.bottom-left{text-align:left;bottom:0;left:0}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a .marker.bottom-right{text-align:right;bottom:0;right:0}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a .marker.top-left{top:0;left:0;text-align:left}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td>a .marker.top-right{top:0;right:0;text-align:right}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.other-month{background:#fff;visibility:visible;color:#928b81;border:none}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.other-month a:hover{cursor:auto;box-shadow:none}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.disabled{background:#e1ded9}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.disabled span,.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.disabled em{color:#999}.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.disabled:hover,.calendar-wrap .calendar-area .calendar-items-wrap .calendar-items .calendar-item td.disabled:focus{cursor:not-allowed}.calendar-wrap .calendar-area .calendar-filter{min-width:240px;max-width:240px;margin-right:10px;padding-right:20px;border-right:1px solid rgba(0,0,0,.1)}@media (max-width: 575.98px){.calendar-wrap .calendar-area .calendar-filter{min-width:auto;max-width:none;padding-right:0;margin-right:0;border-right:none;border-bottom:1px solid rgba(0,0,0,.1);padding-bottom:20px;margin-bottom:10px}}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-group{margin-top:1rem}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-group h4{font-size:1rem;font-weight:600}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-group h5{font-size:.8rem;color:#696059}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-group p{font-size:.8rem}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-group:not(:last-child){border-bottom:1px solid rgba(0,0,0,.1);padding-bottom:1rem}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group{margin-top:1rem}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn{box-sizing:border-box;margin:.5%;color:#1e5c88;background:#fff;padding:.3rem;font-size:.7rem;width:13.28%;border:1px solid #999999;box-shadow:0 .1px .2px #999;border-radius:2px;text-decoration:none}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.btn-month{width:24%}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.change-active{border-color:#60b3ee;box-shadow:inset 0 0 0 2px #1e5c88}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn:hover{background-color:#e9ecef}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn:active:focus{color:#1e5c88;background:none}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.active{background:#1e5c88;color:#fff}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.active:active,.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.active:focus{color:#fff}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.active:hover,.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group .btn.active:focus{background-color:#1e5c8880;box-shadow:inset 0 0 0 2px #1e5c88}.calendar-wrap .calendar-area .calendar-filter .calendar-filter-button-group.block-buttons .btn{margin:0;width:100%}.calendar-read-state .calendar-area .calendar-items-wrap>.calendar-items>.calendar-item>table>caption{background:#696059}.calendar-read-state .calendar-area .calendar-items-wrap>.calendar-items>.calendar-item td{border:1px solid #ccc}.calendar-read-state .calendar-area .calendar-items-wrap>.calendar-items>.calendar-item td:not(.has-custom-click)>a:hover,.calendar-read-state .calendar-area .calendar-items-wrap>.calendar-items>.calendar-item th>a:hover,.calendar-read-state .calendar-area .calendar-items-wrap>.calendar-items>.calendar-item caption>a:hover{cursor:auto;box-shadow:none;background:inherit}\n"] }]
        }], ctorParameters: () => [{ type: TrvCalendarService }, { type: TrvDateService }, { type: i0.ChangeDetectorRef }, { type: TrvUtilityService }], propDecorators: { legend: [{
                type: Input
            }], readState: [{
                type: Input
            }], selectedDatesRead: [{
                type: Input
            }], selectedClass: [{
                type: Input
            }], selectedIcon: [{
                type: Input
            }], toggleDateCondition: [{
                type: Input
            }], markDayFunction: [{
                type: Input
            }], reloadSubject: [{
                type: Input
            }], labels: [{
                type: Input
            }], bounds: [{
                type: Input
            }], visibleBoundsOnInit: [{
                type: Input
            }], visibleMonthCountOnInit: [{
                type: Input
            }], selectedPeriodDescriptionLoading: [{
                type: Input
            }], selectedPeriodDescription: [{
                type: Input
            }], calendarLoadingChange: [{
                type: Output
            }], weekdayDeviations: [{
                type: Input
            }] } });

class TrvDashboardContentComponent {
    constructor() { }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvDashboardContentComponent, isStandalone: true, selector: "trv-dashboard-content", ngImport: i0, template: "<ng-content>\r\n\r\n</ng-content>", styles: [""], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardContentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-dashboard-content', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<ng-content>\r\n\r\n</ng-content>" }]
        }], ctorParameters: () => [] });

class TrvDashboardFooterComponent {
    faChevronUp = faChevronUp;
    constructor() { }
    scrollToTop() {
        window.scrollTo(0, 0);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvDashboardFooterComponent, isStandalone: true, selector: "trv-dashboard-footer", ngImport: i0, template: "<footer class=\"page-footer\">\r\n  <ng-content></ng-content>\r\n  <button class=\"btn btn-sm btn-outline-light ms-auto\"\r\n          (click)=\"scrollToTop()\"\r\n          aria-label=\"Scroll to top\">\r\n    <fa-icon [icon]=\"faChevronUp\"></fa-icon>\r\n  </button>\r\n</footer>", styles: [":root{--bs-border-color: #ced4da}.page-footer{background-color:#505050;color:#fff;font-size:.875rem;padding:.5rem 1rem;display:flex;align-items:center}\n"], dependencies: [{ kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardFooterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-dashboard-footer', changeDetection: ChangeDetectionStrategy.OnPush, imports: [FaIconComponent], template: "<footer class=\"page-footer\">\r\n  <ng-content></ng-content>\r\n  <button class=\"btn btn-sm btn-outline-light ms-auto\"\r\n          (click)=\"scrollToTop()\"\r\n          aria-label=\"Scroll to top\">\r\n    <fa-icon [icon]=\"faChevronUp\"></fa-icon>\r\n  </button>\r\n</footer>", styles: [":root{--bs-border-color: #ced4da}.page-footer{background-color:#505050;color:#fff;font-size:.875rem;padding:.5rem 1rem;display:flex;align-items:center}\n"] }]
        }], ctorParameters: () => [] });

class OverlayService {
    constructor() { }
    getOriginPosition(position) {
        let originPosition;
        switch (position) {
            case 'top':
                originPosition = { originX: 'center', originY: 'top' };
                break;
            case 'top-start':
                originPosition = { originX: 'start', originY: 'top' };
                break;
            case 'top-end':
                originPosition = { originX: 'end', originY: 'top' };
                break;
            case 'bottom':
                originPosition = { originX: 'center', originY: 'bottom' };
                break;
            case 'bottom-start':
                originPosition = { originX: 'start', originY: 'bottom' };
                break;
            case 'bottom-end':
                originPosition = { originX: 'end', originY: 'bottom' };
                break;
            case 'left':
                originPosition = { originX: 'start', originY: 'center' };
                break;
            case 'left-start':
                originPosition = { originX: 'start', originY: 'top' };
                break;
            case 'left-end':
                originPosition = { originX: 'start', originY: 'bottom' };
                break;
            case 'right':
                originPosition = { originX: 'end', originY: 'center' };
                break;
            case 'right-start':
                originPosition = { originX: 'end', originY: 'top' };
                break;
            case 'right-end':
                originPosition = { originX: 'end', originY: 'bottom' };
                break;
            default:
                throw new Error('Invalid overlay position');
        }
        return originPosition;
    }
    getOverlayPosition(position) {
        let overlayPosition;
        switch (position) {
            case 'top':
                overlayPosition = { overlayX: 'center', overlayY: 'bottom' };
                break;
            case 'top-start':
                overlayPosition = { overlayX: 'start', overlayY: 'bottom' };
                break;
            case 'top-end':
                overlayPosition = { overlayX: 'end', overlayY: 'bottom' };
                break;
            case 'bottom':
                overlayPosition = { overlayX: 'center', overlayY: 'top' };
                break;
            case 'bottom-start':
                overlayPosition = { overlayX: 'start', overlayY: 'top' };
                break;
            case 'bottom-end':
                overlayPosition = { overlayX: 'end', overlayY: 'top' };
                break;
            case 'left':
                overlayPosition = { overlayX: 'end', overlayY: 'center' };
                break;
            case 'left-start':
                overlayPosition = { overlayX: 'end', overlayY: 'top' };
                break;
            case 'left-end':
                overlayPosition = { overlayX: 'end', overlayY: 'bottom' };
                break;
            case 'right':
                overlayPosition = { overlayX: 'start', overlayY: 'center' };
                break;
            case 'right-start':
                overlayPosition = { overlayX: 'start', overlayY: 'top' };
                break;
            case 'right-end':
                overlayPosition = { overlayX: 'start', overlayY: 'bottom' };
                break;
            default:
                throw new Error('Invalid overlay position');
        }
        return overlayPosition;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: OverlayService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: OverlayService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: OverlayService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class TrvTooltipContainerComponent {
    cdr;
    message;
    tooltipClass;
    showTimeoutId;
    onHideAnimationDone = new Subject();
    visibility = 'initial';
    constructor(cdr) {
        this.cdr = cdr;
    }
    isVisible() {
        return this.visibility === 'visible';
    }
    show(delay) {
        this.showTimeoutId = setTimeout(() => {
            this.visibility = 'visible';
            this.showTimeoutId = null;
            this.cdr.markForCheck();
        }, delay);
    }
    hide() {
        this.visibility = 'hidden';
        this.cdr.markForCheck();
    }
    animationDone(event) {
        if (event.toState === 'hidden') {
            this.onHideAnimationDone.next(event);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTooltipContainerComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvTooltipContainerComponent, isStandalone: true, selector: "trv-tooltip-container", inputs: { message: "message", tooltipClass: "tooltipClass" }, ngImport: i0, template: "<div class=\"trv-tooltip\"\r\n     [ngClass]=\"tooltipClass\"\r\n     [@trvPopAnimation]=\"visibility\"\r\n     (@trvPopAnimation.done)=\"animationDone($event)\">\r\n  {{message}}\r\n</div>", styles: [".trv-tooltip{position:relative;padding:.5rem;background-color:#222;color:#fff;font-size:12px;border-radius:4px;max-width:250px;padding-left:8px;padding-right:8px;margin:8px;white-space:pre-line}::ng-deep .cdk-overlay-pane>* .trv-tooltip:after{border:solid transparent;content:\" \";height:0;width:0;position:absolute;pointer-events:none;border-width:5px}::ng-deep .cdk-overlay-pane.tooltip-top>* .trv-tooltip:after{top:100%;left:50%;border-top-color:#222;margin-left:-5px}::ng-deep .cdk-overlay-pane.tooltip-bottom>* .trv-tooltip:after{bottom:100%;left:50%;border-bottom-color:#222;margin-left:-5px}::ng-deep .cdk-overlay-pane.tooltip-right>* .trv-tooltip:after{right:100%;top:50%;border-right-color:#222;margin-top:-5px}::ng-deep .cdk-overlay-pane.tooltip-left>* .trv-tooltip:after{left:100%;top:50%;border-left-color:#222;margin-top:-5px}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], animations: [trvPopAnimation], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTooltipContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-tooltip-container', changeDetection: ChangeDetectionStrategy.OnPush, animations: [trvPopAnimation], imports: [NgClass], template: "<div class=\"trv-tooltip\"\r\n     [ngClass]=\"tooltipClass\"\r\n     [@trvPopAnimation]=\"visibility\"\r\n     (@trvPopAnimation.done)=\"animationDone($event)\">\r\n  {{message}}\r\n</div>", styles: [".trv-tooltip{position:relative;padding:.5rem;background-color:#222;color:#fff;font-size:12px;border-radius:4px;max-width:250px;padding-left:8px;padding-right:8px;margin:8px;white-space:pre-line}::ng-deep .cdk-overlay-pane>* .trv-tooltip:after{border:solid transparent;content:\" \";height:0;width:0;position:absolute;pointer-events:none;border-width:5px}::ng-deep .cdk-overlay-pane.tooltip-top>* .trv-tooltip:after{top:100%;left:50%;border-top-color:#222;margin-left:-5px}::ng-deep .cdk-overlay-pane.tooltip-bottom>* .trv-tooltip:after{bottom:100%;left:50%;border-bottom-color:#222;margin-left:-5px}::ng-deep .cdk-overlay-pane.tooltip-right>* .trv-tooltip:after{right:100%;top:50%;border-right-color:#222;margin-top:-5px}::ng-deep .cdk-overlay-pane.tooltip-left>* .trv-tooltip:after{left:100%;top:50%;border-left-color:#222;margin-top:-5px}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { message: [{
                type: Input
            }], tooltipClass: [{
                type: Input
            }] } });

class TrvTooltipDirective {
    overlay;
    elementRef;
    overlayService;
    message;
    tooltipDisabled;
    tooltipClass;
    showDelay;
    manualToggle = false;
    get position() { return this._position; }
    set position(value) {
        if (value !== this._position) {
            this._position = value;
            if (this.overlayRef) {
                this.setPosition();
                this.updatePosition();
            }
        }
    }
    ngUnsubsbribe = new Subject();
    _position;
    overlayRef;
    tooltipInstance;
    constructor(overlay, elementRef, overlayService) {
        this.overlay = overlay;
        this.elementRef = elementRef;
        this.overlayService = overlayService;
    }
    ngOnInit() {
        fromEvent(window, 'scroll').pipe(takeUntil(this.ngUnsubsbribe), throttleTime(300), // emits once, then ignores subsequent emissions for 300ms, repeat...
        tap(event => {
            if (this.manualToggle) {
                return;
            }
            this.hide();
        })).subscribe();
    }
    ngOnDestroy() {
        this.ngUnsubsbribe.next();
        this.ngUnsubsbribe.complete();
        this.overlayRef?.dispose();
    }
    onMouseEnter() {
        if (this.manualToggle) {
            return;
        }
        this.show();
    }
    onMouseLeave() {
        if (this.manualToggle) {
            return;
        }
        this.hide();
    }
    show() {
        if (this.tooltipDisabled || !this.message || this.overlayRef?.hasAttached()) {
            return;
        }
        this.overlayRef = this.overlayRef ?? this.createOverlay();
        this.tooltipInstance = this.tooltipInstance ?? this.attachContainerAndGetInstance();
        this.tooltipInstance.message = this.message;
        this.tooltipInstance.tooltipClass = this.tooltipClass;
        this.initSubscribers();
        this.tooltipInstance.show(this.showDelay ?? 150);
        this.tooltipInstance.onHideAnimationDone
            .pipe(takeUntil(this.ngUnsubsbribe))
            .subscribe(() => {
            this.overlayRef.detach();
            this.tooltipInstance = null;
        });
    }
    hide() {
        if (!this.overlayRef) {
            return;
        }
        this.tooltipInstance?.hide();
    }
    updatePosition() {
        if (!this.overlayRef || !this.tooltipInstance?.isVisible()) {
            return;
        }
        this.overlayRef.updatePosition();
    }
    createOverlay() {
        const config = this.getOverlayConfig();
        return this.overlay.create(config);
    }
    attachContainerAndGetInstance() {
        const containerPortal = new ComponentPortal(TrvTooltipContainerComponent);
        const containerRef = this.overlayRef.attach(containerPortal);
        return containerRef.instance;
    }
    getOverlayConfig() {
        return new OverlayConfig({
            positionStrategy: this.getOverlayPositionStrategy(),
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            hasBackdrop: false
        });
    }
    getOverlayPositionStrategy() {
        return this.overlay.position()
            .flexibleConnectedTo(this.elementRef)
            .withPositions(this.getPositions())
            .withFlexibleDimensions(false)
            .withPush(false);
    }
    getPositions() {
        if (this.position) {
            return this.getUserPositions();
        }
        else {
            return this.getDefaultPositions();
        }
    }
    getUserPositions() {
        const positions = [];
        for (const userPosition of this.position) {
            const originPosition = this.overlayService.getOriginPosition(userPosition);
            const overlayPosition = this.overlayService.getOverlayPosition(userPosition);
            const position = { ...originPosition, ...overlayPosition };
            position.panelClass = `tooltip-${userPosition}`;
            positions.push(position);
        }
        return positions;
    }
    getDefaultPositions() {
        const top = { ...this.overlayService.getOriginPosition('top'), ...this.overlayService.getOverlayPosition('top'), panelClass: 'tooltip-top' };
        const right = { ...this.overlayService.getOriginPosition('right'), ...this.overlayService.getOverlayPosition('right'), panelClass: 'tooltip-right' };
        const bottom = { ...this.overlayService.getOriginPosition('bottom'), ...this.overlayService.getOverlayPosition('bottom'), panelClass: 'tooltip-bottom' };
        const left = { ...this.overlayService.getOriginPosition('left'), ...this.overlayService.getOverlayPosition('left'), panelClass: 'tooltip-left' };
        return [top, right, bottom, left];
    }
    setPosition() {
        const position = this.overlayRef.getConfig().positionStrategy;
        position.withPositions(this.getPositions());
    }
    initSubscribers() {
        this.overlayRef.keydownEvents().pipe(filter(event => event.key === 'Escape' && !hasModifierKey(event)))
            .pipe(takeUntil(this.ngUnsubsbribe))
            .subscribe(() => this.hide());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTooltipDirective, deps: [{ token: i1.Overlay }, { token: i0.ElementRef }, { token: OverlayService }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvTooltipDirective, isStandalone: true, selector: "[trvTooltip]", inputs: { message: ["trvTooltip", "message"], tooltipDisabled: "tooltipDisabled", tooltipClass: "tooltipClass", showDelay: "showDelay", manualToggle: "manualToggle", position: "position" }, host: { listeners: { "mouseenter": "onMouseEnter()", "mouseleave": "onMouseLeave()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[trvTooltip]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i1.Overlay }, { type: i0.ElementRef }, { type: OverlayService }], propDecorators: { message: [{
                type: Input,
                args: ['trvTooltip']
            }], tooltipDisabled: [{
                type: Input
            }], tooltipClass: [{
                type: Input
            }], showDelay: [{
                type: Input
            }], manualToggle: [{
                type: Input
            }], position: [{
                type: Input
            }], onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }], onMouseLeave: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });

class NavigationItem {
    description;
    hidden;
    icon;
    iconCounter;
    iconCounterClasses;
    label;
    key;
    parent;
    subIcon;
    subNavigationItems;
    hasActiveChild;
    dashboardHidden;
    disableNavigation;
    //extended properties (behövs för att kunna använda dem i html-templates)
    routerLink;
    isCollapsable;
    isCollapsed;
    onClick;
    active;
    href;
    isDivider;
    tooltip;
    overflow;
    constructor(obj) {
        this.active = false;
        this.description = obj.description || '';
        this.hidden = obj.hidden || false;
        this.icon = obj.icon || null;
        this.label = obj.label || '';
        this.subIcon = obj.subIcon || null;
        this.subNavigationItems = obj.subNavigationItems || [];
        this.dashboardHidden = obj.dashboardHidden || false;
        this.tooltip = obj.tooltip;
        this.overflow = obj.overflow;
        this.iconCounter = obj.iconCounter;
        this.iconCounterClasses = obj.iconCounterClasses;
        this.disableNavigation = obj.disableNavigation;
    }
}
class RouterNavigationItem extends NavigationItem {
    isRealBrowserLink = false;
    queryParams;
    constructor(obj) {
        super(obj);
        this.isCollapsable = obj.isCollapsable || false;
        this.isCollapsed = obj.isCollapsable ? true : false;
        this.routerLink = obj.routerLink || '';
        this.isRealBrowserLink = obj.isRealBrowserLink || false;
        this.queryParams = obj.queryParams;
        this.key = this.routerLink.replace(/\//g, '').toLowerCase();
    }
}
class ClickNavigationItem extends NavigationItem {
    constructor(obj) {
        super(obj);
        this.onClick = obj.onClick;
        this.active = obj.active;
        this.key = this.label.replace(/[\W_]+/g, '').toLowerCase();
    }
}
class ExternalNavigationItem extends NavigationItem {
    constructor(obj) {
        super(obj);
        this.href = obj.href;
        this.key = this.label.replace(/[\W_]+/g, '').toLowerCase();
    }
}
class DividerNavigationItem extends NavigationItem {
    constructor(obj) {
        super(obj);
        this.isDivider = obj.isDivider;
    }
}
class SelectNavigationItem extends NavigationItem {
    constructor(obj) {
        super(obj);
        this.select = obj.select;
    }
    select;
}

class TrvDashboardService {
    openChange = new EventEmitter();
    modeChange = new EventEmitter();
    hideMenuChange = new EventEmitter();
    hideDashboardChange = new EventEmitter();
    sideNavShowLessChange = new EventEmitter();
    detectChanges = new EventEmitter();
    customRouterLink = new EventEmitter();
    mode;
    sideNavOpen;
    navItems;
    hideMenu;
    hideDashboard;
    sideNavShowLess;
    constructor() {
        this.openChange.subscribe((isOpen) => {
            this.sideNavOpen = isOpen;
        });
    }
    setMenuVisibility(visible) {
        this.hideMenu = !visible;
        this.hideMenuChange.emit(this.hideMenu);
    }
    setDashboardVisibility(visible) {
        const hide = !visible;
        if (this.hideDashboard !== hide) {
            this.hideDashboard = !visible;
            this.hideDashboardChange.emit(this.hideDashboard);
        }
    }
    setNavItems(navItems) {
        this.setParent(navItems);
        this.navItems = navItems;
        return this.navItems;
    }
    setSideNavShowLess(showLess) {
        this.sideNavShowLess = showLess;
        this.sideNavShowLessChange.emit(this.sideNavShowLess);
    }
    getNavItems() {
        return this.navItems;
    }
    getNavItemByUrl(url) {
        const urlRegexp = new RegExp(/[?#]/);
        const urlNoQueryNoHash = url.split(urlRegexp)[0];
        return this.searchRouterItems(this.navItems, urlNoQueryNoHash);
    }
    isEqualPath = (a, b) => {
        if (!a || !b) {
            return false;
        }
        const keyA = this.getTrimmedUrl(a);
        const keyB = this.getTrimmedUrl(b);
        return keyA === keyB;
    };
    searchRouterItems(navItems, url) {
        if (navItems) {
            for (let i = 0; i < navItems.length; i++) {
                const result = this.findRouterNavItem(navItems[i], url);
                if (result != null) {
                    return result;
                }
            }
        }
        return null;
    }
    findRouterNavItem(navItem, url) {
        if (!(navItem instanceof RouterNavigationItem)) {
            return null;
        }
        if (this.isEqualPath(url, navItem.routerLink.split('?')[0])) {
            return navItem;
        }
        else if (navItem.subNavigationItems) {
            return this.searchRouterItems(navItem.subNavigationItems, url);
        }
        return null;
    }
    setParent(navItems, parent) {
        navItems.forEach((ni) => {
            ni.parent = parent;
            if (ni.subNavigationItems) {
                this.setParent(ni.subNavigationItems, ni);
            }
        });
    }
    getTrimmedUrl(url) {
        //Ta bort slash
        const trimmed = url.replace(/\//g, '').toLowerCase();
        if (trimmed) {
            //Ta bort query-string och fragment/ankarlänkar
            const stippedQueryString = trimmed.match(/^[^?|^#]+/);
            return stippedQueryString ? stippedQueryString[0] : '';
        }
        else {
            return trimmed;
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [], propDecorators: { openChange: [{
                type: Output
            }], modeChange: [{
                type: Output
            }], hideMenuChange: [{
                type: Output
            }], hideDashboardChange: [{
                type: Output
            }], sideNavShowLessChange: [{
                type: Output
            }], detectChanges: [{
                type: Output
            }], customRouterLink: [{
                type: Output
            }] } });

class TrvDashboardNavBlocksComponent {
    router;
    dashboardService;
    trvUtilityService;
    cdr;
    ngUnsubscribe = new Subject();
    navigationItems;
    columnClass = 'col-xl-3 col-lg-4 col-md-6 col-sm-6';
    constructor(router, dashboardService, trvUtilityService, cdr) {
        this.router = router;
        this.dashboardService = dashboardService;
        this.trvUtilityService = trvUtilityService;
        this.cdr = cdr;
        this.dashboardService.detectChanges
            .pipe(takeUntil$1(this.ngUnsubscribe))
            .subscribe(() => {
            this.trvUtilityService.detectChanges(this.cdr);
        });
    }
    onResize($event) {
        this.setBlockHeight();
    }
    ngAfterViewInit() {
        this.setBlockHeight();
    }
    navigate(item) {
        if (item.disableNavigation) {
            return;
        }
        if (item.onClick) {
            item.onClick();
        }
        else if (item.routerLink) {
            this.router.navigateByUrl(item.routerLink);
        }
    }
    setBlockHeight() {
        if (!this.navigationItems) {
            return;
        }
        let maxHeight = 0;
        const blocks = document.getElementsByClassName('nav-item-block');
        for (let i = 0; i < blocks.length; i++) {
            const block = blocks[i];
            block.style.height = 'auto';
            if (block.clientHeight > maxHeight) {
                maxHeight = block.clientHeight;
            }
        }
        for (let i = 0; i < blocks.length; i++) {
            const block = blocks[i];
            block.style.height = maxHeight + 'px';
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardNavBlocksComponent, deps: [{ token: i2.Router }, { token: TrvDashboardService }, { token: TrvUtilityService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvDashboardNavBlocksComponent, isStandalone: true, selector: "trv-dashboard-nav-blocks", inputs: { navigationItems: "navigationItems", columnClass: "columnClass" }, host: { listeners: { "window:resize": "onResize($event)" } }, ngImport: i0, template: "@if (navigationItems?.length) {\r\n<div class=\"row\">\r\n  @for (sub of navigationItems; track sub) {\r\n  @if (!sub.isDivider && !sub.hidden) {\r\n  <div [ngClass]=\"columnClass\"\r\n       [trvTooltip]=\"sub.tooltip\">\r\n    @if (!sub.href) {\r\n    <a tabindex=\"0\"\r\n       (click)=\"navigate(sub)\"\r\n       (keyup.space)=\"navigate(sub)\">\r\n      <ng-container *ngTemplateOutlet=\"navItemBlock; context: { sub: sub }\"></ng-container>\r\n    </a>\r\n    }\r\n    @if (sub.href) {\r\n    <a [href]=\"sub.href\"\r\n       target=\"_blank\">\r\n      <ng-container *ngTemplateOutlet=\"navItemBlock; context: { sub: sub }\"></ng-container>\r\n    </a>\r\n    }\r\n  </div>\r\n  }\r\n  }\r\n</div>\r\n}\r\n\r\n<ng-template #navItemBlock\r\n             let-sub=\"sub\">\r\n  <div class=\"nav-item-block\"\r\n       [ngClass]=\"{ 'active': sub.active }\">\r\n    <fa-layers class=\"combo-icon\">\r\n      @if (sub.icon) {\r\n      <fa-icon [icon]=\"sub.icon\"\r\n               class=\"main-icon\"></fa-icon>\r\n      }\r\n      @if (sub.subIcon && !sub.iconLabel) {\r\n      <fa-icon [icon]=\"sub.subIcon\"\r\n               class=\"sub-icon\"></fa-icon>\r\n      }\r\n      @if (sub.iconCounter) {\r\n      <fa-layers-counter [content]=\"sub.iconCounter\"\r\n                         [class]=\"sub.iconCounterClasses\">\r\n      </fa-layers-counter>\r\n      }\r\n    </fa-layers>\r\n    <h3 [ngClass]=\"sub.overflow ? 'labelOverflow-'+ sub.overflow : ''\">{{sub.label}}</h3>\r\n    <p>{{sub.description}}</p>\r\n  </div>\r\n</ng-template>", styles: [":root{--bs-border-color: #ced4da}a{text-decoration:none}:host ::ng-deep .nav-item-block{transition:all ease .25s;position:relative;overflow:hidden;text-align:center;background-color:#fff;box-shadow:0 .35em .35em #0000001a;padding:2em 1.5em;margin:0 0 30px}:host ::ng-deep .nav-item-block:after{content:\"\";position:absolute;right:0;left:0;bottom:-4px;height:4px;transition:all ease .25s}:host ::ng-deep .nav-item-block .combo-icon{min-width:1.3em}:host ::ng-deep .nav-item-block .main-icon{transition:all ease .25s;color:#1e5c88;opacity:.7}:host ::ng-deep .nav-item-block.active{background-color:#e9e9e9}:host ::ng-deep .nav-item-block:hover{background-color:#bce3ff59;box-shadow:0 .05em .25em #0000004d;cursor:pointer}:host ::ng-deep .nav-item-block:hover .main-icon{color:#1e5c88;opacity:1}:host ::ng-deep .nav-item-block:hover:after{content:\"\";bottom:0;background:#1e5c88}:host ::ng-deep .nav-item-block:hover .combo-icon .sub-icon>svg{background:#000}:host ::ng-deep .nav-item-block h3{font-size:1.2em;font-weight:600;color:#1e5c88;margin-top:1em}:host ::ng-deep .nav-item-block p{font-size:.9em;color:#212529}.labelOverflow-visible{overflow:visible}.labelOverflow-hidden{white-space:nowrap;text-overflow:ellipsis;overflow:hidden}.labelOverflow-scroll{overflow:scroll}.labelOverflow-auto{overflow:auto}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: TrvTooltipDirective, selector: "[trvTooltip]", inputs: ["trvTooltip", "tooltipDisabled", "tooltipClass", "showDelay", "manualToggle", "position"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: FaLayersComponent, selector: "fa-layers", inputs: ["size", "fixedWidth"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "component", type: FaLayersCounterComponent, selector: "fa-layers-counter", inputs: ["content", "title", "position"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardNavBlocksComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-dashboard-nav-blocks', changeDetection: ChangeDetectionStrategy.OnPush, imports: [NgClass, TrvTooltipDirective, NgTemplateOutlet, FaLayersComponent, FaIconComponent, FaLayersCounterComponent], template: "@if (navigationItems?.length) {\r\n<div class=\"row\">\r\n  @for (sub of navigationItems; track sub) {\r\n  @if (!sub.isDivider && !sub.hidden) {\r\n  <div [ngClass]=\"columnClass\"\r\n       [trvTooltip]=\"sub.tooltip\">\r\n    @if (!sub.href) {\r\n    <a tabindex=\"0\"\r\n       (click)=\"navigate(sub)\"\r\n       (keyup.space)=\"navigate(sub)\">\r\n      <ng-container *ngTemplateOutlet=\"navItemBlock; context: { sub: sub }\"></ng-container>\r\n    </a>\r\n    }\r\n    @if (sub.href) {\r\n    <a [href]=\"sub.href\"\r\n       target=\"_blank\">\r\n      <ng-container *ngTemplateOutlet=\"navItemBlock; context: { sub: sub }\"></ng-container>\r\n    </a>\r\n    }\r\n  </div>\r\n  }\r\n  }\r\n</div>\r\n}\r\n\r\n<ng-template #navItemBlock\r\n             let-sub=\"sub\">\r\n  <div class=\"nav-item-block\"\r\n       [ngClass]=\"{ 'active': sub.active }\">\r\n    <fa-layers class=\"combo-icon\">\r\n      @if (sub.icon) {\r\n      <fa-icon [icon]=\"sub.icon\"\r\n               class=\"main-icon\"></fa-icon>\r\n      }\r\n      @if (sub.subIcon && !sub.iconLabel) {\r\n      <fa-icon [icon]=\"sub.subIcon\"\r\n               class=\"sub-icon\"></fa-icon>\r\n      }\r\n      @if (sub.iconCounter) {\r\n      <fa-layers-counter [content]=\"sub.iconCounter\"\r\n                         [class]=\"sub.iconCounterClasses\">\r\n      </fa-layers-counter>\r\n      }\r\n    </fa-layers>\r\n    <h3 [ngClass]=\"sub.overflow ? 'labelOverflow-'+ sub.overflow : ''\">{{sub.label}}</h3>\r\n    <p>{{sub.description}}</p>\r\n  </div>\r\n</ng-template>", styles: [":root{--bs-border-color: #ced4da}a{text-decoration:none}:host ::ng-deep .nav-item-block{transition:all ease .25s;position:relative;overflow:hidden;text-align:center;background-color:#fff;box-shadow:0 .35em .35em #0000001a;padding:2em 1.5em;margin:0 0 30px}:host ::ng-deep .nav-item-block:after{content:\"\";position:absolute;right:0;left:0;bottom:-4px;height:4px;transition:all ease .25s}:host ::ng-deep .nav-item-block .combo-icon{min-width:1.3em}:host ::ng-deep .nav-item-block .main-icon{transition:all ease .25s;color:#1e5c88;opacity:.7}:host ::ng-deep .nav-item-block.active{background-color:#e9e9e9}:host ::ng-deep .nav-item-block:hover{background-color:#bce3ff59;box-shadow:0 .05em .25em #0000004d;cursor:pointer}:host ::ng-deep .nav-item-block:hover .main-icon{color:#1e5c88;opacity:1}:host ::ng-deep .nav-item-block:hover:after{content:\"\";bottom:0;background:#1e5c88}:host ::ng-deep .nav-item-block:hover .combo-icon .sub-icon>svg{background:#000}:host ::ng-deep .nav-item-block h3{font-size:1.2em;font-weight:600;color:#1e5c88;margin-top:1em}:host ::ng-deep .nav-item-block p{font-size:.9em;color:#212529}.labelOverflow-visible{overflow:visible}.labelOverflow-hidden{white-space:nowrap;text-overflow:ellipsis;overflow:hidden}.labelOverflow-scroll{overflow:scroll}.labelOverflow-auto{overflow:auto}\n"] }]
        }], ctorParameters: () => [{ type: i2.Router }, { type: TrvDashboardService }, { type: TrvUtilityService }, { type: i0.ChangeDetectorRef }], propDecorators: { navigationItems: [{
                type: Input
            }], columnClass: [{
                type: Input
            }], onResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });

class TrvDashboardPageContentComponent {
    constructor() { }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardPageContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvDashboardPageContentComponent, isStandalone: true, selector: "trv-dashboard-page-content", ngImport: i0, template: "<div class=\"container-fluid\">\r\n  <div class=\"row\">\r\n    <div class=\"col page-container\">\r\n      <ng-content></ng-content>\r\n    </div>\r\n  </div>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.page-container{padding-top:.75rem}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardPageContentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-dashboard-page-content', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<div class=\"container-fluid\">\r\n  <div class=\"row\">\r\n    <div class=\"col page-container\">\r\n      <ng-content></ng-content>\r\n    </div>\r\n  </div>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.page-container{padding-top:.75rem}\n"] }]
        }], ctorParameters: () => [] });

//R = komponenten som visas i modalen
//T = response som skickas från modalen vid stängning
class TrvModalRef {
    overlayRef;
    containerInstance;
    //Komponenten som öppnandes i modalen
    componentInstance;
    _closedWithType = new Subject();
    _closed = new Subject();
    constructor(overlayRef, containerInstance) {
        this.overlayRef = overlayRef;
        this.containerInstance = containerInstance;
        overlayRef.keydownEvents()
            .pipe(filter(event => {
            return event.key === 'Escape' && !hasModifierKey(event);
        }))
            .subscribe(event => {
            event.preventDefault();
            this.dismiss();
        });
    }
    afterCloseWithType() {
        return this._closedWithType.asObservable();
    }
    afterClose() {
        return this._closed.asObservable();
    }
    close(data) {
        this._close('close', data);
    }
    dismiss(dismissType) {
        this._close(dismissType || 'dismiss', null);
    }
    updatePosition(position) {
        const strategy = this.overlayRef.getConfig().positionStrategy;
        if (position && (position.left || position.right)) {
            position.left ? strategy.left(position.left) : strategy.right(position.right);
        }
        else {
            strategy.centerHorizontally();
        }
        if (position && (position.top || position.bottom)) {
            position.top ? strategy.top(position.top) : strategy.bottom(position.bottom);
        }
        else {
            strategy.centerVertically();
        }
        this.overlayRef.updatePosition();
        return this;
    }
    _close(type, data) {
        this.overlayRef.detachBackdrop();
        this._closedWithType.next({ closingEventType: type, result: data });
        this._closedWithType.complete();
        if (type === 'close') {
            this._closed.next(data);
        }
        this._closed.complete();
        //Disposa modalen först då animationen är klar
        this.containerInstance.animationStateChanged.pipe(take(1)).subscribe(event => this.overlayRef.dispose());
        //Trigga exit-animation
        this.containerInstance.startExitAnimation();
    }
}

class TrvSafeHtmlPipe {
    sanitizer;
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(unsafeString) {
        return this.sanitizer.bypassSecurityTrustHtml(unsafeString);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvSafeHtmlPipe, deps: [{ token: i1$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.1", ngImport: i0, type: TrvSafeHtmlPipe, isStandalone: true, name: "trvSafeHtml" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvSafeHtmlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'trvSafeHtml',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i1$1.DomSanitizer }] });

class TrvShouldFocusDirective {
    elementRef;
    shouldFocus;
    clickOnFocus;
    keyEventsOnFocus;
    element;
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.element = elementRef.nativeElement;
    }
    ngOnInit() { }
    // ngOnChanges(changes: SimpleChanges) {
    // 	if (changes.shouldFocus && !changes.shouldFocus.isFirstChange() && changes.shouldFocus.currentValue === true) {
    // 		this.element.focus();
    // 	}
    // }
    ngAfterViewInit() {
        if (this.shouldFocus) {
            setTimeout(() => {
                this.element.focus();
                if (this.element.nodeName === 'INPUT') {
                    const inputElement = this.element;
                    inputElement.select();
                }
                if (this.clickOnFocus != null) {
                    this.element.click();
                }
            }, 50);
        }
    }
    onKeyDown(event) {
        if (this.keyEventsOnFocus == null) {
            return;
        }
        if (event.keyCode === 13 || event.keyCode === 32) {
            event.preventDefault();
            this.element.click();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvShouldFocusDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvShouldFocusDirective, isStandalone: true, selector: "[trvShouldFocus]", inputs: { shouldFocus: ["trvShouldFocus", "shouldFocus"], clickOnFocus: "clickOnFocus", keyEventsOnFocus: "keyEventsOnFocus" }, host: { listeners: { "keydown": "onKeyDown($event)" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvShouldFocusDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[trvShouldFocus]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { shouldFocus: [{
                type: Input,
                args: ['trvShouldFocus']
            }], clickOnFocus: [{
                type: Input
            }], keyEventsOnFocus: [{
                type: Input
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class TrvConfirmModalComponent {
    modalRef;
    title;
    message;
    type;
    yesButtonTitle;
    noButtonTitle;
    faCheck = faCheck;
    faTimes = faTimes;
    faTrashAlt = faTrashAlt;
    constructor(modalRef) {
        this.modalRef = modalRef;
    }
    accept() {
        this.modalRef.close(true);
    }
    dismiss(type) {
        this.modalRef.dismiss(type);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvConfirmModalComponent, deps: [{ token: TrvModalRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvConfirmModalComponent, isStandalone: true, selector: "ng-component", inputs: { title: "title", message: "message", type: "type", yesButtonTitle: "yesButtonTitle", noButtonTitle: "noButtonTitle" }, ngImport: i0, template: "<div class=\"modal-header confirm-modal-header\">\r\n  <h1 class=\"modal-title\"\r\n      id=\"modal-title\">{{title}}</h1>\r\n  <button type=\"button\"\r\n          class=\"btn-close\"\r\n          aria-describedby=\"modal-title\"\r\n          [trvShouldFocus]=\"true\"\r\n          (click)=\"dismiss()\">\r\n  </button>\r\n</div>\r\n<div class=\"modal-body\">\r\n  <p [innerHtml]=\"message | trvSafeHtml\"></p>\r\n</div>\r\n<div class=\"modal-footer\">\r\n  @if (type === 'confirm' || type === 'confirmDelete') {\r\n\r\n  <button type=\"button\"\r\n          class=\"btn btn-secondary\"\r\n          (click)=\"dismiss('deny')\">\r\n    <fa-icon [icon]=\"faTimes\"></fa-icon>\r\n    {{noButtonTitle || 'Nej'}}\r\n  </button>\r\n  <button type=\"button\"\r\n          class=\"btn\"\r\n          (click)=\"accept()\"\r\n          [ngClass]=\"{'btn-success': type === 'confirm', 'btn-danger': type === 'confirmDelete'}\"\r\n          [trvShouldFocus]=\"true\">\r\n    @if (type === 'confirm') {\r\n    <fa-icon [icon]=\"faCheck\"></fa-icon>\r\n    }\r\n    @if (type === 'confirmDelete') {\r\n    <fa-icon [icon]=\"faTrashAlt\"></fa-icon>\r\n    }\r\n    {{yesButtonTitle || 'Ja'}}\r\n  </button>\r\n\r\n  }\r\n  @if (type === 'accept') {\r\n\r\n  <button type=\"btn\"\r\n          class=\"btn btn-success\"\r\n          (click)=\"accept()\"\r\n          [trvShouldFocus]=\"true\">\r\n    <fa-icon [icon]=\"faCheck\"></fa-icon>\r\n    {{yesButtonTitle || 'Ja'}}\r\n  </button>\r\n\r\n  }\r\n  @if (type === 'info') {\r\n  <button type=\"button\"\r\n          class=\"btn btn-link\"\r\n          (click)=\"dismiss()\"\r\n          [trvShouldFocus]=\"true\">\r\n    St\u00E4ng\r\n  </button>\r\n  }\r\n</div>", styles: [""], dependencies: [{ kind: "directive", type: TrvShouldFocusDirective, selector: "[trvShouldFocus]", inputs: ["trvShouldFocus", "clickOnFocus", "keyEventsOnFocus"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "pipe", type: TrvSafeHtmlPipe, name: "trvSafeHtml" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvConfirmModalComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, imports: [
                        TrvShouldFocusDirective,
                        FaIconComponent,
                        NgClass,
                        TrvSafeHtmlPipe,
                    ], template: "<div class=\"modal-header confirm-modal-header\">\r\n  <h1 class=\"modal-title\"\r\n      id=\"modal-title\">{{title}}</h1>\r\n  <button type=\"button\"\r\n          class=\"btn-close\"\r\n          aria-describedby=\"modal-title\"\r\n          [trvShouldFocus]=\"true\"\r\n          (click)=\"dismiss()\">\r\n  </button>\r\n</div>\r\n<div class=\"modal-body\">\r\n  <p [innerHtml]=\"message | trvSafeHtml\"></p>\r\n</div>\r\n<div class=\"modal-footer\">\r\n  @if (type === 'confirm' || type === 'confirmDelete') {\r\n\r\n  <button type=\"button\"\r\n          class=\"btn btn-secondary\"\r\n          (click)=\"dismiss('deny')\">\r\n    <fa-icon [icon]=\"faTimes\"></fa-icon>\r\n    {{noButtonTitle || 'Nej'}}\r\n  </button>\r\n  <button type=\"button\"\r\n          class=\"btn\"\r\n          (click)=\"accept()\"\r\n          [ngClass]=\"{'btn-success': type === 'confirm', 'btn-danger': type === 'confirmDelete'}\"\r\n          [trvShouldFocus]=\"true\">\r\n    @if (type === 'confirm') {\r\n    <fa-icon [icon]=\"faCheck\"></fa-icon>\r\n    }\r\n    @if (type === 'confirmDelete') {\r\n    <fa-icon [icon]=\"faTrashAlt\"></fa-icon>\r\n    }\r\n    {{yesButtonTitle || 'Ja'}}\r\n  </button>\r\n\r\n  }\r\n  @if (type === 'accept') {\r\n\r\n  <button type=\"btn\"\r\n          class=\"btn btn-success\"\r\n          (click)=\"accept()\"\r\n          [trvShouldFocus]=\"true\">\r\n    <fa-icon [icon]=\"faCheck\"></fa-icon>\r\n    {{yesButtonTitle || 'Ja'}}\r\n  </button>\r\n\r\n  }\r\n  @if (type === 'info') {\r\n  <button type=\"button\"\r\n          class=\"btn btn-link\"\r\n          (click)=\"dismiss()\"\r\n          [trvShouldFocus]=\"true\">\r\n    St\u00E4ng\r\n  </button>\r\n  }\r\n</div>" }]
        }], ctorParameters: () => [{ type: TrvModalRef }], propDecorators: { title: [{
                type: Input
            }], message: [{
                type: Input
            }], type: [{
                type: Input
            }], yesButtonTitle: [{
                type: Input
            }], noButtonTitle: [{
                type: Input
            }] } });

class TrvModalConfig {
    size;
    openAsPanel;
    backdropClass;
    position;
    hasBackdrop;
    panelClass;
    dialogClass;
    disposeOnBackdropClick;
    scrollStrategy;
    positionStrategy;
    name;
}

class TrvModalContainerComponent extends BasePortalOutlet {
    _portalOutlet;
    animationStateChanged = new Subject();
    state;
    hostClasses;
    name = 'trv-modal-container';
    constructor(config) {
        super();
        this.state = config.openAsPanel ?? 'pop';
        this.hostClasses = config.dialogClass?.join(' ');
        this.name = config.name ? config.name : this.name;
    }
    ngOnDestroy() {
        this.animationStateChanged.complete();
    }
    attachComponentPortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            throw new Error('Content already attached');
        }
        return this._portalOutlet.attachComponentPortal(portal);
    }
    attachTemplatePortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            throw new Error('Content already attached');
        }
        return this._portalOutlet.attachTemplatePortal(portal);
    }
    onAnimationDone(event) {
        this.animationStateChanged.next(event);
    }
    startExitAnimation() {
        this.state = 'exit';
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvModalContainerComponent, deps: [{ token: TrvModalConfig }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvModalContainerComponent, isStandalone: true, selector: "trv-modal-container", host: { listeners: { "@modalContainer.done": "onAnimationDone($event)" }, properties: { "@modalContainer": "state", "class": "hostClasses", "attr.name": "name" } }, viewQueries: [{ propertyName: "_portalOutlet", first: true, predicate: CdkPortalOutlet, descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div cdkTrapFocus\r\n     [cdkTrapFocusAutoCapture]=\"true\"\r\n     class=\"modal-content\">\r\n  <ng-template cdkPortalOutlet></ng-template>\r\n</div>", styles: [""], dependencies: [{ kind: "ngmodule", type: A11yModule }, { kind: "directive", type: i2$1.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { kind: "ngmodule", type: PortalModule }, { kind: "directive", type: i3.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }], animations: [
            trigger('modalContainer', [
                //Slide <---
                transition('void => right', [
                    style({ transform: 'translateX(100%)' }),
                    animate('100ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translateX(0%)' })),
                ]),
                transition('right => exit', [animate('100ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translateX(100%)' }))]),
                //Slide --->
                transition('void => left', [style({ transform: 'translateX(-100%)' }), animate('100ms cubic-bezier(0.4, 0.0, 0.2, 1)')]),
                transition('left => exit', [animate('100ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translateX(-100%)' }))]),
                //Pop middle
                transition('void => pop', [
                    style({ opacity: 0, transform: 'scale(0.65)' }),
                    animate('150ms cubic-bezier(0, 0, 0.2, 1)', style({ transform: 'none', opacity: 1 })),
                ]),
                transition('pop => exit', [
                    animate('100ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 0, transform: 'scale(0.65)' })),
                ]),
            ]),
        ] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvModalContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-modal-container', animations: [
                        trigger('modalContainer', [
                            //Slide <---
                            transition('void => right', [
                                style({ transform: 'translateX(100%)' }),
                                animate('100ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translateX(0%)' })),
                            ]),
                            transition('right => exit', [animate('100ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translateX(100%)' }))]),
                            //Slide --->
                            transition('void => left', [style({ transform: 'translateX(-100%)' }), animate('100ms cubic-bezier(0.4, 0.0, 0.2, 1)')]),
                            transition('left => exit', [animate('100ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translateX(-100%)' }))]),
                            //Pop middle
                            transition('void => pop', [
                                style({ opacity: 0, transform: 'scale(0.65)' }),
                                animate('150ms cubic-bezier(0, 0, 0.2, 1)', style({ transform: 'none', opacity: 1 })),
                            ]),
                            transition('pop => exit', [
                                animate('100ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 0, transform: 'scale(0.65)' })),
                            ]),
                        ]),
                    ], host: {
                        '[@modalContainer]': 'state',
                        '(@modalContainer.done)': 'onAnimationDone($event)',
                        '[class]': 'hostClasses',
                        '[attr.name]': 'name',
                    }, imports: [A11yModule, PortalModule], template: "<div cdkTrapFocus\r\n     [cdkTrapFocusAutoCapture]=\"true\"\r\n     class=\"modal-content\">\r\n  <ng-template cdkPortalOutlet></ng-template>\r\n</div>" }]
        }], ctorParameters: () => [{ type: TrvModalConfig }], propDecorators: { _portalOutlet: [{
                type: ViewChild,
                args: [CdkPortalOutlet, { static: true }]
            }] } });

const DEFAULT_CONFIG = {
    hasBackdrop: true,
    disposeOnBackdropClick: false,
    size: 'lg',
    maxHeight: 'calc(100vh - 3.5rem)',
    position: { top: '2rem' }
};
class TrvModalService {
    router;
    overlay;
    injector;
    ngUnsubscribe = new Subject();
    openModals = [];
    constructor(router, overlay, injector) {
        this.router = router;
        this.overlay = overlay;
        this.injector = injector;
        router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe(() => this.dismissAll());
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    open(componentOrTemplate, config) {
        return this.openModal(componentOrTemplate, config);
    }
    confirm(title, message, yesButtonTitle, noButtonTitle, config) {
        const modal = this.openModal(TrvConfirmModalComponent, {
            ...config,
            panelClass: ['confirm-modal'].concat(config?.panelClass || [])
        });
        const component = modal.componentInstance;
        component.title = title;
        component.message = message;
        component.type = 'confirm';
        component.yesButtonTitle = yesButtonTitle;
        component.noButtonTitle = noButtonTitle;
        return modal;
    }
    confirmDelete(title, message, yesButtonTitle, noButtonTitle, config) {
        const modal = this.openModal(TrvConfirmModalComponent, {
            ...config,
            panelClass: ['confirm-modal'].concat(config?.panelClass || [])
        });
        const component = modal.componentInstance;
        component.title = title;
        component.message = message;
        component.type = 'confirmDelete';
        component.yesButtonTitle = yesButtonTitle || 'Ta bort';
        component.noButtonTitle = noButtonTitle;
        return modal;
    }
    accept(title, message, yesButtonTitle, config) {
        const modal = this.openModal(TrvConfirmModalComponent, {
            ...config,
            panelClass: ['confirm-modal'].concat(config?.panelClass || [])
        });
        const component = modal.componentInstance;
        component.title = title;
        component.message = message;
        component.type = 'accept';
        component.yesButtonTitle = yesButtonTitle;
        return modal;
    }
    info(title, message, config) {
        const modal = this.openModal(TrvConfirmModalComponent, {
            ...config,
            panelClass: ['confirm-modal'].concat(config?.panelClass || [])
        });
        const component = modal.componentInstance;
        component.title = title;
        component.message = message;
        component.type = 'info';
        return modal;
    }
    dismissAll() {
        this.dismissModals(this.openModals);
    }
    openModal(componentOrTemplate, config) {
        const modalConfig = this.getConfig(config);
        const overlayRef = this.createOverlay(modalConfig);
        const modalContainer = this.attachModalContainer(overlayRef, modalConfig, this.injector);
        const modalRef = this.attachModalContent(componentOrTemplate, modalContainer, overlayRef, modalConfig);
        this.openModals.push(modalRef);
        modalRef.afterCloseWithType().subscribe(() => this.removeOpenModal(modalRef));
        return modalRef;
    }
    dismissModals(modals) {
        let i = modals.length;
        while (i--) {
            modals[i].dismiss();
        }
    }
    removeOpenModal(modalRef) {
        const index = this.openModals.indexOf(modalRef);
        if (index > -1) {
            this.openModals.splice(index, 1);
        }
    }
    createOverlay(config) {
        return this.overlay.create(config);
    }
    getConfig(userConfig) {
        const config = this.mergeConfigs(DEFAULT_CONFIG, userConfig);
        config.scrollStrategy = this.overlay.scrollStrategies.block();
        config.positionStrategy = this.overlay.position().global();
        config.panelClass = config.panelClass || [];
        config.dialogClass = config.dialogClass || [];
        config.backdropClass = config.backdropClass || ['cdk-overlay-dark-backdrop'];
        if (config.panelClass.indexOf('modal') <= 0) {
            config.panelClass.push('modal', 'd-flex', 'align-items-baseline');
        }
        if (config.dialogClass.indexOf('modal-dialog') <= 0) {
            config.dialogClass.push('modal-dialog', 'd-flex', 'flex-grow-1');
        }
        if (config.openAsPanel) {
            config.dialogClass.push('position-fixed');
            config.size = 'md';
            if (config.openAsPanel === 'left') {
                config.position = config.position || {};
                config.position.left = '0px';
                // config.dialogClass.push('ms-0');
            }
            else {
                config.position = config.position || {};
                config.position.right = '0px';
                // config.dialogClass.push('me-0');
            }
            config.panelClass.push('modal-side');
            config.maxHeight = null;
            config.position.top = null;
            config.position.bottom = null;
        }
        else {
            config.panelClass.push('w-auto', 'h-auto');
        }
        if (config.size) {
            config.dialogClass.push('modal-' + config.size);
        }
        return config;
    }
    mergeConfigs(defaultConfig, userConfig) {
        const config = { ...defaultConfig, ...userConfig, position: { ...defaultConfig.position } };
        config.position = { ...config.position };
        return config;
    }
    attachModalContainer(overlay, config, injector) {
        const providers = [{ provide: TrvModalConfig, useValue: config }];
        const containerPortal = new ComponentPortal(TrvModalContainerComponent, null, Injector.create({ parent: injector, providers }));
        const containerRef = overlay.attach(containerPortal);
        return containerRef.instance;
    }
    attachModalContent(componentOrTemplate, modalContainer, overlayRef, config) {
        const trvModalRef = new TrvModalRef(overlayRef, modalContainer);
        if (config.disposeOnBackdropClick) {
            overlayRef.backdropClick()
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(() => trvModalRef.dismiss());
        }
        if (componentOrTemplate instanceof TemplateRef) {
            modalContainer.attachTemplatePortal(new TemplatePortal(componentOrTemplate, null));
        }
        else {
            const injector = this.createInjector(config, trvModalRef, modalContainer);
            const contentRef = modalContainer.attachComponentPortal(new ComponentPortal(componentOrTemplate, null, injector));
            trvModalRef.componentInstance = contentRef.instance;
        }
        trvModalRef.updatePosition(config.position);
        return trvModalRef;
    }
    createInjector(config, modalRef, modalContainer) {
        const injectionTokens = new WeakMap([[TrvModalContainerComponent, modalContainer], [TrvModalRef, modalRef]]);
        return new PortalInjector(this.injector, injectionTokens);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvModalService, deps: [{ token: i2.Router }, { token: i1.Overlay }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvModalService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvModalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i2.Router }, { type: i1.Overlay }, { type: i0.Injector }] });

class TrvTextCutterDirective {
    elementRef;
    renderer;
    trvModalService;
    maxLimit;
    textTitle;
    skipModalLink;
    ignoreSeparator;
    // används om det är en label som kan ändras
    _textToCut;
    get textToCut() {
        return this._textToCut;
    }
    set textToCut(textToCut) {
        this._textToCut = textToCut;
        if (textToCut) {
            this.setInnerText(textToCut);
        }
    }
    element;
    constructor(elementRef, renderer, trvModalService) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.trvModalService = trvModalService;
        this.element = elementRef.nativeElement;
    }
    ngOnInit() { }
    ngAfterViewInit() {
        const textFull = this.textToCut || this.element.innerText;
        this.setInnerText(textFull);
    }
    setInnerText(textToCut) {
        const textCut = this.shorten(textToCut, this.maxLimit);
        if (textCut.length < textToCut.length) {
            let element;
            if (this.skipModalLink) {
                element = this.renderer.createElement('span');
            }
            else {
                element = this.renderer.createElement('a');
                this.renderer.addClass(element, 'trv-prevent-table-row-click');
                this.renderer.setAttribute(element, 'tabindex', '0');
                this.renderer.setAttribute(element, 'role', 'button');
                this.renderer.listen(element, 'click', () => {
                    this.trvModalService.info(this.textTitle, textToCut, { size: 'md' });
                });
            }
            element.innerText = textCut + '...';
            this.element.innerText = '';
            this.renderer.appendChild(this.element, element);
        }
        else {
            this.element.innerText = textCut;
        }
    }
    shorten(str, maxLen) {
        if (str.length <= maxLen) {
            return str;
        }
        const separator = ' ';
        let subStr = str.substring(0, str.lastIndexOf(separator, maxLen));
        const lastChar = subStr[subStr.length - 1];
        if (!lastChar || this.ignoreSeparator) {
            return str.slice(0, maxLen);
        }
        if (lastChar.match(/\W/g)) {
            // ta bort specialtecken som sista tecken
            subStr = subStr.slice(0, -1);
        }
        return subStr;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTextCutterDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: TrvModalService }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvTextCutterDirective, isStandalone: true, selector: "[trvTextCutter]", inputs: { maxLimit: ["trvTextCutter", "maxLimit"], textTitle: "textTitle", skipModalLink: "skipModalLink", ignoreSeparator: "ignoreSeparator", textToCut: "textToCut" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTextCutterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[trvTextCutter]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: TrvModalService }], propDecorators: { maxLimit: [{
                type: Input,
                args: ['trvTextCutter']
            }], textTitle: [{
                type: Input
            }], skipModalLink: [{
                type: Input
            }], ignoreSeparator: [{
                type: Input
            }], textToCut: [{
                type: Input
            }] } });

class TrvDashboardPageHeaderComponent {
    dashboardService;
    router;
    elementRef;
    renderer;
    trvUtilityService;
    cdr;
    ngUnsubscribe = new Subject();
    customTitle;
    customNavigationItem;
    customRouterLink; // train/:id
    titleMaxLength;
    hideBreadcrumb;
    disableBreadcrumb = false;
    navItem;
    containerElement;
    placeholderElement;
    isSticky;
    constructor(dashboardService, router, elementRef, renderer, trvUtilityService, cdr) {
        this.dashboardService = dashboardService;
        this.router = router;
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.trvUtilityService = trvUtilityService;
        this.cdr = cdr;
    }
    ngOnInit() {
        this.hideBreadcrumb = this.hideBreadcrumb || this.dashboardService.hideMenu;
        this.dashboardService.hideMenuChange
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((hide) => {
            this.hideBreadcrumb = hide;
            this.trvUtilityService.detectChanges(this.cdr);
        });
        this.dashboardService.detectChanges
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
            this.trvUtilityService.detectChanges(this.cdr);
        });
        let routerLink = this.router.url;
        if (this.customRouterLink) {
            routerLink = this.customRouterLink;
            this.dashboardService.customRouterLink.emit(this.customRouterLink);
        }
        this.navItem = this.customNavigationItem || this.dashboardService.getNavItemByUrl(routerLink);
        if (this.navItem) {
            setTimeout(() => {
                this.isSticky = this.checkPositionStyle();
                if (!this.isSticky) {
                    this.setPositionFixed();
                    merge(this.dashboardService.openChange, fromEvent(window, 'resize'))
                        .subscribe((isOpen) => {
                        setTimeout(() => this.setPositionFixed());
                    });
                }
            });
        }
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    ngOnChanges(changes) {
        if (changes.customRouterLink?.currentValue && !changes.customRouterLink?.isFirstChange()) {
            this.dashboardService.customRouterLink.emit(this.customRouterLink);
        }
    }
    checkPositionStyle() {
        const element = this.elementRef.nativeElement;
        this.containerElement = element.querySelector('.page-header');
        const positionStyle = window.getComputedStyle(this.containerElement).position;
        return positionStyle === 'sticky';
    }
    setPositionFixed() {
        if (!this.placeholderElement) {
            this.createPlaceholder();
        }
        const container = this.containerElement;
        this.renderer.setStyle(container, 'position', 'fixed');
        this.renderer.setStyle(container, 'top', this.placeholderElement.offsetTop + 'px');
        this.renderer.setStyle(container, 'left', this.placeholderElement.offsetLeft + 'px');
        this.renderer.setStyle(container, 'width', this.placeholderElement.clientWidth + 'px');
        setTimeout(() => {
            this.renderer.setStyle(this.placeholderElement, 'height', container.clientHeight + 'px');
        });
    }
    createPlaceholder() {
        this.placeholderElement = this.renderer.createElement('div');
        this.renderer.setStyle(this.placeholderElement, 'width', '100%');
        this.renderer.appendChild(this.elementRef.nativeElement, this.placeholderElement);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardPageHeaderComponent, deps: [{ token: TrvDashboardService }, { token: i2.Router }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: TrvUtilityService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvDashboardPageHeaderComponent, isStandalone: true, selector: "trv-dashboard-page-header", inputs: { customTitle: "customTitle", customNavigationItem: "customNavigationItem", customRouterLink: "customRouterLink", titleMaxLength: "titleMaxLength", hideBreadcrumb: "hideBreadcrumb", disableBreadcrumb: "disableBreadcrumb" }, usesOnChanges: true, ngImport: i0, template: "@if (navItem) {\r\n<div class=\"page-header container-fluid\">\r\n  <div class=\"page-header__content\">\r\n    <fa-layers class=\"combo-icon\">\r\n      <fa-icon [icon]=\"navItem.icon\"\r\n               class=\"main-icon\"></fa-icon>\r\n      @if (navItem.subIcon) {\r\n      <fa-icon [icon]=\"navItem.subIcon\"\r\n               class=\"sub-icon\"></fa-icon>\r\n      }\r\n      @if (navItem.iconCounter) {\r\n      <fa-layers-counter [content]=\"navItem.iconCounter\"\r\n                         [class]=\"navItem.iconCounterClasses\">\r\n      </fa-layers-counter>\r\n      }\r\n    </fa-layers>\r\n    <div class=\"breadcrumb-title-wrap\">\r\n      @if (hideBreadcrumb) {\r\n      <ng-content select=\"[trv-dashboard-page-header-top-left]\"></ng-content>\r\n      }\r\n      @if (!hideBreadcrumb) {\r\n      <nav aria-label=\"breadcrumb\">\r\n        <ol class=\"breadcrumb\">\r\n          <li class=\"breadcrumb-item\">\r\n            @if (navItem.routerLink !== '/') {\r\n            <a routerLink=\"/\">Start</a>\r\n            }\r\n          </li>\r\n          @if (navItem.parent) {\r\n\r\n          <ng-container *ngTemplateOutlet=\"recursiveParent; context: { navItem: navItem.parent }\"></ng-container>\r\n\r\n          }\r\n        </ol>\r\n      </nav>\r\n      }\r\n      @if (!titleMaxLength) {\r\n      <h1>{{customTitle || navItem.label}}</h1>\r\n      }\r\n      @if (titleMaxLength) {\r\n      <h1 [trvTextCutter]=\"titleMaxLength\"\r\n          [skipModalLink]=\"true\"\r\n          [textToCut]=\"customTitle || navItem.label\"></h1>\r\n      }\r\n    </div>\r\n    <div class=\"custom-header-container\">\r\n      <ng-content></ng-content>\r\n    </div>\r\n  </div>\r\n</div>\r\n}\r\n\r\n<ng-template #recursiveParent\r\n             let-navItem=\"navItem\"\r\n             let-isActive=\"isActive\">\r\n  @if (navItem.parent) {\r\n  <ng-container *ngTemplateOutlet=\"recursiveParent; context: { navItem: navItem.parent }\"></ng-container>\r\n  }\r\n  <li class=\"breadcrumb-item\"\r\n      aria-current=\"page\">\r\n    @if (!disableBreadcrumb && !navItem.disableNavigation) {\r\n    <a [routerLink]=\"navItem.routerLink\">{{navItem.label}}</a>\r\n    }\r\n    @if (disableBreadcrumb || navItem.disableNavigation) {\r\n    <span>{{navItem.label}}</span>\r\n    }\r\n  </li>\r\n</ng-template>", styles: [":root{--bs-border-color: #ced4da}:host ::ng-deep .page-header{position:relative;z-index:1000}@supports (position: sticky){:host ::ng-deep .page-header{position:sticky;top:60px;z-index:1000}@media (max-width: 575.98px){:host ::ng-deep .page-header{top:90px}}}:host ::ng-deep .page-header{background-color:#fff;box-shadow:0 4px 2px -2px #80808054}:host ::ng-deep .page-header__content{display:flex;align-items:center}:host ::ng-deep .page-header .combo-icon{min-width:1.3em}:host ::ng-deep .page-header .breadcrumb-title-wrap{padding:1rem}:host ::ng-deep .page-header .breadcrumb-title-wrap h1{margin:0;padding:0;font-size:1.5em;font-weight:400}:host ::ng-deep .page-header .breadcrumb-title-wrap .breadcrumb{padding:0;margin:0 0 .1em;font-size:.875rem}:host ::ng-deep .page-header .custom-header-container{flex:1;display:flex;justify-content:flex-end;text-align:right}:host ::ng-deep .page-header .custom-header-container button fa-icon{margin-right:.5rem}\n"], dependencies: [{ kind: "component", type: FaLayersComponent, selector: "fa-layers", inputs: ["size", "fixedWidth"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "component", type: FaLayersCounterComponent, selector: "fa-layers-counter", inputs: ["content", "title", "position"] }, { kind: "directive", type: RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: TrvTextCutterDirective, selector: "[trvTextCutter]", inputs: ["trvTextCutter", "textTitle", "skipModalLink", "ignoreSeparator", "textToCut"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardPageHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-dashboard-page-header', changeDetection: ChangeDetectionStrategy.OnPush, imports: [FaLayersComponent, FaIconComponent, FaLayersCounterComponent, RouterLink, NgTemplateOutlet, TrvTextCutterDirective], template: "@if (navItem) {\r\n<div class=\"page-header container-fluid\">\r\n  <div class=\"page-header__content\">\r\n    <fa-layers class=\"combo-icon\">\r\n      <fa-icon [icon]=\"navItem.icon\"\r\n               class=\"main-icon\"></fa-icon>\r\n      @if (navItem.subIcon) {\r\n      <fa-icon [icon]=\"navItem.subIcon\"\r\n               class=\"sub-icon\"></fa-icon>\r\n      }\r\n      @if (navItem.iconCounter) {\r\n      <fa-layers-counter [content]=\"navItem.iconCounter\"\r\n                         [class]=\"navItem.iconCounterClasses\">\r\n      </fa-layers-counter>\r\n      }\r\n    </fa-layers>\r\n    <div class=\"breadcrumb-title-wrap\">\r\n      @if (hideBreadcrumb) {\r\n      <ng-content select=\"[trv-dashboard-page-header-top-left]\"></ng-content>\r\n      }\r\n      @if (!hideBreadcrumb) {\r\n      <nav aria-label=\"breadcrumb\">\r\n        <ol class=\"breadcrumb\">\r\n          <li class=\"breadcrumb-item\">\r\n            @if (navItem.routerLink !== '/') {\r\n            <a routerLink=\"/\">Start</a>\r\n            }\r\n          </li>\r\n          @if (navItem.parent) {\r\n\r\n          <ng-container *ngTemplateOutlet=\"recursiveParent; context: { navItem: navItem.parent }\"></ng-container>\r\n\r\n          }\r\n        </ol>\r\n      </nav>\r\n      }\r\n      @if (!titleMaxLength) {\r\n      <h1>{{customTitle || navItem.label}}</h1>\r\n      }\r\n      @if (titleMaxLength) {\r\n      <h1 [trvTextCutter]=\"titleMaxLength\"\r\n          [skipModalLink]=\"true\"\r\n          [textToCut]=\"customTitle || navItem.label\"></h1>\r\n      }\r\n    </div>\r\n    <div class=\"custom-header-container\">\r\n      <ng-content></ng-content>\r\n    </div>\r\n  </div>\r\n</div>\r\n}\r\n\r\n<ng-template #recursiveParent\r\n             let-navItem=\"navItem\"\r\n             let-isActive=\"isActive\">\r\n  @if (navItem.parent) {\r\n  <ng-container *ngTemplateOutlet=\"recursiveParent; context: { navItem: navItem.parent }\"></ng-container>\r\n  }\r\n  <li class=\"breadcrumb-item\"\r\n      aria-current=\"page\">\r\n    @if (!disableBreadcrumb && !navItem.disableNavigation) {\r\n    <a [routerLink]=\"navItem.routerLink\">{{navItem.label}}</a>\r\n    }\r\n    @if (disableBreadcrumb || navItem.disableNavigation) {\r\n    <span>{{navItem.label}}</span>\r\n    }\r\n  </li>\r\n</ng-template>", styles: [":root{--bs-border-color: #ced4da}:host ::ng-deep .page-header{position:relative;z-index:1000}@supports (position: sticky){:host ::ng-deep .page-header{position:sticky;top:60px;z-index:1000}@media (max-width: 575.98px){:host ::ng-deep .page-header{top:90px}}}:host ::ng-deep .page-header{background-color:#fff;box-shadow:0 4px 2px -2px #80808054}:host ::ng-deep .page-header__content{display:flex;align-items:center}:host ::ng-deep .page-header .combo-icon{min-width:1.3em}:host ::ng-deep .page-header .breadcrumb-title-wrap{padding:1rem}:host ::ng-deep .page-header .breadcrumb-title-wrap h1{margin:0;padding:0;font-size:1.5em;font-weight:400}:host ::ng-deep .page-header .breadcrumb-title-wrap .breadcrumb{padding:0;margin:0 0 .1em;font-size:.875rem}:host ::ng-deep .page-header .custom-header-container{flex:1;display:flex;justify-content:flex-end;text-align:right}:host ::ng-deep .page-header .custom-header-container button fa-icon{margin-right:.5rem}\n"] }]
        }], ctorParameters: () => [{ type: TrvDashboardService }, { type: i2.Router }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: TrvUtilityService }, { type: i0.ChangeDetectorRef }], propDecorators: { customTitle: [{
                type: Input
            }], customNavigationItem: [{
                type: Input
            }], customRouterLink: [{
                type: Input
            }], titleMaxLength: [{
                type: Input
            }], hideBreadcrumb: [{
                type: Input
            }], disableBreadcrumb: [{
                type: Input
            }] } });

class TrvDashboardNavPageComponent {
    dashboardService;
    router;
    navItem;
    navigationItems;
    constructor(dashboardService, router) {
        this.dashboardService = dashboardService;
        this.router = router;
    }
    ngOnInit() {
        this.navItem = this.dashboardService.getNavItemByUrl(this.router.url);
        this.navigationItems = this.navItem.subNavigationItems;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardNavPageComponent, deps: [{ token: TrvDashboardService }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvDashboardNavPageComponent, isStandalone: true, selector: "trv-dashboard-nav-page", ngImport: i0, template: "<trv-dashboard-page-header></trv-dashboard-page-header>\r\n<trv-dashboard-page-content>\r\n  <trv-dashboard-nav-blocks [navigationItems]=\"navigationItems\"></trv-dashboard-nav-blocks>\r\n</trv-dashboard-page-content>", styles: [""], dependencies: [{ kind: "component", type: TrvDashboardPageHeaderComponent, selector: "trv-dashboard-page-header", inputs: ["customTitle", "customNavigationItem", "customRouterLink", "titleMaxLength", "hideBreadcrumb", "disableBreadcrumb"] }, { kind: "component", type: TrvDashboardPageContentComponent, selector: "trv-dashboard-page-content" }, { kind: "component", type: TrvDashboardNavBlocksComponent, selector: "trv-dashboard-nav-blocks", inputs: ["navigationItems", "columnClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardNavPageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-dashboard-nav-page', changeDetection: ChangeDetectionStrategy.OnPush, imports: [TrvDashboardPageHeaderComponent, TrvDashboardPageContentComponent, TrvDashboardNavBlocksComponent], template: "<trv-dashboard-page-header></trv-dashboard-page-header>\r\n<trv-dashboard-page-content>\r\n  <trv-dashboard-nav-blocks [navigationItems]=\"navigationItems\"></trv-dashboard-nav-blocks>\r\n</trv-dashboard-page-content>" }]
        }], ctorParameters: () => [{ type: TrvDashboardService }, { type: i2.Router }] });

let TrvSelectDefaultInputs = {
    placeholder: 'Välj',
    displayMethod: undefined,
    optionTooltipDisplayMethod: undefined,
    selectedDisplayMethod: undefined,
    faIcon: undefined,
    source: [],
    valueKey: '',
    sortKey: '',
    sortDescending: false,
    enableSearch: false,
    disableClear: false,
    multi: false,
    hideShowSelected: false,
    favorites: undefined,
    labels: undefined,
    displayAllSelectedOptions: false,
    useCustomSelectionTemplate: false,
    preventCloseOnSelect: false,
    disableSelectedOptions: false,
    sortMethod: undefined,
    searchMethod: undefined,
};

const trvUnsubscribeMixin = (base = class {
}) => class extends base {
    ngUnsubscribe = new Subject();
    ngOnDestroy() {
        /**
         * ngUnsubscribe.complete() behövs inte
         * https://stackoverflow.com/questions/44289859/do-i-need-to-complete-a-subject-for-it-to-be-garbage-collected
         */
        this.ngUnsubscribe.next();
    }
};

class TrvPopoverContainerComponent extends BasePortalOutlet {
    overlayconfig;
    elementRef;
    _portalOutlet;
    _focusTrap;
    autoFocus = true;
    animationStateChanged = new Subject();
    outsideClick = new Subject();
    insideClick = new Subject();
    visibility = 'visible';
    constructor(overlayconfig, elementRef) {
        super();
        this.overlayconfig = overlayconfig;
        this.elementRef = elementRef;
    }
    ngOnDestroy() {
        this.outsideClick.complete();
        this.insideClick.complete();
    }
    attachComponentPortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            throw new Error('Content already attached');
        }
        return this._portalOutlet.attachComponentPortal(portal);
    }
    attachTemplatePortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            throw new Error('Content already attached');
        }
        return this._portalOutlet.attachTemplatePortal(portal);
    }
    onDocumentClick(event) {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.outsideClick.next(event);
        }
        else {
            this.insideClick.next(event);
        }
    }
    onAnimationDone(event) {
        this.animationStateChanged.next(event);
        if (this.visibility === 'visible') {
            this._focusTrap.focusTrap.focusFirstTabbableElement();
        }
    }
    startExitAnimation() {
        this.visibility = 'hidden';
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvPopoverContainerComponent, deps: [{ token: i1.OverlayConfig }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvPopoverContainerComponent, isStandalone: true, selector: "ng-component", host: { listeners: { "document:click": "this.onDocumentClick($event)", "@state.done": "onAnimationDone($event)" }, properties: { "@state": "visibility" }, classAttribute: "popover-container" }, viewQueries: [{ propertyName: "_portalOutlet", first: true, predicate: CdkPortalOutlet, descendants: true, static: true }, { propertyName: "_focusTrap", first: true, predicate: CdkTrapFocus, descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"popover-container\">\r\n  <div class=\"popover-content\"\r\n       [cdkTrapFocus]=\"autoFocus\"\r\n       [cdkTrapFocusAutoCapture]=\"autoFocus\"\r\n       #focusTrap=\"cdkTrapFocus\">\r\n    <ng-template cdkPortalOutlet></ng-template>\r\n  </div>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.popover-container{margin:10px}.popover-container:focus{outline:none}.popover-container .popover-content{min-width:150px;font-size:.975rem;background-color:#fff;border:1px solid #dee2e6;box-shadow:0 2px 4px #0000002e}.popover-container button:focus{outline:none}\n"], dependencies: [{ kind: "ngmodule", type: A11yModule }, { kind: "directive", type: i2$1.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { kind: "ngmodule", type: PortalModule }, { kind: "directive", type: i3.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }], animations: [
            trigger('state', [
                transition('void => visible', [
                    style({ opacity: 0, transform: 'scale(0)' }),
                    animate('100ms cubic-bezier(0, 0, 0.2, 1)', style({ transform: 'none', opacity: 1 }))
                ]),
                transition('visible => hidden', [
                    animate('0ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 0, transform: 'scale(0)' }))
                ])
            ])
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvPopoverContainerComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, animations: [
                        trigger('state', [
                            transition('void => visible', [
                                style({ opacity: 0, transform: 'scale(0)' }),
                                animate('100ms cubic-bezier(0, 0, 0.2, 1)', style({ transform: 'none', opacity: 1 }))
                            ]),
                            transition('visible => hidden', [
                                animate('0ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 0, transform: 'scale(0)' }))
                            ])
                        ])
                    ], host: {
                        '(document:click)': 'this.onDocumentClick($event)',
                        '[@state]': 'visibility',
                        '(@state.done)': 'onAnimationDone($event)',
                        class: 'popover-container'
                    }, imports: [A11yModule, PortalModule], template: "<div class=\"popover-container\">\r\n  <div class=\"popover-content\"\r\n       [cdkTrapFocus]=\"autoFocus\"\r\n       [cdkTrapFocusAutoCapture]=\"autoFocus\"\r\n       #focusTrap=\"cdkTrapFocus\">\r\n    <ng-template cdkPortalOutlet></ng-template>\r\n  </div>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.popover-container{margin:10px}.popover-container:focus{outline:none}.popover-container .popover-content{min-width:150px;font-size:.975rem;background-color:#fff;border:1px solid #dee2e6;box-shadow:0 2px 4px #0000002e}.popover-container button:focus{outline:none}\n"] }]
        }], ctorParameters: () => [{ type: i1.OverlayConfig }, { type: i0.ElementRef }], propDecorators: { _portalOutlet: [{
                type: ViewChild,
                args: [CdkPortalOutlet, { static: true }]
            }], _focusTrap: [{
                type: ViewChild,
                args: [CdkTrapFocus, { static: true }]
            }] } });

class TrvPopoverRef {
    overlayRef;
    allowMultiple;
    manualToggle;
    popoverContainer;
    onOutsideClick;
    _afterClosed = new Subject();
    _afterClosedWithType = new Subject();
    componentInstance;
    constructor(overlayRef, allowMultiple, manualToggle, popoverContainer, onOutsideClick) {
        this.overlayRef = overlayRef;
        this.allowMultiple = allowMultiple;
        this.manualToggle = manualToggle;
        this.popoverContainer = popoverContainer;
        this.onOutsideClick = onOutsideClick;
        popoverContainer.insideClick.subscribe((event) => this.onInsideClick(event));
        popoverContainer.outsideClick.subscribe((event) => this.onOutsideClick(event));
        overlayRef
            .keydownEvents()
            .pipe(filter((event) => {
            return event.key === 'Escape' && !hasModifierKey(event);
        }))
            .subscribe((event) => {
            event.preventDefault();
            this.dismiss();
        });
    }
    close(data) {
        this._close('close', data);
    }
    dismiss() {
        this._close('dismiss', null);
        if (this.overlayRef) {
            this.overlayRef.dispose();
        }
    }
    afterCloseWithType() {
        return this._afterClosedWithType.asObservable();
    }
    afterClose() {
        return this._afterClosed.asObservable();
    }
    //Klickade inuti popover
    onInsideClick(event) { }
    //Klickade utanför popover
    onOutsideContainerClick(event) {
        this.onOutsideClick(event);
    }
    _close(type, data) {
        this._afterClosedWithType.next({ closingEventType: type, result: data });
        this._afterClosedWithType.complete();
        if (type === 'close') {
            this._afterClosed.next(data);
        }
        this._afterClosed.complete();
        this.popoverContainer.animationStateChanged.pipe(take(1)).subscribe((event) => this.overlayRef.dispose());
        this.popoverContainer.startExitAnimation();
    }
}

class TrvPopoverService {
    openPopovers = [];
    constructor() { }
    register(popoverRef) {
        this.openPopovers.push(popoverRef);
    }
    deregister(popoverRef) {
        const openPopoverIndex = this.openPopovers.findIndex(p => p === popoverRef);
        if (openPopoverIndex !== -1) {
            this.openPopovers.splice(openPopoverIndex, 1);
        }
    }
    deregisterNonMultiple() {
        for (let i = this.openPopovers.length - 1; i >= 0; i--) {
            const currentPopover = this.openPopovers[i];
            if (currentPopover.allowMultiple === false && currentPopover.manualToggle === false) {
                currentPopover.dismiss();
                this.deregister(currentPopover);
            }
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvPopoverService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvPopoverService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvPopoverService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class TrvPopoverDirective {
    overlay;
    elementRef;
    viewContainerRef;
    injector;
    router;
    popoverService;
    overlayService;
    cdr;
    componentOrTemplate;
    manualToggle = false;
    position;
    popoverClass;
    allowMultiple = false;
    disablePopover;
    autoFocus = true;
    shown = new EventEmitter();
    hidden = new EventEmitter();
    clickOutside = new EventEmitter();
    afterClose = new EventEmitter();
    afterCloseWithType = new EventEmitter();
    toggleClass = 'trv-popover-toggle';
    get active() {
        return this.isOpen;
    }
    ngUnsubscribe = new Subject();
    overlayRef;
    popoverRef;
    isOpen = false;
    constructor(overlay, elementRef, viewContainerRef, injector, router, popoverService, overlayService, cdr) {
        this.overlay = overlay;
        this.elementRef = elementRef;
        this.viewContainerRef = viewContainerRef;
        this.injector = injector;
        this.router = router;
        this.popoverService = popoverService;
        this.overlayService = overlayService;
        this.cdr = cdr;
        this.router.events.pipe(filter((e) => e instanceof NavigationStart)).subscribe((e) => {
            if (this.popoverRef) {
                this.hide();
            }
        });
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    show() {
        if (!this.componentOrTemplate) {
            throw new Error('Template or component missing');
        }
        //Om den redan visas
        if (this.overlayRef?.hasAttached()) {
            return;
        }
        this.popoverService.deregisterNonMultiple();
        const config = this.getOverlayConfig();
        this.overlayRef = this.createOverlay(config);
        const popoverContainer = this.attachPopoverContainer(this.overlayRef, config, this.injector);
        popoverContainer.autoFocus = this.autoFocus;
        this.popoverRef = this.attachPopoverContent(this.componentOrTemplate, popoverContainer);
        this.popoverService.register(this.popoverRef);
        this.isOpen = true;
        this.setupSubscriptions();
        this.shown.emit(this.popoverRef);
    }
    hide() {
        if (this.popoverRef) {
            this.popoverRef.dismiss();
            this.popoverService.deregister(this.popoverRef);
            this.isOpen = false;
            this.cdr.detectChanges();
            this.hidden.emit(this.popoverRef);
        }
    }
    onClick(event) {
        if (this.manualToggle || this.disablePopover) {
            return;
        }
        // event.preventDefault();
        // event.stopPropagation();
        if (this.overlayRef && this.overlayRef.hasAttached()) {
            this.popoverRef.dismiss();
            this.isOpen = false;
            this.cdr.detectChanges();
        }
        else {
            this.show();
        }
    }
    get componentInstance() {
        return this.popoverRef != null ? this.popoverRef.componentInstance : null;
    }
    onClickOutside(event) {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.clickOutside.emit();
            if (!this.manualToggle) {
                this.hide();
            }
        }
    }
    createOverlay(config) {
        return this.overlay.create(config);
    }
    attachPopoverContainer(overlayRef, config, injector) {
        const providers = [{ provide: OverlayConfig, useValue: config }];
        const containerPortal = new ComponentPortal(TrvPopoverContainerComponent, null, Injector.create({ parent: injector, providers }));
        const containerRef = this.overlayRef.attach(containerPortal);
        return containerRef.instance;
    }
    attachPopoverContent(componentOrTemplate, popoverContainer) {
        const trvPopoverRef = new TrvPopoverRef(this.overlayRef, this.allowMultiple, this.manualToggle, popoverContainer, this.onClickOutside.bind(this));
        if (componentOrTemplate instanceof TemplateRef) {
            popoverContainer.attachTemplatePortal(new TemplatePortal(componentOrTemplate, this.viewContainerRef));
        }
        else {
            const injector = this.createInjector(trvPopoverRef, popoverContainer);
            const contentRef = popoverContainer.attachComponentPortal(new ComponentPortal(componentOrTemplate, null, injector));
            trvPopoverRef.componentInstance = contentRef.instance;
        }
        return trvPopoverRef;
    }
    createInjector(popoverRef, popoverContainer) {
        const providers = [
            { provide: TrvPopoverContainerComponent, useValue: popoverContainer },
            { provide: TrvPopoverRef, useValue: popoverRef },
        ];
        return Injector.create({ parent: this.injector, providers });
    }
    getOverlayConfig() {
        return new OverlayConfig({
            positionStrategy: this.getOverlayPositionStrategy(),
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            panelClass: this.popoverClass ? this.popoverClass : null,
        });
    }
    getOverlayPositionStrategy() {
        return this.overlay
            .position()
            .flexibleConnectedTo(this.elementRef)
            .withPositions(this.getPositions())
            .withFlexibleDimensions(false)
            .withPush(false);
    }
    getPositions() {
        if (this.position) {
            return this.getUserPositions();
        }
        else {
            return this.getDefaultPositions();
        }
    }
    setupSubscriptions() {
        //Template kan inte skicka tillbaka data
        if (this.componentOrTemplate instanceof TemplateRef) {
            return;
        }
        if (this.afterClose.observers.length > 0) {
            this.popoverRef
                .afterClose()
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((res) => this.afterClose.emit(res));
        }
        if (this.afterCloseWithType.observers.length > 0) {
            this.popoverRef
                .afterCloseWithType()
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((res) => this.afterCloseWithType.emit(res));
        }
    }
    getUserPositions() {
        const positions = [];
        for (const position of this.position) {
            const originPosition = this.overlayService.getOriginPosition(position);
            const overlayPosition = this.overlayService.getOverlayPosition(position);
            positions.push({ ...originPosition, ...overlayPosition });
        }
        return positions;
    }
    getDefaultPositions() {
        const top = { ...this.overlayService.getOriginPosition('top'), ...this.overlayService.getOverlayPosition('top') };
        const right = { ...this.overlayService.getOriginPosition('right'), ...this.overlayService.getOverlayPosition('right') };
        const bottom = { ...this.overlayService.getOriginPosition('bottom'), ...this.overlayService.getOverlayPosition('bottom') };
        const left = { ...this.overlayService.getOriginPosition('left'), ...this.overlayService.getOverlayPosition('left') };
        return [top, right, bottom, left];
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvPopoverDirective, deps: [{ token: i1.Overlay }, { token: i0.ElementRef }, { token: i0.ViewContainerRef }, { token: i0.Injector }, { token: i2.Router }, { token: TrvPopoverService }, { token: OverlayService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvPopoverDirective, isStandalone: true, selector: "[trvPopover]", inputs: { componentOrTemplate: ["trvPopover", "componentOrTemplate"], manualToggle: "manualToggle", position: "position", popoverClass: "popoverClass", allowMultiple: "allowMultiple", disablePopover: "disablePopover", autoFocus: "autoFocus" }, outputs: { shown: "shown", hidden: "hidden", clickOutside: "clickOutside", afterClose: "afterClose", afterCloseWithType: "afterCloseWithType" }, host: { listeners: { "click": "this.onClick($event)" }, properties: { "class": "this.toggleClass", "class.active": "this.active" } }, exportAs: ["trvPopover"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvPopoverDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[trvPopover]',
                    exportAs: 'trvPopover',
                    host: { '(click)': 'this.onClick($event)' },
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i1.Overlay }, { type: i0.ElementRef }, { type: i0.ViewContainerRef }, { type: i0.Injector }, { type: i2.Router }, { type: TrvPopoverService }, { type: OverlayService }, { type: i0.ChangeDetectorRef }], propDecorators: { componentOrTemplate: [{
                type: Input,
                args: ['trvPopover']
            }], manualToggle: [{
                type: Input
            }], position: [{
                type: Input
            }], popoverClass: [{
                type: Input
            }], allowMultiple: [{
                type: Input
            }], disablePopover: [{
                type: Input
            }], autoFocus: [{
                type: Input
            }], shown: [{
                type: Output
            }], hidden: [{
                type: Output
            }], clickOutside: [{
                type: Output
            }], afterClose: [{
                type: Output
            }], afterCloseWithType: [{
                type: Output
            }], toggleClass: [{
                type: HostBinding,
                args: ['class']
            }], active: [{
                type: HostBinding,
                args: ['class.active']
            }] } });

class SelectOption {
    value;
    selectedLabel;
    label;
    selected;
    focused;
    trackIndex;
    matchIndex;
    constructor(value, displayMethod, selectedDisplayMethod, trackIndex) {
        this.value = value;
        this.label = displayMethod ? displayMethod(value) : value == null ? '' : value + '';
        this.selectedLabel = selectedDisplayMethod ? selectedDisplayMethod(value) : this.label;
        this.selected = false;
        if (trackIndex) {
            this.trackIndex = ++trackIndex;
        }
    }
}

class TrvSelectPopoverComponent {
    trvUtilityService;
    cdr;
    constructor(trvUtilityService, cdr) {
        this.trvUtilityService = trvUtilityService;
        this.cdr = cdr;
    }
    select;
    ngContentTemplate;
    maxVisibleItems = 6;
    optionsContainerRef;
    optionsListViewport;
    searchInputRef;
    showSelectedOptionsButtonRef;
    itemPixelHeight = 42;
    viewPortPixelHeight = 0;
    viewPortPixelWidth = 0;
    optionsContainerId = "options" + new Date().valueOf();
    faSearch = faSearch;
    faInfoCircle = faInfoCircle;
    faStar = faStar;
    navigateOptionListIndex = -1;
    currentlyFocusedOption;
    optionFocusedClass = "option-focused";
    ngUnsubscribe = new Subject();
    ngAfterViewInit() {
        this.setViewportWidth();
        this.setViewportHeight();
        this.select.optionsChanged$.pipe(takeUntil$1(this.ngUnsubscribe)).subscribe(() => this.setViewportHeight());
    }
    ngOnDestroy() {
        this.resetCurrentlyFocusedOption();
        this.ngUnsubscribe.next();
    }
    onSearchInputKeyDown = (event) => {
        if ((event.shiftKey && event.key === "Tab") || event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            this.select.popover.hide();
            this.select.selectButtonRef.nativeElement.focus();
        }
        else if (event.key === "ArrowDown") {
            event.preventDefault();
            event.stopPropagation();
            this.focusFirstListItem();
        }
    };
    onShowSelectedOptionsButtonKeyDown = (event) => {
        if (!this.select.enableSearch && ((event.shiftKey && event.key === "Tab") || event.key === "Escape")) {
            event.preventDefault();
            event.stopPropagation();
            this.select.popover.hide();
            this.select.selectButtonRef.nativeElement.focus();
        }
        else if (event.key === "ArrowDown") {
            event.preventDefault();
            event.stopPropagation();
            this.focusFirstListItem();
        }
    };
    onOptionsListContainerKeyDown = (event) => {
        if (event.shiftKey && event.key === "Tab") {
            //shift tab
            event.preventDefault();
            this.focusAboveList();
        }
        else if (event.key === "Enter" || event.key === "Space") {
            // space or enter
            event.preventDefault();
            this.toggleFocusedOption();
        }
        else if (event.key === "Tab" || event.key === "Escape") {
            // escape
            this.resetCurrentlyFocusedOption();
            this.navigateOptionListIndex = -1;
            this.select.popover.hide();
            this.select.focusSelect();
        }
        else if (event.key === "ArrowUp") {
            //arrow up
            event.preventDefault();
            if (this.navigateOptionListIndex <= 0) {
                this.focusAboveList();
            }
            else {
                this.navigateOptionList(-1, true);
            }
        }
        if (event.key === "ArrowDown") {
            //arrow down
            event.preventDefault();
            this.navigateOptionList(1, true);
        }
    };
    setViewportWidth() {
        let longestLabelWidth = 0;
        if (this.select.options?.length) {
            var longestLabel = this.select.options.reduce((a, b) => (a.label.length > b.label.length ? a : b));
            const textCalculatorDiv = document.querySelector(".trv-select-text-width-calculator");
            textCalculatorDiv.className = "trv-select-text-calculator-spawner";
            textCalculatorDiv.innerHTML = longestLabel.label;
            textCalculatorDiv.style.position = "absolute";
            textCalculatorDiv.style.whiteSpace = "nowrap";
            //textCalculatorDiv.style.font = 'inherit';
            //40 = checkbox width, 25 = scrollbar
            longestLabelWidth = textCalculatorDiv.clientWidth + 40 + 35 + (this.select.enableFavorites ? 45 : 0);
            textCalculatorDiv.remove();
        }
        const selectArea = this.select.selectInputGroupRef.nativeElement;
        const selectAreaWidth = selectArea.clientWidth - 3;
        this.viewPortPixelWidth = selectAreaWidth > longestLabelWidth ? selectAreaWidth : longestLabelWidth;
    }
    setViewportHeight() {
        const numberOfItems = this.select.options.length;
        if (numberOfItems <= this.maxVisibleItems) {
            this.viewPortPixelHeight = this.itemPixelHeight * numberOfItems;
        }
        else {
            this.viewPortPixelHeight = this.itemPixelHeight * this.maxVisibleItems;
        }
        setTimeout(() => this.optionsListViewport?.checkViewportSize());
        this.cdr.detectChanges();
    }
    focusFirstListItem = () => {
        const listContainer = this.optionsListViewport.elementRef.nativeElement;
        listContainer.focus();
        listContainer.scrollTop = 0;
        this.resetCurrentlyFocusedOption();
        this.navigateOptionListIndex = -1;
        this.navigateOptionList(0, false);
    };
    navigateOptionList = (diff, scroll) => {
        this.resetCurrentlyFocusedOption();
        if (!this.select.options.length) {
            this.navigateOptionListIndex = -1;
            this.select.popover.hide();
            return;
        }
        scroll = scroll !== undefined ? scroll : true;
        this.navigateOptionListIndex += diff;
        if (this.navigateOptionListIndex < 0) {
            this.navigateOptionListIndex = 0;
        }
        else if (this.navigateOptionListIndex >= this.select.options.length) {
            this.navigateOptionListIndex = this.select.options.length - 1;
        }
        this.currentlyFocusedOption = this.select.options[this.navigateOptionListIndex];
        this.currentlyFocusedOption.focused = true;
        if (scroll) {
            this.optionsListViewport.scrollToIndex(this.navigateOptionListIndex, "smooth");
        }
        this.trvUtilityService.detectChanges(this.cdr);
    };
    focusAboveList = () => {
        this.resetCurrentlyFocusedOption();
        this.navigateOptionListIndex = -1;
        this.optionsListViewport.scrollToIndex(0, "smooth");
        if (this.select.enableSearch && (this.select.options.length || this.select.searchControl.value)) {
            this.searchInputRef.nativeElement.focus();
            this.searchInputRef.nativeElement.select();
        }
        else {
            this.select.selectButtonRef.nativeElement.focus();
            this.select.popover.hide();
        }
        this.trvUtilityService.detectChanges(this.cdr);
    };
    resetCurrentlyFocusedOption = () => {
        if (this.currentlyFocusedOption) {
            this.currentlyFocusedOption.focused = false;
            this.currentlyFocusedOption = null;
        }
    };
    toggleFocusedOption = () => {
        if (this.currentlyFocusedOption) {
            this.select.toggleSelectOption(this.currentlyFocusedOption, !this.currentlyFocusedOption.selected);
        }
        this.trvUtilityService.detectChanges(this.cdr);
    };
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvSelectPopoverComponent, deps: [{ token: TrvUtilityService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvSelectPopoverComponent, isStandalone: true, selector: "trv-select-popover", inputs: { select: "select", ngContentTemplate: "ngContentTemplate", maxVisibleItems: "maxVisibleItems" }, viewQueries: [{ propertyName: "optionsContainerRef", first: true, predicate: ["optionsContainerRef"], descendants: true }, { propertyName: "optionsListViewport", first: true, predicate: ["optionsListViewport"], descendants: true }, { propertyName: "searchInputRef", first: true, predicate: ["searchInputRef"], descendants: true }, { propertyName: "showSelectedOptionsButtonRef", first: true, predicate: ["showSelectedOptionsButtonRef"], descendants: true }], ngImport: i0, template: "<div class=\"options-container\"\r\n     [style.width.px]=\"viewPortPixelWidth\"\r\n     id=\"{{optionsContainerId}}\"\r\n     #optionsContainerRef>\r\n  <div class=\"trv-select-text-width-calculator\"></div>\r\n  <div class=\"options-search form-group\">\r\n    @if (select.enableSearch) {\r\n    <fa-icon [icon]=\"faSearch\"></fa-icon>\r\n    }\r\n    @if ((select.enableSearch && (select.options.length || select.searchControl.value)) || (select.multi && !select.useCustomSelectionTemplate)) {\r\n    <div class=\"input-group p-1\">\r\n      @if (select.enableSearch) {\r\n      <input [trvShouldFocus]=\"true\"\r\n             (keydown)=\"onSearchInputKeyDown($event)\"\r\n             #searchInputRef\r\n             [formControl]=\"select.searchControl\"\r\n             trvDebounce\r\n             class=\"trv-select-search-input form-control form-control-sm\"\r\n             type=\"text\"\r\n             placeholder=\"S\u00F6k...\">\r\n      }\r\n      @if (select.multi && !select.useCustomSelectionTemplate && !select.hideShowSelected) {\r\n      <button type=\"button\"\r\n              #showSelectedOptionsButtonRef\r\n              class=\"btn btn-sm btn-outline-primary-bg-contrast\"\r\n              [ngClass]=\"{ 'active': select.showSelectedOnly }\"\r\n              (keydown)=\"onShowSelectedOptionsButtonKeyDown($event)\"\r\n              (click)=\"select.filterSelected()\">\r\n        {{select.labels.showSelected}}\r\n      </button>\r\n      }\r\n    </div>\r\n    }\r\n    @if (!select.options.length && !select.searchControl.value) {\r\n    <div class=\"no-options text-muted\">\r\n      <fa-icon [icon]=\"faInfoCircle\"\r\n               class=\"me-2\"></fa-icon> {{select.labels.noMoreOptions}}\r\n    </div>\r\n    }\r\n    @if (!select.options.length && select.searchControl.value) {\r\n    <div class=\"no-options text-muted\">\r\n      <fa-icon [icon]=\"faInfoCircle\"\r\n               class=\"me-2\"></fa-icon> {{select.labels.noMatches}}\r\n    </div>\r\n    }\r\n  </div>\r\n\r\n  <ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container>\r\n  <cdk-virtual-scroll-viewport class=\"options-list-container\"\r\n                               [style.height.px]=\"viewPortPixelHeight\"\r\n                               [style.width.px]=\"viewPortPixelWidth\"\r\n                               [itemSize]=\"itemPixelHeight\"\r\n                               tabindex=\"0\"\r\n                               #optionsListViewport\r\n                               (keydown)=\"onOptionsListContainerKeyDown($event)\"\r\n                               [trvShouldFocus]=\"!select.enableSearch\">\r\n    <ul class=\"options-list\"\r\n        [ngClass]=\"{ 'enable-favorites' : select.enableFavorites }\">\r\n      <ng-container *cdkVirtualFor=\"let option of select.options; let i = index; trackBy:select.optionsIdentifier\">\r\n        <li class=\"options-list-item\"\r\n            [trvTooltip]=\"select.optionTooltipDisplayMethod != null ? select.optionTooltipDisplayMethod(option.value) : null\"\r\n            [ngClass]=\"{ \r\n              'option-focused': option.focused,\r\n              'option-disabled': select.disableSelectedOptions && option.selected\r\n            }\">\r\n          @if (!select.useCustomSelectionTemplate) {\r\n          @if (select.multi) {\r\n          <div class=\"form-check option-label-container\">\r\n            <input type=\"checkbox\"\r\n                   class=\"form-check-input\"\r\n                   [checked]=\"option.selected\"\r\n                   (change)=\"select.toggleSelectOption(option, $any($event.target).checked)\"\r\n                   id=\"trv-select-check-{{i}}\"\r\n                   tabindex=\"-1\">\r\n            <label class=\"form-check-label option-label\"\r\n                   for=\"trv-select-check-{{i}}\"\r\n                   [innerHtml]=\"option?.label | trvSafeHtml\"\r\n                   tabindex=\"-1\"></label>\r\n          </div>\r\n          }\r\n          @if (!select.multi) {\r\n          <div class=\"form-check option-label-container\"\r\n               tabindex=\"0\">\r\n            <input type=\"radio\"\r\n                   class=\"form-check-input\"\r\n                   [checked]=\"option.selected\"\r\n                   (change)=\"select.toggleSelectOption(option, $any($event.target).checked)\"\r\n                   id=\"trv-select-radio-{{i}}\">\r\n            <label class=\"form-check-label option-label\"\r\n                   for=\"trv-select-radio-{{i}}\"\r\n                   [innerHTML]=\"option?.label | trvSafeHtml\"></label>\r\n          </div>\r\n          }\r\n          }\r\n          @if (select.useCustomSelectionTemplate) {\r\n          <div class=\"option-label-container no-custom-control\"\r\n               tabindex=\"0\"\r\n               (click)=\"select.toggleSelectOption(option, true)\"\r\n               (keyup.space)=\"select.toggleSelectOption(option, true)\">\r\n            <span class=\"option-label\"\r\n                  [innerHTML]=\"option?.label | trvSafeHtml\"></span>\r\n          </div>\r\n          }\r\n          @if (select.enableFavorites) {\r\n          <button class=\"btn btn-link toggle-favorite\"\r\n                  [ngClass]=\"{ 'active' : select.isFavorite(option) }\"\r\n                  (click)=\"select.toggleFavorite(option, $event)\">\r\n            <fa-icon [icon]=\"faStar\"></fa-icon>\r\n          </button>\r\n          }\r\n        </li>\r\n      </ng-container>\r\n    </ul>\r\n  </cdk-virtual-scroll-viewport>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.text-container{overflow:hidden}.options-container{min-width:245px}.options-container .options-search{background-color:#e9e9e9;position:relative;margin-bottom:0!important}.options-container .options-search .no-options{font-size:.8rem;text-align:center;background-color:#fff;padding:.8rem 0}.options-container .options-search .trv-select-search-input{padding-left:28px}.options-container .options-search>.ng-fa-icon{position:absolute;top:7px;left:12px;color:#1e5c88;opacity:.5;z-index:9}.options-container .options-list-container{min-width:245px}.options-container .options-list-container:focus{outline:none}.options-container .options-list-container .options-list{list-style-type:none;padding:0;margin:0}.options-container .options-list-container .options-list .options-list-item{cursor:pointer;color:#1e5c88;display:flex;align-items:center}.options-container .options-list-container .options-list .options-list-item.option-focused{background:#c6dce5}.options-container .options-list-container .options-list .options-list-item.option-focused .option-label{text-decoration:underline}.options-container .options-list-container .options-list .options-list-item.option-disabled{opacity:.5;color:#999;cursor:default}.options-container .options-list-container .options-list .options-list-item:hover:not(.option-disabled){background:#c6dce5}.options-container .options-list-container .options-list .options-list-item .option-label-container{padding:.75rem .75rem .75rem 2.25rem;margin-bottom:0;flex:1;position:relative;height:42px}.options-container .options-list-container .options-list .options-list-item .option-label-container .option-label{white-space:nowrap;cursor:pointer;width:100%}.options-container .options-list-container .options-list .options-list-item .option-label-container .option-label:after{content:\"\";position:absolute;inset:0}.options-container .options-list-container .options-list .options-list-item .option-label-container .form-check-input{margin-top:.15rem}.options-container .options-list-container .options-list .options-list-item .option-label-container.no-custom-control{padding-left:1rem}.options-container .options-list-container .options-list.enable-favorites .toggle-favorite{color:#e9e9e9}.options-container .options-list-container .options-list.enable-favorites .toggle-favorite.active{color:#1e5c88}.options-container .options-list-container .options-list.enable-favorites .toggle-favorite:hover{color:#071520}\n"], dependencies: [{ kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: TrvTooltipDirective, selector: "[trvTooltip]", inputs: ["trvTooltip", "tooltipDisabled", "tooltipClass", "showDelay", "manualToggle", "position"] }, { kind: "directive", type: TrvShouldFocusDirective, selector: "[trvShouldFocus]", inputs: ["trvShouldFocus", "clickOnFocus", "keyEventsOnFocus"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: CdkVirtualScrollViewport, selector: "cdk-virtual-scroll-viewport", inputs: ["orientation", "appendOnly"], outputs: ["scrolledIndexChange"] }, { kind: "directive", type: CdkFixedSizeVirtualScroll, selector: "cdk-virtual-scroll-viewport[itemSize]", inputs: ["itemSize", "minBufferPx", "maxBufferPx"] }, { kind: "directive", type: CdkVirtualForOf, selector: "[cdkVirtualFor][cdkVirtualForOf]", inputs: ["cdkVirtualForOf", "cdkVirtualForTrackBy", "cdkVirtualForTemplate", "cdkVirtualForTemplateCacheSize"] }, { kind: "pipe", type: TrvSafeHtmlPipe, name: "trvSafeHtml" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvSelectPopoverComponent, decorators: [{
            type: Component,
            args: [{ selector: "trv-select-popover", changeDetection: ChangeDetectionStrategy.OnPush, imports: [
                        FaIconComponent,
                        ReactiveFormsModule,
                        TrvTooltipDirective,
                        TrvShouldFocusDirective,
                        NgClass,
                        NgTemplateOutlet,
                        CdkVirtualScrollViewport,
                        CdkFixedSizeVirtualScroll,
                        CdkVirtualForOf,
                        TrvSafeHtmlPipe,
                    ], template: "<div class=\"options-container\"\r\n     [style.width.px]=\"viewPortPixelWidth\"\r\n     id=\"{{optionsContainerId}}\"\r\n     #optionsContainerRef>\r\n  <div class=\"trv-select-text-width-calculator\"></div>\r\n  <div class=\"options-search form-group\">\r\n    @if (select.enableSearch) {\r\n    <fa-icon [icon]=\"faSearch\"></fa-icon>\r\n    }\r\n    @if ((select.enableSearch && (select.options.length || select.searchControl.value)) || (select.multi && !select.useCustomSelectionTemplate)) {\r\n    <div class=\"input-group p-1\">\r\n      @if (select.enableSearch) {\r\n      <input [trvShouldFocus]=\"true\"\r\n             (keydown)=\"onSearchInputKeyDown($event)\"\r\n             #searchInputRef\r\n             [formControl]=\"select.searchControl\"\r\n             trvDebounce\r\n             class=\"trv-select-search-input form-control form-control-sm\"\r\n             type=\"text\"\r\n             placeholder=\"S\u00F6k...\">\r\n      }\r\n      @if (select.multi && !select.useCustomSelectionTemplate && !select.hideShowSelected) {\r\n      <button type=\"button\"\r\n              #showSelectedOptionsButtonRef\r\n              class=\"btn btn-sm btn-outline-primary-bg-contrast\"\r\n              [ngClass]=\"{ 'active': select.showSelectedOnly }\"\r\n              (keydown)=\"onShowSelectedOptionsButtonKeyDown($event)\"\r\n              (click)=\"select.filterSelected()\">\r\n        {{select.labels.showSelected}}\r\n      </button>\r\n      }\r\n    </div>\r\n    }\r\n    @if (!select.options.length && !select.searchControl.value) {\r\n    <div class=\"no-options text-muted\">\r\n      <fa-icon [icon]=\"faInfoCircle\"\r\n               class=\"me-2\"></fa-icon> {{select.labels.noMoreOptions}}\r\n    </div>\r\n    }\r\n    @if (!select.options.length && select.searchControl.value) {\r\n    <div class=\"no-options text-muted\">\r\n      <fa-icon [icon]=\"faInfoCircle\"\r\n               class=\"me-2\"></fa-icon> {{select.labels.noMatches}}\r\n    </div>\r\n    }\r\n  </div>\r\n\r\n  <ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container>\r\n  <cdk-virtual-scroll-viewport class=\"options-list-container\"\r\n                               [style.height.px]=\"viewPortPixelHeight\"\r\n                               [style.width.px]=\"viewPortPixelWidth\"\r\n                               [itemSize]=\"itemPixelHeight\"\r\n                               tabindex=\"0\"\r\n                               #optionsListViewport\r\n                               (keydown)=\"onOptionsListContainerKeyDown($event)\"\r\n                               [trvShouldFocus]=\"!select.enableSearch\">\r\n    <ul class=\"options-list\"\r\n        [ngClass]=\"{ 'enable-favorites' : select.enableFavorites }\">\r\n      <ng-container *cdkVirtualFor=\"let option of select.options; let i = index; trackBy:select.optionsIdentifier\">\r\n        <li class=\"options-list-item\"\r\n            [trvTooltip]=\"select.optionTooltipDisplayMethod != null ? select.optionTooltipDisplayMethod(option.value) : null\"\r\n            [ngClass]=\"{ \r\n              'option-focused': option.focused,\r\n              'option-disabled': select.disableSelectedOptions && option.selected\r\n            }\">\r\n          @if (!select.useCustomSelectionTemplate) {\r\n          @if (select.multi) {\r\n          <div class=\"form-check option-label-container\">\r\n            <input type=\"checkbox\"\r\n                   class=\"form-check-input\"\r\n                   [checked]=\"option.selected\"\r\n                   (change)=\"select.toggleSelectOption(option, $any($event.target).checked)\"\r\n                   id=\"trv-select-check-{{i}}\"\r\n                   tabindex=\"-1\">\r\n            <label class=\"form-check-label option-label\"\r\n                   for=\"trv-select-check-{{i}}\"\r\n                   [innerHtml]=\"option?.label | trvSafeHtml\"\r\n                   tabindex=\"-1\"></label>\r\n          </div>\r\n          }\r\n          @if (!select.multi) {\r\n          <div class=\"form-check option-label-container\"\r\n               tabindex=\"0\">\r\n            <input type=\"radio\"\r\n                   class=\"form-check-input\"\r\n                   [checked]=\"option.selected\"\r\n                   (change)=\"select.toggleSelectOption(option, $any($event.target).checked)\"\r\n                   id=\"trv-select-radio-{{i}}\">\r\n            <label class=\"form-check-label option-label\"\r\n                   for=\"trv-select-radio-{{i}}\"\r\n                   [innerHTML]=\"option?.label | trvSafeHtml\"></label>\r\n          </div>\r\n          }\r\n          }\r\n          @if (select.useCustomSelectionTemplate) {\r\n          <div class=\"option-label-container no-custom-control\"\r\n               tabindex=\"0\"\r\n               (click)=\"select.toggleSelectOption(option, true)\"\r\n               (keyup.space)=\"select.toggleSelectOption(option, true)\">\r\n            <span class=\"option-label\"\r\n                  [innerHTML]=\"option?.label | trvSafeHtml\"></span>\r\n          </div>\r\n          }\r\n          @if (select.enableFavorites) {\r\n          <button class=\"btn btn-link toggle-favorite\"\r\n                  [ngClass]=\"{ 'active' : select.isFavorite(option) }\"\r\n                  (click)=\"select.toggleFavorite(option, $event)\">\r\n            <fa-icon [icon]=\"faStar\"></fa-icon>\r\n          </button>\r\n          }\r\n        </li>\r\n      </ng-container>\r\n    </ul>\r\n  </cdk-virtual-scroll-viewport>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.text-container{overflow:hidden}.options-container{min-width:245px}.options-container .options-search{background-color:#e9e9e9;position:relative;margin-bottom:0!important}.options-container .options-search .no-options{font-size:.8rem;text-align:center;background-color:#fff;padding:.8rem 0}.options-container .options-search .trv-select-search-input{padding-left:28px}.options-container .options-search>.ng-fa-icon{position:absolute;top:7px;left:12px;color:#1e5c88;opacity:.5;z-index:9}.options-container .options-list-container{min-width:245px}.options-container .options-list-container:focus{outline:none}.options-container .options-list-container .options-list{list-style-type:none;padding:0;margin:0}.options-container .options-list-container .options-list .options-list-item{cursor:pointer;color:#1e5c88;display:flex;align-items:center}.options-container .options-list-container .options-list .options-list-item.option-focused{background:#c6dce5}.options-container .options-list-container .options-list .options-list-item.option-focused .option-label{text-decoration:underline}.options-container .options-list-container .options-list .options-list-item.option-disabled{opacity:.5;color:#999;cursor:default}.options-container .options-list-container .options-list .options-list-item:hover:not(.option-disabled){background:#c6dce5}.options-container .options-list-container .options-list .options-list-item .option-label-container{padding:.75rem .75rem .75rem 2.25rem;margin-bottom:0;flex:1;position:relative;height:42px}.options-container .options-list-container .options-list .options-list-item .option-label-container .option-label{white-space:nowrap;cursor:pointer;width:100%}.options-container .options-list-container .options-list .options-list-item .option-label-container .option-label:after{content:\"\";position:absolute;inset:0}.options-container .options-list-container .options-list .options-list-item .option-label-container .form-check-input{margin-top:.15rem}.options-container .options-list-container .options-list .options-list-item .option-label-container.no-custom-control{padding-left:1rem}.options-container .options-list-container .options-list.enable-favorites .toggle-favorite{color:#e9e9e9}.options-container .options-list-container .options-list.enable-favorites .toggle-favorite.active{color:#1e5c88}.options-container .options-list-container .options-list.enable-favorites .toggle-favorite:hover{color:#071520}\n"] }]
        }], ctorParameters: () => [{ type: TrvUtilityService }, { type: i0.ChangeDetectorRef }], propDecorators: { select: [{
                type: Input
            }], ngContentTemplate: [{
                type: Input
            }], maxVisibleItems: [{
                type: Input
            }], optionsContainerRef: [{
                type: ViewChild,
                args: ["optionsContainerRef"]
            }], optionsListViewport: [{
                type: ViewChild,
                args: ["optionsListViewport"]
            }], searchInputRef: [{
                type: ViewChild,
                args: ["searchInputRef"]
            }], showSelectedOptionsButtonRef: [{
                type: ViewChild,
                args: ["showSelectedOptionsButtonRef"]
            }] } });

class TrvSelectComponent extends trvUnsubscribeMixin() {
    utilityService;
    changeDetectorRef;
    constructor(utilityService, changeDetectorRef) {
        super();
        this.utilityService = utilityService;
        this.changeDetectorRef = changeDetectorRef;
    }
    optionTooltipDisplayMethod = TrvSelectDefaultInputs.optionTooltipDisplayMethod;
    placeholder = TrvSelectDefaultInputs.placeholder;
    displayMethod = TrvSelectDefaultInputs.displayMethod;
    selectedDisplayMethod = TrvSelectDefaultInputs.selectedDisplayMethod;
    faIcon = TrvSelectDefaultInputs.faIcon;
    source = TrvSelectDefaultInputs.source;
    valueKey = TrvSelectDefaultInputs.valueKey;
    sortKey = TrvSelectDefaultInputs.sortKey;
    sortDescending = TrvSelectDefaultInputs.sortDescending;
    enableSearch = TrvSelectDefaultInputs.enableSearch;
    disableClear = TrvSelectDefaultInputs.disableClear;
    multi = TrvSelectDefaultInputs.multi;
    hideShowSelected = TrvSelectDefaultInputs.hideShowSelected;
    favorites = TrvSelectDefaultInputs.favorites;
    labels = TrvSelectDefaultInputs.labels;
    displayAllSelectedOptions = TrvSelectDefaultInputs.displayAllSelectedOptions;
    useCustomSelectionTemplate = TrvSelectDefaultInputs.useCustomSelectionTemplate;
    preventCloseOnSelect = TrvSelectDefaultInputs.preventCloseOnSelect;
    disableSelectedOptions = TrvSelectDefaultInputs.disableSelectedOptions;
    sortMethod = TrvSelectDefaultInputs.sortMethod;
    searchMethod = TrvSelectDefaultInputs.searchMethod;
    maxVisibleItems = 6;
    optionSelected = new EventEmitter();
    optionDeselected = new EventEmitter();
    clear = new EventEmitter();
    favoritesChanged = new EventEmitter();
    popover;
    selectInputGroupRef;
    selectButtonRef;
    optionsContainerId = "options" + new Date().valueOf();
    selectedOptions = [];
    options = [];
    optionsChanged$ = new Subject();
    selectDisabled = false;
    searchControl = new UntypedFormControl();
    trackIndex = 0;
    popoverHasOpened = false;
    rendered = false;
    sourceChanged = false;
    showSelectedOnly = false;
    internalSource = null;
    value;
    enableFavorites;
    faHandPointer = faHandPointer;
    faTimes = faTimes;
    faAngleDown = faAngleDown;
    propagateChange = () => { };
    propagateTouched = () => { };
    ngOnInit() {
        if (this.useCustomSelectionTemplate) {
            this.multi = true;
        }
        this.enableFavorites = this.favoritesChanged.observers.length > 0;
        this.searchControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(this.onSearchInputChange);
        this.labels = this.labels || {
            selectedCount: "valda",
            showSelected: "Visa valda",
            noMoreOptions: "Inga objekt att välja",
            noMatches: "Inga objekt matchar söksträngen",
        };
    }
    ngOnChanges(changes) {
        if (changes.source) {
            this.onSourceChanged(changes.source.currentValue, changes.source.previousValue);
        }
    }
    writeValue(value) {
        this.value = value;
        if (!this.internalSource) {
            if (value && this.multi) {
                this.selectedOptions = value.map(v => new SelectOption(v, this.displayMethod, this.selectedDisplayMethod));
            }
            else if (value) {
                this.selectedOptions = [new SelectOption(value, this.displayMethod, this.selectedDisplayMethod)];
            }
            else {
                this.selectedOptions = [];
            }
        }
        else {
            this.populateSelectedOptions();
        }
        this.utilityService.detectChanges(this.changeDetectorRef);
    }
    dispatchSelectedOptionsChanged() {
        let value = null;
        if (this.multi) {
            value = this.selectedOptions.map(opt => opt.value);
        }
        else if (this.selectedOptions.length) {
            value = this.selectedOptions[0].value;
        }
        this.value = value;
        this.propagateTouched();
        this.propagateChange(value);
        this.utilityService.markForCheck(this.changeDetectorRef);
    }
    onPopoverToggleClick($event) {
        if (this.selectDisabled) {
            $event.preventDefault();
            $event.stopPropagation();
        }
    }
    onPopoverShown() {
        this.popoverHasOpened = true;
        if (this.sourceChanged) {
            this.initAfterSourceChange();
        }
    }
    onPopoverHidden() {
        // this.setPopoverVisibleClass(false);
        //this.dispatchSelectedOptionsChanged();
    }
    onSearchInputChange = (value) => {
        if (value == null) {
            return;
        }
        this.searchAndSort(this.searchAndSortParams());
    };
    onSourceChanged(newVal, oldVal) {
        if ((!newVal || !newVal.length) && (!oldVal || !oldVal.length)) {
            return;
        }
        if (!newVal && oldVal) {
            this.source = [];
            return;
        }
        this.sourceChanged = true;
        if (this.internalSource) {
            this.initAfterSourceChange();
        }
    }
    initAfterSourceChange() {
        this.internalSource = (this.source || []).map(item => {
            return new SelectOption(item, this.displayMethod, this.selectedDisplayMethod, this.trackIndex);
        });
        this.searchAndSort(this.searchAndSortParams());
        if (this.value) {
            this.populateSelectedOptions();
        }
        this.sourceChanged = false;
    }
    toggleFavorite(option, $event) {
        $event.preventDefault();
        $event.stopPropagation();
        this.favorites = this.favorites || [];
        const index = this.favorites.findIndex(val => this.matchesFavorite(val, option, this.valueKey));
        if (index < 0) {
            const val = this.valueKey ? option.value[this.valueKey] : option.value;
            this.favorites.push(val);
        }
        else {
            this.favorites.splice(index, 1);
        }
        this.favoritesChanged.emit(this.favorites);
        this.searchAndSort(this.searchAndSortParams());
    }
    isFavorite(option) {
        if (!this.favorites || !this.favorites.length) {
            return false;
        }
        return this.favorites.some(val => this.matchesFavorite(val, option, this.valueKey));
    }
    toggleSelectOption(option, selected) {
        if (this.disableSelectedOptions && option.selected) {
            return;
        }
        if (selected) {
            this.addSelectOption(option);
        }
        else {
            this.removeSelectOption(option);
        }
    }
    addSelectOption(option) {
        option.selected = true;
        if (!this.multi) {
            this.selectedOptions = this.selectedOptions.filter(so => {
                so.selected = false;
                return false;
            });
        }
        this.selectedOptions.push(option);
        this.dispatchSelectedOptionsChanged();
        this.optionSelected.emit(option.value);
        if ((!this.multi || this.useCustomSelectionTemplate) && !this.preventCloseOnSelect) {
            this.popover.hide();
            this.focusSelect();
        }
        this.utilityService.detectChanges(this.changeDetectorRef);
    }
    refreshSelectedDisplayLabel() {
        this.selectedOptions.map(so => {
            so.selectedLabel = this.selectedDisplayMethod ? this.selectedDisplayMethod(so.value) : so.label;
        });
    }
    removeSelectOption(option) {
        option.selected = false;
        this.selectedOptions.splice(this.selectedOptions.indexOf(option), 1);
        this.dispatchSelectedOptionsChanged();
        this.optionDeselected.emit(option.value);
        this.utilityService.detectChanges(this.changeDetectorRef);
    }
    filterSelected() {
        this.showSelectedOnly = !this.showSelectedOnly;
        if (this.showSelectedOnly) {
            this.options = this.internalSource.filter(o => o.selected);
            this.optionsChanged$.next();
        }
        else {
            this.searchAndSort(this.searchAndSortParams());
        }
    }
    clearSelected(skipDispatch) {
        this.selectedOptions = [];
        this.showSelectedOnly = false;
        if (this.internalSource) {
            this.internalSource.forEach(o => (o.selected = false));
            this.searchAndSort(this.searchAndSortParams());
        }
        if (skipDispatch) {
            return;
        }
        this.dispatchSelectedOptionsChanged();
        this.clear.emit();
        this.focusSelect();
    }
    optionsIdentifier(index, option) {
        return option.trackIndex;
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) {
        this.propagateTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.selectDisabled = isDisabled;
        this.utilityService.detectChanges(this.changeDetectorRef);
    }
    matchesFavorite = (val, option, valueKey) => {
        if (!valueKey) {
            return option.value === val;
        }
        else {
            return option.value[valueKey] === val;
        }
    };
    searchAndSortParams() {
        return {
            source: this.internalSource,
            selectedOptions: this.selectedOptions,
            searchInput: this.searchControl.value,
            displayMethod: this.displayMethod,
            valueKey: this.valueKey,
            sortKey: this.sortKey,
            sortDescending: this.sortDescending,
            favorites: this.favorites,
            sortMethod: this.sortMethod,
        };
    }
    populateSelectedOptions() {
        const value = this.value;
        this.clearSelected(true);
        if (value && this.multi) {
            this.selectedOptions = value.map((v) => this.findOption(v));
        }
        else if (value) {
            this.selectedOptions = [this.findOption(value)];
        }
        this.refreshSelectedDisplayLabel();
        this.selectedOptions = this.selectedOptions.filter(s => {
            if (!s) {
                return false;
            }
            s.selected = true;
            return true;
        });
    }
    findOption(value) {
        const selectedOption = this.internalSource.find(i => (this.valueKey ? i.value[this.valueKey] === value[this.valueKey] : i.value === value));
        if (selectedOption && typeof selectedOption.value === "object" && typeof value === "object") {
            Object.assign(selectedOption.value, value);
        }
        return selectedOption || new SelectOption(value, this.displayMethod, this.selectedDisplayMethod);
    }
    searchAndSort(params) {
        const newList = params.source.filter((item, itemIndex, options) => {
            if (this.searchMethod != null) {
                return this.searchMethod(params.searchInput || "", item, itemIndex, options);
            }
            if (!params.searchInput) {
                item.matchIndex = null;
                return true;
            }
            const label = this.utilityService.removeHtml(item.label.toLowerCase());
            const index = label.search(this.utilityService.escapeRegExp(params.searchInput.toLowerCase()));
            if (params.valueKey) {
                item.matchIndex = index;
            }
            return index !== -1 && (index === 0 || label[index - 1] === " " || !/[a-zåäöA-ZÅÄÖ]/.test(label[index - 1]));
        });
        this.options = this.sortList(newList, params);
        this.optionsChanged$.next();
    }
    sortList(list, params) {
        list.sort((a, b) => {
            if (params.favorites && params.favorites.length) {
                const aIsFavorite = params.favorites.some(val => this.matchesFavorite(val, a, params.valueKey));
                const bIsFavorite = params.favorites.some(val => this.matchesFavorite(val, b, params.valueKey));
                if (aIsFavorite && !bIsFavorite) {
                    return -1;
                }
                else if (!aIsFavorite && bIsFavorite) {
                    return 1;
                }
            }
            return this.sortMethod ? this.sortMethod(a, b) : this.sortFunc(a, b, params);
        });
        return list;
    }
    sortFunc = (a, b, params) => {
        if (params.sortDescending) {
            const temp = a;
            a = b;
            b = temp;
        }
        if (!params.valueKey || !params.sortKey) {
            return a.label.localeCompare(b.label);
        }
        if (!params.searchInput || a.matchIndex === b.matchIndex) {
            if (params.sortKey) {
                if (!isNaN(a.value[params.sortKey])) {
                    return a.value[params.sortKey] - b.value[params.sortKey];
                }
                return a.value[params.sortKey].localeCompare(b.value[params.sortKey]);
            }
            else {
                return a.value.localeCompare(b.value);
            }
        }
        return a.matchIndex - b.matchIndex;
    };
    focusSelect() {
        setTimeout(() => this.selectButtonRef.nativeElement.focus());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvSelectComponent, deps: [{ token: TrvUtilityService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvSelectComponent, isStandalone: true, selector: "trv-select", inputs: { optionTooltipDisplayMethod: "optionTooltipDisplayMethod", placeholder: "placeholder", displayMethod: "displayMethod", selectedDisplayMethod: "selectedDisplayMethod", faIcon: "faIcon", source: "source", valueKey: "valueKey", sortKey: "sortKey", sortDescending: "sortDescending", enableSearch: "enableSearch", disableClear: "disableClear", multi: "multi", hideShowSelected: "hideShowSelected", favorites: "favorites", labels: "labels", displayAllSelectedOptions: "displayAllSelectedOptions", useCustomSelectionTemplate: "useCustomSelectionTemplate", preventCloseOnSelect: "preventCloseOnSelect", disableSelectedOptions: "disableSelectedOptions", sortMethod: "sortMethod", searchMethod: "searchMethod", maxVisibleItems: "maxVisibleItems" }, outputs: { optionSelected: "optionSelected", optionDeselected: "optionDeselected", clear: "clear", favoritesChanged: "favoritesChanged" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TrvSelectComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "popover", first: true, predicate: ["popover"], descendants: true }, { propertyName: "selectInputGroupRef", first: true, predicate: ["selectInputGroupRef"], descendants: true }, { propertyName: "selectButtonRef", first: true, predicate: ["selectButtonRef"], descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div class=\"input-group\" #selectInputGroupRef>\r\n    <button\r\n        class=\"trv-select-button form-control\"\r\n        type=\"button\"\r\n        #selectButtonRef\r\n        [ngClass]=\"{\r\n            disabled: selectDisabled,\r\n            'text-primary': selectedOptions.length,\r\n            'display-all-selected-options': displayAllSelectedOptions,\r\n            'has-selected-options': selectedOptions.length\r\n        }\"\r\n        [trvPopover]=\"selectPopoverTemplateRef\"\r\n        popoverClass=\"trv-popover-no-container-margin\"\r\n        [position]=\"['bottom-start', 'bottom-end', 'top-start', 'top-end']\"\r\n        #popover=\"trvPopover\"\r\n        (hidden)=\"onPopoverHidden()\"\r\n        (shown)=\"onPopoverShown()\"\r\n        (keydown.space)=\"onPopoverToggleClick($event)\"\r\n        (keydown.enter)=\"onPopoverToggleClick($event)\"\r\n        (click)=\"onPopoverToggleClick($event)\"\r\n        [disabled]=\"selectDisabled\"\r\n    >\r\n        @if (faIcon) {\r\n        <fa-icon [icon]=\"faIcon\" class=\"select-icon me-2\"></fa-icon>\r\n        } @if (!displayAllSelectedOptions && !useCustomSelectionTemplate) { @if (selectedOptions.length === 1) {\r\n        <span class=\"selected-options-label\" [innerHTML]=\"selectedOptions[0].selectedLabel | trvSafeHtml\"></span>\r\n        } @if (selectedOptions.length > 1) {\r\n        <span class=\"selected-options-label\">{{ selectedOptions.length }} {{ labels.selectedCount }}</span>\r\n        } } @if (displayAllSelectedOptions && !useCustomSelectionTemplate) {\r\n        <div>\r\n            @for (option of selectedOptions; track option) {\r\n            <span class=\"selected-option-block-label\" [innerHTML]=\"option.selectedLabel | trvSafeHtml\"></span>\r\n            }\r\n        </div>\r\n        } @if (placeholder && !selectedOptions.length || useCustomSelectionTemplate) {\r\n        <span class=\"placeholder\">{{ placeholder }}</span>\r\n        }\r\n        <fa-icon [icon]=\"faAngleDown\" class=\"select-icon ms-auto ps-2\"></fa-icon>\r\n    </button>\r\n    @if ((selectedOptions.length && !useCustomSelectionTemplate) && !disableClear) {\r\n    <button class=\"btn btn-outline-primary input-clear-btn\" type=\"button\" [disabled]=\"selectDisabled\" (click)=\"clearSelected()\">\r\n        <fa-icon [icon]=\"faTimes\"></fa-icon>\r\n    </button>\r\n    }\r\n</div>\r\n\r\n<ng-template #selectPopoverTemplateRef>\r\n    <trv-select-popover [select]=\"this\" [maxVisibleItems]=\"maxVisibleItems\" [ngContentTemplate]=\"ngContent\"></trv-select-popover>\r\n</ng-template>\r\n\r\n<ng-template #ngContent>\r\n    <ng-content></ng-content>\r\n</ng-template>\r\n", styles: [":root{--bs-border-color: #ced4da}:host{display:block;min-width:120px}:host.ng-invalid.ng-dirty .input-group .form-control{border:solid 1px #870000}.selected-options-table{box-shadow:0 .35em .35em #0000001a;margin-bottom:.5rem}.selected-options-table td{padding:.25rem .25rem .25rem .75rem;vertical-align:middle}.input-clear-btn:disabled{background-color:var(--bs-secondary-bg)!important;opacity:1}.input-clear-btn:hover:focus,.input-clear-btn:focus:focus{color:var(--bs-body-color);background-color:var(--bs-body-bg);border-color:#8faec4;outline:0;box-shadow:0 0 0 .25rem #1e5c8840}.input-clear-btn:hover,.input-clear-btn:focus,.input-clear-btn{cursor:pointer}.trv-select-button{background:#fff;border:solid 1px #ced4da;position:relative;text-align:left;display:flex;flex-wrap:nowrap;align-items:center}.trv-select-button .selected-options-label{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;width:100%;padding-right:.75rem}.trv-select-button.display-all-selected-options{height:auto;align-items:center}.trv-select-button.display-all-selected-options.has-selected-options{padding:.15rem .75rem}.trv-select-button.display-all-selected-options.has-selected-options fa-icon{margin-top:2px}.trv-select-button.display-all-selected-options .selected-option-block-label{background-color:#dee2e6;color:#212529;display:inline-block;margin-right:.25rem;min-width:30px;padding:.1rem .5rem;margin:.15rem;text-align:center;border:1px solid #adb5bd;border-radius:3px;font-size:.875rem}.trv-select-button .placeholder{color:#999;background-color:transparent;opacity:unset;cursor:pointer}.trv-select-button:focus:focus{color:var(--bs-body-color);background-color:var(--bs-body-bg);border-color:#8faec4;outline:0;box-shadow:0 0 0 .25rem #1e5c8840}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: TrvPopoverDirective, selector: "[trvPopover]", inputs: ["trvPopover", "manualToggle", "position", "popoverClass", "allowMultiple", "disablePopover", "autoFocus"], outputs: ["shown", "hidden", "clickOutside", "afterClose", "afterCloseWithType"], exportAs: ["trvPopover"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "component", type: TrvSelectPopoverComponent, selector: "trv-select-popover", inputs: ["select", "ngContentTemplate", "maxVisibleItems"] }, { kind: "pipe", type: TrvSafeHtmlPipe, name: "trvSafeHtml" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: "trv-select", changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TrvSelectComponent),
                            multi: true,
                        },
                    ], imports: [NgClass, TrvPopoverDirective, FaIconComponent, TrvSelectPopoverComponent, TrvSafeHtmlPipe], template: "<div class=\"input-group\" #selectInputGroupRef>\r\n    <button\r\n        class=\"trv-select-button form-control\"\r\n        type=\"button\"\r\n        #selectButtonRef\r\n        [ngClass]=\"{\r\n            disabled: selectDisabled,\r\n            'text-primary': selectedOptions.length,\r\n            'display-all-selected-options': displayAllSelectedOptions,\r\n            'has-selected-options': selectedOptions.length\r\n        }\"\r\n        [trvPopover]=\"selectPopoverTemplateRef\"\r\n        popoverClass=\"trv-popover-no-container-margin\"\r\n        [position]=\"['bottom-start', 'bottom-end', 'top-start', 'top-end']\"\r\n        #popover=\"trvPopover\"\r\n        (hidden)=\"onPopoverHidden()\"\r\n        (shown)=\"onPopoverShown()\"\r\n        (keydown.space)=\"onPopoverToggleClick($event)\"\r\n        (keydown.enter)=\"onPopoverToggleClick($event)\"\r\n        (click)=\"onPopoverToggleClick($event)\"\r\n        [disabled]=\"selectDisabled\"\r\n    >\r\n        @if (faIcon) {\r\n        <fa-icon [icon]=\"faIcon\" class=\"select-icon me-2\"></fa-icon>\r\n        } @if (!displayAllSelectedOptions && !useCustomSelectionTemplate) { @if (selectedOptions.length === 1) {\r\n        <span class=\"selected-options-label\" [innerHTML]=\"selectedOptions[0].selectedLabel | trvSafeHtml\"></span>\r\n        } @if (selectedOptions.length > 1) {\r\n        <span class=\"selected-options-label\">{{ selectedOptions.length }} {{ labels.selectedCount }}</span>\r\n        } } @if (displayAllSelectedOptions && !useCustomSelectionTemplate) {\r\n        <div>\r\n            @for (option of selectedOptions; track option) {\r\n            <span class=\"selected-option-block-label\" [innerHTML]=\"option.selectedLabel | trvSafeHtml\"></span>\r\n            }\r\n        </div>\r\n        } @if (placeholder && !selectedOptions.length || useCustomSelectionTemplate) {\r\n        <span class=\"placeholder\">{{ placeholder }}</span>\r\n        }\r\n        <fa-icon [icon]=\"faAngleDown\" class=\"select-icon ms-auto ps-2\"></fa-icon>\r\n    </button>\r\n    @if ((selectedOptions.length && !useCustomSelectionTemplate) && !disableClear) {\r\n    <button class=\"btn btn-outline-primary input-clear-btn\" type=\"button\" [disabled]=\"selectDisabled\" (click)=\"clearSelected()\">\r\n        <fa-icon [icon]=\"faTimes\"></fa-icon>\r\n    </button>\r\n    }\r\n</div>\r\n\r\n<ng-template #selectPopoverTemplateRef>\r\n    <trv-select-popover [select]=\"this\" [maxVisibleItems]=\"maxVisibleItems\" [ngContentTemplate]=\"ngContent\"></trv-select-popover>\r\n</ng-template>\r\n\r\n<ng-template #ngContent>\r\n    <ng-content></ng-content>\r\n</ng-template>\r\n", styles: [":root{--bs-border-color: #ced4da}:host{display:block;min-width:120px}:host.ng-invalid.ng-dirty .input-group .form-control{border:solid 1px #870000}.selected-options-table{box-shadow:0 .35em .35em #0000001a;margin-bottom:.5rem}.selected-options-table td{padding:.25rem .25rem .25rem .75rem;vertical-align:middle}.input-clear-btn:disabled{background-color:var(--bs-secondary-bg)!important;opacity:1}.input-clear-btn:hover:focus,.input-clear-btn:focus:focus{color:var(--bs-body-color);background-color:var(--bs-body-bg);border-color:#8faec4;outline:0;box-shadow:0 0 0 .25rem #1e5c8840}.input-clear-btn:hover,.input-clear-btn:focus,.input-clear-btn{cursor:pointer}.trv-select-button{background:#fff;border:solid 1px #ced4da;position:relative;text-align:left;display:flex;flex-wrap:nowrap;align-items:center}.trv-select-button .selected-options-label{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;width:100%;padding-right:.75rem}.trv-select-button.display-all-selected-options{height:auto;align-items:center}.trv-select-button.display-all-selected-options.has-selected-options{padding:.15rem .75rem}.trv-select-button.display-all-selected-options.has-selected-options fa-icon{margin-top:2px}.trv-select-button.display-all-selected-options .selected-option-block-label{background-color:#dee2e6;color:#212529;display:inline-block;margin-right:.25rem;min-width:30px;padding:.1rem .5rem;margin:.15rem;text-align:center;border:1px solid #adb5bd;border-radius:3px;font-size:.875rem}.trv-select-button .placeholder{color:#999;background-color:transparent;opacity:unset;cursor:pointer}.trv-select-button:focus:focus{color:var(--bs-body-color);background-color:var(--bs-body-bg);border-color:#8faec4;outline:0;box-shadow:0 0 0 .25rem #1e5c8840}\n"] }]
        }], ctorParameters: () => [{ type: TrvUtilityService }, { type: i0.ChangeDetectorRef }], propDecorators: { optionTooltipDisplayMethod: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], displayMethod: [{
                type: Input
            }], selectedDisplayMethod: [{
                type: Input
            }], faIcon: [{
                type: Input
            }], source: [{
                type: Input
            }], valueKey: [{
                type: Input
            }], sortKey: [{
                type: Input
            }], sortDescending: [{
                type: Input
            }], enableSearch: [{
                type: Input
            }], disableClear: [{
                type: Input
            }], multi: [{
                type: Input
            }], hideShowSelected: [{
                type: Input
            }], favorites: [{
                type: Input
            }], labels: [{
                type: Input
            }], displayAllSelectedOptions: [{
                type: Input
            }], useCustomSelectionTemplate: [{
                type: Input
            }], preventCloseOnSelect: [{
                type: Input
            }], disableSelectedOptions: [{
                type: Input
            }], sortMethod: [{
                type: Input
            }], searchMethod: [{
                type: Input
            }], maxVisibleItems: [{
                type: Input
            }], optionSelected: [{
                type: Output
            }], optionDeselected: [{
                type: Output
            }], clear: [{
                type: Output
            }], favoritesChanged: [{
                type: Output
            }], popover: [{
                type: ViewChild,
                args: ["popover"]
            }], selectInputGroupRef: [{
                type: ViewChild,
                args: ["selectInputGroupRef"]
            }], selectButtonRef: [{
                type: ViewChild,
                args: ["selectButtonRef"]
            }] } });

class TrvDashboardSideNavComponent {
    router;
    dashboardService;
    changeDetectorRef;
    utilityService;
    ngUnsubscribe = new Subject();
    isOpen;
    openChange;
    navItems;
    showLessEnabled;
    mode;
    collapseOpen;
    activeUrl;
    showLess;
    faThumbtack = faThumbtack;
    faAngleRight = faAngleRight;
    faAngleDown = faAngleDown;
    faPlus = faPlus;
    faMinus = faMinus;
    faAngleDoubleLeft = faAngleDoubleLeft;
    faAngleDoubleRight = faAngleDoubleRight;
    constructor(router, dashboardService, changeDetectorRef, utilityService) {
        this.router = router;
        this.dashboardService = dashboardService;
        this.changeDetectorRef = changeDetectorRef;
        this.utilityService = utilityService;
        this.openChange = dashboardService.openChange;
    }
    ngOnInit() {
        this.navItems = this.dashboardService.setNavItems(this.navItems);
        this.mode = this.dashboardService.mode;
        const startOpen = this.isOpen != null ? this.isOpen : this.mode === 'side' ? true : false;
        this.updateIsOpen(startOpen);
        this.dashboardService.modeChange
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((mode) => {
            this.mode = mode;
            this.utilityService.detectChanges(this.changeDetectorRef);
        });
        this.dashboardService.openChange
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((isOpen) => {
            this.isOpen = isOpen;
            this.utilityService.detectChanges(this.changeDetectorRef);
        });
        this.dashboardService.detectChanges
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
            this.utilityService.detectChanges(this.changeDetectorRef);
        });
        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((event) => {
            this.activeUrl = decodeURI(event.url);
            if (this.mode === 'over') {
                this.updateIsOpen(false);
            }
            this.expandActive();
            this.utilityService.detectChanges(this.changeDetectorRef);
        });
        this.dashboardService.customRouterLink
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((customRouterLink) => {
            this.activeUrl = customRouterLink;
            this.expandActive();
            this.utilityService.detectChanges(this.changeDetectorRef);
        });
        this.handleBrowserWidth();
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    toggleNavigationMode() {
        this.mode = this.mode === 'side' ? 'over' : 'side';
        this.dashboardService.modeChange.emit(this.mode);
    }
    toggleSideNavShowLess() {
        this.showLess = !this.showLess;
        this.dashboardService.sideNavShowLessChange.emit(this.showLess);
    }
    onResize(event) {
        this.handleBrowserWidth();
    }
    onClick($event) {
        if (this.mode === 'side') {
            return;
        }
        if (this.isOpen) {
            const backdrop = document.querySelector('.menu-backdrop');
            if (backdrop.contains($event.target)) {
                this.updateIsOpen(false);
            }
        }
    }
    expandActive() {
        this.expandRecursive(this.navItems);
    }
    expandRecursive(navItems) {
        if (!navItems || !navItems.length) {
            return false;
        }
        let returnValue = false;
        navItems.forEach(ni => {
            if (!(ni instanceof RouterNavigationItem)) {
                return;
            }
            if (this.dashboardService.isEqualPath(ni.routerLink, this.activeUrl.split('?')[0])) {
                if (ni.isCollapsable) {
                    ni.isCollapsed = false;
                }
                ni.hasActiveChild = false;
                returnValue = true;
            }
            else if (this.expandRecursive(ni.subNavigationItems)) {
                if (ni.isCollapsable) {
                    ni.isCollapsed = false;
                }
                ni.hasActiveChild = true;
                returnValue = true;
            }
            else {
                if (ni.isCollapsable) {
                    ni.isCollapsed = true;
                }
                ni.hasActiveChild = false;
            }
        });
        return returnValue;
    }
    isExternalNavigationItem(navItem) {
        return navItem instanceof ExternalNavigationItem;
    }
    updateIsOpen(isOpen) {
        this.isOpen = isOpen;
        this.openChange.emit(this.isOpen);
    }
    navigateByUrl(routerLink) {
        this.router.navigateByUrl(routerLink);
    }
    handleBrowserWidth() {
        let mode;
        if (window.innerWidth < 1200) {
            mode = 'over';
            this.dashboardService.modeChange.emit(mode);
        }
        else {
            mode = 'side';
            this.dashboardService.modeChange.emit(mode);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardSideNavComponent, deps: [{ token: i2.Router }, { token: TrvDashboardService }, { token: i0.ChangeDetectorRef }, { token: TrvUtilityService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvDashboardSideNavComponent, isStandalone: true, selector: "trv-dashboard-side-nav", inputs: { isOpen: "isOpen", navItems: "navItems", showLessEnabled: "showLessEnabled" }, outputs: { openChange: "openChange" }, host: { listeners: { "window:resize": "onResize($event)", "document:click": "onClick($event)" } }, ngImport: i0, template: "@if (mode === 'over' && isOpen) {\r\n<div #backdropRef class=\"menu-backdrop\"></div>\r\n}\r\n\r\n<nav\r\n  class=\"col sidebar mode-{{ mode }}\"\r\n  [ngClass]=\"{\r\n    'sidebar-full-width': !showLess,\r\n    'sidebar-less-info': showLess\r\n  }\"\r\n  [hidden]=\"!isOpen\">\r\n  <div class=\"menu-pin-container\"></div>\r\n  <div\r\n    class=\"sidebar-sticky\"\r\n    [ngClass]=\"{\r\n      'sidebar-full': !showLessEnabled,\r\n      'sidebar-show-less': showLessEnabled\r\n    }\">\r\n    <div class=\"scroll-content\">\r\n      <ul class=\"nav flex-column\">\r\n        <ng-container *ngTemplateOutlet=\"recursiveList; context: { navItems: navItems }\"></ng-container>\r\n      </ul>\r\n    </div>\r\n  </div>\r\n  @if (showLessEnabled) {\r\n  <div class=\"nav-item-container show-less-item\">\r\n    <a\r\n      class=\"nav-item-link\"\r\n      style=\"text-align: right\"\r\n      (click)=\"toggleSideNavShowLess()\"\r\n      (keyup.enter)=\"toggleSideNavShowLess()\"\r\n      [trvTooltip]=\"showLess ? 'Visa fullst\u00E4ndig meny' : 'Visa endast ikoner'\"\r\n      [position]=\"['right']\"\r\n      [showDelay]=\"150\">\r\n      @if (showLess) {\r\n      <fa-icon [icon]=\"faAngleDoubleRight\"></fa-icon>\r\n      } @if (!showLess) {\r\n      <fa-icon class=\"me-3\" [icon]=\"faAngleDoubleLeft\"></fa-icon>\r\n      }\r\n    </a>\r\n  </div>\r\n  }\r\n</nav>\r\n\r\n<ng-template #recursiveList let-navItems=\"navItems\">\r\n  @for (navItem of navItems; track navItem) { @if (!navItem.hidden) { @if(navItem.select){\r\n  <li class=\"nav-item\">\r\n    <div class=\"nav-item-container\">\r\n      <a tabindex=\"0\" class=\"nav-item-link\" [trvTooltip]=\"navItem.label\" [tooltipDisabled]=\"!showLess\">\r\n        <div [trvTooltip]=\"navItem.tooltip\" [tooltipDisabled]=\"showLess\">\r\n          @if (navItem.icon && !navItem.parent && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.icon\" class=\"me-2\"></fa-icon>\r\n          } @if (navItem.icon && navItem.iconCounter) {\r\n          <fa-layers [ngClass]=\"{ 'me-3': !navItem.parent }\">\r\n            <fa-icon [icon]=\"navItem.icon\"></fa-icon>\r\n            <fa-layers-counter [content]=\"navItem.iconCounter\" [class]=\"navItem.iconCounterClasses\"> </fa-layers-counter>\r\n          </fa-layers>\r\n          }\r\n          <trv-select\r\n            class=\"nav-item-select\"\r\n            [source]=\"navItem.select.items\"\r\n            (optionSelected)=\"navItem.select.optionSelected($event)\"\r\n            [formControl]=\"navItem.select.formControl\"\r\n            [placeholder]=\"navItem.select.options.placeholder\"\r\n            [displayMethod]=\"navItem.select.options.displayMethod\"\r\n            [selectedDisplayMethod]=\"navItem.select.options.selectedDisplayMethod\"\r\n            [faIcon]=\"navItem.select.options.faIcon\"\r\n            [valueKey]=\"navItem.select.options.valueKey\"\r\n            [sortKey]=\"navItem.select.options.sortKey\"\r\n            [sortDescending]=\"navItem.select.options.sortDescending\"\r\n            [enableSearch]=\"navItem.select.options.enableSearch\"\r\n            [disableClear]=\"navItem.select.options.disableClear\"\r\n            [multi]=\"navItem.select.options.multi\"\r\n            [hideShowSelected]=\"navItem.select.options.hideShowSelected\"\r\n            [favorites]=\"navItem.select.options.favorites\"\r\n            [labels]=\"navItem.select.options.labels\"\r\n            [displayAllSelectedOptions]=\"navItem.select.options.displayAllSelectedOptions\"\r\n            [useCustomSelectionTemplate]=\"navItem.select.options.useCustomSelectionTemplate\"\r\n            [preventCloseOnSelect]=\"navItem.select.options.preventCloseOnSelect\"\r\n            [disableSelectedOptions]=\"navItem.select.options.disableSelectedOptions\"\r\n            [sortMethod]=\"navItem.select.options.sortMethod\"\r\n            [searchMethod]=\"navItem.select.options.searchMethod\">\r\n          </trv-select>\r\n        </div>\r\n      </a>\r\n    </div>\r\n  </li>\r\n  } @else if (navItem.isDivider){\r\n  <li class=\"nav-item-divider\" [trvTooltip]=\"navItem.tooltip\" [tooltipDisabled]=\"showLess\">\r\n    <span [ngClass]=\"navItem.overflow ? 'labelOverflow-' + navItem.overflow : ''\">\r\n      {{ showLess ? navItem.label[0] : navItem.label }}\r\n    </span>\r\n  </li>\r\n  } @else if (!navItem.isDivider) {\r\n  <li\r\n    class=\"nav-item\"\r\n    [id]=\"'nav-item-' + navItem.key\"\r\n    [ngClass]=\"{\r\n      expanded: navItem.isCollapsable && !navItem.isCollapsed,\r\n      active: dashboardService.isEqualPath(navItem.routerLink, activeUrl) || navItem.active,\r\n      'child-active': navItem.hasActiveChild\r\n    }\">\r\n    <div class=\"nav-item-container\">\r\n      @if (navItem.href) {\r\n      <a\r\n        class=\"nav-item-link\"\r\n        [ngClass]=\"{\r\n          'sub-item-link': !showLess,\r\n          'sub-item-link-less': showLess\r\n        }\"\r\n        [href]=\"navItem.href\"\r\n        target=\"_blank\"\r\n        [trvTooltip]=\"navItem.label\"\r\n        [tooltipDisabled]=\"!showLess\"\r\n        [position]=\"['right']\"\r\n        [showDelay]=\"150\">\r\n        <div [trvTooltip]=\"navItem.tooltip\" [tooltipDisabled]=\"showLess\">\r\n          @if (navItem.parent && (navItem.subIcon || navItem.icon) && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.subIcon || navItem.icon\"></fa-icon>\r\n          } @if (navItem.icon && !navItem.parent && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.icon\" class=\"me-2\"></fa-icon>\r\n          } @if (navItem.icon && navItem.iconCounter) {\r\n          <fa-layers [ngClass]=\"{ 'me-3': !navItem.parent }\">\r\n            <fa-icon [icon]=\"navItem.icon\"></fa-icon>\r\n            <fa-layers-counter [content]=\"navItem.iconCounter\" [class]=\"navItem.iconCounterClasses\"> </fa-layers-counter>\r\n          </fa-layers>\r\n          }\r\n          <span [ngClass]=\"navItem.overflow ? 'labelOverflow-' + navItem.overflow : ''\">{{ showLess ? '' : navItem.label }}</span>\r\n        </div>\r\n      </a>\r\n      } @if (navItem.onClick) {\r\n      <a\r\n        tabindex=\"0\"\r\n        class=\"nav-item-link\"\r\n        [ngClass]=\"{\r\n          'sub-item-link': !showLess,\r\n          'sub-item-link-less': showLess\r\n        }\"\r\n        (click)=\"navItem.onClick ? navItem.onClick() : null\"\r\n        (keyup.enter)=\"navItem.onClick ? navItem.onClick() : null\"\r\n        [trvTooltip]=\"navItem.label\"\r\n        [tooltipDisabled]=\"!showLess\"\r\n        [position]=\"['right']\"\r\n        [showDelay]=\"150\">\r\n        <div [trvTooltip]=\"navItem.tooltip\" [tooltipDisabled]=\"showLess\">\r\n          @if (navItem.parent && (navItem.subIcon || navItem.icon) && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.subIcon || navItem.icon\"></fa-icon>\r\n          } @if (navItem.icon && !navItem.parent && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.icon\" class=\"me-2\"></fa-icon>\r\n          } @if (navItem.icon && navItem.iconCounter) {\r\n          <fa-layers [ngClass]=\"{ 'me-3': !navItem.parent }\">\r\n            <fa-icon [icon]=\"navItem.icon\"></fa-icon>\r\n            <fa-layers-counter [content]=\"navItem.iconCounter\" [class]=\"navItem.iconCounterClasses\"> </fa-layers-counter>\r\n          </fa-layers>\r\n          }\r\n          <span [ngClass]=\"navItem.overflow ? 'labelOverflow-' + navItem.overflow : ''\">{{ showLess ? '' : navItem.label }}</span>\r\n        </div>\r\n      </a>\r\n      } @if (navItem.routerLink) {\r\n      <a\r\n        tabindex=\"0\"\r\n        class=\"nav-item-link\"\r\n        [ngClass]=\"{\r\n          'sub-item-link': !showLess,\r\n          'sub-item-link-less': showLess\r\n        }\"\r\n        (click)=\"!navItem.isRealBrowserLink && !navItem.disableNavigation ? navigateByUrl(navItem.routerLink) : undefined\"\r\n        (keyup.enter)=\"!navItem.isRealBrowserLink && !navItem.disableNavigation ? navigateByUrl(navItem.routerLink) : undefined\"\r\n        [routerLink]=\"navItem.isRealBrowserLink && !navItem.disableNavigation ? [navItem.routerLink] : undefined\"\r\n        [queryParams]=\"navItem.isRealBrowserLink && !navItem.disableNavigation ? navItem.queryParams : undefined\"\r\n        [attr.aria-expanded]=\"navItem.isCollapsable ? !navItem.isCollapsed : ''\"\r\n        [attr.aria-controls]=\"navItem.isCollapsable ? 'sub-nav-' + navItem.key : ''\"\r\n        [trvTooltip]=\"navItem.label\"\r\n        [tooltipDisabled]=\"!showLess\"\r\n        [position]=\"['right']\"\r\n        [showDelay]=\"150\">\r\n        <div [trvTooltip]=\"navItem.tooltip\" [tooltipDisabled]=\"showLess\">\r\n          @if (navItem.parent && (navItem.subIcon || navItem.icon) && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.subIcon || navItem.icon\"></fa-icon>\r\n          } @if (navItem.icon && !navItem.parent && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.icon\" class=\"me-2\"></fa-icon>\r\n          } @if (navItem.icon && navItem.iconCounter) {\r\n          <fa-layers [ngClass]=\"{ 'me-3': !navItem.parent }\">\r\n            <fa-icon [icon]=\"navItem.icon\"></fa-icon>\r\n            <fa-layers-counter [content]=\"navItem.iconCounter\" [class]=\"navItem.iconCounterClasses\"> </fa-layers-counter>\r\n          </fa-layers>\r\n          }\r\n          <span [ngClass]=\"navItem.overflow ? 'labelOverflow-' + navItem.overflow : ''\">{{ showLess ? '' : navItem.label }}</span>\r\n        </div>\r\n      </a>\r\n      } @if (navItem.isCollapsable && !showLess) {\r\n\r\n      <fa-icon\r\n        class=\"toggle-collapse\"\r\n        tabindex=\"0\"\r\n        class=\"nav-item__toggle\"\r\n        (click)=\"navItem.isCollapsed = !navItem.isCollapsed\"\r\n        (keyup.enter)=\"navItem.isCollapsed = !navItem.isCollapsed\"\r\n        [icon]=\"navItem.isCollapsed ? faPlus : faMinus\"></fa-icon>\r\n\r\n      }\r\n    </div>\r\n    @if (navItem.subNavigationItems?.length && !navItem.isCollapsed) {\r\n    <ul\r\n      [id]=\"'sub-nav-' + navItem.key\"\r\n      class=\"sub-nav list-unstyled\"\r\n      [@trvCollapseStateAnimation]=\"navItem.isCollapsed ? 'collapsed' : 'expanded'\">\r\n      <ng-container *ngTemplateOutlet=\"recursiveList; context: { navItems: navItem.subNavigationItems }\"> </ng-container>\r\n    </ul>\r\n    }\r\n  </li>\r\n  } } }\r\n</ng-template>\r\n", styles: [":root{--bs-border-color: #ced4da}::-webkit-scrollbar{width:9px;height:9px}::-webkit-scrollbar-thumb{background-color:#838382;border-radius:20px;border:transparent}::-webkit-scrollbar-track{background:transparent}.menu-backdrop{position:fixed;top:0;right:0;left:0;background:#212529;opacity:.5;z-index:1001}.sidebar-full-width{width:260px}.sidebar-less-info{width:55px}.sidebar-less-info .nav-item-link{text-align:center!important}.sidebar{position:fixed;top:0;left:0;z-index:1001;padding:0;background:#fff;overflow:hidden;border-right:1px solid #ccc}.sidebar:after{content:\"\";width:1rem;box-shadow:inset -7px 0 9px -7px #00000080;position:absolute;right:0;top:0;pointer-events:none}.sidebar .nav-item-container{display:flex;align-items:center;justify-content:space-between;flex-shrink:0}.sidebar .nav-item-container>.ng-fa-icon{padding:.8em;width:40px;align-self:normal;display:flex;justify-content:center;align-items:center;color:#212529;fill-opacity:.5}.sidebar .nav-item-container>.ng-fa-icon:hover,.sidebar .nav-item-container>.ng-fa-icon:focus{color:#212529;background:#f4f3f2;cursor:pointer;fill-opacity:1}.sidebar .nav-item-container .nav-item-select{flex:1 0 auto}.sidebar .nav-item-container .nav-item__toggle:focus{outline:none;background:#dee2e6}.sidebar .nav-item-container .nav-item-link{flex:1;outline:none;padding:.6rem .5rem;text-decoration:none;font-weight:600;color:#212529}.sidebar .nav-item-container .nav-item-link fa-icon{font-size:1.3rem;min-width:2rem;display:inline-block;color:#1e5c88}.sidebar .nav-item-container .nav-item-link fa-layers{min-width:2rem;margin-left:-.5rem;margin-right:.5rem}.sidebar .nav-item-container .nav-item-link fa-layers>fa-icon{color:#1e5c88!important;font-size:1.25rem!important}.sidebar .nav-item-container .nav-item-link fa-layers>fa-layers-counter{font-size:2rem}.sidebar .nav-item-container .nav-item-link.readonly{color:#696059}.sidebar .nav-item-container .nav-item-link.readonly:hover{color:#696059;cursor:default}.sidebar .nav-item-container .nav-item-link.text-uppercase{text-transform:uppercase}.sidebar .nav-item-container .nav-item-link:hover,.sidebar .nav-item-container .nav-item-link:focus{cursor:pointer;background:#dcdcdc}.sidebar .nav-item-container .nav-item-link:hover fa-icon,.sidebar .nav-item-container .nav-item-link:focus fa-icon{color:#212529}.sidebar .nav-item-container .nav-item-link>div{display:flex;align-items:center}.sidebar .menu-pin-container{height:60px;position:relative;width:100%;flex-shrink:0}.sidebar .menu-pin-container .btn{font-size:.7em;right:-24px;bottom:-22px;position:absolute;border-radius:0 4px 4px 0;box-shadow:inset 7px 0 9px -7px #000000e6;background-color:#e9e9e9;border:none;color:#212529}.sidebar .menu-pin-container .btn.active,.sidebar .menu-pin-container .btn:hover{background-color:transparent}@media (max-width: 575.98px){.sidebar .menu-pin-container{height:90px}}.sidebar.mode-over{box-shadow:6px 0 5px -2px #00000024}.sidebar.mode-over:after{box-shadow:none}.sidebar.mode-side{display:flex;flex-direction:column;height:100%}.sidebar .sidebar-full{height:95vh}.sidebar .sidebar-show-less{height:88vh}.sidebar .show-less-item{width:inherit;background:inherit;border-top:2px solid #ccc}.sidebar .scroll-content,.sidebar .sidebar-sticky:hover,.sidebar .sidebar-sticky:focus{visibility:visible}.sidebar .sidebar-sticky{position:relative;top:0;visibility:hidden;flex-grow:1;overflow-y:auto}@supports (overflow: overlay){.sidebar .sidebar-sticky{overflow-y:overlay}}.sidebar .sidebar-sticky .nav{font-size:1em;color:#1e5c88}.sidebar .sidebar-sticky .nav .nav-item-divider{text-transform:uppercase;border-bottom:2px solid #ccc;border-top:1px solid #e9e9e9;margin-top:-1px;padding:1rem 1rem .5rem;color:#696059}.sidebar .sidebar-sticky .nav .nav-item{max-width:100%;border-bottom:1px solid #ccc}.sidebar .sidebar-sticky .nav .nav-item:last-child{border-bottom:none}.sidebar .sidebar-sticky .nav .nav-item.expanded ul>li{background-color:#f4f3f2}.sidebar .sidebar-sticky .nav .nav-item.expanded ul>li a{box-shadow:inset #ccc 5px 0}.sidebar .sidebar-sticky .nav .nav-item.expanded ul>li ul>li a{box-shadow:inset #ccc 10px 0}.sidebar .sidebar-sticky .nav .nav-item.expanded ul>li ul>li ul>li a{box-shadow:inset #ccc 20px 0}.sidebar .sidebar-sticky .nav .nav-item.child-active>.nav-item-container a,.sidebar .sidebar-sticky .nav .nav-item.child-active>.nav-item-container>.ng-fa-icon{color:#212529}.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container a{background:#ccc;color:#212529}.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container a:hover,.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container a:focus{color:#212529}.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container a fa-icon{color:#212529}.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container>fa-icon{background:#ccc;color:#212529}.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container>fa-icon:hover,.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container>fa-icon:focus{background:#b7b7b7;color:#212529}.sidebar .sidebar-sticky .nav .nav-item .sub-nav{overflow:hidden}.sidebar .sidebar-sticky .nav .nav-item .sub-nav .sub-item-link fa-icon,.sidebar .sidebar-sticky .nav .nav-item .sub-nav .sub-item-link-less fa-icon{font-size:1rem}.sidebar .sidebar-sticky .nav .nav-item .sub-nav .nav-item{border-top:solid 1px #ccc;border-bottom:none}.sidebar .sidebar-sticky .nav .nav-item .sub-nav .nav-item .sub-item-link{padding-left:1.5rem;font-weight:400;text-transform:none;max-width:100%}.sidebar .sidebar-sticky .nav .nav-item .sub-nav .nav-item .sub-item-link-less{padding-left:.8rem}.sidebar .sidebar-sticky .nav .nav-item .sub-nav .nav-item.active>.nav-item-container a.nav-item-link{background:#ccc}.sidebar .sidebar-sticky .nav .nav-item .sub-nav .nav-item.active>.nav-item-container a.nav-item-link:hover{color:#212529}.sidebar .sidebar-sticky .nav .nav-item .sub-nav li li .sub-item-link{padding-left:2.5rem}.sidebar .sidebar-sticky .nav .nav-item .sub-nav li li .sub-item-link-less{padding-left:1.1rem}.sidebar .sidebar-sticky .nav .nav-item .sub-nav li li li .sub-item-link{padding-left:3.5rem}.sidebar .sidebar-sticky .nav .nav-item .sub-nav li li li .sub-item-link-less{padding-left:1.4rem}@supports (position: -webkit-sticky) or (position: sticky){.sidebar .sidebar-sticky{position:-webkit-sticky;position:sticky}}.labelOverflow-visible{overflow:visible}.labelOverflow-hidden{white-space:nowrap;text-overflow:ellipsis;overflow:hidden}.labelOverflow-scroll{overflow:scroll}.labelOverflow-auto{overflow:auto}\n"], dependencies: [{ kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: TrvTooltipDirective, selector: "[trvTooltip]", inputs: ["trvTooltip", "tooltipDisabled", "tooltipClass", "showDelay", "manualToggle", "position"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "component", type: FaLayersComponent, selector: "fa-layers", inputs: ["size", "fixedWidth"] }, { kind: "component", type: FaLayersCounterComponent, selector: "fa-layers-counter", inputs: ["content", "title", "position"] }, { kind: "component", type: TrvSelectComponent, selector: "trv-select", inputs: ["optionTooltipDisplayMethod", "placeholder", "displayMethod", "selectedDisplayMethod", "faIcon", "source", "valueKey", "sortKey", "sortDescending", "enableSearch", "disableClear", "multi", "hideShowSelected", "favorites", "labels", "displayAllSelectedOptions", "useCustomSelectionTemplate", "preventCloseOnSelect", "disableSelectedOptions", "sortMethod", "searchMethod", "maxVisibleItems"], outputs: ["optionSelected", "optionDeselected", "clear", "favoritesChanged"] }, { kind: "directive", type: RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }], animations: [trvCollapseStateAnimation], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardSideNavComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-dashboard-side-nav', changeDetection: ChangeDetectionStrategy.OnPush, animations: [trvCollapseStateAnimation], imports: [ReactiveFormsModule, NgClass, NgTemplateOutlet, TrvTooltipDirective, FaIconComponent, FaLayersComponent, FaLayersCounterComponent, TrvSelectComponent, RouterLink], template: "@if (mode === 'over' && isOpen) {\r\n<div #backdropRef class=\"menu-backdrop\"></div>\r\n}\r\n\r\n<nav\r\n  class=\"col sidebar mode-{{ mode }}\"\r\n  [ngClass]=\"{\r\n    'sidebar-full-width': !showLess,\r\n    'sidebar-less-info': showLess\r\n  }\"\r\n  [hidden]=\"!isOpen\">\r\n  <div class=\"menu-pin-container\"></div>\r\n  <div\r\n    class=\"sidebar-sticky\"\r\n    [ngClass]=\"{\r\n      'sidebar-full': !showLessEnabled,\r\n      'sidebar-show-less': showLessEnabled\r\n    }\">\r\n    <div class=\"scroll-content\">\r\n      <ul class=\"nav flex-column\">\r\n        <ng-container *ngTemplateOutlet=\"recursiveList; context: { navItems: navItems }\"></ng-container>\r\n      </ul>\r\n    </div>\r\n  </div>\r\n  @if (showLessEnabled) {\r\n  <div class=\"nav-item-container show-less-item\">\r\n    <a\r\n      class=\"nav-item-link\"\r\n      style=\"text-align: right\"\r\n      (click)=\"toggleSideNavShowLess()\"\r\n      (keyup.enter)=\"toggleSideNavShowLess()\"\r\n      [trvTooltip]=\"showLess ? 'Visa fullst\u00E4ndig meny' : 'Visa endast ikoner'\"\r\n      [position]=\"['right']\"\r\n      [showDelay]=\"150\">\r\n      @if (showLess) {\r\n      <fa-icon [icon]=\"faAngleDoubleRight\"></fa-icon>\r\n      } @if (!showLess) {\r\n      <fa-icon class=\"me-3\" [icon]=\"faAngleDoubleLeft\"></fa-icon>\r\n      }\r\n    </a>\r\n  </div>\r\n  }\r\n</nav>\r\n\r\n<ng-template #recursiveList let-navItems=\"navItems\">\r\n  @for (navItem of navItems; track navItem) { @if (!navItem.hidden) { @if(navItem.select){\r\n  <li class=\"nav-item\">\r\n    <div class=\"nav-item-container\">\r\n      <a tabindex=\"0\" class=\"nav-item-link\" [trvTooltip]=\"navItem.label\" [tooltipDisabled]=\"!showLess\">\r\n        <div [trvTooltip]=\"navItem.tooltip\" [tooltipDisabled]=\"showLess\">\r\n          @if (navItem.icon && !navItem.parent && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.icon\" class=\"me-2\"></fa-icon>\r\n          } @if (navItem.icon && navItem.iconCounter) {\r\n          <fa-layers [ngClass]=\"{ 'me-3': !navItem.parent }\">\r\n            <fa-icon [icon]=\"navItem.icon\"></fa-icon>\r\n            <fa-layers-counter [content]=\"navItem.iconCounter\" [class]=\"navItem.iconCounterClasses\"> </fa-layers-counter>\r\n          </fa-layers>\r\n          }\r\n          <trv-select\r\n            class=\"nav-item-select\"\r\n            [source]=\"navItem.select.items\"\r\n            (optionSelected)=\"navItem.select.optionSelected($event)\"\r\n            [formControl]=\"navItem.select.formControl\"\r\n            [placeholder]=\"navItem.select.options.placeholder\"\r\n            [displayMethod]=\"navItem.select.options.displayMethod\"\r\n            [selectedDisplayMethod]=\"navItem.select.options.selectedDisplayMethod\"\r\n            [faIcon]=\"navItem.select.options.faIcon\"\r\n            [valueKey]=\"navItem.select.options.valueKey\"\r\n            [sortKey]=\"navItem.select.options.sortKey\"\r\n            [sortDescending]=\"navItem.select.options.sortDescending\"\r\n            [enableSearch]=\"navItem.select.options.enableSearch\"\r\n            [disableClear]=\"navItem.select.options.disableClear\"\r\n            [multi]=\"navItem.select.options.multi\"\r\n            [hideShowSelected]=\"navItem.select.options.hideShowSelected\"\r\n            [favorites]=\"navItem.select.options.favorites\"\r\n            [labels]=\"navItem.select.options.labels\"\r\n            [displayAllSelectedOptions]=\"navItem.select.options.displayAllSelectedOptions\"\r\n            [useCustomSelectionTemplate]=\"navItem.select.options.useCustomSelectionTemplate\"\r\n            [preventCloseOnSelect]=\"navItem.select.options.preventCloseOnSelect\"\r\n            [disableSelectedOptions]=\"navItem.select.options.disableSelectedOptions\"\r\n            [sortMethod]=\"navItem.select.options.sortMethod\"\r\n            [searchMethod]=\"navItem.select.options.searchMethod\">\r\n          </trv-select>\r\n        </div>\r\n      </a>\r\n    </div>\r\n  </li>\r\n  } @else if (navItem.isDivider){\r\n  <li class=\"nav-item-divider\" [trvTooltip]=\"navItem.tooltip\" [tooltipDisabled]=\"showLess\">\r\n    <span [ngClass]=\"navItem.overflow ? 'labelOverflow-' + navItem.overflow : ''\">\r\n      {{ showLess ? navItem.label[0] : navItem.label }}\r\n    </span>\r\n  </li>\r\n  } @else if (!navItem.isDivider) {\r\n  <li\r\n    class=\"nav-item\"\r\n    [id]=\"'nav-item-' + navItem.key\"\r\n    [ngClass]=\"{\r\n      expanded: navItem.isCollapsable && !navItem.isCollapsed,\r\n      active: dashboardService.isEqualPath(navItem.routerLink, activeUrl) || navItem.active,\r\n      'child-active': navItem.hasActiveChild\r\n    }\">\r\n    <div class=\"nav-item-container\">\r\n      @if (navItem.href) {\r\n      <a\r\n        class=\"nav-item-link\"\r\n        [ngClass]=\"{\r\n          'sub-item-link': !showLess,\r\n          'sub-item-link-less': showLess\r\n        }\"\r\n        [href]=\"navItem.href\"\r\n        target=\"_blank\"\r\n        [trvTooltip]=\"navItem.label\"\r\n        [tooltipDisabled]=\"!showLess\"\r\n        [position]=\"['right']\"\r\n        [showDelay]=\"150\">\r\n        <div [trvTooltip]=\"navItem.tooltip\" [tooltipDisabled]=\"showLess\">\r\n          @if (navItem.parent && (navItem.subIcon || navItem.icon) && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.subIcon || navItem.icon\"></fa-icon>\r\n          } @if (navItem.icon && !navItem.parent && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.icon\" class=\"me-2\"></fa-icon>\r\n          } @if (navItem.icon && navItem.iconCounter) {\r\n          <fa-layers [ngClass]=\"{ 'me-3': !navItem.parent }\">\r\n            <fa-icon [icon]=\"navItem.icon\"></fa-icon>\r\n            <fa-layers-counter [content]=\"navItem.iconCounter\" [class]=\"navItem.iconCounterClasses\"> </fa-layers-counter>\r\n          </fa-layers>\r\n          }\r\n          <span [ngClass]=\"navItem.overflow ? 'labelOverflow-' + navItem.overflow : ''\">{{ showLess ? '' : navItem.label }}</span>\r\n        </div>\r\n      </a>\r\n      } @if (navItem.onClick) {\r\n      <a\r\n        tabindex=\"0\"\r\n        class=\"nav-item-link\"\r\n        [ngClass]=\"{\r\n          'sub-item-link': !showLess,\r\n          'sub-item-link-less': showLess\r\n        }\"\r\n        (click)=\"navItem.onClick ? navItem.onClick() : null\"\r\n        (keyup.enter)=\"navItem.onClick ? navItem.onClick() : null\"\r\n        [trvTooltip]=\"navItem.label\"\r\n        [tooltipDisabled]=\"!showLess\"\r\n        [position]=\"['right']\"\r\n        [showDelay]=\"150\">\r\n        <div [trvTooltip]=\"navItem.tooltip\" [tooltipDisabled]=\"showLess\">\r\n          @if (navItem.parent && (navItem.subIcon || navItem.icon) && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.subIcon || navItem.icon\"></fa-icon>\r\n          } @if (navItem.icon && !navItem.parent && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.icon\" class=\"me-2\"></fa-icon>\r\n          } @if (navItem.icon && navItem.iconCounter) {\r\n          <fa-layers [ngClass]=\"{ 'me-3': !navItem.parent }\">\r\n            <fa-icon [icon]=\"navItem.icon\"></fa-icon>\r\n            <fa-layers-counter [content]=\"navItem.iconCounter\" [class]=\"navItem.iconCounterClasses\"> </fa-layers-counter>\r\n          </fa-layers>\r\n          }\r\n          <span [ngClass]=\"navItem.overflow ? 'labelOverflow-' + navItem.overflow : ''\">{{ showLess ? '' : navItem.label }}</span>\r\n        </div>\r\n      </a>\r\n      } @if (navItem.routerLink) {\r\n      <a\r\n        tabindex=\"0\"\r\n        class=\"nav-item-link\"\r\n        [ngClass]=\"{\r\n          'sub-item-link': !showLess,\r\n          'sub-item-link-less': showLess\r\n        }\"\r\n        (click)=\"!navItem.isRealBrowserLink && !navItem.disableNavigation ? navigateByUrl(navItem.routerLink) : undefined\"\r\n        (keyup.enter)=\"!navItem.isRealBrowserLink && !navItem.disableNavigation ? navigateByUrl(navItem.routerLink) : undefined\"\r\n        [routerLink]=\"navItem.isRealBrowserLink && !navItem.disableNavigation ? [navItem.routerLink] : undefined\"\r\n        [queryParams]=\"navItem.isRealBrowserLink && !navItem.disableNavigation ? navItem.queryParams : undefined\"\r\n        [attr.aria-expanded]=\"navItem.isCollapsable ? !navItem.isCollapsed : ''\"\r\n        [attr.aria-controls]=\"navItem.isCollapsable ? 'sub-nav-' + navItem.key : ''\"\r\n        [trvTooltip]=\"navItem.label\"\r\n        [tooltipDisabled]=\"!showLess\"\r\n        [position]=\"['right']\"\r\n        [showDelay]=\"150\">\r\n        <div [trvTooltip]=\"navItem.tooltip\" [tooltipDisabled]=\"showLess\">\r\n          @if (navItem.parent && (navItem.subIcon || navItem.icon) && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.subIcon || navItem.icon\"></fa-icon>\r\n          } @if (navItem.icon && !navItem.parent && !navItem.iconCounter) {\r\n          <fa-icon [icon]=\"navItem.icon\" class=\"me-2\"></fa-icon>\r\n          } @if (navItem.icon && navItem.iconCounter) {\r\n          <fa-layers [ngClass]=\"{ 'me-3': !navItem.parent }\">\r\n            <fa-icon [icon]=\"navItem.icon\"></fa-icon>\r\n            <fa-layers-counter [content]=\"navItem.iconCounter\" [class]=\"navItem.iconCounterClasses\"> </fa-layers-counter>\r\n          </fa-layers>\r\n          }\r\n          <span [ngClass]=\"navItem.overflow ? 'labelOverflow-' + navItem.overflow : ''\">{{ showLess ? '' : navItem.label }}</span>\r\n        </div>\r\n      </a>\r\n      } @if (navItem.isCollapsable && !showLess) {\r\n\r\n      <fa-icon\r\n        class=\"toggle-collapse\"\r\n        tabindex=\"0\"\r\n        class=\"nav-item__toggle\"\r\n        (click)=\"navItem.isCollapsed = !navItem.isCollapsed\"\r\n        (keyup.enter)=\"navItem.isCollapsed = !navItem.isCollapsed\"\r\n        [icon]=\"navItem.isCollapsed ? faPlus : faMinus\"></fa-icon>\r\n\r\n      }\r\n    </div>\r\n    @if (navItem.subNavigationItems?.length && !navItem.isCollapsed) {\r\n    <ul\r\n      [id]=\"'sub-nav-' + navItem.key\"\r\n      class=\"sub-nav list-unstyled\"\r\n      [@trvCollapseStateAnimation]=\"navItem.isCollapsed ? 'collapsed' : 'expanded'\">\r\n      <ng-container *ngTemplateOutlet=\"recursiveList; context: { navItems: navItem.subNavigationItems }\"> </ng-container>\r\n    </ul>\r\n    }\r\n  </li>\r\n  } } }\r\n</ng-template>\r\n", styles: [":root{--bs-border-color: #ced4da}::-webkit-scrollbar{width:9px;height:9px}::-webkit-scrollbar-thumb{background-color:#838382;border-radius:20px;border:transparent}::-webkit-scrollbar-track{background:transparent}.menu-backdrop{position:fixed;top:0;right:0;left:0;background:#212529;opacity:.5;z-index:1001}.sidebar-full-width{width:260px}.sidebar-less-info{width:55px}.sidebar-less-info .nav-item-link{text-align:center!important}.sidebar{position:fixed;top:0;left:0;z-index:1001;padding:0;background:#fff;overflow:hidden;border-right:1px solid #ccc}.sidebar:after{content:\"\";width:1rem;box-shadow:inset -7px 0 9px -7px #00000080;position:absolute;right:0;top:0;pointer-events:none}.sidebar .nav-item-container{display:flex;align-items:center;justify-content:space-between;flex-shrink:0}.sidebar .nav-item-container>.ng-fa-icon{padding:.8em;width:40px;align-self:normal;display:flex;justify-content:center;align-items:center;color:#212529;fill-opacity:.5}.sidebar .nav-item-container>.ng-fa-icon:hover,.sidebar .nav-item-container>.ng-fa-icon:focus{color:#212529;background:#f4f3f2;cursor:pointer;fill-opacity:1}.sidebar .nav-item-container .nav-item-select{flex:1 0 auto}.sidebar .nav-item-container .nav-item__toggle:focus{outline:none;background:#dee2e6}.sidebar .nav-item-container .nav-item-link{flex:1;outline:none;padding:.6rem .5rem;text-decoration:none;font-weight:600;color:#212529}.sidebar .nav-item-container .nav-item-link fa-icon{font-size:1.3rem;min-width:2rem;display:inline-block;color:#1e5c88}.sidebar .nav-item-container .nav-item-link fa-layers{min-width:2rem;margin-left:-.5rem;margin-right:.5rem}.sidebar .nav-item-container .nav-item-link fa-layers>fa-icon{color:#1e5c88!important;font-size:1.25rem!important}.sidebar .nav-item-container .nav-item-link fa-layers>fa-layers-counter{font-size:2rem}.sidebar .nav-item-container .nav-item-link.readonly{color:#696059}.sidebar .nav-item-container .nav-item-link.readonly:hover{color:#696059;cursor:default}.sidebar .nav-item-container .nav-item-link.text-uppercase{text-transform:uppercase}.sidebar .nav-item-container .nav-item-link:hover,.sidebar .nav-item-container .nav-item-link:focus{cursor:pointer;background:#dcdcdc}.sidebar .nav-item-container .nav-item-link:hover fa-icon,.sidebar .nav-item-container .nav-item-link:focus fa-icon{color:#212529}.sidebar .nav-item-container .nav-item-link>div{display:flex;align-items:center}.sidebar .menu-pin-container{height:60px;position:relative;width:100%;flex-shrink:0}.sidebar .menu-pin-container .btn{font-size:.7em;right:-24px;bottom:-22px;position:absolute;border-radius:0 4px 4px 0;box-shadow:inset 7px 0 9px -7px #000000e6;background-color:#e9e9e9;border:none;color:#212529}.sidebar .menu-pin-container .btn.active,.sidebar .menu-pin-container .btn:hover{background-color:transparent}@media (max-width: 575.98px){.sidebar .menu-pin-container{height:90px}}.sidebar.mode-over{box-shadow:6px 0 5px -2px #00000024}.sidebar.mode-over:after{box-shadow:none}.sidebar.mode-side{display:flex;flex-direction:column;height:100%}.sidebar .sidebar-full{height:95vh}.sidebar .sidebar-show-less{height:88vh}.sidebar .show-less-item{width:inherit;background:inherit;border-top:2px solid #ccc}.sidebar .scroll-content,.sidebar .sidebar-sticky:hover,.sidebar .sidebar-sticky:focus{visibility:visible}.sidebar .sidebar-sticky{position:relative;top:0;visibility:hidden;flex-grow:1;overflow-y:auto}@supports (overflow: overlay){.sidebar .sidebar-sticky{overflow-y:overlay}}.sidebar .sidebar-sticky .nav{font-size:1em;color:#1e5c88}.sidebar .sidebar-sticky .nav .nav-item-divider{text-transform:uppercase;border-bottom:2px solid #ccc;border-top:1px solid #e9e9e9;margin-top:-1px;padding:1rem 1rem .5rem;color:#696059}.sidebar .sidebar-sticky .nav .nav-item{max-width:100%;border-bottom:1px solid #ccc}.sidebar .sidebar-sticky .nav .nav-item:last-child{border-bottom:none}.sidebar .sidebar-sticky .nav .nav-item.expanded ul>li{background-color:#f4f3f2}.sidebar .sidebar-sticky .nav .nav-item.expanded ul>li a{box-shadow:inset #ccc 5px 0}.sidebar .sidebar-sticky .nav .nav-item.expanded ul>li ul>li a{box-shadow:inset #ccc 10px 0}.sidebar .sidebar-sticky .nav .nav-item.expanded ul>li ul>li ul>li a{box-shadow:inset #ccc 20px 0}.sidebar .sidebar-sticky .nav .nav-item.child-active>.nav-item-container a,.sidebar .sidebar-sticky .nav .nav-item.child-active>.nav-item-container>.ng-fa-icon{color:#212529}.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container a{background:#ccc;color:#212529}.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container a:hover,.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container a:focus{color:#212529}.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container a fa-icon{color:#212529}.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container>fa-icon{background:#ccc;color:#212529}.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container>fa-icon:hover,.sidebar .sidebar-sticky .nav .nav-item.active>.nav-item-container>fa-icon:focus{background:#b7b7b7;color:#212529}.sidebar .sidebar-sticky .nav .nav-item .sub-nav{overflow:hidden}.sidebar .sidebar-sticky .nav .nav-item .sub-nav .sub-item-link fa-icon,.sidebar .sidebar-sticky .nav .nav-item .sub-nav .sub-item-link-less fa-icon{font-size:1rem}.sidebar .sidebar-sticky .nav .nav-item .sub-nav .nav-item{border-top:solid 1px #ccc;border-bottom:none}.sidebar .sidebar-sticky .nav .nav-item .sub-nav .nav-item .sub-item-link{padding-left:1.5rem;font-weight:400;text-transform:none;max-width:100%}.sidebar .sidebar-sticky .nav .nav-item .sub-nav .nav-item .sub-item-link-less{padding-left:.8rem}.sidebar .sidebar-sticky .nav .nav-item .sub-nav .nav-item.active>.nav-item-container a.nav-item-link{background:#ccc}.sidebar .sidebar-sticky .nav .nav-item .sub-nav .nav-item.active>.nav-item-container a.nav-item-link:hover{color:#212529}.sidebar .sidebar-sticky .nav .nav-item .sub-nav li li .sub-item-link{padding-left:2.5rem}.sidebar .sidebar-sticky .nav .nav-item .sub-nav li li .sub-item-link-less{padding-left:1.1rem}.sidebar .sidebar-sticky .nav .nav-item .sub-nav li li li .sub-item-link{padding-left:3.5rem}.sidebar .sidebar-sticky .nav .nav-item .sub-nav li li li .sub-item-link-less{padding-left:1.4rem}@supports (position: -webkit-sticky) or (position: sticky){.sidebar .sidebar-sticky{position:-webkit-sticky;position:sticky}}.labelOverflow-visible{overflow:visible}.labelOverflow-hidden{white-space:nowrap;text-overflow:ellipsis;overflow:hidden}.labelOverflow-scroll{overflow:scroll}.labelOverflow-auto{overflow:auto}\n"] }]
        }], ctorParameters: () => [{ type: i2.Router }, { type: TrvDashboardService }, { type: i0.ChangeDetectorRef }, { type: TrvUtilityService }], propDecorators: { isOpen: [{
                type: Input
            }], openChange: [{
                type: Output
            }], navItems: [{
                type: Input
            }], showLessEnabled: [{
                type: Input
            }], onResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event']]
            }] } });

class TrvDashboardTopNavComponent {
    dashboardService;
    trvUtilityService;
    cdr;
    ngUnsubscribe = new Subject();
    menuLabel = 'Applikationsnamn';
    hideMenuToggle = false;
    faBars = faBars;
    menuIsOpen;
    constructor(dashboardService, trvUtilityService, cdr) {
        this.dashboardService = dashboardService;
        this.trvUtilityService = trvUtilityService;
        this.cdr = cdr;
        this.menuIsOpen = this.dashboardService.sideNavOpen ?? false;
    }
    ngOnInit() {
        this.hideMenuToggle = this.hideMenuToggle || this.dashboardService.hideMenu;
        this.dashboardService.hideMenuChange
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(hide => {
            this.hideMenuToggle = hide;
            this.trvUtilityService.detectChanges(this.cdr);
        });
        this.dashboardService.openChange
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
            this.trvUtilityService.detectChanges(this.cdr);
        });
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    onToggleSideNavClick() {
        this.menuIsOpen = !this.dashboardService.sideNavOpen;
        this.dashboardService.openChange.emit(this.menuIsOpen);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardTopNavComponent, deps: [{ token: TrvDashboardService }, { token: TrvUtilityService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvDashboardTopNavComponent, isStandalone: true, selector: "trv-dashboard-top-nav", inputs: { menuLabel: "menuLabel", hideMenuToggle: "hideMenuToggle" }, ngImport: i0, template: "<nav class=\"topbar navbar navbar-dark navbar-expand trv-navbar fixed-top p-0 d-flex justify-content-between\">\r\n  <div class=\"menu-toggle\">\r\n    @if (!hideMenuToggle) {\r\n    <button type=\"button\"\r\n            class=\"btn btn-link\"\r\n            [ngClass]=\"{'active' : dashboardService.sideNavOpen}\"\r\n            (click)=\"onToggleSideNavClick()\">\r\n      <div class=\"nav-icon\">\r\n        <span></span>\r\n      </div>\r\n\r\n      {{menuLabel}}\r\n    </button>\r\n    }\r\n  </div>\r\n\r\n  <div class=\"logo\">\r\n    <div class=\"logo-img\">\r\n      <div>\r\n        <svg version=\"1.1\"\r\n             id=\"Layer_1\"\r\n             xmlns=\"http://www.w3.org/2000/svg\"\r\n             xmlns:xlink=\"http://www.w3.org/1999/xlink\"\r\n             x=\"0px\"\r\n             y=\"0px\"\r\n             width=\"147.3px\"\r\n             height=\"28.8px\"\r\n             viewBox=\"0 0 147.3 28.8\"\r\n             style=\"enable-background:new 0 0 147.3 28.8;\"\r\n             xml:space=\"preserve\">\r\n          <style type=\"text/css\">\r\n            .st0 {\r\n              fill: #FFFFFF;\r\n            }\r\n          </style>\r\n          <g>\r\n            <path class=\"st0\"\r\n                  d=\"M47.3,23.4c0-0.6-0.2-0.8-0.2-0.8s-0.3,0-0.4,0c-0.2,0-0.4,0-0.5-0.1c-0.2-0.1-0.3-0.3-0.4-0.5\r\n           c-0.2-0.3-1.2-1.9-1.2-1.9c0.8-0.2,1.4-0.6,1.9-1.6c0.3-0.6,0.4-1.5,0.4-2.3c0-0.8-0.1-2.4-0.8-3.2c-0.6-0.6-1.5-1.1-3.2-1.1h-4.7\r\n           V24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2v-3.7h2.4c0,0,1.6,2.7,1.9,3.1c0.2,0.4,0.5,0.5,0.7,0.7c0.2,0.1,0.5,0.2,0.8,0.2\r\n           c0.4,0,1.1,0,1.1,0S47.3,24,47.3,23.4z M44.4,18.2c-0.5,0.6-1.3,0.5-2,0.6c0,0-0.9,0-1.2,0c-0.6,0.1-1.1,0.5-1.1,0.5v-5.8h2.2\r\n           c0.2,0,0.5,0,0.6,0c0.5,0,1,0.1,1.4,0.5c0.5,0.6,0.5,1.3,0.5,2.1v0.1C44.8,17.1,44.7,17.8,44.4,18.2z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M115.8,23.4c0-0.6-0.2-0.8-0.2-0.8s-0.3,0-0.4,0c-0.2,0-0.4,0-0.5-0.1c-0.2-0.1-0.3-0.3-0.4-0.5\r\n           c-0.2-0.3-1.2-1.9-1.2-1.9c0.8-0.2,1.4-0.6,1.9-1.6c0.3-0.6,0.4-1.5,0.4-2.3c0-0.8-0.1-2.4-0.8-3.2c-0.6-0.6-1.5-1.1-3.2-1.1h-4.7\r\n           V24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2v-3.7h2.4c0,0,1.6,2.7,1.9,3.1c0.2,0.4,0.5,0.5,0.7,0.7c0.2,0.1,0.5,0.2,0.8,0.2\r\n           c0.4,0,1.1,0,1.1,0S115.8,24,115.8,23.4z M112.9,18.2c-0.5,0.6-1.3,0.5-2,0.6c0,0-0.9,0-1.2,0c-0.6,0.1-1.1,0.5-1.1,0.5v-5.8h2.2\r\n           c0.2,0,0.5,0,0.6,0c0.5,0,1,0.1,1.4,0.5c0.5,0.6,0.5,1.3,0.5,2.1v0.1C113.3,17.1,113.2,17.8,112.9,18.2z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M68.1,11.9h-7.6V24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2v-5.3h4.3c0,0,0.2-0.2,0.2-0.8c0-0.6-0.2-0.8-0.2-0.8\r\n           s-2.7,0-3.3,0c-0.6,0.1-1.1,0.5-1.1,0.5v-4.2H68c0,0,0.2-0.2,0.2-0.8C68.2,12.1,68.1,11.9,68.1,11.9z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M36.3,11.9H27c0,0-0.2,0.2-0.2,0.8s0.2,0.8,0.2,0.8h3.6V24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2V13.4h3.6\r\n           c0,0,0.2-0.2,0.2-0.8C36.4,12.1,36.3,11.9,36.3,11.9z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M147.1,11.9h-9.2c0,0-0.2,0.2-0.2,0.8s0.2,0.8,0.2,0.8h3.6V24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2V13.4h3.6\r\n           c0,0,0.2-0.2,0.2-0.8C147.3,12.1,147.1,11.9,147.1,11.9z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M104.7,23.4c0-0.6-0.2-0.8-0.2-0.8h-6v-4h4.7c0,0,0.2-0.2,0.2-0.8c0-0.6-0.2-0.8-0.2-0.8s-3.3,0-3.7,0\r\n           c-0.6,0.1-1.1,0.5-1.1,0.5v-4.2h5.7c0,0,0.2-0.2,0.2-0.8s-0.2-0.8-0.2-0.8h-7.7v12.4h8C104.6,24.2,104.7,24,104.7,23.4z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M137,23.4c0-0.6-0.2-0.8-0.2-0.8h-6v-4h4.7c0,0,0.2-0.2,0.2-0.8c0-0.6-0.2-0.8-0.2-0.8s-3.3,0-3.7,0\r\n           c-0.6,0.1-1.1,0.5-1.1,0.5v-4.2h5.7c0,0,0.2-0.2,0.2-0.8s-0.2-0.8-0.2-0.8h-7.7v12.4h8C136.8,24.2,137,24,137,23.4z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M70.1,12.1c0,0,0.3-0.2,1-0.2c0.8,0,1,0.2,1,0.2V24c0,0-0.3,0.2-1,0.2c-0.8,0-1-0.2-1-0.2V12.1z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M75,24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2v-5.3h1.3c0,0,2.7,4.3,3,4.7c0.2,0.4,0.5,0.5,0.7,0.7\r\n           c0.2,0.1,0.5,0.2,0.8,0.2h1.1c0,0,0.2-0.2,0.2-0.8c0-0.6-0.2-0.8-0.2-0.8h-0.4c-0.2,0-0.4,0-0.5-0.1c-0.2-0.1-0.3-0.3-0.4-0.5\r\n           C82.4,21.8,80,18,80,18l3.8-6h-2.1l-3.4,5.4H77v-5.2c0,0-0.3-0.2-1-0.2c-0.8,0-1,0.2-1,0.2V24z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M118.1,24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2v-5.3h1.3c0,0,2.7,4.3,3,4.7c0.2,0.4,0.5,0.5,0.7,0.7\r\n           c0.2,0.1,0.5,0.2,0.8,0.2h1.1c0,0,0.2-0.2,0.2-0.8c0-0.6-0.2-0.8-0.2-0.8h-0.4c-0.2,0-0.4,0-0.5-0.1c-0.2-0.1-0.3-0.3-0.4-0.5\r\n           c-0.2-0.3-2.6-4.1-2.6-4.1l3.8-6h-2.1l-3.4,5.4h-1.3v-5.2c0,0-0.3-0.2-1-0.2c-0.8,0-1,0.2-1,0.2V24z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M94.6,14.6c0.1-0.8,0.1-1.8,0.1-2.5c0,0-0.3-0.2-1.1-0.2c-0.8,0-1.1,0.2-1.1,0.2c0,0.7-0.1,1.8-0.1,3\r\n           c-0.1,1.1-0.3,2.2-0.7,3.3c-0.5,1.4-1.8,3.7-1.8,3.7h-0.2c0,0-0.9-1.7-1.8-3.7c-0.3-0.7-0.7-2.5-0.7-3.3c-0.1-1.3-0.1-2.4-0.1-3\r\n           c0,0-0.3-0.2-1.1-0.2c-0.8,0-1.1,0.2-1.1,0.2c0,0.7,0,1.7,0.1,2.5c0.1,0.7,0.2,2,0.6,3c0.7,2,3.4,6.7,3.4,6.7h1.7\r\n           c0,0,2.7-4.7,3.4-6.7C94.4,16.6,94.5,15.5,94.6,14.6z\" />\r\n            <g>\r\n              <path class=\"st0\"\r\n                    d=\"M56.6,24c0-0.8-0.2-3.1-0.2-3.1H51c0,0-0.2,2.2-0.2,3.1c0,0-0.2,0.2-1,0.2s-1-0.2-1-0.2\r\n             c0-0.2,0.1-1.9,0.2-2.9c0.3-2.5,1.2-4.3,1.5-4.9c0.3-0.6,1.5-3,2.4-4.3h1.7c0.8,1.2,2.1,3.8,2.4,4.3c0.3,0.6,1.2,2.7,1.5,4.9\r\n             c0.1,1.1,0.2,2.4,0.2,2.9c0,0-0.2,0.2-1,0.2C56.8,24.2,56.6,24,56.6,24z M53.6,14c-0.3,0.6-1,1.8-1.3,2.5c-0.4,1-0.9,2.3-1.1,3.4\r\n             c0,0,0.4-0.4,1.1-0.5c0.5,0,3.8,0,3.8,0c-0.2-1.1-0.7-2.1-1-2.9c-0.4-0.9-1-2-1.3-2.5H53.6z\" />\r\n            </g>\r\n          </g>\r\n          <g>\r\n            <path class=\"st0\"\r\n                  d=\"M10,28.8c6.4,0,9.3-4.7,9.5-6.1c-1,1.5-5,3.8-9.5,3.8s-8.5-2.3-9.5-3.8C0.7,24.1,3.6,28.8,10,28.8z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M10,0.9C9.9,0.9,9.8,1,9.8,1.1S9.9,1.3,10,1.3c0.1,0,0.2-0.1,0.2-0.2S10.1,0.9,10,0.9z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M10,4.6L10,4.6L10,4.6z\" />\r\n            <g>\r\n              <path class=\"st0\"\r\n                    d=\"M11.4,4.7l0.1-0.1c1.6,0.7,3,1.8,3.9,2.9c0.6,0.8,1,1.7,0.9,2.5c-0.1,0.7-0.7,1.2-1.4,1.1\r\n             c-0.7-0.1-1.2-0.7-1.1-1.4c0.1-0.7,0.8-1.1,0.8-1.1C14.1,6.7,12.7,5.4,11.4,4.7z\" />\r\n              <path class=\"st0\"\r\n                    d=\"M8.6,4.7L8.5,4.6c-1.6,0.7-3,1.8-3.9,2.9C4,8.3,3.6,9.2,3.7,10c0.1,0.7,0.7,1.2,1.4,1.1\r\n             c0.7-0.1,1.2-0.7,1.1-1.4c-0.1-0.7-0.8-1-0.8-1C5.9,6.7,7.3,5.4,8.6,4.7z\" />\r\n              <path class=\"st0\"\r\n                    d=\"M9.8,5.1C9.4,6.4,8.7,8.9,8.7,9.7s0.5,1.4,1.3,1.4s1.3-0.6,1.3-1.4c0-0.8-0.8-3.3-1.1-4.6\r\n             C10.1,5.1,9.9,5.1,9.8,5.1z\" />\r\n            </g>\r\n            <path class=\"st0\"\r\n                  d=\"M11.8,15.2c1,0,4.2,0.1,4.2,0.1c1.2-0.4,1.8-1.2,2-2.6c0,0-3.7-0.2-8-0.2s-8,0.2-8,0.2\r\n           c0.2,1.5,0.8,2.3,1.9,2.6c0,0,3.3-0.1,4.2-0.1v7.5c0,0.4,0.8,0.9,1.8,0.9s1.8-0.5,1.8-0.9c0,0,0-5,0-5.6c-0.4-1.3-1.5-1.9-1.5-1.9\r\n           L11.8,15.2z\" />\r\n          </g>\r\n          <g>\r\n            <g>\r\n              <path class=\"st0\"\r\n                    d=\"M10,0.7c0.2,0,0.3-0.1,0.3-0.3C10.3,0.1,10.2,0,10,0S9.7,0.1,9.7,0.3S9.8,0.7,10,0.7z\" />\r\n              <path class=\"st0\"\r\n                    d=\"M9.2,1.5c0.2,0,0.3-0.1,0.3-0.3S9.4,0.9,9.2,0.9C9,0.8,8.9,0.9,8.9,1.1S9,1.5,9.2,1.5z\" />\r\n              <path class=\"st0\"\r\n                    d=\"M10.8,1.5c0.2,0,0.3-0.1,0.3-0.3S11,0.9,10.8,0.9S10.5,1,10.5,1.2C10.5,1.3,10.6,1.5,10.8,1.5z\" />\r\n            </g>\r\n            <path class=\"st0\"\r\n                  d=\"M2.7,19.8c0,0-0.5-2-2.3-1.9c-0.7,1.3-0.4,2.7,0.4,3.9c0.7,1.1,2.6,2.4,3.9,2.5C4.7,24.3,1.9,22.2,2.7,19.8z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M17.3,19.8c0,0,0.5-2,2.3-1.9c0.7,1.3,0.4,2.7-0.4,3.9c-0.7,1.1-2.6,2.4-3.9,2.5\r\n           C15.3,24.3,18.1,22.2,17.3,19.8z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M10,2.2c0.2,0,0.3-0.1,0.3-0.3S10.2,1.6,10,1.6S9.7,1.7,9.7,1.9S9.8,2.2,10,2.2z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M10,4.2c0.5,0,0.9-0.4,0.9-0.9S10.5,2.4,10,2.4S9.1,2.8,9.1,3.3S9.5,4.2,10,4.2z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M20,7.9c-1.4-1.7-4.3-3.1-8-3.8l-0.1,0.1c3.5,0.9,5.3,2.2,7,4.1c-0.5,0.1-0.9,0.5-1.1,1\r\n           c-0.3,0.8,0.1,1.7,1,2.1L20,7.9z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M0,7.9c1.4-1.7,4.3-3.1,8-3.8l0.1,0.1C4.6,5.1,2.7,6.4,1,8.3c0.5,0.1,0.9,0.5,1.1,1c0.3,0.8-0.1,1.7-1,2.1\r\n           L0,7.9z\" />\r\n          </g>\r\n        </svg>\r\n      </div>\r\n    </div>\r\n  </div>\r\n\r\n  <div class=\"top-nav-content\">\r\n    <div class=\"d-flex h-100\">\r\n      <ng-content></ng-content>\r\n    </div>\r\n  </div>\r\n</nav>", styles: [":root{--bs-border-color: #ced4da}::ng-deep .trv-navbar{width:100%;background:#d70000;font-size:1em;display:flex;height:60px;min-width:320px}::ng-deep .trv-navbar .menu-toggle{flex:1;justify-content:flex-start;max-width:260px;display:flex;align-self:stretch;min-width:260px}::ng-deep .trv-navbar .menu-toggle .btn{text-transform:uppercase;color:#fff;border:none;border-radius:0;text-decoration:none;width:100%;text-align:left;display:flex;align-items:center}::ng-deep .trv-navbar .menu-toggle .btn:hover{background:#5f0000}::ng-deep .trv-navbar .menu-toggle .btn:focus{outline:none;box-shadow:none}::ng-deep .trv-navbar .menu-toggle .btn .active:hover .nav-icon:before{transform:translateY(5px) rotate(135deg)}::ng-deep .trv-navbar .menu-toggle .btn .active:hover .nav-icon:after{transform:translateY(-7px) rotate(-135deg)}::ng-deep .trv-navbar .menu-toggle .btn .active:hover .nav-icon span{transform:scale(0)}::ng-deep .trv-navbar .menu-toggle .btn .nav-icon:not(.active){cursor:pointer;display:inline-block;vertical-align:middle;width:1.9em;height:1.9rem;padding:5px 4px 4px;margin-right:.5rem;transition:all .2s ease-in-out}::ng-deep .trv-navbar .menu-toggle .btn .nav-icon:not(.active):after,::ng-deep .trv-navbar .menu-toggle .btn .nav-icon:not(.active):before,::ng-deep .trv-navbar .menu-toggle .btn .nav-icon:not(.active) span{display:block;transition:all .2s ease-in-out;content:\"\";margin:2px 0 4px;height:2px;border-radius:3px;background-color:#fff}::ng-deep .trv-navbar .menu-toggle .btn.active .nav-icon:before{transform:translateY(6px) rotate(135deg)}::ng-deep .trv-navbar .menu-toggle .btn.active .nav-icon:after{transform:translateY(-6px) rotate(-135deg)}::ng-deep .trv-navbar .menu-toggle .btn.active .nav-icon span{transform:scale(0)}@media (max-width: 575.98px){::ng-deep .trv-navbar .menu-toggle{max-width:none;min-width:auto}::ng-deep .trv-navbar .menu-toggle .btn{width:auto;white-space:nowrap}}::ng-deep .trv-navbar .logo{display:flex;justify-content:flex-start;align-items:center;justify-content:center}::ng-deep .trv-navbar .logo span{color:#fff;margin-top:.1em}::ng-deep .trv-navbar .logo .logo-img{width:150px;margin-top:-4px}::ng-deep .trv-navbar .logo .logo-img div{max-width:100%;height:auto}::ng-deep .trv-navbar .top-nav-content{align-self:stretch}::ng-deep .trv-navbar .top-nav-content__btn{color:#fff;align-items:center;height:100%;display:flex;border-radius:0}::ng-deep .trv-navbar .top-nav-content__btn fa-icon{font-size:1.3rem}::ng-deep .trv-navbar .top-nav-content__btn:hover,::ng-deep .trv-navbar .top-nav-content__btn:focus{background:#5f0000;text-decoration:none}::ng-deep .trv-navbar .top-nav-content__btn:hover .btn-badge,::ng-deep .trv-navbar .top-nav-content__btn:focus .btn-badge{background:#af0000}::ng-deep .trv-navbar .top-nav-content__btn:focus{text-decoration:none}::ng-deep .trv-navbar .top-nav-content__btn .btn-badge{font-size:.675rem;font-weight:600;background:#000;margin-top:-.75rem;margin-left:-6px;padding-left:.25rem;padding-right:.25rem;border-radius:4px;box-shadow:#00000052 0 2px 3px}@media (max-width: 575.98px){::ng-deep .trv-navbar{flex-wrap:wrap;height:auto}::ng-deep .trv-navbar .menu-toggle{order:2}::ng-deep .trv-navbar .logo{order:1;width:100%;margin:auto;padding:1rem}::ng-deep .trv-navbar .top-nav-content{order:3}::ng-deep .trv-navbar .top-nav-content__btn-text{display:none}}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardTopNavComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-dashboard-top-nav', changeDetection: ChangeDetectionStrategy.OnPush, imports: [NgClass], template: "<nav class=\"topbar navbar navbar-dark navbar-expand trv-navbar fixed-top p-0 d-flex justify-content-between\">\r\n  <div class=\"menu-toggle\">\r\n    @if (!hideMenuToggle) {\r\n    <button type=\"button\"\r\n            class=\"btn btn-link\"\r\n            [ngClass]=\"{'active' : dashboardService.sideNavOpen}\"\r\n            (click)=\"onToggleSideNavClick()\">\r\n      <div class=\"nav-icon\">\r\n        <span></span>\r\n      </div>\r\n\r\n      {{menuLabel}}\r\n    </button>\r\n    }\r\n  </div>\r\n\r\n  <div class=\"logo\">\r\n    <div class=\"logo-img\">\r\n      <div>\r\n        <svg version=\"1.1\"\r\n             id=\"Layer_1\"\r\n             xmlns=\"http://www.w3.org/2000/svg\"\r\n             xmlns:xlink=\"http://www.w3.org/1999/xlink\"\r\n             x=\"0px\"\r\n             y=\"0px\"\r\n             width=\"147.3px\"\r\n             height=\"28.8px\"\r\n             viewBox=\"0 0 147.3 28.8\"\r\n             style=\"enable-background:new 0 0 147.3 28.8;\"\r\n             xml:space=\"preserve\">\r\n          <style type=\"text/css\">\r\n            .st0 {\r\n              fill: #FFFFFF;\r\n            }\r\n          </style>\r\n          <g>\r\n            <path class=\"st0\"\r\n                  d=\"M47.3,23.4c0-0.6-0.2-0.8-0.2-0.8s-0.3,0-0.4,0c-0.2,0-0.4,0-0.5-0.1c-0.2-0.1-0.3-0.3-0.4-0.5\r\n           c-0.2-0.3-1.2-1.9-1.2-1.9c0.8-0.2,1.4-0.6,1.9-1.6c0.3-0.6,0.4-1.5,0.4-2.3c0-0.8-0.1-2.4-0.8-3.2c-0.6-0.6-1.5-1.1-3.2-1.1h-4.7\r\n           V24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2v-3.7h2.4c0,0,1.6,2.7,1.9,3.1c0.2,0.4,0.5,0.5,0.7,0.7c0.2,0.1,0.5,0.2,0.8,0.2\r\n           c0.4,0,1.1,0,1.1,0S47.3,24,47.3,23.4z M44.4,18.2c-0.5,0.6-1.3,0.5-2,0.6c0,0-0.9,0-1.2,0c-0.6,0.1-1.1,0.5-1.1,0.5v-5.8h2.2\r\n           c0.2,0,0.5,0,0.6,0c0.5,0,1,0.1,1.4,0.5c0.5,0.6,0.5,1.3,0.5,2.1v0.1C44.8,17.1,44.7,17.8,44.4,18.2z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M115.8,23.4c0-0.6-0.2-0.8-0.2-0.8s-0.3,0-0.4,0c-0.2,0-0.4,0-0.5-0.1c-0.2-0.1-0.3-0.3-0.4-0.5\r\n           c-0.2-0.3-1.2-1.9-1.2-1.9c0.8-0.2,1.4-0.6,1.9-1.6c0.3-0.6,0.4-1.5,0.4-2.3c0-0.8-0.1-2.4-0.8-3.2c-0.6-0.6-1.5-1.1-3.2-1.1h-4.7\r\n           V24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2v-3.7h2.4c0,0,1.6,2.7,1.9,3.1c0.2,0.4,0.5,0.5,0.7,0.7c0.2,0.1,0.5,0.2,0.8,0.2\r\n           c0.4,0,1.1,0,1.1,0S115.8,24,115.8,23.4z M112.9,18.2c-0.5,0.6-1.3,0.5-2,0.6c0,0-0.9,0-1.2,0c-0.6,0.1-1.1,0.5-1.1,0.5v-5.8h2.2\r\n           c0.2,0,0.5,0,0.6,0c0.5,0,1,0.1,1.4,0.5c0.5,0.6,0.5,1.3,0.5,2.1v0.1C113.3,17.1,113.2,17.8,112.9,18.2z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M68.1,11.9h-7.6V24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2v-5.3h4.3c0,0,0.2-0.2,0.2-0.8c0-0.6-0.2-0.8-0.2-0.8\r\n           s-2.7,0-3.3,0c-0.6,0.1-1.1,0.5-1.1,0.5v-4.2H68c0,0,0.2-0.2,0.2-0.8C68.2,12.1,68.1,11.9,68.1,11.9z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M36.3,11.9H27c0,0-0.2,0.2-0.2,0.8s0.2,0.8,0.2,0.8h3.6V24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2V13.4h3.6\r\n           c0,0,0.2-0.2,0.2-0.8C36.4,12.1,36.3,11.9,36.3,11.9z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M147.1,11.9h-9.2c0,0-0.2,0.2-0.2,0.8s0.2,0.8,0.2,0.8h3.6V24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2V13.4h3.6\r\n           c0,0,0.2-0.2,0.2-0.8C147.3,12.1,147.1,11.9,147.1,11.9z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M104.7,23.4c0-0.6-0.2-0.8-0.2-0.8h-6v-4h4.7c0,0,0.2-0.2,0.2-0.8c0-0.6-0.2-0.8-0.2-0.8s-3.3,0-3.7,0\r\n           c-0.6,0.1-1.1,0.5-1.1,0.5v-4.2h5.7c0,0,0.2-0.2,0.2-0.8s-0.2-0.8-0.2-0.8h-7.7v12.4h8C104.6,24.2,104.7,24,104.7,23.4z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M137,23.4c0-0.6-0.2-0.8-0.2-0.8h-6v-4h4.7c0,0,0.2-0.2,0.2-0.8c0-0.6-0.2-0.8-0.2-0.8s-3.3,0-3.7,0\r\n           c-0.6,0.1-1.1,0.5-1.1,0.5v-4.2h5.7c0,0,0.2-0.2,0.2-0.8s-0.2-0.8-0.2-0.8h-7.7v12.4h8C136.8,24.2,137,24,137,23.4z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M70.1,12.1c0,0,0.3-0.2,1-0.2c0.8,0,1,0.2,1,0.2V24c0,0-0.3,0.2-1,0.2c-0.8,0-1-0.2-1-0.2V12.1z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M75,24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2v-5.3h1.3c0,0,2.7,4.3,3,4.7c0.2,0.4,0.5,0.5,0.7,0.7\r\n           c0.2,0.1,0.5,0.2,0.8,0.2h1.1c0,0,0.2-0.2,0.2-0.8c0-0.6-0.2-0.8-0.2-0.8h-0.4c-0.2,0-0.4,0-0.5-0.1c-0.2-0.1-0.3-0.3-0.4-0.5\r\n           C82.4,21.8,80,18,80,18l3.8-6h-2.1l-3.4,5.4H77v-5.2c0,0-0.3-0.2-1-0.2c-0.8,0-1,0.2-1,0.2V24z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M118.1,24c0,0,0.3,0.2,1,0.2c0.8,0,1-0.2,1-0.2v-5.3h1.3c0,0,2.7,4.3,3,4.7c0.2,0.4,0.5,0.5,0.7,0.7\r\n           c0.2,0.1,0.5,0.2,0.8,0.2h1.1c0,0,0.2-0.2,0.2-0.8c0-0.6-0.2-0.8-0.2-0.8h-0.4c-0.2,0-0.4,0-0.5-0.1c-0.2-0.1-0.3-0.3-0.4-0.5\r\n           c-0.2-0.3-2.6-4.1-2.6-4.1l3.8-6h-2.1l-3.4,5.4h-1.3v-5.2c0,0-0.3-0.2-1-0.2c-0.8,0-1,0.2-1,0.2V24z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M94.6,14.6c0.1-0.8,0.1-1.8,0.1-2.5c0,0-0.3-0.2-1.1-0.2c-0.8,0-1.1,0.2-1.1,0.2c0,0.7-0.1,1.8-0.1,3\r\n           c-0.1,1.1-0.3,2.2-0.7,3.3c-0.5,1.4-1.8,3.7-1.8,3.7h-0.2c0,0-0.9-1.7-1.8-3.7c-0.3-0.7-0.7-2.5-0.7-3.3c-0.1-1.3-0.1-2.4-0.1-3\r\n           c0,0-0.3-0.2-1.1-0.2c-0.8,0-1.1,0.2-1.1,0.2c0,0.7,0,1.7,0.1,2.5c0.1,0.7,0.2,2,0.6,3c0.7,2,3.4,6.7,3.4,6.7h1.7\r\n           c0,0,2.7-4.7,3.4-6.7C94.4,16.6,94.5,15.5,94.6,14.6z\" />\r\n            <g>\r\n              <path class=\"st0\"\r\n                    d=\"M56.6,24c0-0.8-0.2-3.1-0.2-3.1H51c0,0-0.2,2.2-0.2,3.1c0,0-0.2,0.2-1,0.2s-1-0.2-1-0.2\r\n             c0-0.2,0.1-1.9,0.2-2.9c0.3-2.5,1.2-4.3,1.5-4.9c0.3-0.6,1.5-3,2.4-4.3h1.7c0.8,1.2,2.1,3.8,2.4,4.3c0.3,0.6,1.2,2.7,1.5,4.9\r\n             c0.1,1.1,0.2,2.4,0.2,2.9c0,0-0.2,0.2-1,0.2C56.8,24.2,56.6,24,56.6,24z M53.6,14c-0.3,0.6-1,1.8-1.3,2.5c-0.4,1-0.9,2.3-1.1,3.4\r\n             c0,0,0.4-0.4,1.1-0.5c0.5,0,3.8,0,3.8,0c-0.2-1.1-0.7-2.1-1-2.9c-0.4-0.9-1-2-1.3-2.5H53.6z\" />\r\n            </g>\r\n          </g>\r\n          <g>\r\n            <path class=\"st0\"\r\n                  d=\"M10,28.8c6.4,0,9.3-4.7,9.5-6.1c-1,1.5-5,3.8-9.5,3.8s-8.5-2.3-9.5-3.8C0.7,24.1,3.6,28.8,10,28.8z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M10,0.9C9.9,0.9,9.8,1,9.8,1.1S9.9,1.3,10,1.3c0.1,0,0.2-0.1,0.2-0.2S10.1,0.9,10,0.9z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M10,4.6L10,4.6L10,4.6z\" />\r\n            <g>\r\n              <path class=\"st0\"\r\n                    d=\"M11.4,4.7l0.1-0.1c1.6,0.7,3,1.8,3.9,2.9c0.6,0.8,1,1.7,0.9,2.5c-0.1,0.7-0.7,1.2-1.4,1.1\r\n             c-0.7-0.1-1.2-0.7-1.1-1.4c0.1-0.7,0.8-1.1,0.8-1.1C14.1,6.7,12.7,5.4,11.4,4.7z\" />\r\n              <path class=\"st0\"\r\n                    d=\"M8.6,4.7L8.5,4.6c-1.6,0.7-3,1.8-3.9,2.9C4,8.3,3.6,9.2,3.7,10c0.1,0.7,0.7,1.2,1.4,1.1\r\n             c0.7-0.1,1.2-0.7,1.1-1.4c-0.1-0.7-0.8-1-0.8-1C5.9,6.7,7.3,5.4,8.6,4.7z\" />\r\n              <path class=\"st0\"\r\n                    d=\"M9.8,5.1C9.4,6.4,8.7,8.9,8.7,9.7s0.5,1.4,1.3,1.4s1.3-0.6,1.3-1.4c0-0.8-0.8-3.3-1.1-4.6\r\n             C10.1,5.1,9.9,5.1,9.8,5.1z\" />\r\n            </g>\r\n            <path class=\"st0\"\r\n                  d=\"M11.8,15.2c1,0,4.2,0.1,4.2,0.1c1.2-0.4,1.8-1.2,2-2.6c0,0-3.7-0.2-8-0.2s-8,0.2-8,0.2\r\n           c0.2,1.5,0.8,2.3,1.9,2.6c0,0,3.3-0.1,4.2-0.1v7.5c0,0.4,0.8,0.9,1.8,0.9s1.8-0.5,1.8-0.9c0,0,0-5,0-5.6c-0.4-1.3-1.5-1.9-1.5-1.9\r\n           L11.8,15.2z\" />\r\n          </g>\r\n          <g>\r\n            <g>\r\n              <path class=\"st0\"\r\n                    d=\"M10,0.7c0.2,0,0.3-0.1,0.3-0.3C10.3,0.1,10.2,0,10,0S9.7,0.1,9.7,0.3S9.8,0.7,10,0.7z\" />\r\n              <path class=\"st0\"\r\n                    d=\"M9.2,1.5c0.2,0,0.3-0.1,0.3-0.3S9.4,0.9,9.2,0.9C9,0.8,8.9,0.9,8.9,1.1S9,1.5,9.2,1.5z\" />\r\n              <path class=\"st0\"\r\n                    d=\"M10.8,1.5c0.2,0,0.3-0.1,0.3-0.3S11,0.9,10.8,0.9S10.5,1,10.5,1.2C10.5,1.3,10.6,1.5,10.8,1.5z\" />\r\n            </g>\r\n            <path class=\"st0\"\r\n                  d=\"M2.7,19.8c0,0-0.5-2-2.3-1.9c-0.7,1.3-0.4,2.7,0.4,3.9c0.7,1.1,2.6,2.4,3.9,2.5C4.7,24.3,1.9,22.2,2.7,19.8z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M17.3,19.8c0,0,0.5-2,2.3-1.9c0.7,1.3,0.4,2.7-0.4,3.9c-0.7,1.1-2.6,2.4-3.9,2.5\r\n           C15.3,24.3,18.1,22.2,17.3,19.8z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M10,2.2c0.2,0,0.3-0.1,0.3-0.3S10.2,1.6,10,1.6S9.7,1.7,9.7,1.9S9.8,2.2,10,2.2z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M10,4.2c0.5,0,0.9-0.4,0.9-0.9S10.5,2.4,10,2.4S9.1,2.8,9.1,3.3S9.5,4.2,10,4.2z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M20,7.9c-1.4-1.7-4.3-3.1-8-3.8l-0.1,0.1c3.5,0.9,5.3,2.2,7,4.1c-0.5,0.1-0.9,0.5-1.1,1\r\n           c-0.3,0.8,0.1,1.7,1,2.1L20,7.9z\" />\r\n            <path class=\"st0\"\r\n                  d=\"M0,7.9c1.4-1.7,4.3-3.1,8-3.8l0.1,0.1C4.6,5.1,2.7,6.4,1,8.3c0.5,0.1,0.9,0.5,1.1,1c0.3,0.8-0.1,1.7-1,2.1\r\n           L0,7.9z\" />\r\n          </g>\r\n        </svg>\r\n      </div>\r\n    </div>\r\n  </div>\r\n\r\n  <div class=\"top-nav-content\">\r\n    <div class=\"d-flex h-100\">\r\n      <ng-content></ng-content>\r\n    </div>\r\n  </div>\r\n</nav>", styles: [":root{--bs-border-color: #ced4da}::ng-deep .trv-navbar{width:100%;background:#d70000;font-size:1em;display:flex;height:60px;min-width:320px}::ng-deep .trv-navbar .menu-toggle{flex:1;justify-content:flex-start;max-width:260px;display:flex;align-self:stretch;min-width:260px}::ng-deep .trv-navbar .menu-toggle .btn{text-transform:uppercase;color:#fff;border:none;border-radius:0;text-decoration:none;width:100%;text-align:left;display:flex;align-items:center}::ng-deep .trv-navbar .menu-toggle .btn:hover{background:#5f0000}::ng-deep .trv-navbar .menu-toggle .btn:focus{outline:none;box-shadow:none}::ng-deep .trv-navbar .menu-toggle .btn .active:hover .nav-icon:before{transform:translateY(5px) rotate(135deg)}::ng-deep .trv-navbar .menu-toggle .btn .active:hover .nav-icon:after{transform:translateY(-7px) rotate(-135deg)}::ng-deep .trv-navbar .menu-toggle .btn .active:hover .nav-icon span{transform:scale(0)}::ng-deep .trv-navbar .menu-toggle .btn .nav-icon:not(.active){cursor:pointer;display:inline-block;vertical-align:middle;width:1.9em;height:1.9rem;padding:5px 4px 4px;margin-right:.5rem;transition:all .2s ease-in-out}::ng-deep .trv-navbar .menu-toggle .btn .nav-icon:not(.active):after,::ng-deep .trv-navbar .menu-toggle .btn .nav-icon:not(.active):before,::ng-deep .trv-navbar .menu-toggle .btn .nav-icon:not(.active) span{display:block;transition:all .2s ease-in-out;content:\"\";margin:2px 0 4px;height:2px;border-radius:3px;background-color:#fff}::ng-deep .trv-navbar .menu-toggle .btn.active .nav-icon:before{transform:translateY(6px) rotate(135deg)}::ng-deep .trv-navbar .menu-toggle .btn.active .nav-icon:after{transform:translateY(-6px) rotate(-135deg)}::ng-deep .trv-navbar .menu-toggle .btn.active .nav-icon span{transform:scale(0)}@media (max-width: 575.98px){::ng-deep .trv-navbar .menu-toggle{max-width:none;min-width:auto}::ng-deep .trv-navbar .menu-toggle .btn{width:auto;white-space:nowrap}}::ng-deep .trv-navbar .logo{display:flex;justify-content:flex-start;align-items:center;justify-content:center}::ng-deep .trv-navbar .logo span{color:#fff;margin-top:.1em}::ng-deep .trv-navbar .logo .logo-img{width:150px;margin-top:-4px}::ng-deep .trv-navbar .logo .logo-img div{max-width:100%;height:auto}::ng-deep .trv-navbar .top-nav-content{align-self:stretch}::ng-deep .trv-navbar .top-nav-content__btn{color:#fff;align-items:center;height:100%;display:flex;border-radius:0}::ng-deep .trv-navbar .top-nav-content__btn fa-icon{font-size:1.3rem}::ng-deep .trv-navbar .top-nav-content__btn:hover,::ng-deep .trv-navbar .top-nav-content__btn:focus{background:#5f0000;text-decoration:none}::ng-deep .trv-navbar .top-nav-content__btn:hover .btn-badge,::ng-deep .trv-navbar .top-nav-content__btn:focus .btn-badge{background:#af0000}::ng-deep .trv-navbar .top-nav-content__btn:focus{text-decoration:none}::ng-deep .trv-navbar .top-nav-content__btn .btn-badge{font-size:.675rem;font-weight:600;background:#000;margin-top:-.75rem;margin-left:-6px;padding-left:.25rem;padding-right:.25rem;border-radius:4px;box-shadow:#00000052 0 2px 3px}@media (max-width: 575.98px){::ng-deep .trv-navbar{flex-wrap:wrap;height:auto}::ng-deep .trv-navbar .menu-toggle{order:2}::ng-deep .trv-navbar .logo{order:1;width:100%;margin:auto;padding:1rem}::ng-deep .trv-navbar .top-nav-content{order:3}::ng-deep .trv-navbar .top-nav-content__btn-text{display:none}}\n"] }]
        }], ctorParameters: () => [{ type: TrvDashboardService }, { type: TrvUtilityService }, { type: i0.ChangeDetectorRef }], propDecorators: { menuLabel: [{
                type: Input
            }], hideMenuToggle: [{
                type: Input
            }] } });

class TrvDashboardComponent {
    dashboardService;
    changeDetectorRef;
    router;
    ngUnsubscribe = new Subject();
    hideMenu = false;
    hideDashboard = false;
    mode = 'side';
    showLess;
    contentRef;
    footerRef;
    mainRef;
    constructor(dashboardService, changeDetectorRef, router) {
        this.dashboardService = dashboardService;
        this.changeDetectorRef = changeDetectorRef;
        this.router = router;
        this.router.events.pipe(filter(event => event instanceof NavigationStart))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((event) => {
            if (this.dashboardHidden(event.url)) {
                this.dashboardService.setDashboardVisibility(false);
            }
            else {
                this.dashboardService.setDashboardVisibility(true);
            }
        });
    }
    ngOnInit() {
        this.dashboardService.mode = this.mode;
        this.dashboardService.modeChange
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((mode) => {
            this.mode = mode;
            this.setContentHeight();
            this.changeDetectorRef.detectChanges();
        });
        this.dashboardService.openChange
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((isOpen) => {
            this.setContentHeight();
            this.changeDetectorRef.detectChanges();
        });
        this.hideMenu = this.dashboardService.hideMenu;
        this.dashboardService.hideMenuChange
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((hide) => {
            this.hideMenu = hide;
            this.changeDetectorRef.detectChanges();
        });
        this.hideDashboard = this.dashboardService.hideDashboard;
        this.dashboardService.hideDashboardChange
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((hide) => {
            this.hideDashboard = hide;
            this.setContentHeight();
            this.changeDetectorRef.detectChanges();
        });
        this.dashboardService.sideNavShowLessChange
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((showLess) => {
            this.showLess = showLess;
            this.changeDetectorRef.detectChanges();
        });
    }
    ngAfterViewInit() {
        this.setContentHeight();
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    onResize(event) {
        this.setContentHeight();
    }
    setContentHeight() {
        setTimeout(() => {
            const content = this.contentRef?.nativeElement;
            const footer = this.footerRef?.nativeElement;
            const main = this.mainRef?.nativeElement;
            if (this.dashboardService.hideDashboard || (!content || !footer || !main)) {
                content.style.minHeight = 'unset';
            }
            else if (content && footer && main) {
                content.style.minHeight = window.innerHeight - main.offsetTop - footer.clientHeight + 'px';
            }
        });
    }
    dashboardHidden(url) {
        const activeItem = this.dashboardService.getNavItemByUrl(url);
        return activeItem?.dashboardHidden;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardComponent, deps: [{ token: TrvDashboardService }, { token: i0.ChangeDetectorRef }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvDashboardComponent, isStandalone: true, selector: "trv-dashboard", host: { listeners: { "window:resize": "onResize($event)" } }, viewQueries: [{ propertyName: "contentRef", first: true, predicate: ["contentRef"], descendants: true }, { propertyName: "footerRef", first: true, predicate: ["footerRef"], descendants: true }, { propertyName: "mainRef", first: true, predicate: ["mainRef"], descendants: true }], ngImport: i0, template: "@if (!hideDashboard) {\r\n<div class=\"top-container\">\r\n  <ng-content select=\"trv-dashboard-top-nav\"></ng-content>\r\n</div>\r\n}\r\n<div class=\"container-fluid\"\r\n     [ngClass]=\"{\r\n       'mode-side' : mode === 'side',\r\n       'mode-over' : mode === 'over',\r\n       'mode-hidden': hideDashboard,\r\n       'side-nav-open': dashboardService.sideNavOpen && !hideMenu && !hideDashboard && !showLess,\r\n       'show-less': dashboardService.sideNavOpen && !hideMenu && !hideDashboard && showLess\r\n      }\">\r\n  <div class=\"row\">\r\n    @if (!hideMenu && !hideDashboard) {\r\n    <ng-content select=\"trv-dashboard-side-nav\"></ng-content>\r\n    }\r\n    <main role=\"main\"\r\n          #mainRef\r\n          [ngClass]=\"{ 'd-flex' : hideDashboard, 'justify-content-center': hideDashboard, 'min-vh-100': hideDashboard }\">\r\n      <div class=\"content-container\"\r\n           [ngClass]=\"{'w-100' : hideDashboard}\"\r\n           #contentRef>\r\n        <ng-content select=\"trv-dashboard-content\"></ng-content>\r\n      </div>\r\n      @if (!hideDashboard) {\r\n      <div class=\"footer-container\"\r\n           #footerRef>\r\n        <ng-content select=\"trv-dashboard-footer\"></ng-content>\r\n      </div>\r\n      }\r\n    </main>\r\n  </div>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.container-fluid,.container-fluid .row{padding:0;margin:0}.container-fluid .row>main{margin-right:0;margin-left:0;margin-top:60px;width:100%;padding:0}@media (max-width: 575.98px){.container-fluid .row>main{margin-top:90px}}.container-fluid.mode-side.side-nav-open>.row>main{padding-left:260px}.container-fluid.mode-side.show-less>.row>main{padding-left:55px}.mode-hidden .row>main{margin-top:0}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDashboardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-dashboard', changeDetection: ChangeDetectionStrategy.OnPush, imports: [NgClass], template: "@if (!hideDashboard) {\r\n<div class=\"top-container\">\r\n  <ng-content select=\"trv-dashboard-top-nav\"></ng-content>\r\n</div>\r\n}\r\n<div class=\"container-fluid\"\r\n     [ngClass]=\"{\r\n       'mode-side' : mode === 'side',\r\n       'mode-over' : mode === 'over',\r\n       'mode-hidden': hideDashboard,\r\n       'side-nav-open': dashboardService.sideNavOpen && !hideMenu && !hideDashboard && !showLess,\r\n       'show-less': dashboardService.sideNavOpen && !hideMenu && !hideDashboard && showLess\r\n      }\">\r\n  <div class=\"row\">\r\n    @if (!hideMenu && !hideDashboard) {\r\n    <ng-content select=\"trv-dashboard-side-nav\"></ng-content>\r\n    }\r\n    <main role=\"main\"\r\n          #mainRef\r\n          [ngClass]=\"{ 'd-flex' : hideDashboard, 'justify-content-center': hideDashboard, 'min-vh-100': hideDashboard }\">\r\n      <div class=\"content-container\"\r\n           [ngClass]=\"{'w-100' : hideDashboard}\"\r\n           #contentRef>\r\n        <ng-content select=\"trv-dashboard-content\"></ng-content>\r\n      </div>\r\n      @if (!hideDashboard) {\r\n      <div class=\"footer-container\"\r\n           #footerRef>\r\n        <ng-content select=\"trv-dashboard-footer\"></ng-content>\r\n      </div>\r\n      }\r\n    </main>\r\n  </div>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.container-fluid,.container-fluid .row{padding:0;margin:0}.container-fluid .row>main{margin-right:0;margin-left:0;margin-top:60px;width:100%;padding:0}@media (max-width: 575.98px){.container-fluid .row>main{margin-top:90px}}.container-fluid.mode-side.side-nav-open>.row>main{padding-left:260px}.container-fluid.mode-side.show-less>.row>main{padding-left:55px}.mode-hidden .row>main{margin-top:0}\n"] }]
        }], ctorParameters: () => [{ type: TrvDashboardService }, { type: i0.ChangeDetectorRef }, { type: i2.Router }], propDecorators: { contentRef: [{
                type: ViewChild,
                args: ['contentRef']
            }], footerRef: [{
                type: ViewChild,
                args: ['footerRef']
            }], mainRef: [{
                type: ViewChild,
                args: ['mainRef']
            }], onResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });

const TrvDashboardBundle = [
    TrvDashboardComponent,
    TrvDashboardTopNavComponent,
    TrvDashboardSideNavComponent,
    TrvDashboardContentComponent,
    TrvDashboardNavPageComponent,
    TrvDashboardPageHeaderComponent,
    TrvDashboardPageContentComponent,
    TrvDashboardFooterComponent,
    TrvDashboardNavBlocksComponent
];

class TrvDateStringParserDirective {
    renderer;
    element;
    dateService;
    trvUtilityService;
    cdr;
    constructor(renderer, element, dateService, trvUtilityService, cdr) {
        this.renderer = renderer;
        this.element = element;
        this.dateService = dateService;
        this.trvUtilityService = trvUtilityService;
        this.cdr = cdr;
    }
    ngUnsubscribe = new Subject();
    inputEvent;
    keyEvent;
    parseError = false;
    propagatedValue;
    propagateChange = () => { };
    propagateTouched = () => { };
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) {
        this.propagateTouched = fn;
    }
    ngOnInit() {
        this.propagatedValue = this.element.nativeElement.value;
        this.inputEvent = fromEvent(this.element.nativeElement, 'blur')
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((event) => {
            this.onInputValueChanged(event.target.value);
        });
        this.keyEvent = fromEvent(this.element.nativeElement, 'keydown')
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((event) => {
            const value = event.target.value;
            if (event.keyCode === 13) {
                this.onInputValueChanged(value);
            }
            else if (event.keyCode === 38 || event.keyCode === 40) {
                this.onArrowKeysTriggered(event);
            }
        });
    }
    writeValue(value) {
        const parsedValue = this.parseDate(value);
        this.renderer.setProperty(this.element.nativeElement, 'value', parsedValue);
        this.propagatedValue = parsedValue;
    }
    setDisabledState(isDisabled) {
        this.renderer.setProperty(this.element.nativeElement, 'disabled', isDisabled);
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    validate(c) {
        return (!this.parseError) ? null : {
            dateStringParseError: {
                valid: false,
            },
        };
    }
    onInputValueChanged(value) {
        const parsedValue = this.parseDate(value);
        if (!this.parseError) {
            this.renderer.setProperty(this.element.nativeElement, 'value', parsedValue);
        }
        if (this.parseError || parsedValue !== this.propagatedValue) {
            this.dispatchValueChanged(parsedValue);
        }
    }
    onArrowKeysTriggered(event) {
        const value = this.parseDate(event.target.value);
        if (!value) {
            return;
        }
        event.preventDefault();
        const selectionStart = event.target.selectionStart;
        const unit = this.getSelectionUnit(selectionStart);
        const valueAsDateTime = this.dateService.fromISO(value);
        const opts = {};
        opts[unit] = 1;
        if (event.keyCode === 38) { // up
            valueAsDateTime.plus(opts);
        }
        else if (event.keyCode === 40) { //down
            valueAsDateTime.minus(opts);
        }
        const newValue = valueAsDateTime.toISODate();
        this.renderer.setProperty(this.element.nativeElement, 'value', newValue);
        event.target.selectionStart = selectionStart;
        event.target.selectionEnd = selectionStart;
        this.dispatchValueChanged(newValue);
    }
    dispatchValueChanged(value) {
        this.propagatedValue = value;
        this.propagateTouched();
        this.propagateChange(value);
        this.trvUtilityService.detectChanges(this.cdr);
    }
    parseDate(dateString) {
        this.parseError = false;
        if (!dateString) {
            this.parseError = false;
            return '';
        }
        //remove anything that isn't a number
        dateString = dateString.replace(/[^\d]/g, '');
        if (!dateString) {
            this.parseError = true;
            return '';
        }
        if (dateString.length < 6 || dateString.length > 8) {
            this.parseError = true;
            return '';
        }
        else if (dateString.length !== 8) {
            dateString = '20' + dateString;
        }
        //return in format yyyy-MM-dd
        dateString = dateString.substr(0, 4) + '-' + dateString.substr(4, 2) + '-' + dateString.substr(6, 2);
        if (!this.dateService.fromISO(dateString).isValid) {
            this.parseError = true;
            return '';
        }
        return dateString;
    }
    getSelectionUnit(selectionStart) {
        if (selectionStart <= 4) {
            return 'years';
        }
        else if (selectionStart >= 5 && selectionStart <= 7) {
            return 'months';
        }
        else {
            return 'days';
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateStringParserDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: TrvDateService }, { token: TrvUtilityService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvDateStringParserDirective, isStandalone: true, selector: "input[type=text][formControlName][trvDateStringParser],input[type=text][formControl][trvDateStringParser],input[type=text][ngModel][trvDateStringParser]", providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TrvDateStringParserDirective),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => TrvDateStringParserDirective),
                multi: true
            }
        ], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateStringParserDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[type=text][formControlName][trvDateStringParser],input[type=text][formControl][trvDateStringParser],input[type=text][ngModel][trvDateStringParser]',
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TrvDateStringParserDirective),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => TrvDateStringParserDirective),
                            multi: true
                        }
                    ],
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: TrvDateService }, { type: TrvUtilityService }, { type: i0.ChangeDetectorRef }] });

class TrvTimeStringParserDirective {
    renderer;
    element;
    trvUtilityService;
    cdr;
    constructor(renderer, element, trvUtilityService, cdr) {
        this.renderer = renderer;
        this.element = element;
        this.trvUtilityService = trvUtilityService;
        this.cdr = cdr;
    }
    ngUnsubscribe = new Subject();
    blurEvent;
    keyEvent;
    parseError = false;
    propagatedValue = '';
    propagateChange = () => { };
    propagateTouched = () => { };
    ngOnInit() {
        this.blurEvent = fromEvent(this.element.nativeElement, 'blur')
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((event) => {
            this.onInputValueChanged(event.target.value);
        });
        this.keyEvent = fromEvent(this.element.nativeElement, 'keydown')
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((event) => {
            const value = event.target.value;
            if (event.keyCode === 13) {
                this.onInputValueChanged(value);
            }
            else if (event.keyCode === 38 || event.keyCode === 40) {
                this.onArrowKeysTriggered(event);
            }
        });
    }
    ngOnDestroy() {
        if (this.parseError) {
            setTimeout(() => {
                this.parseError = false;
                this.propagateChange();
            });
        }
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) {
        this.propagateTouched = fn;
    }
    writeValue = (value) => {
        const parsedValue = this.parseTime(value);
        this.renderer.setProperty(this.element.nativeElement, 'value', parsedValue);
        this.propagatedValue = parsedValue;
    };
    setDisabledState = (isDisabled) => {
        this.renderer.setProperty(this.element.nativeElement, 'disabled', isDisabled);
    };
    validate = (c) => {
        return !this.parseError ? null : {
            timeStringParseError: {
                valid: false,
            }
        };
    };
    onInputValueChanged(value) {
        const parsedValue = this.parseTime(value);
        if (!this.parseError) {
            this.renderer.setProperty(this.element.nativeElement, 'value', parsedValue);
        }
        if (this.parseError || parsedValue !== this.propagatedValue) {
            this.dispatchValueChanged(parsedValue);
        }
    }
    onArrowKeysTriggered(event) {
        const value = this.parseTime(event.target.value);
        if (!value) {
            return;
        }
        event.preventDefault();
        const selectionEnd = event.target.selectionEnd;
        const values = value.split(':').map((s) => +s);
        const unit = this.getSelectionUnit(selectionEnd);
        const newValues = this.getNewValues(unit, values, event.keyCode);
        if (value === JSON.stringify(newValues)) {
            return;
        }
        const newTimestring = newValues.map(n => n > 9 ? '' + n : '0' + n).join(':');
        this.renderer.setProperty(this.element.nativeElement, 'value', newTimestring);
        event.target.selectionStart = selectionEnd;
        event.target.selectionEnd = selectionEnd;
        this.dispatchValueChanged(newTimestring);
    }
    dispatchValueChanged(value) {
        this.propagatedValue = value;
        this.propagateTouched();
        this.propagateChange(value);
        this.trvUtilityService.detectChanges(this.cdr);
    }
    parseTime(timeString) {
        if (!timeString) {
            this.parseError = false;
            return '';
        }
        //remove anything that isn't a number
        timeString = timeString.replace(/[^\d]/g, '');
        if (!timeString || timeString.length > 4) {
            this.parseError = true;
            return '';
        }
        if (timeString.length === 1) {
            timeString = '0' + timeString + '00';
        }
        else if (timeString.length === 2) {
            timeString = timeString + '00';
        }
        else if (timeString.length === 3) {
            timeString = '0' + timeString;
        }
        const hours = parseInt(timeString.substr(0, 2));
        const minutes = parseInt(timeString.substr(2, 2));
        if ((hours < 0 || hours > 23) || (minutes < 0 || minutes > 59)) {
            this.parseError = true;
            return '';
        }
        //return in format HH:mm
        this.parseError = false;
        return timeString.substr(0, 2) + ':' + timeString.substr(2, 2);
    }
    getSelectionUnit(selection) {
        if (selection <= 2) {
            return TimePartialUnit.Hours;
        }
        else {
            return TimePartialUnit.Minutes;
        }
    }
    getNewValues(unit, values, keyCode) {
        const x = keyCode === 38 ? 1 : -1;
        let hours = values[TimePartialUnit.Hours];
        let minutes = values[TimePartialUnit.Minutes];
        const value = values[unit];
        if (unit === TimePartialUnit.Hours && value + x >= 0 && value + x < 24) {
            hours = value + x;
        }
        else if (unit === TimePartialUnit.Minutes) {
            if (value + x === -1 && hours > 0) {
                hours--;
                minutes = 59;
            }
            else if (value + x === 60 && hours < 23) {
                hours++;
                minutes = 0;
            }
            else if (value + x >= 0 && value + x < 60) {
                minutes = value + x;
            }
        }
        return [hours, minutes];
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTimeStringParserDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: TrvUtilityService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvTimeStringParserDirective, isStandalone: true, selector: "input[type=text][formControlName][trvTimeStringParser],input[type=text][formControl][trvTimeStringParser],input[type=text][ngModel][trvTimeStringParser]", providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TrvTimeStringParserDirective),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => TrvTimeStringParserDirective),
                multi: true
            }
        ], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTimeStringParserDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[type=text][formControlName][trvTimeStringParser],input[type=text][formControl][trvTimeStringParser],input[type=text][ngModel][trvTimeStringParser]',
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TrvTimeStringParserDirective),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => TrvTimeStringParserDirective),
                            multi: true
                        }
                    ],
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: TrvUtilityService }, { type: i0.ChangeDetectorRef }] });
var TimePartialUnit;
(function (TimePartialUnit) {
    TimePartialUnit[TimePartialUnit["Hours"] = 0] = "Hours";
    TimePartialUnit[TimePartialUnit["Minutes"] = 1] = "Minutes";
})(TimePartialUnit || (TimePartialUnit = {}));

class TrvDatePickerComponent extends trvUnsubscribeMixin() {
    calendarService;
    dateService;
    formBuilder;
    cdr;
    utilityService;
    showTime;
    disabledText;
    defaultTimeMode = 'start';
    skipClear;
    hideIcon;
    isDateSelectableFunction;
    _calendarFilter = {};
    set calendarFilter(calendarFilter) {
        this._calendarFilter = calendarFilter ?? {};
        if (this._calendarFilter?.selectedMonth) {
            this._calendarFilter.selectedMonth = this._calendarFilter.selectedMonth.toLowerCase();
        }
        this.buildCalendar();
    }
    get calendarFilter() {
        return this._calendarFilter;
    }
    _minDate;
    set minDate(minDate) {
        this._minDate = minDate;
        this.calendar.minDT = this.createAndValidateDateTime(this._minDate);
        this.buildCalendar();
    }
    get minDate() {
        return this._minDate;
    }
    _maxDate;
    set maxDate(maxDate) {
        this._maxDate = maxDate;
        this.calendar.maxDT = this.createAndValidateDateTime(this._maxDate);
        this.buildCalendar();
    }
    get maxDate() {
        return this._maxDate;
    }
    focusOut = new EventEmitter();
    focusIn = new EventEmitter();
    shown = new EventEmitter();
    class = 'trv-date-picker';
    datePopoverRef;
    dateInputElementRef;
    timeInputElementRef;
    fromDateRangeValue;
    toDateRangeValue;
    toIsInfinite;
    isPartOfDateRange;
    selectableYearOffset = 0;
    fromOrToDatePicker;
    faTimes = faTimes;
    faCalendarAlt = faCalendarAlt;
    faCaretDown = faCaretDown;
    faChevronLeft = faChevronLeft;
    faChevronRight = faChevronRight;
    isDisabled;
    calendarContainerId = 'calendar' + new Date().valueOf();
    //Internal FormGroup
    datePickerFormGroup;
    calendar = {
        selectableYears: null,
        selectableMonths: null,
        selectedMonth: null,
        minDT: null,
        maxDT: null,
    };
    propagateChange = () => { };
    propagateTouched = () => { };
    constructor(calendarService, dateService, formBuilder, cdr, utilityService) {
        super();
        this.calendarService = calendarService;
        this.dateService = dateService;
        this.formBuilder = formBuilder;
        this.cdr = cdr;
        this.utilityService = utilityService;
        this.initForm();
    }
    ngOnInit() {
        this.buildCalendar();
    }
    writeValue(value) {
        const dateTime = this.dateService.fromISO(value);
        if (!value || !dateTime.isValid) {
            this.datePickerFormGroup.setValue({ date: null, time: null }, { emitEvent: false });
            return;
        }
        this.datePickerFormGroup.setValue({
            date: dateTime.toISODate(),
            time: dateTime.toFormat('HH:mm'),
        }, { emitEvent: false });
        this.setVisibleCalendarMonth(value != null);
        this.buildMonth();
    }
    setDisabledState(isDisabled) {
        this.isDisabled = isDisabled;
        isDisabled === true
            ? this.datePickerFormGroup.disable({ emitEvent: false })
            : this.datePickerFormGroup.enable({ emitEvent: false });
        this.utilityService.detectChanges(this.cdr);
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) {
        this.propagateTouched = fn;
    }
    validate(c) {
        const errors = { ...this.dateControl.errors, ...this.timeControl.errors };
        if (this.isOutsideValidInterval(this.dateService.fromISO(c.value))) {
            errors.dateStringOutsideIntervalError = {
                valid: false,
                minDate: this.calendar.minDT?.toFormat('yyyy-MM-dd HH:mm') ?? null,
                maxDate: this.calendar.maxDT?.toFormat('yyyy-MM-dd HH:mm') ?? null,
            };
        }
        return Object.keys(errors).length ? errors : null;
    }
    onFocusOut() {
        if (this.isDisabled) {
            return;
        }
        this.focusOut.emit();
    }
    onFocusIn() {
        if (this.isDisabled) {
            return;
        }
        this.focusIn.emit();
    }
    showPopover(event) {
        // this.preventDefaultAndStopPropagationForEvent(event);
        if (this.isDateSelectableFunction) {
            this.buildMonth();
        }
        const dateString = this.datePickerFormGroup.get('date').value;
        const dateTime = dateString ? this.dateService.fromISO(dateString) : null;
        if (this.isPartOfDateRange) {
            if (this.fromOrToDatePicker === 'from') {
                this.fromDateRangeValue = dateTime;
            }
            else {
                this.toDateRangeValue = dateTime;
            }
        }
        this.datePopoverRef.show();
    }
    hidePopover() {
        this.datePopoverRef.hide();
    }
    onClickOutsidePopover() {
        this.hidePopover();
    }
    clearInput(event) {
        this.preventDefaultAndStopPropagationForEvent(event);
        this.datePickerFormGroup.reset();
        this.hidePopover();
    }
    selectYear(selectableYear, yearsPopover, event) {
        this.preventDefaultAndStopPropagationForEvent(event);
        this.calendarFilter.selectedYear = selectableYear.year;
        this.buildMonth();
        yearsPopover.hide();
    }
    selectMonth(selectableMonth, monthsPopover, event) {
        this.preventDefaultAndStopPropagationForEvent(event);
        this.calendarFilter.selectedMonth = selectableMonth.month;
        this.buildMonth();
        monthsPopover.hide();
    }
    selectDay(day) {
        if (day.isOutsideAvailableInterval || !day.isCurrentMonth || this.isDisabled) {
            return;
        }
        let dt = day.dateTime;
        if (this.defaultTimeMode === 'start') {
            dt = dt.startOf('day');
        }
        else if (this.defaultTimeMode === 'end') {
            dt = dt.endOf('day');
        }
        this.datePickerFormGroup.setValue({
            date: dt.toISODate(),
            time: this.defaultTimeMode === 'none' ? null : dt.toFormat('HH:mm'),
        });
        this.datePopoverRef.hide();
        this.showTime ? this.timeInputElementRef?.nativeElement?.focus() : this.dateInputElementRef?.nativeElement?.focus();
    }
    //Om datepickern är en del av daterange-komponenten
    setDateRangeFromDate(dateTime) {
        this.fromDateRangeValue = dateTime;
    }
    setDateRangeToDate(dateTime) {
        this.toDateRangeValue = dateTime;
    }
    setDateRangeToIsInfinite(toIsInfinite) {
        this.toIsInfinite = toIsInfinite;
    }
    isWithinDateRange(dateTime) {
        if (!this.toIsInfinite &&
            this.fromDateRangeValue &&
            this.toDateRangeValue &&
            dateTime > this.fromDateRangeValue &&
            dateTime < this.toDateRangeValue) {
            return true;
        }
    }
    isOppositeDateRangeDay(dateTime) {
        if (!this.isPartOfDateRange) {
            return;
        }
        let dateToMatch;
        if (this.fromOrToDatePicker === 'from') {
            dateToMatch = this.toDateRangeValue;
        }
        else {
            dateToMatch = this.fromDateRangeValue;
        }
        return dateToMatch?.hasSame(dateTime, 'day');
    }
    setIsPartOfDateRange(fromOrTo) {
        this.isPartOfDateRange = true;
        this.fromOrToDatePicker = fromOrTo;
    }
    onDayHover(dateTime) {
        if (!this.isPartOfDateRange) {
            return;
        }
        if (this.fromOrToDatePicker === 'from') {
            this.setDateRangeFromDate(dateTime);
        }
        else {
            this.setDateRangeToDate(dateTime);
        }
    }
    onDayHoverOut(dateTime) {
        if (!this.isPartOfDateRange) {
            return;
        }
        const dateValue = this.datePickerFormGroup.get('date').value;
        const dt = dateValue ? this.dateService.fromISO(dateValue) : null;
        if (this.fromOrToDatePicker === 'from') {
            this.setDateRangeFromDate(dt);
        }
        else {
            this.setDateRangeToDate(dt);
        }
    }
    setSelectableYearOffset(offset, event) {
        this.preventDefaultAndStopPropagationForEvent(event);
        this.selectableYearOffset = this.selectableYearOffset + offset;
        this.calendar.selectableYears = this.getSelectableYears();
    }
    resetSelectableYearOffset() {
        this.selectableYearOffset = 0;
        this.calendar.selectableYears = this.getSelectableYears();
    }
    isOutsideValidInterval(dt) {
        if (!dt) {
            return false;
        }
        dt = dt.set({ millisecond: 0 });
        return ((this.isDateSelectableFunction && !this.isDateSelectableFunction(dt.toISO({ suppressMilliseconds: true }))) ||
            (this.calendar.minDT && dt < this.calendar.minDT) ||
            (this.calendar.maxDT && dt > this.calendar.maxDT));
    }
    buildCalendar() {
        this.setVisibleCalendarMonth();
        this.calendar.selectableYears = this.getSelectableYears();
        this.buildMonth();
    }
    getSelectableYears() {
        const currentYear = this.calendarFilter.selectedYear + this.selectableYearOffset * 12;
        const years = [currentYear];
        const numberOfYearsBack = 6;
        const numberOfYearsForward = 7;
        for (let i = 1; i < numberOfYearsBack; i++) {
            years.splice(0, 0, currentYear - i);
        }
        for (let i = 1; i < numberOfYearsForward; i++) {
            years.push(currentYear + i);
        }
        return years.map((year) => {
            return { year, disabled: this.isYearDisabled(year) };
        });
    }
    isYearDisabled(year) {
        if (!this.calendar.minDT && !this.calendar.maxDT) {
            return false;
        }
        const yearStart = this.dateService.fromObject({ year: year, month: 1, day: 1 });
        if ((this.calendar.minDT && yearStart < this.calendar.minDT.startOf('year')) ||
            (this.calendar.maxDT && yearStart > this.calendar.maxDT.endOf('year'))) {
            return true;
        }
        return false;
    }
    isMonthDisabled(monthName) {
        if (!this.calendar.minDT && !this.calendar.maxDT) {
            return false;
        }
        const month = this.dateService.getMonthNames().indexOf(monthName) + 1;
        const monthStart = this.dateService.fromObject({ year: this.calendarFilter.selectedYear, month: month, day: 1 });
        if ((this.calendar.minDT && monthStart < this.calendar.minDT.startOf('month')) ||
            (this.calendar.maxDT && monthStart > this.calendar.maxDT.endOf('month'))) {
            return true;
        }
        return false;
    }
    buildMonth() {
        this.calendar.selectableMonths = this.dateService.getMonthNames()?.map((month) => {
            return { month, disabled: this.isMonthDisabled(month) };
        });
        const selectableFunction = this.isDateSelectableFunction
            ? (day) => this.isDateSelectableFunction(day.isoString)
            : null;
        this.calendar.selectedMonth = this.calendarService.buildMonth(this.dateService.fromObject({
            year: this.calendarFilter.selectedYear,
            month: this.dateService.getMonthNames().indexOf(this.calendarFilter.selectedMonth) + 1,
        }), null, null, this.calendar.minDT?.set({ millisecond: 0 }).toISO({ suppressMilliseconds: true }) ?? null, this.calendar.maxDT?.set({ millisecond: 0 }).toISO({ suppressMilliseconds: true }) ?? null, selectableFunction, null);
    }
    setVisibleCalendarMonth(skipFilter) {
        if (this.calendarFilter?.selectedYear && this.calendarFilter?.selectedMonth && !skipFilter) {
            return;
        }
        const nowDT = this.dateService.now();
        const formControlDT = this.createAndValidateDateTime(this.dateControl.value);
        if (formControlDT) {
            this.calendarFilter.selectedYear = formControlDT.year;
            this.calendarFilter.selectedMonth = formControlDT.monthLong;
        }
        else if (this.calendar.minDT && nowDT < this.calendar.minDT) {
            this.calendarFilter.selectedYear = this.calendar.minDT.year;
            this.calendarFilter.selectedMonth = this.calendar.minDT.monthLong;
        }
        else if (this.calendar.maxDT && nowDT > this.calendar.maxDT) {
            this.calendarFilter.selectedYear = this.calendar.maxDT.year;
            this.calendarFilter.selectedMonth = this.calendar.maxDT.monthLong;
        }
        else {
            this.calendarFilter.selectedYear = nowDT.year;
            this.calendarFilter.selectedMonth = nowDT.monthLong;
        }
    }
    createAndValidateDateTime(date) {
        if (!date) {
            return null;
        }
        const dateTime = this.dateService.fromISO(date);
        if (!dateTime.isValid) {
            throw Error(`Invalid date format: ${date}`);
        }
        return dateTime;
    }
    preventDefaultAndStopPropagationForEvent(event) {
        event?.preventDefault();
        event?.stopPropagation();
    }
    initForm() {
        this.datePickerFormGroup = this.formBuilder.group({
            date: null,
            time: null,
        });
        this.datePickerFormGroup.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => {
            this.propagateTouched();
            //Om datum- eller tidsinput inte har parsningsfel
            if (this.dateControl.errors?.dateStringParseError || this.timeControl.errors?.timeStringParseError) {
                this.propagateChange(null);
            }
            else {
                const date = value.date;
                const dateTime = date ? this.dateAndTimeToDateTime(value.date, value.time) : null;
                if (dateTime?.isValid) {
                    this.setVisibleCalendarMonth(true);
                    this.buildMonth();
                }
                this.propagateChange(dateTime?.isValid ? dateTime.toISO({ suppressMilliseconds: true }) : null);
            }
        });
    }
    dateAndTimeToDateTime(date /* yyyy-MM-dd */, timeHHmm /* HH:mm */) {
        const [hh, mm] = (timeHHmm || '00:00').split(':');
        const dt = DateTime.fromISO(date).set({ hour: +hh, minute: +mm });
        return dt;
    }
    get dateControl() {
        return this.datePickerFormGroup.get('date');
    }
    get timeControl() {
        return this.datePickerFormGroup.get('time');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDatePickerComponent, deps: [{ token: TrvCalendarService }, { token: TrvDateService }, { token: i1$2.FormBuilder }, { token: i0.ChangeDetectorRef }, { token: TrvUtilityService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvDatePickerComponent, isStandalone: true, selector: "trv-date-picker", inputs: { showTime: "showTime", disabledText: "disabledText", defaultTimeMode: "defaultTimeMode", skipClear: "skipClear", hideIcon: "hideIcon", isDateSelectableFunction: "isDateSelectableFunction", calendarFilter: "calendarFilter", minDate: "minDate", maxDate: "maxDate" }, outputs: { focusOut: "focusOut", focusIn: "focusIn", shown: "shown" }, host: { properties: { "class": "this.class" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TrvDatePickerComponent),
                multi: true,
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => TrvDatePickerComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "datePopoverRef", first: true, predicate: ["datePopoverRef"], descendants: true }, { propertyName: "dateInputElementRef", first: true, predicate: ["dateInputRef"], descendants: true }, { propertyName: "timeInputElementRef", first: true, predicate: ["timeInputRef"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"input-group\"\r\n     [ngClass]=\"{ 'has-time-input': showTime, 'disabled': isDisabled }\"\r\n     [formGroup]=\"datePickerFormGroup\"\r\n     [tabindex]=\"isDisabled ? 0 : -1\"\r\n     (focusin)=\"onFocusIn()\"\r\n     (focusout)=\"onFocusOut()\">\r\n  @if (!hideIcon) {\r\n  <button type=\"button\"\r\n          class=\"btn btn-outline-primary input-datepicker-icon\"\r\n          tabindex=\"-1\"\r\n          (click)=\"showPopover($event)\">\r\n    <fa-icon [icon]=\"faCalendarAlt\"></fa-icon>\r\n  </button>\r\n  }\r\n  <div #datePopoverRef=\"trvPopover\"\r\n       popoverClass=\"trv-popover-no-container-margin\"\r\n       [trvPopover]=\"calendarTemplateRef\"\r\n       [tabindex]=\"isDisabled ? 0 : -1\"\r\n       [manualToggle]=\"true\"\r\n       [position]=\"['bottom-start', 'bottom-end', 'top-start', 'top-end', 'right', 'left']\"\r\n       (click)=\"showPopover($event)\"\r\n       (keydown.tab)=\"datePopoverRef.hide()\"\r\n       (clickOutside)=\"onClickOutsidePopover()\"\r\n       (shown)=\"shown.emit()\">\r\n    @if (isDisabled && disabledText) {\r\n    <span class=\"input-disabled-text\"\r\n          [ngClass]=\"{'with-icon': !hideIcon}\">\r\n      {{disabledText}}\r\n    </span>\r\n    }\r\n    <input type=\"text\"\r\n           #dateInputRef\r\n           trvDateStringParser\r\n           class=\"form-control date-input\"\r\n           placeholder=\"\u00E5\u00E5\u00E5\u00E5-mm-dd\"\r\n           formControlName=\"date\"\r\n           [ngClass]=\"{ 'disabled': isDisabled }\"\r\n           (keyup.enter)=\"showPopover($event)\">\r\n  </div>\r\n  @if (showTime) {\r\n  <input #timeInputRef\r\n         type=\"text\"\r\n         class=\"form-control time-input\"\r\n         placeholder=\"hh:mm\"\r\n         formControlName=\"time\"\r\n         trvTimeStringParser\r\n         [ngClass]=\"{ 'disabled': isDisabled }\">\r\n  }\r\n  @if (!skipClear) {\r\n  <button type=\"button\"\r\n          class=\"btn btn-outline-primary input-clear-btn\"\r\n          (click)=\"clearInput($event)\"\r\n          [disabled]=\"isDisabled\">\r\n    <fa-icon [icon]=\"faTimes\"></fa-icon>\r\n  </button>\r\n  }\r\n</div>\r\n<ng-template #calendarTemplateRef>\r\n  <div class=\"calendar-container\"\r\n       id=\"{{calendarContainerId}}\">\r\n    <div class=\"calendar-item-nav\">\r\n      <div class=\"year-select-drop\">\r\n        <button type=\"button\"\r\n                class=\"btn btn-sm btn-link\"\r\n                #yearsPopover=\"trvPopover\"\r\n                [trvPopover]=\"yearPopoverTemplate\"\r\n                popoverClass=\"trv-popover-no-container-margin\"\r\n                [allowMultiple]=\"true\"\r\n                [position]=\"['bottom', 'top']\"\r\n                (shown)=\"monthsPopover?.hide()\"\r\n                (hidden)=\"resetSelectableYearOffset()\"\r\n                [disabled]=\"isDisabled\">\r\n          {{calendarFilter.selectedYear}}\r\n          <fa-icon [icon]=\"faCaretDown\"></fa-icon>\r\n        </button>\r\n        <ng-template #yearPopoverTemplate>\r\n          <div class=\"date-range-areas popover-menu\"\r\n               (click)=\"preventDefaultAndStopPropagationForEvent($event)\">\r\n            <div class=\"year-popover-pagination\">\r\n              <button class=\"btn btn-link\"\r\n                      (click)=\"setSelectableYearOffset(-1, $event)\">\r\n                <fa-icon [icon]=\"faChevronLeft\"></fa-icon>\r\n              </button>{{calendar.selectableYears[0].year + ' - ' +\r\n              calendar.selectableYears[calendar.selectableYears.length - 1].year}}\r\n              <button class=\"btn btn-link\"\r\n                      (click)=\"setSelectableYearOffset(1, $event)\">\r\n                <fa-icon [icon]=\"faChevronRight\"></fa-icon>\r\n              </button>\r\n            </div>\r\n            <div class=\"years-select\">\r\n              @for (selectableYear of calendar.selectableYears; track selectableYear) {\r\n              <button type=\"button\"\r\n                      class=\"btn btn-sm btn-link\"\r\n                      [ngClass]=\"{ 'active' : selectableYear.year === calendarFilter.selectedYear }\"\r\n                      [disabled]=\"selectableYear.disabled\"\r\n                      (click)=\"selectYear(selectableYear, yearsPopover, $event)\">{{selectableYear.year}}</button>\r\n              }\r\n            </div>\r\n          </div>\r\n        </ng-template>\r\n      </div>\r\n      <div class=\"month-select-drop\">\r\n        <div>\r\n          <button type=\"button\"\r\n                  class=\"btn btn-sm btn-link\"\r\n                  #monthsPopover=\"trvPopover\"\r\n                  [trvPopover]=\"monthsPopoverTemplate\"\r\n                  popoverClass=\"trv-popover-no-container-margin\"\r\n                  [allowMultiple]=\"true\"\r\n                  [position]=\"['bottom', 'top']\"\r\n                  (shown)=\"yearsPopover?.hide()\"\r\n                  [disabled]=\"isDisabled\">\r\n            {{calendarFilter.selectedMonth}}\r\n            <fa-icon [icon]=\"faCaretDown\"></fa-icon>\r\n          </button>\r\n          <ng-template #monthsPopoverTemplate>\r\n            <div class=\"date-range-areas popover-menu\"\r\n                 (click)=\"preventDefaultAndStopPropagationForEvent($event)\">\r\n              <div class=\"month-select\">\r\n                @for (selectableMonth of calendar.selectableMonths; track selectableMonth) {\r\n                <button type=\"button\"\r\n                        class=\"btn btn-sm btn-link\"\r\n                        [ngClass]=\"{ 'active' : selectableMonth.month === calendarFilter.selectedMonth }\"\r\n                        [disabled]=\"selectableMonth.disabled\"\r\n                        (click)=\"selectMonth(selectableMonth, monthsPopover, $event)\">{{selectableMonth.month}}</button>\r\n                }\r\n              </div>\r\n            </div>\r\n          </ng-template>\r\n        </div>\r\n      </div>\r\n      <ng-content></ng-content>\r\n    </div>\r\n    <div class=\"calendar-item\">\r\n      <table>\r\n        <thead>\r\n          <tr>\r\n            <th scope=\"col\">v.</th>\r\n            @for (weekday of calendar.selectedMonth.weekdays; track weekday) {\r\n            <th>\r\n              <span>{{weekday.name | trvCapitalize}}</span>\r\n            </th>\r\n            }\r\n          </tr>\r\n        </thead>\r\n        <tbody>\r\n          @for (week of calendar.selectedMonth.weeks; track week) {\r\n          <tr>\r\n            <th scope=\"row\"\r\n                [ngClass]=\"{ 'current-week' : week.isCurrentWeek }\">\r\n              <span>{{week.number}}</span>\r\n            </th>\r\n            @for (day of week.days; track day) {\r\n            <td (mouseover)=\"onDayHover(day.dateTime)\"\r\n                (mouseout)=\"onDayHoverOut(day.dateTime)\"\r\n                [ngClass]=\"{\r\n                    'other-month' : !day.isCurrentMonth,\r\n                    'disabled': day.isOutsideAvailableInterval || isDisabled,\r\n                    'current-day' : day.isToday,\r\n                    'within-date-range': isWithinDateRange(day.dateTime),\r\n                    'opposite-date-range-day': isOppositeDateRangeDay(day.dateTime),\r\n                    'date-range-from': isOppositeDateRangeDay(day.dateTime) && fromOrToDatePicker === 'to',\r\n                    'date-range-to': isOppositeDateRangeDay(day.dateTime) && fromOrToDatePicker === 'from',\r\n                    'selected' : day.dateTime.toISODate() === dateControl.value\r\n                } \">\r\n              <a role=\"button\"\r\n                 tabindex=\"0\"\r\n                 (click)=\"selectDay(day)\"\r\n                 (keyup.space)=\"selectDay(day)\"\r\n                 (keyup.enter)=\"selectDay(day)\">\r\n                {{day.number}}\r\n              </a>\r\n            </td>\r\n            }\r\n          </tr>\r\n          }\r\n        </tbody>\r\n      </table>\r\n    </div>\r\n  </div>\r\n</ng-template>", styles: [":root{--bs-border-color: #ced4da}:host .input-group{width:auto}:host .input-group.disabled{opacity:.5}:host .input-group:focus{outline:none}:host .input-group input::placeholder{opacity:.5;font-size:14px}:host .input-group input.disabled:hover{pointer-events:none}:host .input-group input.disabled::placeholder{opacity:0}:host .input-group .input-group-prepend .input-datepicker-icon:hover,:host .input-group .input-group-prepend .input-datepicker-icon:focus{color:#1e5c88}:host .input-group .input-group-prepend .input-datepicker-icon:active{color:#fff}:host .input-group .input-disabled-text{position:absolute;z-index:9;left:12px;top:6px}:host .input-group .input-disabled-text.with-icon{left:42px}:host .input-group .date-input{width:6.7rem;max-width:6.7rem;min-width:auto;padding:.375rem .4rem}:host .input-group .time-input{border:var(--bs-border-width) solid var(--bs-border-color);border-left:none;width:4rem;max-width:4rem;padding:.375rem .4rem}:host .form-control:hover,:host .form-control:focus{position:relative;z-index:10}:host .form-control:hover:focus,:host .form-control:focus:focus{color:var(--bs-body-color);background-color:var(--bs-body-bg);border-color:#8faec4;outline:0;box-shadow:0 0 0 .25rem #1e5c8840}:host.ng-invalid.ng-touched .date-input{border:solid 1px #870000!important;background-image:none}:host.ng-invalid.ng-touched .time-input{border:solid 1px #870000!important;background-image:none}:host.ng-invalid.ng-touched .btn{border:solid 1px #870000!important;background-image:none}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "directive", type: TrvPopoverDirective, selector: "[trvPopover]", inputs: ["trvPopover", "manualToggle", "position", "popoverClass", "allowMultiple", "disablePopover", "autoFocus"], outputs: ["shown", "hidden", "clickOutside", "afterClose", "afterCloseWithType"], exportAs: ["trvPopover"] }, { kind: "directive", type: TrvDateStringParserDirective, selector: "input[type=text][formControlName][trvDateStringParser],input[type=text][formControl][trvDateStringParser],input[type=text][ngModel][trvDateStringParser]" }, { kind: "directive", type: TrvTimeStringParserDirective, selector: "input[type=text][formControlName][trvTimeStringParser],input[type=text][formControl][trvTimeStringParser],input[type=text][ngModel][trvTimeStringParser]" }, { kind: "pipe", type: TrvCapitalizePipe, name: "trvCapitalize" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDatePickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-date-picker', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TrvDatePickerComponent),
                            multi: true,
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => TrvDatePickerComponent),
                            multi: true,
                        },
                    ], imports: [
                        NgClass,
                        ReactiveFormsModule,
                        FaIconComponent,
                        TrvPopoverDirective,
                        TrvDateStringParserDirective,
                        TrvTimeStringParserDirective,
                        TrvCapitalizePipe,
                    ], template: "<div class=\"input-group\"\r\n     [ngClass]=\"{ 'has-time-input': showTime, 'disabled': isDisabled }\"\r\n     [formGroup]=\"datePickerFormGroup\"\r\n     [tabindex]=\"isDisabled ? 0 : -1\"\r\n     (focusin)=\"onFocusIn()\"\r\n     (focusout)=\"onFocusOut()\">\r\n  @if (!hideIcon) {\r\n  <button type=\"button\"\r\n          class=\"btn btn-outline-primary input-datepicker-icon\"\r\n          tabindex=\"-1\"\r\n          (click)=\"showPopover($event)\">\r\n    <fa-icon [icon]=\"faCalendarAlt\"></fa-icon>\r\n  </button>\r\n  }\r\n  <div #datePopoverRef=\"trvPopover\"\r\n       popoverClass=\"trv-popover-no-container-margin\"\r\n       [trvPopover]=\"calendarTemplateRef\"\r\n       [tabindex]=\"isDisabled ? 0 : -1\"\r\n       [manualToggle]=\"true\"\r\n       [position]=\"['bottom-start', 'bottom-end', 'top-start', 'top-end', 'right', 'left']\"\r\n       (click)=\"showPopover($event)\"\r\n       (keydown.tab)=\"datePopoverRef.hide()\"\r\n       (clickOutside)=\"onClickOutsidePopover()\"\r\n       (shown)=\"shown.emit()\">\r\n    @if (isDisabled && disabledText) {\r\n    <span class=\"input-disabled-text\"\r\n          [ngClass]=\"{'with-icon': !hideIcon}\">\r\n      {{disabledText}}\r\n    </span>\r\n    }\r\n    <input type=\"text\"\r\n           #dateInputRef\r\n           trvDateStringParser\r\n           class=\"form-control date-input\"\r\n           placeholder=\"\u00E5\u00E5\u00E5\u00E5-mm-dd\"\r\n           formControlName=\"date\"\r\n           [ngClass]=\"{ 'disabled': isDisabled }\"\r\n           (keyup.enter)=\"showPopover($event)\">\r\n  </div>\r\n  @if (showTime) {\r\n  <input #timeInputRef\r\n         type=\"text\"\r\n         class=\"form-control time-input\"\r\n         placeholder=\"hh:mm\"\r\n         formControlName=\"time\"\r\n         trvTimeStringParser\r\n         [ngClass]=\"{ 'disabled': isDisabled }\">\r\n  }\r\n  @if (!skipClear) {\r\n  <button type=\"button\"\r\n          class=\"btn btn-outline-primary input-clear-btn\"\r\n          (click)=\"clearInput($event)\"\r\n          [disabled]=\"isDisabled\">\r\n    <fa-icon [icon]=\"faTimes\"></fa-icon>\r\n  </button>\r\n  }\r\n</div>\r\n<ng-template #calendarTemplateRef>\r\n  <div class=\"calendar-container\"\r\n       id=\"{{calendarContainerId}}\">\r\n    <div class=\"calendar-item-nav\">\r\n      <div class=\"year-select-drop\">\r\n        <button type=\"button\"\r\n                class=\"btn btn-sm btn-link\"\r\n                #yearsPopover=\"trvPopover\"\r\n                [trvPopover]=\"yearPopoverTemplate\"\r\n                popoverClass=\"trv-popover-no-container-margin\"\r\n                [allowMultiple]=\"true\"\r\n                [position]=\"['bottom', 'top']\"\r\n                (shown)=\"monthsPopover?.hide()\"\r\n                (hidden)=\"resetSelectableYearOffset()\"\r\n                [disabled]=\"isDisabled\">\r\n          {{calendarFilter.selectedYear}}\r\n          <fa-icon [icon]=\"faCaretDown\"></fa-icon>\r\n        </button>\r\n        <ng-template #yearPopoverTemplate>\r\n          <div class=\"date-range-areas popover-menu\"\r\n               (click)=\"preventDefaultAndStopPropagationForEvent($event)\">\r\n            <div class=\"year-popover-pagination\">\r\n              <button class=\"btn btn-link\"\r\n                      (click)=\"setSelectableYearOffset(-1, $event)\">\r\n                <fa-icon [icon]=\"faChevronLeft\"></fa-icon>\r\n              </button>{{calendar.selectableYears[0].year + ' - ' +\r\n              calendar.selectableYears[calendar.selectableYears.length - 1].year}}\r\n              <button class=\"btn btn-link\"\r\n                      (click)=\"setSelectableYearOffset(1, $event)\">\r\n                <fa-icon [icon]=\"faChevronRight\"></fa-icon>\r\n              </button>\r\n            </div>\r\n            <div class=\"years-select\">\r\n              @for (selectableYear of calendar.selectableYears; track selectableYear) {\r\n              <button type=\"button\"\r\n                      class=\"btn btn-sm btn-link\"\r\n                      [ngClass]=\"{ 'active' : selectableYear.year === calendarFilter.selectedYear }\"\r\n                      [disabled]=\"selectableYear.disabled\"\r\n                      (click)=\"selectYear(selectableYear, yearsPopover, $event)\">{{selectableYear.year}}</button>\r\n              }\r\n            </div>\r\n          </div>\r\n        </ng-template>\r\n      </div>\r\n      <div class=\"month-select-drop\">\r\n        <div>\r\n          <button type=\"button\"\r\n                  class=\"btn btn-sm btn-link\"\r\n                  #monthsPopover=\"trvPopover\"\r\n                  [trvPopover]=\"monthsPopoverTemplate\"\r\n                  popoverClass=\"trv-popover-no-container-margin\"\r\n                  [allowMultiple]=\"true\"\r\n                  [position]=\"['bottom', 'top']\"\r\n                  (shown)=\"yearsPopover?.hide()\"\r\n                  [disabled]=\"isDisabled\">\r\n            {{calendarFilter.selectedMonth}}\r\n            <fa-icon [icon]=\"faCaretDown\"></fa-icon>\r\n          </button>\r\n          <ng-template #monthsPopoverTemplate>\r\n            <div class=\"date-range-areas popover-menu\"\r\n                 (click)=\"preventDefaultAndStopPropagationForEvent($event)\">\r\n              <div class=\"month-select\">\r\n                @for (selectableMonth of calendar.selectableMonths; track selectableMonth) {\r\n                <button type=\"button\"\r\n                        class=\"btn btn-sm btn-link\"\r\n                        [ngClass]=\"{ 'active' : selectableMonth.month === calendarFilter.selectedMonth }\"\r\n                        [disabled]=\"selectableMonth.disabled\"\r\n                        (click)=\"selectMonth(selectableMonth, monthsPopover, $event)\">{{selectableMonth.month}}</button>\r\n                }\r\n              </div>\r\n            </div>\r\n          </ng-template>\r\n        </div>\r\n      </div>\r\n      <ng-content></ng-content>\r\n    </div>\r\n    <div class=\"calendar-item\">\r\n      <table>\r\n        <thead>\r\n          <tr>\r\n            <th scope=\"col\">v.</th>\r\n            @for (weekday of calendar.selectedMonth.weekdays; track weekday) {\r\n            <th>\r\n              <span>{{weekday.name | trvCapitalize}}</span>\r\n            </th>\r\n            }\r\n          </tr>\r\n        </thead>\r\n        <tbody>\r\n          @for (week of calendar.selectedMonth.weeks; track week) {\r\n          <tr>\r\n            <th scope=\"row\"\r\n                [ngClass]=\"{ 'current-week' : week.isCurrentWeek }\">\r\n              <span>{{week.number}}</span>\r\n            </th>\r\n            @for (day of week.days; track day) {\r\n            <td (mouseover)=\"onDayHover(day.dateTime)\"\r\n                (mouseout)=\"onDayHoverOut(day.dateTime)\"\r\n                [ngClass]=\"{\r\n                    'other-month' : !day.isCurrentMonth,\r\n                    'disabled': day.isOutsideAvailableInterval || isDisabled,\r\n                    'current-day' : day.isToday,\r\n                    'within-date-range': isWithinDateRange(day.dateTime),\r\n                    'opposite-date-range-day': isOppositeDateRangeDay(day.dateTime),\r\n                    'date-range-from': isOppositeDateRangeDay(day.dateTime) && fromOrToDatePicker === 'to',\r\n                    'date-range-to': isOppositeDateRangeDay(day.dateTime) && fromOrToDatePicker === 'from',\r\n                    'selected' : day.dateTime.toISODate() === dateControl.value\r\n                } \">\r\n              <a role=\"button\"\r\n                 tabindex=\"0\"\r\n                 (click)=\"selectDay(day)\"\r\n                 (keyup.space)=\"selectDay(day)\"\r\n                 (keyup.enter)=\"selectDay(day)\">\r\n                {{day.number}}\r\n              </a>\r\n            </td>\r\n            }\r\n          </tr>\r\n          }\r\n        </tbody>\r\n      </table>\r\n    </div>\r\n  </div>\r\n</ng-template>", styles: [":root{--bs-border-color: #ced4da}:host .input-group{width:auto}:host .input-group.disabled{opacity:.5}:host .input-group:focus{outline:none}:host .input-group input::placeholder{opacity:.5;font-size:14px}:host .input-group input.disabled:hover{pointer-events:none}:host .input-group input.disabled::placeholder{opacity:0}:host .input-group .input-group-prepend .input-datepicker-icon:hover,:host .input-group .input-group-prepend .input-datepicker-icon:focus{color:#1e5c88}:host .input-group .input-group-prepend .input-datepicker-icon:active{color:#fff}:host .input-group .input-disabled-text{position:absolute;z-index:9;left:12px;top:6px}:host .input-group .input-disabled-text.with-icon{left:42px}:host .input-group .date-input{width:6.7rem;max-width:6.7rem;min-width:auto;padding:.375rem .4rem}:host .input-group .time-input{border:var(--bs-border-width) solid var(--bs-border-color);border-left:none;width:4rem;max-width:4rem;padding:.375rem .4rem}:host .form-control:hover,:host .form-control:focus{position:relative;z-index:10}:host .form-control:hover:focus,:host .form-control:focus:focus{color:var(--bs-body-color);background-color:var(--bs-body-bg);border-color:#8faec4;outline:0;box-shadow:0 0 0 .25rem #1e5c8840}:host.ng-invalid.ng-touched .date-input{border:solid 1px #870000!important;background-image:none}:host.ng-invalid.ng-touched .time-input{border:solid 1px #870000!important;background-image:none}:host.ng-invalid.ng-touched .btn{border:solid 1px #870000!important;background-image:none}\n"] }]
        }], ctorParameters: () => [{ type: TrvCalendarService }, { type: TrvDateService }, { type: i1$2.FormBuilder }, { type: i0.ChangeDetectorRef }, { type: TrvUtilityService }], propDecorators: { showTime: [{
                type: Input
            }], disabledText: [{
                type: Input
            }], defaultTimeMode: [{
                type: Input
            }], skipClear: [{
                type: Input
            }], hideIcon: [{
                type: Input
            }], isDateSelectableFunction: [{
                type: Input
            }], calendarFilter: [{
                type: Input
            }], minDate: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], focusOut: [{
                type: Output
            }], focusIn: [{
                type: Output
            }], shown: [{
                type: Output
            }], class: [{
                type: HostBinding,
                args: ['class']
            }], datePopoverRef: [{
                type: ViewChild,
                args: ['datePopoverRef']
            }], dateInputElementRef: [{
                type: ViewChild,
                args: ['dateInputRef']
            }], timeInputElementRef: [{
                type: ViewChild,
                args: ['timeInputRef']
            }] } });

class TrvDateRangeComponent extends trvUnsubscribeMixin() {
    dateService;
    formBuilder;
    changeDetectorRef;
    utilityService;
    constructor(dateService, formBuilder, changeDetectorRef, utilityService) {
        super();
        this.dateService = dateService;
        this.formBuilder = formBuilder;
        this.changeDetectorRef = changeDetectorRef;
        this.utilityService = utilityService;
        this.initForm();
    }
    showTime;
    showToIsInfinite;
    minDate;
    maxDate;
    defaultTimeModeFrom;
    defaultTimeModeTo;
    calendarFilters = {
        from: {},
        to: {},
    };
    classes = 'trv-date-range';
    fromDatePicker;
    toDatePicker;
    valueRequired;
    disabled = false;
    isResetted = false;
    dateRangeInvalid = false;
    dateRangeFormGroup;
    faCalendar = faCalendar;
    faTimes = faTimes;
    propagateChange = () => { };
    propagateTouched = () => { };
    ngAfterViewInit() {
        this.fromDatePicker.setIsPartOfDateRange('from');
        this.toDatePicker.setIsPartOfDateRange('to');
        this.setFromDatePickerValues(this.fromControl.value, this.toControl.value);
        this.setToDatePickerValues(this.fromControl.value, this.toControl.value, this.toIsInfiniteControl.value);
    }
    writeValue(value) {
        if (value === null) {
            this.isResetted = true;
        }
        this.dateRangeFormGroup.setValue({
            from: value?.from ?? null,
            to: value?.to ?? null,
            toIsInfinite: value?.toIsInfinite != null ? value.toIsInfinite : false,
        }, { emitEvent: false });
        this.setFromDatePickerValues(value?.from, value?.to);
        this.setToDatePickerValues(value?.from, value?.to, value?.toIsInfinite);
        if (value?.toIsInfinite && this.toControl.enabled) {
            this.toControl.disable({ onlySelf: true, emitEvent: false });
        }
        else if (this.toControl.disabled) {
            this.toControl.enable({ onlySelf: true, emitEvent: false });
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) {
        this.propagateTouched = fn;
    }
    getFromMaxDate() {
        return this.maxDate;
    }
    getToMinDate() {
        return this.minDate;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        if (isDisabled) {
            this.fromControl.disable({ emitEvent: false });
            this.toControl.disable({ emitEvent: false });
            this.dateRangeInvalid = false;
            this.dateRangeFormGroup.updateValueAndValidity();
            this.utilityService.detectChanges(this.changeDetectorRef);
        }
        else {
            this.fromControl.enable({ emitEvent: false });
            if (!this.toIsInfiniteControl.value) {
                this.toControl.enable({ emitEvent: false });
            }
        }
    }
    onDatePickerFocusOut() {
        if (!this.disabled) {
            this.dateRangeFormGroup.markAsTouched();
        }
    }
    validate = (control) => {
        this.setFormGroupTouched(control.touched);
        if (control.validator?.length) {
            const validators = control.validator({});
            const required = validators?.required ? true : false;
            if (this.valueRequired !== required) {
                this.setValidators(required);
                this.valueRequired = required;
            }
            if (this.valueRequired && !(control.value?.from || control.value?.to)) {
                if (this.dateRangeFormGroup.touched) {
                    this.dateRangeInvalid = true;
                    this.utilityService.detectChanges(this.changeDetectorRef);
                }
                return { required: true };
            }
        }
        const errors = { ...this.fromControl.errors, ...this.toControl.errors };
        if (this.toIsBeforeFrom(control.value)) {
            this.dateRangeInvalid = true;
            errors.toBeforeFrom = true;
        }
        this.dateRangeInvalid = Object.keys(errors).length && (this.fromControl.dirty || this.toControl.dirty) ? true : false;
        return Object.keys(errors).length ? errors : null;
    };
    onFromDateChanged(date) {
        if (!date) {
            return;
        }
        this.toDatePicker.minDate = this.getToMinDate();
        this.toControl.updateValueAndValidity();
        if (!this.toControl.value) {
            const dt = this.dateService.fromISO(date);
            this.calendarFilters.to = {
                selectedYear: dt.year,
                selectedMonth: dt.monthLong,
            };
        }
    }
    onToDateChanged(date) {
        if (!date) {
            return;
        }
        this.fromDatePicker.maxDate = this.getFromMaxDate();
        this.fromControl.updateValueAndValidity();
        const toDT = this.dateService.fromISO(date);
        const fromDT = this.dateService.fromISO(this.fromControl.value);
        const sameDay = fromDT && fromDT.isValid && toDT.isValid && fromDT.toISODate() === toDT.toISODate();
        //Om tiden är 00:00, sätt till slutet på dagen
        if (date && this.showTime && sameDay && toDT.hour === 0 && toDT.minute === 0) {
            const endOfDay = toDT.endOf('day').set({ millisecond: 0 });
            if (this.maxDate && endOfDay > this.dateService.fromISO(this.maxDate)) {
                this.toControl.setValue(this.maxDate);
            }
            else {
                this.toControl.setValue(endOfDay.toISO({ suppressMilliseconds: true }));
            }
        }
        if (!this.fromControl.value) {
            this.calendarFilters.from = {
                selectedYear: toDT.year,
                selectedMonth: toDT.monthLong,
            };
        }
        return this.toControl.value;
    }
    onToIsInfiniteChanged(toIsInfinite) {
        if (toIsInfinite) {
            this.toControl.setValidators(null);
            this.toControl.setValue(null, { emitEvent: false });
            this.toControl.disable({ onlySelf: true, emitEvent: false });
            this.setToDatePickerValues(this.fromControl.value, null, true);
            this.setFromDatePickerValues(this.fromControl.value, null);
        }
        else {
            this.toControl.enable({ onlySelf: true, emitEvent: false });
            this.setToDatePickerValues(this.fromControl.value, null, false);
            if (this.valueRequired) {
                this.toControl.setValidators(Validators.required);
                this.toControl.updateValueAndValidity({ emitEvent: false });
            }
        }
    }
    onFromShown() {
        this.toDatePicker.hidePopover();
    }
    onToShown() {
        this.fromDatePicker.hidePopover();
    }
    setValidators(required) {
        if (required) {
            this.fromControl.setValidators(Validators.required);
            this.toControl.setValidators(Validators.required);
        }
        else {
            this.fromControl.setValidators(null);
            this.toControl.setValidators(null);
        }
    }
    setFormGroupTouched(touched) {
        if (touched) {
            this.dateRangeFormGroup.markAllAsTouched();
        }
        else if (touched === false) {
            this.dateRangeFormGroup.markAsUntouched();
            this.fromControl.markAsUntouched();
            this.toControl.markAsUntouched();
            this.toIsInfiniteControl.markAsUntouched();
            setTimeout(() => {
                this.utilityService.detectChanges(this.changeDetectorRef);
            });
        }
    }
    setFromDatePickerValues(fromDate, toDate) {
        this.fromDatePicker?.setDateRangeToDate(toDate ? this.dateService.fromISO(toDate) : null);
        this.fromDatePicker?.setDateRangeFromDate(fromDate ? this.dateService.fromISO(fromDate) : null);
    }
    setToDatePickerValues(fromDate, toDate, toIsInfinite) {
        this.toDatePicker?.setDateRangeToDate(toDate ? this.dateService.fromISO(toDate) : null);
        this.toDatePicker?.setDateRangeFromDate(fromDate ? this.dateService.fromISO(fromDate) : null);
        this.toDatePicker?.setDateRangeToIsInfinite(toIsInfinite);
    }
    emitChanges(formValues) {
        if (this.fromControl.touched && (this.toControl.touched || this.toIsInfiniteControl.touched)) {
            this.propagateTouched();
        }
        this.propagateChange({ from: formValues.from, to: formValues.to ?? null, toIsInfinite: formValues.toIsInfinite });
    }
    toIsBeforeFrom(dr) {
        return dr?.to && dr?.from && new Date(dr.to) < new Date(dr.from);
    }
    initForm() {
        this.dateRangeFormGroup = new FormGroup({
            from: new FormControl(null),
            to: new FormControl(null),
            toIsInfinite: new FormControl(false),
        });
        this.initSubscribers();
    }
    initSubscribers() {
        this.fromControl.valueChanges
            .pipe(startWith(this.fromControl.value), distinctUntilChanged((prev, next) => {
            if (this.isResetted) {
                this.isResetted = false;
                return false;
            }
            return next === prev;
        }), takeUntil(this.ngUnsubscribe))
            .subscribe((from) => {
            this.onFromDateChanged(from);
            this.toDatePicker?.setDateRangeFromDate(this.dateService.fromISO(from));
            this.fromDatePicker?.setDateRangeFromDate(this.dateService.fromISO(from));
            this.emitChanges({ ...this.dateRangeFormGroup.value, from });
        });
        this.toControl.valueChanges
            .pipe(startWith(this.toControl.value), distinctUntilChanged((prev, next) => {
            if (this.isResetted) {
                this.isResetted = false;
                return false;
            }
            return next === prev;
        }), takeUntil(this.ngUnsubscribe))
            .subscribe((to) => {
            const adjustedToDate = this.onToDateChanged(to);
            this.fromDatePicker?.setDateRangeToDate(this.dateService.fromISO(to));
            this.toDatePicker?.setDateRangeToDate(this.dateService.fromISO(to));
            this.emitChanges({ ...this.dateRangeFormGroup.value, to: adjustedToDate });
        });
        this.toIsInfiniteControl.valueChanges
            .pipe(distinctUntilChanged((p, q) => p === q), takeUntil(this.ngUnsubscribe))
            .subscribe((toIsInfinite) => {
            this.onToIsInfiniteChanged(toIsInfinite);
            this.emitChanges({ ...this.dateRangeFormGroup.value, toIsInfinite });
        });
    }
    get fromControl() {
        return this.dateRangeFormGroup.get('from');
    }
    get toControl() {
        return this.dateRangeFormGroup.get('to');
    }
    get toIsInfiniteControl() {
        return this.dateRangeFormGroup.get('toIsInfinite');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateRangeComponent, deps: [{ token: TrvDateService }, { token: i1$2.FormBuilder }, { token: i0.ChangeDetectorRef }, { token: TrvUtilityService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvDateRangeComponent, isStandalone: true, selector: "trv-date-range", inputs: { showTime: "showTime", showToIsInfinite: "showToIsInfinite", minDate: "minDate", maxDate: "maxDate", defaultTimeModeFrom: "defaultTimeModeFrom", defaultTimeModeTo: "defaultTimeModeTo", calendarFilters: "calendarFilters" }, host: { properties: { "class": "this.classes" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TrvDateRangeComponent),
                multi: true,
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => TrvDateRangeComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "fromDatePicker", first: true, predicate: ["fromDatePicker"], descendants: true }, { propertyName: "toDatePicker", first: true, predicate: ["toDatePicker"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<ng-container [formGroup]=\"dateRangeFormGroup\">\r\n  <div class=\"date-range-container\"\r\n       [ngClass]=\"{ 'date-range-invalid': dateRangeInvalid,'date-range-container--time': showTime }\">\r\n    <trv-date-picker id=\"date-picker-from\"\r\n                     #fromDatePicker\r\n                     formControlName=\"from\"\r\n                     [showTime]=\"showTime\"\r\n                     [minDate]=\"minDate\"\r\n                     [maxDate]=\"getFromMaxDate()\"\r\n                     [calendarFilter]=\"calendarFilters.from\"\r\n                     [skipClear]=\"true\"\r\n                     (focusOut)=\"onDatePickerFocusOut()\"\r\n                     (shown)=\"onFromShown()\"\r\n                     [defaultTimeMode]=\"defaultTimeModeFrom\">\r\n    </trv-date-picker>\r\n    <trv-date-picker id=\"date-picker-to\"\r\n                     #toDatePicker\r\n                     formControlName=\"to\"\r\n                     [showTime]=\"showTime\"\r\n                     [minDate]=\"getToMinDate()\"\r\n                     [maxDate]=\"maxDate\"\r\n                     [calendarFilter]=\"calendarFilters.to\"\r\n                     [skipClear]=\"true\"\r\n                     [disabledText]=\"showToIsInfinite && dateRangeFormGroup.get('toIsInfinite').value ? 'Tillsvidare' : ''\"\r\n                     [hideIcon]=\"true\"\r\n                     (focusOut)=\"onDatePickerFocusOut()\"\r\n                     (shown)=\"onToShown()\"\r\n                     [defaultTimeMode]=\"defaultTimeModeTo\">\r\n      @if (showToIsInfinite) {\r\n      <div class=\"to-is-infinite form-check ms-auto me-2\">\r\n        <input formControlName=\"toIsInfinite\"\r\n               type=\"checkbox\"\r\n               class=\"form-check-input\"\r\n               id=\"input-to-is-infinite\">\r\n        <label for=\"input-to-is-infinite\"\r\n               class=\"form-check-label text-white\">Tillsvidare</label>\r\n      </div>\r\n      }\r\n    </trv-date-picker>\r\n  </div>\r\n</ng-container>", styles: [":root{--bs-border-color: #ced4da}:host ::ng-deep .date-range-container{display:flex}:host ::ng-deep .date-range-container--time .input-group .has-time-input{width:7.5rem}:host ::ng-deep .trv-date-picker .form-control.disabled{background-color:#fff}:host ::ng-deep .trv-date-picker .form-control.disabled::placeholder{opacity:0!important}:host ::ng-deep .trv-date-picker+.trv-date-picker{margin-left:-1px}:host ::ng-deep .trv-date-picker .form-control:hover,:host ::ng-deep .trv-date-picker .form-control:focus{background-color:#e9ecef;box-shadow:none}:host ::ng-deep .trv-date-picker .form-control:focus{border-color:#ced4da}:host ::ng-deep .trv-date-picker:first-child:focus{position:relative}:host ::ng-deep .trv-date-picker:first-child:focus:after{position:absolute;content:\"\";height:100%;right:0;background:#ced4da;width:1px}:host ::ng-deep .trv-date-picker:last-child .input-group{position:relative}:host ::ng-deep .trv-date-picker .time-input{margin-left:-1px}:host ::ng-deep .date-range-invalid .trv-date-picker .input-group:not(.disabled) .date-input,:host ::ng-deep .date-range-invalid .trv-date-picker .input-group:not(.disabled) .time-input,:host ::ng-deep .date-range-invalid .trv-date-picker .input-group:not(.disabled) .btn{border:solid 1px #870000!important;background-image:none!important}:host ::ng-deep .date-range-invalid .trv-date-picker .input-group:not(.disabled) .date-input:hover,:host ::ng-deep .date-range-invalid .trv-date-picker .input-group:not(.disabled) .time-input:hover,:host ::ng-deep .date-range-invalid .trv-date-picker .input-group:not(.disabled) .btn:hover{border:solid 1px #870000!important}\n"], dependencies: [{ kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1$2.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: TrvDatePickerComponent, selector: "trv-date-picker", inputs: ["showTime", "disabledText", "defaultTimeMode", "skipClear", "hideIcon", "isDateSelectableFunction", "calendarFilter", "minDate", "maxDate"], outputs: ["focusOut", "focusIn", "shown"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateRangeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-date-range', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TrvDateRangeComponent),
                            multi: true,
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => TrvDateRangeComponent),
                            multi: true,
                        },
                    ], imports: [ReactiveFormsModule, NgClass, TrvDatePickerComponent], template: "<ng-container [formGroup]=\"dateRangeFormGroup\">\r\n  <div class=\"date-range-container\"\r\n       [ngClass]=\"{ 'date-range-invalid': dateRangeInvalid,'date-range-container--time': showTime }\">\r\n    <trv-date-picker id=\"date-picker-from\"\r\n                     #fromDatePicker\r\n                     formControlName=\"from\"\r\n                     [showTime]=\"showTime\"\r\n                     [minDate]=\"minDate\"\r\n                     [maxDate]=\"getFromMaxDate()\"\r\n                     [calendarFilter]=\"calendarFilters.from\"\r\n                     [skipClear]=\"true\"\r\n                     (focusOut)=\"onDatePickerFocusOut()\"\r\n                     (shown)=\"onFromShown()\"\r\n                     [defaultTimeMode]=\"defaultTimeModeFrom\">\r\n    </trv-date-picker>\r\n    <trv-date-picker id=\"date-picker-to\"\r\n                     #toDatePicker\r\n                     formControlName=\"to\"\r\n                     [showTime]=\"showTime\"\r\n                     [minDate]=\"getToMinDate()\"\r\n                     [maxDate]=\"maxDate\"\r\n                     [calendarFilter]=\"calendarFilters.to\"\r\n                     [skipClear]=\"true\"\r\n                     [disabledText]=\"showToIsInfinite && dateRangeFormGroup.get('toIsInfinite').value ? 'Tillsvidare' : ''\"\r\n                     [hideIcon]=\"true\"\r\n                     (focusOut)=\"onDatePickerFocusOut()\"\r\n                     (shown)=\"onToShown()\"\r\n                     [defaultTimeMode]=\"defaultTimeModeTo\">\r\n      @if (showToIsInfinite) {\r\n      <div class=\"to-is-infinite form-check ms-auto me-2\">\r\n        <input formControlName=\"toIsInfinite\"\r\n               type=\"checkbox\"\r\n               class=\"form-check-input\"\r\n               id=\"input-to-is-infinite\">\r\n        <label for=\"input-to-is-infinite\"\r\n               class=\"form-check-label text-white\">Tillsvidare</label>\r\n      </div>\r\n      }\r\n    </trv-date-picker>\r\n  </div>\r\n</ng-container>", styles: [":root{--bs-border-color: #ced4da}:host ::ng-deep .date-range-container{display:flex}:host ::ng-deep .date-range-container--time .input-group .has-time-input{width:7.5rem}:host ::ng-deep .trv-date-picker .form-control.disabled{background-color:#fff}:host ::ng-deep .trv-date-picker .form-control.disabled::placeholder{opacity:0!important}:host ::ng-deep .trv-date-picker+.trv-date-picker{margin-left:-1px}:host ::ng-deep .trv-date-picker .form-control:hover,:host ::ng-deep .trv-date-picker .form-control:focus{background-color:#e9ecef;box-shadow:none}:host ::ng-deep .trv-date-picker .form-control:focus{border-color:#ced4da}:host ::ng-deep .trv-date-picker:first-child:focus{position:relative}:host ::ng-deep .trv-date-picker:first-child:focus:after{position:absolute;content:\"\";height:100%;right:0;background:#ced4da;width:1px}:host ::ng-deep .trv-date-picker:last-child .input-group{position:relative}:host ::ng-deep .trv-date-picker .time-input{margin-left:-1px}:host ::ng-deep .date-range-invalid .trv-date-picker .input-group:not(.disabled) .date-input,:host ::ng-deep .date-range-invalid .trv-date-picker .input-group:not(.disabled) .time-input,:host ::ng-deep .date-range-invalid .trv-date-picker .input-group:not(.disabled) .btn{border:solid 1px #870000!important;background-image:none!important}:host ::ng-deep .date-range-invalid .trv-date-picker .input-group:not(.disabled) .date-input:hover,:host ::ng-deep .date-range-invalid .trv-date-picker .input-group:not(.disabled) .time-input:hover,:host ::ng-deep .date-range-invalid .trv-date-picker .input-group:not(.disabled) .btn:hover{border:solid 1px #870000!important}\n"] }]
        }], ctorParameters: () => [{ type: TrvDateService }, { type: i1$2.FormBuilder }, { type: i0.ChangeDetectorRef }, { type: TrvUtilityService }], propDecorators: { showTime: [{
                type: Input
            }], showToIsInfinite: [{
                type: Input
            }], minDate: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], defaultTimeModeFrom: [{
                type: Input
            }], defaultTimeModeTo: [{
                type: Input
            }], calendarFilters: [{
                type: Input
            }], classes: [{
                type: HostBinding,
                args: ['class']
            }], fromDatePicker: [{
                type: ViewChild,
                args: ['fromDatePicker']
            }], toDatePicker: [{
                type: ViewChild,
                args: ['toDatePicker']
            }] } });

//Lista från https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Complete_list_of_MIME_types
const knownFileTypes = [
    { extension: 'aac', mimeType: 'audio/aac' },
    { extension: 'abw', mimeType: 'application/x-abiword' },
    { extension: 'arc', mimeType: 'application/x-freearc' },
    { extension: 'avi', mimeType: 'video/x-msvideo' },
    { extension: 'azw', mimeType: 'application/vnd.amazon.ebook' },
    { extension: 'bin', mimeType: 'application/octet-stream' },
    { extension: 'bmp', mimeType: 'image/bmp' },
    { extension: 'bz', mimeType: 'application/x-bzip' },
    { extension: 'bz2', mimeType: 'application/x-bzip2' },
    { extension: 'csh', mimeType: 'application/x-csh' },
    { extension: 'css', mimeType: 'text/css' },
    { extension: 'csv', mimeType: 'text/csv' },
    { extension: 'doc', mimeType: 'application/msword' },
    { extension: 'docx', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
    { extension: 'eot', mimeType: 'application/vnd.ms-fontobject' },
    { extension: 'epub', mimeType: 'application/epub+zip' },
    { extension: 'gz', mimeType: 'application/gzip' },
    { extension: 'gif', mimeType: 'image/gif' },
    { extension: 'htm', mimeType: 'text/html' },
    { extension: 'html', mimeType: 'text/html' },
    { extension: 'ico', mimeType: 'image/vnd.microsoft.icon' },
    { extension: 'ics', mimeType: 'text/calendar' },
    { extension: 'jar', mimeType: 'application/java-archive' },
    { extension: 'jpeg', mimeType: 'image/jpeg' },
    { extension: 'jpg', mimeType: 'image/jpeg' },
    { extension: 'js', mimeType: 'text/javascript' },
    { extension: 'json', mimeType: 'application/json' },
    { extension: 'jsonld', mimeType: 'application/ld+json' },
    { extension: 'mid', mimeType: 'audio/midi' },
    { extension: 'midi', mimeType: 'audio/midi' },
    { extension: 'mjs', mimeType: 'text/javascript' },
    { extension: 'mp3', mimeType: 'audio/mpeg' },
    { extension: 'mp4', mimeType: 'video/mp4' },
    { extension: 'mpeg', mimeType: 'video/mpeg' },
    { extension: 'mpkg', mimeType: 'application/vnd.apple.installer+xml' },
    { extension: 'odp', mimeType: 'application/vnd.oasis.opendocument.presentation' },
    { extension: 'ods', mimeType: 'application/vnd.oasis.opendocument.spreadsheet' },
    { extension: 'odt', mimeType: 'application/vnd.oasis.opendocument.text' },
    { extension: 'oga', mimeType: 'audio/ogg' },
    { extension: 'ogv', mimeType: 'video/ogg' },
    { extension: 'ogx', mimeType: 'application/ogg' },
    { extension: 'otf', mimeType: 'font/otf' },
    { extension: 'png', mimeType: 'image/png' },
    { extension: 'pdf', mimeType: 'application/pdf' },
    { extension: 'php', mimeType: 'appliction/php' },
    { extension: 'ppt', mimeType: 'application/vnd.ms-powerpoint' },
    { extension: 'pptx', mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' },
    { extension: 'rar', mimeType: 'application/x-rar-compressed' },
    { extension: 'rtf', mimeType: 'application/rtf' },
    { extension: 'sh', mimeType: 'application/x-sh' },
    { extension: 'svg', mimeType: 'image/svg+xml' },
    { extension: 'swf', mimeType: 'application/x-shockwave-flash' },
    { extension: 'tar', mimeType: 'application/x-tar' },
    { extension: 'tif', mimeType: 'image/tiff' },
    { extension: 'tiff', mimeType: 'image/tiff' },
    { extension: 'ts', mimeType: 'video/mp2t' },
    { extension: 'ttf', mimeType: 'font/ttf' },
    { extension: 'txt', mimeType: 'text/plain' },
    { extension: 'vsd', mimeType: 'application/vnd.visio' },
    { extension: 'wav', mimeType: 'audio/wav' },
    { extension: 'weba', mimeType: 'audio/webm' },
    { extension: 'webm', mimeType: 'video/webm' },
    { extension: 'webp', mimeType: 'image/webp' },
    { extension: 'woff', mimeType: 'font/woff' },
    { extension: 'woff2', mimeType: 'font/woff2' },
    { extension: 'xhtml', mimeType: 'application/xhtml+xml' },
    { extension: 'xls', mimeType: 'application/vnd.ms-excel' },
    { extension: 'xlsx', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    { extension: 'xltx', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.template' },
    { extension: 'xml', mimeType: 'application/xml' },
    { extension: 'xml', mimeType: 'text/xml' },
    { extension: 'xul', mimeType: 'application/vnd.mozilla.xul+xml' },
    { extension: 'zip', mimeType: 'application/zip' },
    { extension: '3gp', mimeType: 'video/3gpp' },
    { extension: '3gp', mimeType: 'audio/3gpp' },
    { extension: '3g2', mimeType: 'video/3gpp2' },
    { extension: '3g2', mimeType: 'audio/3gpp2' },
    { extension: '7z', mimeType: 'application/x-7z-compressed' }
];

class TrvFileUploadService {
    knownFileTypes = knownFileTypes;
    constructor() { }
    getAllowedTypes(fileExtensionsString) {
        if (!fileExtensionsString) {
            return this.knownFileTypes;
        }
        const fileExtensions = fileExtensionsString.split(',');
        return this.knownFileTypes.filter(kft => fileExtensions.some(fe => fe.trim() === kft.extension));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFileUploadService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFileUploadService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFileUploadService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class TrvDragAndDropFilesDirective {
    filesAdded = new EventEmitter();
    dragOver = new EventEmitter();
    dragEnteredElements = [];
    onDragEnter(event) {
        this.preventDefaultAction(event);
        this.dragEnteredElements.push(event.target);
        this.dragOver.emit(true);
    }
    onDragOver(event) {
        event.dataTransfer.dropEffect = 'copy';
        this.preventDefaultAction(event);
    }
    onDragLeave(event) {
        this.preventDefaultAction(event);
        this.dragEnteredElements.splice(this.dragEnteredElements.indexOf(event.target), 1);
        if (this.dragEnteredElements.length === 0) {
            this.dragOver.emit(false);
        }
    }
    onDrop(event) {
        this.preventDefaultAction(event);
        this.dragOver.emit(false);
        this.dragEnteredElements = [];
        const files = event.dataTransfer.files;
        if (files && files.length) {
            this.filesAdded.emit(event.dataTransfer.files);
        }
    }
    preventDefaultAction(event) {
        event.preventDefault();
        event.stopPropagation();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDragAndDropFilesDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvDragAndDropFilesDirective, isStandalone: true, selector: "[trvDragAndDropFiles]", outputs: { filesAdded: "filesAdded", dragOver: "dragOver" }, host: { listeners: { "dragenter": "onDragEnter($event)", "dragover": "onDragOver($event)", "dragleave": "onDragLeave($event)", "drop": "onDrop($event)" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDragAndDropFilesDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[trvDragAndDropFiles]',
                    standalone: true
                }]
        }], propDecorators: { filesAdded: [{
                type: Output
            }], dragOver: [{
                type: Output
            }], onDragEnter: [{
                type: HostListener,
                args: ['dragenter', ['$event']]
            }], onDragOver: [{
                type: HostListener,
                args: ['dragover', ['$event']]
            }], onDragLeave: [{
                type: HostListener,
                args: ['dragleave', ['$event']]
            }], onDrop: [{
                type: HostListener,
                args: ['drop', ['$event']]
            }] } });

class TrvFileUploadComponent {
    fileUploadService;
    changeDetectorRef;
    utilityService;
    constructor(fileUploadService, changeDetectorRef, utilityService) {
        this.fileUploadService = fileUploadService;
        this.changeDetectorRef = changeDetectorRef;
        this.utilityService = utilityService;
    }
    validExtensions;
    maxSize;
    maxFiles;
    labels;
    additionalKnownFileTypes;
    customAcceptString;
    fileButton;
    faFileUpload = faFileUpload;
    faFileAlt = faFileAlt;
    files = [];
    accept;
    dragOver;
    fileTypeErrors = [];
    fileSizeErrors = [];
    validFileTypes;
    formatedMaxSize;
    propagateChange = () => { };
    propagateTouched = () => { };
    ngOnInit() {
        this.accept = this.customAcceptString != null ? this.customAcceptString :
            this.validExtensions?.split(',').map(ve => ve.charAt(0) !== '.' ? '.' + ve : ve).join(',');
        this.validFileTypes = this.fileUploadService.getAllowedTypes(this.validExtensions).concat(this.additionalKnownFileTypes || []);
        this.formatedMaxSize = this.formatBytes(this.maxSize);
        this.maxFiles = this.maxFiles || 10;
        this.labels = this.labels || {
            dragAndDrop: 'Dra och släpp filer',
            selectFiles: 'Välj filer att ladda upp',
            selectMoreFiles: 'Välj fler filer att ladda upp'
        };
    }
    writeValue(value) {
        if (!value || !value.length) {
            this.files = Array();
            this.utilityService.detectChanges(this.changeDetectorRef);
            return;
        }
        this.files = value;
        this.utilityService.detectChanges(this.changeDetectorRef);
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) {
        this.propagateTouched = fn;
    }
    validate(c) {
        const files = c.value;
        this.fileTypeErrors = [];
        this.fileSizeErrors = [];
        if (!files || !files.length) {
            return null;
        }
        const errors = {};
        if (this.validFileTypes.length && !this.validType(files)) {
            errors.mimeTypeError = {
                valid: false,
                allowedFileTypes: this.validExtensions,
                invalidFiles: this.fileTypeErrors.join(', ')
            };
        }
        if (this.maxSize && !this.validSize(files)) {
            errors.maxFileSizeError = {
                valid: false,
                maxSize: this.formatedMaxSize,
                invalidFiles: this.fileSizeErrors.join(', ')
            };
        }
        if (this.maxFiles && files.length > this.maxFiles) {
            errors.maximumNumberOfFiles = {
                valid: false,
                maximumNumberOfFiles: this.maxFiles
            };
        }
        if (!Object.keys(errors).length) {
            return null;
        }
        return errors;
    }
    removeFile(index) {
        this.files.splice(index, 1);
        this.propagateChange(this.files);
    }
    openFileDialog() {
        this.fileButton.nativeElement.click();
        return false;
    }
    onDragOver(dragOver) {
        this.dragOver = dragOver;
    }
    chooseFile(event) {
        if (!event?.target) {
            return;
        }
        const target = event.target;
        if (target.files.length) {
            this.addFiles(target.files);
            target.value = '';
        }
    }
    addFiles(filesToAdd) {
        this.propagateTouched();
        if (filesToAdd && filesToAdd.length > 0) {
            for (let i = 0; i < filesToAdd.length && this.files.length < this.maxFiles; i++) {
                this.files.push(filesToAdd[i]);
            }
            this.propagateChange(this.files);
        }
    }
    formatBytes(bytes) {
        if (!bytes || bytes === 0) {
            return '0 Byte';
        }
        const k = 1024;
        const decimals = 1;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
    }
    validType(files) {
        let valid = true;
        for (const file of files) {
            let exists = false;
            //Gå i första hand på mime-typen, annars filändelse
            if (file.type) {
                exists = this.validFileTypes.some(vft => vft.mimeType === file.type);
            }
            else {
                const extension = file.name.split('.').pop();
                exists = this.validFileTypes.some(vft => vft.extension === extension);
            }
            if (!exists) {
                valid = false;
                this.fileTypeErrors.push(file.name);
            }
        }
        return valid;
    }
    validSize(files) {
        const filesAboveMaxSize = files.filter(x => x.size > this.maxSize);
        filesAboveMaxSize.forEach(f => this.fileSizeErrors.push(f.name));
        return !filesAboveMaxSize.length;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFileUploadComponent, deps: [{ token: TrvFileUploadService }, { token: i0.ChangeDetectorRef }, { token: TrvUtilityService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvFileUploadComponent, isStandalone: true, selector: "trv-file-upload", inputs: { validExtensions: "validExtensions", maxSize: "maxSize", maxFiles: "maxFiles", labels: "labels", additionalKnownFileTypes: "additionalKnownFileTypes", customAcceptString: "customAcceptString" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TrvFileUploadComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => TrvFileUploadComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "fileButton", first: true, predicate: ["fileButton"], descendants: true, read: ElementRef }], ngImport: i0, template: "<div class=\"dropzone\"\r\n     [ngClass]=\"{ \r\n       'zone-over': dragOver === true ,\r\n       'invalid': fileTypeErrors?.length || fileSizeErrors?.length\r\n      }\"\r\n     trvDragAndDropFiles\r\n     (filesAdded)=\"addFiles($event)\"\r\n     (dragOver)=\"onDragOver($event)\">\r\n  @if (!files?.length) {\r\n  <div class=\"text-wrapper text-center\">\r\n    <fa-icon [icon]=\"faFileUpload\"\r\n             size=\"3x\"\r\n             class=\"upload-icon\"></fa-icon>\r\n    <div class=\"info-text\">\r\n      <p class=\"semi-bold\">{{labels.dragAndDrop}}</p>\r\n    </div>\r\n  </div>\r\n  }\r\n  @if (files?.length) {\r\n  <div class=\"file-list\">\r\n    <ul>\r\n      @for (file of files; track file; let i = $index) {\r\n      <li class=\"selected-file\">\r\n        <div class=\"flexbox\">\r\n          <fa-icon [icon]=\"faFileAlt\"\r\n                   size=\"2x\"\r\n                   class=\"file-icon me-3\">\r\n          </fa-icon>\r\n          <div>\r\n            <h6 class=\"semi-bold text-break\">{{file.name}}</h6>\r\n            @if (file.size) {\r\n            <p>{{formatBytes(file.size)}}</p>\r\n            }\r\n          </div>\r\n          <button type=\"button\"\r\n                  class=\"button remove flex-end\"\r\n                  (click)=\"removeFile(i)\">\r\n            <span>&times;</span>\r\n          </button>\r\n        </div>\r\n      </li>\r\n      }\r\n    </ul>\r\n  </div>\r\n  }\r\n  @if (files.length < maxFiles)\r\n    {\r\n    <div\r\n    class=\"text-center\">\r\n    <button type=\"button\"\r\n            class=\"btn btn-primary btn-lg\"\r\n            (click)=\"openFileDialog()\">\r\n      {{files.length > 0 ? labels.selectMoreFiles : labels.selectFiles}}\r\n    </button>\r\n    <input #fileButton\r\n           type=\"file\"\r\n           class=\"upload-button\"\r\n           [multiple]=\"maxFiles > 1\"\r\n           (change)=\"chooseFile($event)\"\r\n           [attr.accept]=\"accept\">\r\n</div>\r\n}\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.dropzone{width:100%;min-height:150px;min-width:275px;background-color:#f9f9fa;border:dashed 2px #aaa;padding:15px}.dropzone.invalid{border-color:#870000}.dropzone p{margin-bottom:0}.dropzone .semi-bold{font-weight:600}.dropzone .btn-lg,.dropzone .btn-group-lg>.btn{font-size:1rem}.dropzone .text-wrapper{vertical-align:middle}.dropzone .text-wrapper .info-text{padding:15px}.dropzone .text-wrapper .upload-icon{color:#1e5c88;margin-top:15px;margin-bottom:10px}.dropzone .file-list ul{list-style:none;padding-left:0}.dropzone .file-list ul li{background-color:#fff;box-shadow:0 0 8px #0c0c0d26;padding:10px;margin-bottom:10px}.dropzone .file-list ul li .flexbox{display:flex;flex-direction:row;flex-wrap:nowrap;align-items:center}.dropzone .file-list ul li .flexbox .flex-end{margin-left:auto}.dropzone .file-list ul li .flexbox h6{margin-bottom:0}.dropzone .file-list ul li .flexbox .file-icon{margin-right:5px;color:#1e5c88}.dropzone .file-list ul li .flexbox .remove{cursor:pointer;background-color:transparent;border:0;padding:0;font-size:2rem;font-weight:700;color:#c00;text-shadow:0 1px 0 #fff}.dropzone .file-list ul li .flexbox .remove:hover{color:#900}.zone-over{border:dashed 2px rgb(30,92,136)}.upload-button{visibility:hidden;width:1px;height:1px}\n"], dependencies: [{ kind: "directive", type: TrvDragAndDropFilesDirective, selector: "[trvDragAndDropFiles]", outputs: ["filesAdded", "dragOver"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFileUploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-file-upload', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TrvFileUploadComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => TrvFileUploadComponent),
                            multi: true
                        }
                    ], imports: [TrvDragAndDropFilesDirective, NgClass, FaIconComponent], template: "<div class=\"dropzone\"\r\n     [ngClass]=\"{ \r\n       'zone-over': dragOver === true ,\r\n       'invalid': fileTypeErrors?.length || fileSizeErrors?.length\r\n      }\"\r\n     trvDragAndDropFiles\r\n     (filesAdded)=\"addFiles($event)\"\r\n     (dragOver)=\"onDragOver($event)\">\r\n  @if (!files?.length) {\r\n  <div class=\"text-wrapper text-center\">\r\n    <fa-icon [icon]=\"faFileUpload\"\r\n             size=\"3x\"\r\n             class=\"upload-icon\"></fa-icon>\r\n    <div class=\"info-text\">\r\n      <p class=\"semi-bold\">{{labels.dragAndDrop}}</p>\r\n    </div>\r\n  </div>\r\n  }\r\n  @if (files?.length) {\r\n  <div class=\"file-list\">\r\n    <ul>\r\n      @for (file of files; track file; let i = $index) {\r\n      <li class=\"selected-file\">\r\n        <div class=\"flexbox\">\r\n          <fa-icon [icon]=\"faFileAlt\"\r\n                   size=\"2x\"\r\n                   class=\"file-icon me-3\">\r\n          </fa-icon>\r\n          <div>\r\n            <h6 class=\"semi-bold text-break\">{{file.name}}</h6>\r\n            @if (file.size) {\r\n            <p>{{formatBytes(file.size)}}</p>\r\n            }\r\n          </div>\r\n          <button type=\"button\"\r\n                  class=\"button remove flex-end\"\r\n                  (click)=\"removeFile(i)\">\r\n            <span>&times;</span>\r\n          </button>\r\n        </div>\r\n      </li>\r\n      }\r\n    </ul>\r\n  </div>\r\n  }\r\n  @if (files.length < maxFiles)\r\n    {\r\n    <div\r\n    class=\"text-center\">\r\n    <button type=\"button\"\r\n            class=\"btn btn-primary btn-lg\"\r\n            (click)=\"openFileDialog()\">\r\n      {{files.length > 0 ? labels.selectMoreFiles : labels.selectFiles}}\r\n    </button>\r\n    <input #fileButton\r\n           type=\"file\"\r\n           class=\"upload-button\"\r\n           [multiple]=\"maxFiles > 1\"\r\n           (change)=\"chooseFile($event)\"\r\n           [attr.accept]=\"accept\">\r\n</div>\r\n}\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.dropzone{width:100%;min-height:150px;min-width:275px;background-color:#f9f9fa;border:dashed 2px #aaa;padding:15px}.dropzone.invalid{border-color:#870000}.dropzone p{margin-bottom:0}.dropzone .semi-bold{font-weight:600}.dropzone .btn-lg,.dropzone .btn-group-lg>.btn{font-size:1rem}.dropzone .text-wrapper{vertical-align:middle}.dropzone .text-wrapper .info-text{padding:15px}.dropzone .text-wrapper .upload-icon{color:#1e5c88;margin-top:15px;margin-bottom:10px}.dropzone .file-list ul{list-style:none;padding-left:0}.dropzone .file-list ul li{background-color:#fff;box-shadow:0 0 8px #0c0c0d26;padding:10px;margin-bottom:10px}.dropzone .file-list ul li .flexbox{display:flex;flex-direction:row;flex-wrap:nowrap;align-items:center}.dropzone .file-list ul li .flexbox .flex-end{margin-left:auto}.dropzone .file-list ul li .flexbox h6{margin-bottom:0}.dropzone .file-list ul li .flexbox .file-icon{margin-right:5px;color:#1e5c88}.dropzone .file-list ul li .flexbox .remove{cursor:pointer;background-color:transparent;border:0;padding:0;font-size:2rem;font-weight:700;color:#c00;text-shadow:0 1px 0 #fff}.dropzone .file-list ul li .flexbox .remove:hover{color:#900}.zone-over{border:dashed 2px rgb(30,92,136)}.upload-button{visibility:hidden;width:1px;height:1px}\n"] }]
        }], ctorParameters: () => [{ type: TrvFileUploadService }, { type: i0.ChangeDetectorRef }, { type: TrvUtilityService }], propDecorators: { validExtensions: [{
                type: Input
            }], maxSize: [{
                type: Input
            }], maxFiles: [{
                type: Input
            }], labels: [{
                type: Input
            }], additionalKnownFileTypes: [{
                type: Input
            }], customAcceptString: [{
                type: Input
            }], fileButton: [{
                type: ViewChild,
                args: ['fileButton', { read: ElementRef }]
            }] } });

class TrvFilterSummaryComponent {
    cdr;
    trvUtilityService;
    labels;
    message;
    filter;
    clear = new EventEmitter();
    // eslint-disable-next-line @angular-eslint/no-output-native
    change = new EventEmitter();
    summaryItems = [];
    faTimes = faTimes;
    faTrashAlt = faTrashAlt;
    constructor(cdr, trvUtilityService) {
        this.cdr = cdr;
        this.trvUtilityService = trvUtilityService;
    }
    ngOnChanges(changes) {
        if (changes.filter?.currentValue) {
            this.prepareLabels(changes.filter.currentValue);
        }
    }
    removeItem(key) {
        this.setFilterValue(key, null);
        this.change.emit();
    }
    removeArrayItem(key, index, event) {
        if (event) {
            event.stopPropagation();
        }
        const arrayValue = this.getFilterValue(key);
        arrayValue.splice(index, 1);
        this.setFilterValue(key, arrayValue);
        this.change.emit();
    }
    removeArray(key) {
        this.setFilterValue(key, []);
        this.change.emit();
    }
    removeBoolean(key) {
        this.setFilterValue(key, false);
        this.change.emit();
    }
    clearFilter() {
        this.clear.emit();
    }
    trackByForArrayLabels(index, item) {
        return item.label;
    }
    trackByForSummaryItems(index, item) {
        return item.name;
    }
    prepareLabels(filter) {
        if (!filter || !this.labels) {
            return;
        }
        const updatedSummaryLabels = [];
        for (const label of this.labels) {
            const filterValue = this.getFilterValue(label.name);
            if (this.objectOrArrayHasValue(filterValue)) {
                const isArray = Array.isArray(filterValue);
                const summaryLabel = {
                    name: label.name,
                    title: label.title,
                    value: filterValue,
                    isArray: isArray,
                    isBoolean: typeof filterValue === 'boolean',
                    arrayLabels: isArray ? filterValue
                        .map((fv, index) => ({ label: label.getLabel(fv), index }))
                        .filter(labelItem => labelItem.label) : null,
                    label: !isArray ? label.getLabel(filterValue) : null,
                    getLabel: label.getLabel
                };
                if (summaryLabel.label || summaryLabel.arrayLabels?.length > 0) {
                    updatedSummaryLabels.push(summaryLabel);
                }
            }
        }
        this.summaryItems = updatedSummaryLabels;
        this.trvUtilityService.detectChanges(this.cdr);
    }
    //Kolla om itemet är ett objekt/boolean/array och har ett värde/längd.
    objectOrArrayHasValue(object) {
        let hasValue = false;
        if (object != null) {
            if (Array.isArray(object)) {
                if (object.length > 0) {
                    hasValue = true;
                }
            }
            else if (typeof object === 'boolean') {
                return true;
            }
            else if (typeof object === 'number') {
                return true;
            }
            else if (Object(object)) {
                if (Object.keys(object).length > 0 &&
                    !Object.keys(object).map(key => object[key]).every(value => !value)) {
                    hasValue = true;
                }
            }
            else {
                hasValue = true;
            }
        }
        return hasValue;
    }
    getFilterValue(key) {
        if (this.filter) {
            return key.split('.').reduce((p, c) => p && p[c] || null, this.filter);
        }
        else {
            return null;
        }
    }
    setFilterValue(path, value) {
        const pathLength = path.split('.').length;
        path.split('.').reduce((prev, cur, index) => prev[cur] = pathLength === ++index ? value : prev[cur] || {}, this.filter);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFilterSummaryComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: TrvUtilityService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvFilterSummaryComponent, isStandalone: true, selector: "trv-filter-summary", inputs: { labels: "labels", message: "message", filter: "filter" }, outputs: { clear: "clear", change: "change" }, usesOnChanges: true, ngImport: i0, template: "@if (labels?.length && summaryItems?.length) {\r\n<div class=\"filter-summary overflow-hidden\"\r\n     @trvCollapseVoidAnimation>\r\n  <div class=\"left-container filter-summary-items\">\r\n    @for (summaryItem of summaryItems; track trackByForSummaryItems($index, summaryItem)) {\r\n    <div @trvPopAltAnimation>\r\n      <!-- Array -->\r\n      @if (summaryItem.isArray) {\r\n\r\n      <div [ngClass]=\"{ 'single-group-item': summaryItem.arrayLabels.length === 1 }\"\r\n           class=\"summary-group\">\r\n        @if (summaryItem.arrayLabels.length > 1) {\r\n        <div @trvPopAltAnimation\r\n             class=\"group-icon\">\r\n          <button (click)=\"removeArray(summaryItem.name)\"\r\n                  type=\"button\"\r\n                  class=\"btn btn-link remove-button\">\r\n            <fa-icon [icon]=\"faTimes\"\r\n                     class=\"mx-2\"></fa-icon>\r\n          </button>\r\n          @if (summaryItem.title) {\r\n          <span class=\"summary-item-title me-2\">\r\n            {{summaryItem.title}}\r\n          </span>\r\n          }\r\n        </div>\r\n        }\r\n        <div>\r\n          <ng-container *ngTemplateOutlet=\"arrayItems; context: { item: summaryItem, showTitle: summaryItem.arrayLabels.length === 1 }\">\r\n          </ng-container>\r\n        </div>\r\n      </div>\r\n\r\n      }\r\n      <!-- Object -->\r\n      @if (!summaryItem.isArray && !summaryItem.isBoolean) {\r\n      <div class=\"summary-item\"\r\n           @trvPopAltAnimation>\r\n        <ng-container *ngTemplateOutlet=\"singleItem; context: { item: summaryItem, value: summaryItem.label }\">\r\n        </ng-container>\r\n      </div>\r\n      }\r\n      <!-- Boolean -->\r\n      @if (summaryItem.isBoolean && summaryItem.value === true) {\r\n      <div class=\"summary-item\"\r\n           @trvPopAltAnimation>\r\n        <ng-container *ngTemplateOutlet=\"singleItem; context: { item: summaryItem, value: summaryItem.label }\">\r\n        </ng-container>\r\n      </div>\r\n      }\r\n    </div>\r\n    }\r\n  </div>\r\n\r\n  <div class=\"right-container filter-summary-handle\">\r\n    @if (message) {\r\n    <span class=\"me-1 filter-summary-info\">\r\n      {{message}}\r\n    </span>\r\n    }\r\n    <button type=\"button\"\r\n            class=\"btn btn-link\"\r\n            (click)=\"clearFilter()\">\r\n      <fa-icon [icon]=\"faTimes\"></fa-icon>\r\n    </button>\r\n  </div>\r\n</div>\r\n}\r\n\r\n<ng-template #arrayItems\r\n             let-item=\"item\"\r\n             let-showTitle=\"showTitle\">\r\n  @for (arrayItem of item.arrayLabels; track trackByForArrayLabels($index, arrayItem)) {\r\n  <span @trvPopAltAnimation\r\n        class=\"summary-item\">\r\n    @if (showTitle && item.title) {\r\n    <span class=\"summary-item-title ms-1\">\r\n      {{item.title}}:\r\n    </span>\r\n    }\r\n    <button (click)=\"removeArrayItem(item.name, arrayItem.index, $event)\"\r\n            type=\"button\"\r\n            class=\"btn btn-link remove-button\">\r\n      <span class=\"labelText\">{{arrayItem.label}}</span>\r\n      <fa-icon class=\"me-1 ms-2\"\r\n               [icon]=\"faTimes\"></fa-icon>\r\n    </button>\r\n  </span>\r\n  }\r\n</ng-template>\r\n\r\n<ng-template #singleItem\r\n             let-item=\"item\"\r\n             let-value=\"value\">\r\n  @if (item.title) {\r\n  <span class=\"summary-item-title ms-1\">\r\n    {{item.title}}:\r\n  </span>\r\n  }\r\n  <button (click)=\"removeItem(item.name)\"\r\n          type=\"button\"\r\n          class=\"btn btn-link remove-button\">\r\n    <span class=\"labelText\">{{value}}</span>\r\n    <fa-icon class=\"me-1 ms-2\"\r\n             [icon]=\"faTimes\"></fa-icon>\r\n  </button>\r\n</ng-template>", styles: [":root{--bs-border-color: #ced4da}.filter-summary{display:flex;flex-flow:row nowrap;border-top:1px solid #dee2e6;border-bottom:1px solid #dee2e6;background-color:#fff;padding-left:5px}.filter-summary .left-container{display:flex;flex:1 1;flex-wrap:wrap;align-items:center;justify-content:flex-end}.filter-summary .left-container>div{display:flex;min-height:2.55rem;align-items:center;margin-right:.25rem}.filter-summary .filter-summary-items{padding:.25rem 0}.filter-summary .right-container{display:flex;flex-flow:row nowrap}.filter-summary .filter-summary-info{display:inline-flex;align-items:center;font-size:.875rem;padding:0 .5rem}.filter-summary .filter-summary-handle{border-left:1px solid #dee2e6}.filter-summary .filter-summary-handle .btn{border-radius:0;border:none}.filter-summary .filter-summary-handle .btn fa-icon{font-size:18px;color:#8a8a8a}.filter-summary .filter-summary-handle .btn:hover,.filter-summary .filter-summary-handle .btn:focus{background:#dee2e6}.filter-summary .summary-group{display:flex;align-items:baseline;background-color:#a4cfe1;color:#1e5c88;border-radius:20px;padding:.2rem;transition:all .3s cubic-bezier(0,0,.2,1)}.filter-summary .summary-group.single-group-item{background-color:transparent;padding:0}.filter-summary .summary-group .group-icon{align-self:center;white-space:nowrap}.filter-summary .summary-group .group-icon .remove-button{transition:width .25s;padding:0}.filter-summary .summary-group .group-icon .remove-button fa-icon{opacity:.7}.filter-summary .summary-group .group-icon .remove-button:focus fa-icon,.filter-summary .summary-group .group-icon .remove-button:hover fa-icon{opacity:1}.filter-summary .summary-group .summary-item{margin:.05rem .25rem .1rem 0;width:fit-content}.filter-summary .summary-item{background-color:#1e5c88;color:#fff;height:26px;padding:0 8px 2px;border-radius:20px;cursor:pointer;display:inline-block}.filter-summary .summary-item .remove-button{padding:0;color:#fff;text-decoration:none;font-size:.875rem;white-space:nowrap}.filter-summary .summary-item .remove-button fa-icon{opacity:.7;transition:ease opacity .15s}.filter-summary .summary-item .remove-button:focus fa-icon,.filter-summary .summary-item .remove-button:hover fa-icon{opacity:1}.filter-summary .summary-item:hover{background-color:#15405e}.filter-summary .summary-item-title{font-size:.875rem;vertical-align:middle}@media (max-width: 575.98px){.filter-summary{flex-direction:column-reverse}.filter-summary .filter-summary-items{flex-wrap:nowrap;white-space:nowrap;padding-bottom:10px;overflow-x:auto;overflow-y:visible;justify-content:flex-start}.filter-summary .filter-summary-handle{border:none}.filter-summary .filter-summary-handle .btn{margin-left:auto}}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], animations: [trvCollapseVoidAnimation, trvPopAltAnimation], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFilterSummaryComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-filter-summary', changeDetection: ChangeDetectionStrategy.OnPush, animations: [trvCollapseVoidAnimation, trvPopAltAnimation], imports: [NgClass, FaIconComponent, NgTemplateOutlet], template: "@if (labels?.length && summaryItems?.length) {\r\n<div class=\"filter-summary overflow-hidden\"\r\n     @trvCollapseVoidAnimation>\r\n  <div class=\"left-container filter-summary-items\">\r\n    @for (summaryItem of summaryItems; track trackByForSummaryItems($index, summaryItem)) {\r\n    <div @trvPopAltAnimation>\r\n      <!-- Array -->\r\n      @if (summaryItem.isArray) {\r\n\r\n      <div [ngClass]=\"{ 'single-group-item': summaryItem.arrayLabels.length === 1 }\"\r\n           class=\"summary-group\">\r\n        @if (summaryItem.arrayLabels.length > 1) {\r\n        <div @trvPopAltAnimation\r\n             class=\"group-icon\">\r\n          <button (click)=\"removeArray(summaryItem.name)\"\r\n                  type=\"button\"\r\n                  class=\"btn btn-link remove-button\">\r\n            <fa-icon [icon]=\"faTimes\"\r\n                     class=\"mx-2\"></fa-icon>\r\n          </button>\r\n          @if (summaryItem.title) {\r\n          <span class=\"summary-item-title me-2\">\r\n            {{summaryItem.title}}\r\n          </span>\r\n          }\r\n        </div>\r\n        }\r\n        <div>\r\n          <ng-container *ngTemplateOutlet=\"arrayItems; context: { item: summaryItem, showTitle: summaryItem.arrayLabels.length === 1 }\">\r\n          </ng-container>\r\n        </div>\r\n      </div>\r\n\r\n      }\r\n      <!-- Object -->\r\n      @if (!summaryItem.isArray && !summaryItem.isBoolean) {\r\n      <div class=\"summary-item\"\r\n           @trvPopAltAnimation>\r\n        <ng-container *ngTemplateOutlet=\"singleItem; context: { item: summaryItem, value: summaryItem.label }\">\r\n        </ng-container>\r\n      </div>\r\n      }\r\n      <!-- Boolean -->\r\n      @if (summaryItem.isBoolean && summaryItem.value === true) {\r\n      <div class=\"summary-item\"\r\n           @trvPopAltAnimation>\r\n        <ng-container *ngTemplateOutlet=\"singleItem; context: { item: summaryItem, value: summaryItem.label }\">\r\n        </ng-container>\r\n      </div>\r\n      }\r\n    </div>\r\n    }\r\n  </div>\r\n\r\n  <div class=\"right-container filter-summary-handle\">\r\n    @if (message) {\r\n    <span class=\"me-1 filter-summary-info\">\r\n      {{message}}\r\n    </span>\r\n    }\r\n    <button type=\"button\"\r\n            class=\"btn btn-link\"\r\n            (click)=\"clearFilter()\">\r\n      <fa-icon [icon]=\"faTimes\"></fa-icon>\r\n    </button>\r\n  </div>\r\n</div>\r\n}\r\n\r\n<ng-template #arrayItems\r\n             let-item=\"item\"\r\n             let-showTitle=\"showTitle\">\r\n  @for (arrayItem of item.arrayLabels; track trackByForArrayLabels($index, arrayItem)) {\r\n  <span @trvPopAltAnimation\r\n        class=\"summary-item\">\r\n    @if (showTitle && item.title) {\r\n    <span class=\"summary-item-title ms-1\">\r\n      {{item.title}}:\r\n    </span>\r\n    }\r\n    <button (click)=\"removeArrayItem(item.name, arrayItem.index, $event)\"\r\n            type=\"button\"\r\n            class=\"btn btn-link remove-button\">\r\n      <span class=\"labelText\">{{arrayItem.label}}</span>\r\n      <fa-icon class=\"me-1 ms-2\"\r\n               [icon]=\"faTimes\"></fa-icon>\r\n    </button>\r\n  </span>\r\n  }\r\n</ng-template>\r\n\r\n<ng-template #singleItem\r\n             let-item=\"item\"\r\n             let-value=\"value\">\r\n  @if (item.title) {\r\n  <span class=\"summary-item-title ms-1\">\r\n    {{item.title}}:\r\n  </span>\r\n  }\r\n  <button (click)=\"removeItem(item.name)\"\r\n          type=\"button\"\r\n          class=\"btn btn-link remove-button\">\r\n    <span class=\"labelText\">{{value}}</span>\r\n    <fa-icon class=\"me-1 ms-2\"\r\n             [icon]=\"faTimes\"></fa-icon>\r\n  </button>\r\n</ng-template>", styles: [":root{--bs-border-color: #ced4da}.filter-summary{display:flex;flex-flow:row nowrap;border-top:1px solid #dee2e6;border-bottom:1px solid #dee2e6;background-color:#fff;padding-left:5px}.filter-summary .left-container{display:flex;flex:1 1;flex-wrap:wrap;align-items:center;justify-content:flex-end}.filter-summary .left-container>div{display:flex;min-height:2.55rem;align-items:center;margin-right:.25rem}.filter-summary .filter-summary-items{padding:.25rem 0}.filter-summary .right-container{display:flex;flex-flow:row nowrap}.filter-summary .filter-summary-info{display:inline-flex;align-items:center;font-size:.875rem;padding:0 .5rem}.filter-summary .filter-summary-handle{border-left:1px solid #dee2e6}.filter-summary .filter-summary-handle .btn{border-radius:0;border:none}.filter-summary .filter-summary-handle .btn fa-icon{font-size:18px;color:#8a8a8a}.filter-summary .filter-summary-handle .btn:hover,.filter-summary .filter-summary-handle .btn:focus{background:#dee2e6}.filter-summary .summary-group{display:flex;align-items:baseline;background-color:#a4cfe1;color:#1e5c88;border-radius:20px;padding:.2rem;transition:all .3s cubic-bezier(0,0,.2,1)}.filter-summary .summary-group.single-group-item{background-color:transparent;padding:0}.filter-summary .summary-group .group-icon{align-self:center;white-space:nowrap}.filter-summary .summary-group .group-icon .remove-button{transition:width .25s;padding:0}.filter-summary .summary-group .group-icon .remove-button fa-icon{opacity:.7}.filter-summary .summary-group .group-icon .remove-button:focus fa-icon,.filter-summary .summary-group .group-icon .remove-button:hover fa-icon{opacity:1}.filter-summary .summary-group .summary-item{margin:.05rem .25rem .1rem 0;width:fit-content}.filter-summary .summary-item{background-color:#1e5c88;color:#fff;height:26px;padding:0 8px 2px;border-radius:20px;cursor:pointer;display:inline-block}.filter-summary .summary-item .remove-button{padding:0;color:#fff;text-decoration:none;font-size:.875rem;white-space:nowrap}.filter-summary .summary-item .remove-button fa-icon{opacity:.7;transition:ease opacity .15s}.filter-summary .summary-item .remove-button:focus fa-icon,.filter-summary .summary-item .remove-button:hover fa-icon{opacity:1}.filter-summary .summary-item:hover{background-color:#15405e}.filter-summary .summary-item-title{font-size:.875rem;vertical-align:middle}@media (max-width: 575.98px){.filter-summary{flex-direction:column-reverse}.filter-summary .filter-summary-items{flex-wrap:nowrap;white-space:nowrap;padding-bottom:10px;overflow-x:auto;overflow-y:visible;justify-content:flex-start}.filter-summary .filter-summary-handle{border:none}.filter-summary .filter-summary-handle .btn{margin-left:auto}}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: TrvUtilityService }], propDecorators: { labels: [{
                type: Input
            }], message: [{
                type: Input
            }], filter: [{
                type: Input
            }], clear: [{
                type: Output
            }], change: [{
                type: Output
            }] } });

class TrvFormFieldGroupComponent {
    label;
    hideHeader;
    headerTemplate;
    showAllValidationErrors;
    constructor() { }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFormFieldGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvFormFieldGroupComponent, isStandalone: true, selector: "trv-form-field-group", inputs: { label: "label", hideHeader: "hideHeader", headerTemplate: "headerTemplate", showAllValidationErrors: "showAllValidationErrors" }, ngImport: i0, template: "@if (!hideHeader && !headerTemplate) {\r\n<div class=\"form-field-group-title\">\r\n\t<h3>\r\n\t\t{{label}}\r\n\t</h3>\r\n</div>\r\n}\r\n@if (headerTemplate) {\r\n<div class=\"form-field-group-title\">\r\n\t<ng-container [ngTemplateOutlet]=\"headerTemplate\"></ng-container>\r\n</div>\r\n}\r\n<div [ngClass]=\"{'form-field-group-content': !hideHeader }\">\r\n\t<ng-content></ng-content>\r\n</div>", styles: [":host ::ng-deep .form-field-group-title{border-bottom:1px solid rgb(191,197,204);padding-bottom:.55rem;display:flex;align-items:center;justify-content:space-between;margin-top:.5rem;margin-bottom:.5rem}:host ::ng-deep .form-field-group-title h3{font-size:1rem;text-transform:uppercase;color:#464242;margin:0}.form-field-group-content{padding-top:.5rem;padding-bottom:.5rem}\n"], dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFormFieldGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-form-field-group', changeDetection: ChangeDetectionStrategy.OnPush, imports: [NgTemplateOutlet, NgClass], template: "@if (!hideHeader && !headerTemplate) {\r\n<div class=\"form-field-group-title\">\r\n\t<h3>\r\n\t\t{{label}}\r\n\t</h3>\r\n</div>\r\n}\r\n@if (headerTemplate) {\r\n<div class=\"form-field-group-title\">\r\n\t<ng-container [ngTemplateOutlet]=\"headerTemplate\"></ng-container>\r\n</div>\r\n}\r\n<div [ngClass]=\"{'form-field-group-content': !hideHeader }\">\r\n\t<ng-content></ng-content>\r\n</div>", styles: [":host ::ng-deep .form-field-group-title{border-bottom:1px solid rgb(191,197,204);padding-bottom:.55rem;display:flex;align-items:center;justify-content:space-between;margin-top:.5rem;margin-bottom:.5rem}:host ::ng-deep .form-field-group-title h3{font-size:1rem;text-transform:uppercase;color:#464242;margin:0}.form-field-group-content{padding-top:.5rem;padding-bottom:.5rem}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], hideHeader: [{
                type: Input
            }], headerTemplate: [{
                type: Input
            }], showAllValidationErrors: [{
                type: Input
            }] } });

class TrvFormUtilityService {
    formBuilder;
    formValidated = new EventEmitter();
    skipValidation = false;
    constructor(formBuilder) {
        this.formBuilder = formBuilder;
    }
    isValid(form, submitType, disableSubmittedControl = false) {
        if (this.skipValidation) {
            return true;
        }
        if (form instanceof UntypedFormGroup && !disableSubmittedControl) {
            if (submitType) {
                form.setControl('_submitType', this.formBuilder.control(submitType));
            }
            form.setControl('_submitted', this.formBuilder.control(true));
        }
        this.setControlsTouched(form.controls);
        this.formValidated.emit(form);
        return form.valid;
    }
    requiredIfSubmitTypes(types, control) {
        const formSubmitType = control.root.get('_submitType');
        if (formSubmitType && types.some((s) => s === formSubmitType.value)) {
            return Validators.required(control);
        }
        return null;
    }
    requiredTrueIfSubmitTypes(types, control) {
        const formSubmitType = control.root.get('_submitType');
        if (formSubmitType && types.some((s) => s === formSubmitType.value)) {
            return Validators.requiredTrue(control);
        }
        return null;
    }
    setSkipValidation(skip) {
        this.skipValidation = skip;
    }
    getSkipValidation() {
        return this.skipValidation;
    }
    specialCharsValidator = (regexString, invalidateNewLine) => {
        return (control) => {
            const text = control.value;
            if (!text) {
                return null;
            }
            if (invalidateNewLine) {
                regexString = regexString.replace('\n', '');
            }
            else {
                regexString = regexString.replace('[^', '[^\n');
            }
            const regex = new RegExp(regexString, 'g');
            const matches = [...text.matchAll(regex)];
            if (matches?.length) {
                return {
                    specialChars: matches.map((match) => match?.toString().replace('\n', '[radbrytning]')).join(', '),
                };
            }
            return null;
        };
    };
    setControlsTouched(controls) {
        const keys = Object.keys(controls);
        keys.forEach((key) => {
            const control = controls[key];
            if (control instanceof UntypedFormGroup ||
                control instanceof UntypedFormArray ||
                control instanceof FormGroup ||
                control instanceof FormArray) {
                control.markAsDirty();
                control.markAsTouched();
                this.setControlsTouched(control.controls);
            }
            else if (!control.asyncValidator || (control.asyncValidator && control.pristine)) {
                control.markAsTouched({ onlySelf: false });
                control.markAsDirty({ onlySelf: false });
                // emitEvent = true when control has async validator to trigger validator to be run when pristine
                const doEmit = !!control.asyncValidator;
                control.updateValueAndValidity({ onlySelf: false, emitEvent: doEmit });
            }
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFormUtilityService, deps: [{ token: i1$2.FormBuilder }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFormUtilityService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFormUtilityService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1$2.FormBuilder }], propDecorators: { formValidated: [{
                type: Output
            }] } });

class TrvStringReplacePipe {
    transform(str, obj) {
        if (!str || !obj) {
            return undefined;
        }
        const keys = str.match(/{(.*?)}/g);
        if (!keys || !keys.length) {
            return str;
        }
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i].slice(1, -1);
            const rx = new RegExp(keys[i], 'gm');
            if (obj[key] != null) {
                str = str.replace(rx, obj[keys[i].slice(1, -1)]);
            }
            else {
                return undefined;
            }
        }
        return str;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvStringReplacePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.1", ngImport: i0, type: TrvStringReplacePipe, isStandalone: true, name: "trvStringReplace" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvStringReplacePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'trvStringReplace',
                    standalone: true
                }]
        }] });

class TrvFormComponent {
    readMode;
    validationErrors;
    showAllValidationErrors = false;
    fullWidth;
    constructor() { }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFormComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvFormComponent, isStandalone: true, selector: "trv-form", inputs: { readMode: "readMode", validationErrors: "validationErrors", showAllValidationErrors: "showAllValidationErrors", fullWidth: "fullWidth" }, ngImport: i0, template: "<div [ngClass]=\"{ 'container': !fullWidth, 'container-fluid': fullWidth }\">\r\n  <ng-content></ng-content>\r\n</div>", styles: [":host>.container,:host>.container-fluid{padding:0;margin-left:0}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFormComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-form', changeDetection: ChangeDetectionStrategy.OnPush, imports: [NgClass], template: "<div [ngClass]=\"{ 'container': !fullWidth, 'container-fluid': fullWidth }\">\r\n  <ng-content></ng-content>\r\n</div>", styles: [":host>.container,:host>.container-fluid{padding:0;margin-left:0}\n"] }]
        }], ctorParameters: () => [], propDecorators: { readMode: [{
                type: Input
            }], validationErrors: [{
                type: Input
            }], showAllValidationErrors: [{
                type: Input
            }], fullWidth: [{
                type: Input
            }] } });

class TrvFormFieldValidationMessagesComponent extends trvUnsubscribeMixin() {
    trvForm;
    trvFormUtilityService;
    trvUtilityService;
    changeDetectionRef;
    trvStringReplacePipe;
    control;
    formFieldName;
    validationErrors;
    serverErrorMessage;
    blockAlert;
    showAllValidationErrors;
    messages = [];
    constructor(trvForm, trvFormUtilityService, trvUtilityService, changeDetectionRef, trvStringReplacePipe) {
        super();
        this.trvForm = trvForm;
        this.trvFormUtilityService = trvFormUtilityService;
        this.trvUtilityService = trvUtilityService;
        this.changeDetectionRef = changeDetectionRef;
        this.trvStringReplacePipe = trvStringReplacePipe;
    }
    ngOnInit() {
        this.setValidationErrorMap();
        this.createMessages();
        merge(this.control.valueChanges, this.trvFormUtilityService.formValidated, this.control.statusChanges)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => this.createMessages());
    }
    createMessages() {
        if (!this.control.errors || !this.validationErrors || !this.control.dirty) {
            this.messages = [];
            this.trvUtilityService.detectChanges(this.changeDetectionRef);
            return;
        }
        this.messages = [];
        const controlErrors = Object.keys(this.control.errors);
        for (const error of controlErrors) {
            if (typeof this.validationErrors === 'string') {
                const message = !this.validationErrors ? `Översättning saknas för: ${error}` :
                    this.trvStringReplacePipe.transform(this.validationErrors, this.control.errors);
                this.messages.push(message);
            }
            else {
                const obj = this.validationErrors[error];
                if (!obj || typeof obj === 'string') {
                    const message = !obj ? `Översättning saknas för: ${error}` :
                        this.trvStringReplacePipe.transform(obj, this.control.errors);
                    this.messages.push(message);
                }
                else {
                    throw new Error(`Expected string, found ${typeof obj}`);
                }
            }
        }
        this.trvUtilityService.detectChanges(this.changeDetectionRef);
    }
    setValidationErrorMap() {
        //Om validationError satts på control-nivå, låt den gälla.
        if (!this.validationErrors) {
            try {
                const keysArray = this.formFieldName.split('.');
                let validationObject = this.trvForm.validationErrors;
                keysArray.forEach(key => validationObject = validationObject[key]);
                this.validationErrors = validationObject;
            }
            catch {
                //Hittades ingen error map anta att man inte vill ha validaringsfel.
                // console.warn('Could not find validation error map. Incorrect object structure?');
            }
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFormFieldValidationMessagesComponent, deps: [{ token: TrvFormComponent, optional: true }, { token: TrvFormUtilityService }, { token: TrvUtilityService }, { token: i0.ChangeDetectorRef }, { token: TrvStringReplacePipe }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvFormFieldValidationMessagesComponent, isStandalone: true, selector: "trv-form-field-validation-messages", inputs: { control: "control", formFieldName: "formFieldName", validationErrors: "validationErrors", serverErrorMessage: "serverErrorMessage", blockAlert: "blockAlert", showAllValidationErrors: "showAllValidationErrors" }, providers: [TrvStringReplacePipe], usesInheritance: true, ngImport: i0, template: "@if (messages?.length) {\r\n<div class=\"error-message-container\"\r\n     [ngClass]=\"{ 'display-contents': blockAlert }\">\r\n    <div @transitionMessages\r\n         class=\"invalid-feedback d-block\"\r\n         [ngClass]=\"{ 'block-alert': blockAlert }\">\r\n        @for (message of messages; track message; let i = $index) {\r\n\r\n        @if (i === 0 || showAllValidationErrors) {\r\n        <p class=\"error-message\"\r\n           [innerHtml]=\"message | trvSafeHtml\"></p>\r\n        }\r\n\r\n        }\r\n    </div>\r\n    @if (serverErrorMessage) {\r\n    <div @transitionMessages\r\n         class=\"invalid-feedback d-block\"\r\n         [ngClass]=\"{ 'block-alert': blockAlert }\">\r\n        <p class=\"error-message\"\r\n           [innerHtml]=\"serverErrorMessage | trvSafeHtml\"></p>\r\n    </div>\r\n    }\r\n</div>\r\n}", styles: [".error-message-container{position:relative;min-height:1.45rem;border:1px solid transparent;overflow:hidden}.error-message-container.display-contents{display:contents}.error-message-container .invalid-feedback{margin-top:.1rem}.error-message-container .invalid-feedback.block-alert{padding:.75rem;background:#f6d5d5;color:#6d1817;border:1px solid #f2c4c4;border-radius:.25rem;margin:.5rem 0}.error-message-container p{margin-top:0;margin-bottom:0;overflow:hidden;white-space:nowrap;display:block;text-overflow:ellipsis}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "pipe", type: TrvSafeHtmlPipe, name: "trvSafeHtml" }], animations: [
            trigger('transitionMessages', [
                state('*', style({ transform: 'translateY(0%)' })),
                transition(':enter', [
                    style({ transform: 'translateY(-100%)' }),
                    animate('150ms cubic-bezier(0.55, 0, 0.55, 0.2)'),
                ]),
            ])
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFormFieldValidationMessagesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-form-field-validation-messages', changeDetection: ChangeDetectionStrategy.OnPush, animations: [
                        trigger('transitionMessages', [
                            state('*', style({ transform: 'translateY(0%)' })),
                            transition(':enter', [
                                style({ transform: 'translateY(-100%)' }),
                                animate('150ms cubic-bezier(0.55, 0, 0.55, 0.2)'),
                            ]),
                        ])
                    ], imports: [NgClass, TrvSafeHtmlPipe], providers: [TrvStringReplacePipe], template: "@if (messages?.length) {\r\n<div class=\"error-message-container\"\r\n     [ngClass]=\"{ 'display-contents': blockAlert }\">\r\n    <div @transitionMessages\r\n         class=\"invalid-feedback d-block\"\r\n         [ngClass]=\"{ 'block-alert': blockAlert }\">\r\n        @for (message of messages; track message; let i = $index) {\r\n\r\n        @if (i === 0 || showAllValidationErrors) {\r\n        <p class=\"error-message\"\r\n           [innerHtml]=\"message | trvSafeHtml\"></p>\r\n        }\r\n\r\n        }\r\n    </div>\r\n    @if (serverErrorMessage) {\r\n    <div @transitionMessages\r\n         class=\"invalid-feedback d-block\"\r\n         [ngClass]=\"{ 'block-alert': blockAlert }\">\r\n        <p class=\"error-message\"\r\n           [innerHtml]=\"serverErrorMessage | trvSafeHtml\"></p>\r\n    </div>\r\n    }\r\n</div>\r\n}", styles: [".error-message-container{position:relative;min-height:1.45rem;border:1px solid transparent;overflow:hidden}.error-message-container.display-contents{display:contents}.error-message-container .invalid-feedback{margin-top:.1rem}.error-message-container .invalid-feedback.block-alert{padding:.75rem;background:#f6d5d5;color:#6d1817;border:1px solid #f2c4c4;border-radius:.25rem;margin:.5rem 0}.error-message-container p{margin-top:0;margin-bottom:0;overflow:hidden;white-space:nowrap;display:block;text-overflow:ellipsis}\n"] }]
        }], ctorParameters: () => [{ type: TrvFormComponent, decorators: [{
                    type: Optional
                }] }, { type: TrvFormUtilityService }, { type: TrvUtilityService }, { type: i0.ChangeDetectorRef }, { type: TrvStringReplacePipe }], propDecorators: { control: [{
                type: Input
            }], formFieldName: [{
                type: Input
            }], validationErrors: [{
                type: Input
            }], serverErrorMessage: [{
                type: Input
            }], blockAlert: [{
                type: Input
            }], showAllValidationErrors: [{
                type: Input
            }] } });

class TrvToggleSwitchComponent {
    constructor() {
        this.control = new FormControl(null);
        this.control.valueChanges.subscribe(value => this.propagateChange(value));
    }
    control;
    propagateChange = () => { };
    propagateTouched = () => { };
    writeValue(value) {
        if (!value || typeof (value) !== 'boolean') {
            this.control.setValue(false, { emitEvent: false });
            return;
        }
        this.control.setValue(value, { emitEvent: false });
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) {
        this.propagateTouched = fn;
    }
    setDisabledState(isDisabled) {
        isDisabled ? this.control.disable({ emitEvent: false }) : this.control.enable({ emitEvent: false });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvToggleSwitchComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvToggleSwitchComponent, isStandalone: true, selector: "trv-toggle-switch", providers: [{
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TrvToggleSwitchComponent),
                multi: true
            }], ngImport: i0, template: "<div class=\"switch-container\">\r\n  <label class=\"switch-label\">\r\n    <input [formControl]=\"control\"\r\n           type=\"checkbox\"\r\n           class=\"primary\">\r\n    <span class=\"slider\"></span>\r\n  </label>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.switch-container{padding-top:calc(.375rem + 1px);padding-bottom:calc(.375rem + 1px);display:flex}.switch-label{position:relative;width:36px;height:1.5rem;margin:0;text-align:left}.switch-label input{opacity:1;width:100%;height:1rem;position:absolute;top:.25rem}.slider{position:absolute;cursor:pointer;border-radius:25px;top:.25rem;left:0;right:0;bottom:0;background-color:#bdbdbd;-webkit-transition:.15s;transition:.15s;height:1rem}.slider:before{position:absolute;content:\"\";height:19px;width:19px;left:-2px;bottom:-2px;background-color:#f5f5f5;box-shadow:#0000001f 0 1px 6px,#00000078 0 1px 4px;border-radius:50%;-webkit-transition:.2s;transition:.2s}input.default:checked+.slider{background-color:#444}input.primary:checked+.slider{background-color:#8cb9f3;opacity:1}input:checked+.slider:before{transform:translate(18px);background-color:#1e5c88}input:disabled+.slider,input.primary:disabled:checked+.slider{background-color:#eee;cursor:default}input:disabled+.slider:before,input.primary:disabled:checked+.slider:before{background-color:#eee;box-shadow:none;border:1px solid #ddd}\n"], dependencies: [{ kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1$2.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvToggleSwitchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-toggle-switch', changeDetection: ChangeDetectionStrategy.OnPush, providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TrvToggleSwitchComponent),
                            multi: true
                        }], imports: [ReactiveFormsModule], template: "<div class=\"switch-container\">\r\n  <label class=\"switch-label\">\r\n    <input [formControl]=\"control\"\r\n           type=\"checkbox\"\r\n           class=\"primary\">\r\n    <span class=\"slider\"></span>\r\n  </label>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.switch-container{padding-top:calc(.375rem + 1px);padding-bottom:calc(.375rem + 1px);display:flex}.switch-label{position:relative;width:36px;height:1.5rem;margin:0;text-align:left}.switch-label input{opacity:1;width:100%;height:1rem;position:absolute;top:.25rem}.slider{position:absolute;cursor:pointer;border-radius:25px;top:.25rem;left:0;right:0;bottom:0;background-color:#bdbdbd;-webkit-transition:.15s;transition:.15s;height:1rem}.slider:before{position:absolute;content:\"\";height:19px;width:19px;left:-2px;bottom:-2px;background-color:#f5f5f5;box-shadow:#0000001f 0 1px 6px,#00000078 0 1px 4px;border-radius:50%;-webkit-transition:.2s;transition:.2s}input.default:checked+.slider{background-color:#444}input.primary:checked+.slider{background-color:#8cb9f3;opacity:1}input:checked+.slider:before{transform:translate(18px);background-color:#1e5c88}input:disabled+.slider,input.primary:disabled:checked+.slider{background-color:#eee;cursor:default}input:disabled+.slider:before,input.primary:disabled:checked+.slider:before{background-color:#eee;box-shadow:none;border:1px solid #ddd}\n"] }]
        }], ctorParameters: () => [] });

class FormFieldSwitchGroupComponent {
    elementRef;
    control;
    label;
    validationErrors;
    labelGridColumns;
    inputGridColumns;
    disabled = false;
    controlName;
    colClass;
    labelClass;
    constructor(elementRef) {
        this.elementRef = elementRef;
    }
    ngOnInit() {
        if (!this.control) {
            console.error('formControl missing');
            return;
        }
        this.controlName = this.getControlName(this.control);
        if (!this.controlName) {
            console.error('cannot get name of control');
        }
        this.control.registerOnDisabledChange(isDisabled => this.disabled = isDisabled);
        this.setColumnClasses();
    }
    ngAfterViewInit() {
        this.setIdAttribute();
    }
    getLabelColClass() {
        return 'col-lg-' + this.labelGridColumns;
    }
    getInputColClass() {
        return 'col-lg-' + this.inputGridColumns;
    }
    getControlName(control, name) {
        const group = control.parent;
        if (!group) {
            return name;
        }
        name = name || '';
        Object.keys(group.controls).forEach(key => {
            const childControl = group.get(key);
            if (childControl !== control) {
                return;
            }
            name = !!name ? (key + '.' + name) : key;
        });
        return this.getControlName(group, name);
    }
    setIdAttribute() {
        const componentElement = this.elementRef.nativeElement;
        const contentElement = componentElement && componentElement.querySelector('.form-field-switch-content');
        const formControlElement = contentElement && contentElement.firstElementChild;
        if (!formControlElement || formControlElement.nodeName === 'trv-form-field-validation-messages') {
            return;
        }
        formControlElement.setAttribute('id', this.controlName);
    }
    setColumnClasses() {
        this.colClass = '';
        this.labelClass = '';
        if (!this.labelGridColumns) {
            if (this.inputGridColumns) {
                this.labelGridColumns = 12 - this.inputGridColumns;
            }
            else {
                this.labelGridColumns = 4;
            }
        }
        if (!this.inputGridColumns) {
            if (this.labelGridColumns) {
                this.inputGridColumns = 12 - this.labelGridColumns;
            }
            else {
                this.inputGridColumns = 8;
            }
        }
        this.colClass = this.getInputColClass();
        this.labelClass = this.getLabelColClass();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: FormFieldSwitchGroupComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: FormFieldSwitchGroupComponent, isStandalone: true, selector: "trv-form-field-switch-group", inputs: { control: "control", label: "label", validationErrors: "validationErrors", labelGridColumns: "labelGridColumns", inputGridColumns: "inputGridColumns" }, host: { properties: { "class.disabled": "this.disabled" } }, ngImport: i0, template: "@if (controlName) {\r\n<div class=\"form-group form-field-switch-wrapper\"\r\n\t\t [ngClass]=\"{ 'content-visible': control.value }\">\r\n\t<label for=\"{{controlName}}\"\r\n\t\t\t\t class=\"col-form-label form-field-switch-label\">\r\n\t\t{{label}}\r\n\t</label>\r\n\t<div class=\"form-field-switch\">\r\n\t\t<trv-toggle-switch [formControl]=\"control\"></trv-toggle-switch>\r\n\t\t<trv-form-field-validation-messages [control]=\"control\"\r\n\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t[validationErrors]=\"validationErrors\">\r\n\t\t</trv-form-field-validation-messages>\r\n\t</div>\r\n</div>\r\n}\r\n@if (control.value) {\r\n<div class=\"form-field-switch-content\">\r\n\t<ng-content></ng-content>\r\n</div>\r\n}", styles: [":host{display:block;background:#fff;box-shadow:0 .2rem .5rem #00000026;margin-bottom:1rem}:host .form-field-switch-wrapper{padding:.25rem 1rem;margin-bottom:0;display:flex;justify-content:space-between}:host .form-field-switch-wrapper .form-field-switch-label{font-weight:600}:host .form-field-switch-wrapper .form-field-switch{margin-top:.05rem}:host .form-field-switch-wrapper.content-visible{border-bottom:1px solid #dee2e6}:host .form-field-switch-content{padding:1rem}:host.disabled{opacity:.5}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: TrvToggleSwitchComponent, selector: "trv-toggle-switch" }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: TrvFormFieldValidationMessagesComponent, selector: "trv-form-field-validation-messages", inputs: ["control", "formFieldName", "validationErrors", "serverErrorMessage", "blockAlert", "showAllValidationErrors"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: FormFieldSwitchGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-form-field-switch-group', changeDetection: ChangeDetectionStrategy.OnPush, imports: [NgClass, TrvToggleSwitchComponent, ReactiveFormsModule, TrvFormFieldValidationMessagesComponent], template: "@if (controlName) {\r\n<div class=\"form-group form-field-switch-wrapper\"\r\n\t\t [ngClass]=\"{ 'content-visible': control.value }\">\r\n\t<label for=\"{{controlName}}\"\r\n\t\t\t\t class=\"col-form-label form-field-switch-label\">\r\n\t\t{{label}}\r\n\t</label>\r\n\t<div class=\"form-field-switch\">\r\n\t\t<trv-toggle-switch [formControl]=\"control\"></trv-toggle-switch>\r\n\t\t<trv-form-field-validation-messages [control]=\"control\"\r\n\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t[validationErrors]=\"validationErrors\">\r\n\t\t</trv-form-field-validation-messages>\r\n\t</div>\r\n</div>\r\n}\r\n@if (control.value) {\r\n<div class=\"form-field-switch-content\">\r\n\t<ng-content></ng-content>\r\n</div>\r\n}", styles: [":host{display:block;background:#fff;box-shadow:0 .2rem .5rem #00000026;margin-bottom:1rem}:host .form-field-switch-wrapper{padding:.25rem 1rem;margin-bottom:0;display:flex;justify-content:space-between}:host .form-field-switch-wrapper .form-field-switch-label{font-weight:600}:host .form-field-switch-wrapper .form-field-switch{margin-top:.05rem}:host .form-field-switch-wrapper.content-visible{border-bottom:1px solid #dee2e6}:host .form-field-switch-content{padding:1rem}:host.disabled{opacity:.5}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { control: [{
                type: Input
            }], label: [{
                type: Input
            }], validationErrors: [{
                type: Input
            }], labelGridColumns: [{
                type: Input
            }], inputGridColumns: [{
                type: Input
            }], disabled: [{
                type: HostBinding,
                args: ['class.disabled']
            }] } });

class TrvFormFieldComponent {
    elementRef;
    trvForm;
    trvFormFieldGroup;
    control;
    label;
    labelAbove;
    fieldId;
    validationErrors;
    showAllValidationErrors;
    labelGridColumns;
    inputGridColumns;
    readMode;
    noLabel = false;
    inline = false;
    controlPadding = false;
    _serverErrorMessage;
    set serverErrorMessage(message) {
        this._serverErrorMessage = message;
        if (message && this.control) {
            this.control.setErrors({
                ...this.control.errors,
                serverError: this.serverErrorMessage
            });
        }
        else if (!message && this.control?.errors?.serverError) {
            const errors = this.control.errors;
            delete errors.serverError;
            this.control.setErrors(Object.keys(errors).length > 0 ? errors : null);
        }
    }
    get serverErrorMessage() { return this._serverErrorMessage; }
    formFieldName;
    colClass;
    labelClass;
    constructor(elementRef, trvForm, trvFormFieldGroup) {
        this.elementRef = elementRef;
        this.trvForm = trvForm;
        this.trvFormFieldGroup = trvFormFieldGroup;
    }
    ngOnInit() {
        if (this.readMode == null && this.trvForm?.readMode === true) {
            this.readMode = true;
        }
        if (!this.control && !this.readMode) {
            console.error('FormControl missing');
            return;
        }
        this.formFieldName = this.getControlName(this.control) ||
            (this.fieldId ?? 'form-field-' + this.label.replace(/[^a-zA-Z0-9]/g, '').toLowerCase());
        if (!this.formFieldName) {
            console.error('Cannot get name of form field.');
        }
        this.setColumnClasses();
        this.setShowAllValidationErrorsValue();
    }
    ngAfterViewInit() {
        this.setIdAttribute();
    }
    get formFieldId() {
        return 'form-field-' + this.formFieldName;
    }
    getLabelColClass() {
        return this.inline ? 'me-3' : 'col-lg-' + this.labelGridColumns;
    }
    getInputColClass() {
        return this.inline ? 'me-3' : 'col-lg-' + this.inputGridColumns;
    }
    setColumnClasses() {
        if (this.labelAbove) {
            this.colClass = '';
            this.labelClass = '';
        }
        else {
            if (!this.labelGridColumns) {
                if (this.inputGridColumns) {
                    this.labelGridColumns = 12 - this.inputGridColumns;
                }
                else {
                    this.labelGridColumns = 4;
                }
            }
            if (!this.inputGridColumns) {
                if (this.labelGridColumns) {
                    this.inputGridColumns = 12 - this.labelGridColumns;
                }
                else {
                    this.inputGridColumns = 8;
                }
            }
            this.colClass = this.getInputColClass();
            this.labelClass = this.getLabelColClass();
        }
    }
    getControlName(control, name) {
        const group = control?.parent;
        if (!group) {
            return name;
        }
        name = name || '';
        Object.keys(group.controls).forEach(key => {
            const childControl = group.get(key);
            if (childControl !== control) {
                return;
            }
            name = !!name ? (key + '.' + name) : key;
        });
        return this.getControlName(group, name);
    }
    setIdAttribute() {
        const componentElement = this.elementRef.nativeElement;
        componentElement.setAttribute('id', this.formFieldId);
        const contentElement = componentElement && componentElement.querySelector('.form-field-content');
        const formControlElement = contentElement && contentElement.firstElementChild;
        if (!formControlElement || formControlElement.classList.contains('form-check')) {
            return;
        }
        formControlElement.setAttribute('id', this.formFieldName);
    }
    //Om showAllValidationErrors inte är satt, hämta värdet från föräldrar
    setShowAllValidationErrorsValue() {
        if (!this.showAllValidationErrors) {
            const formFieldValue = this.showAllValidationErrors;
            const formFieldGroupValue = this.trvFormFieldGroup?.showAllValidationErrors;
            const formValue = this.trvForm?.showAllValidationErrors;
            this.showAllValidationErrors = formFieldValue ?? formFieldGroupValue ?? formValue ?? false;
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFormFieldComponent, deps: [{ token: i0.ElementRef }, { token: TrvFormComponent, optional: true }, { token: TrvFormFieldGroupComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvFormFieldComponent, isStandalone: true, selector: "trv-form-field", inputs: { control: "control", label: "label", labelAbove: "labelAbove", fieldId: "fieldId", validationErrors: "validationErrors", showAllValidationErrors: "showAllValidationErrors", labelGridColumns: "labelGridColumns", inputGridColumns: "inputGridColumns", readMode: "readMode", noLabel: "noLabel", inline: "inline", controlPadding: "controlPadding", serverErrorMessage: "serverErrorMessage" }, ngImport: i0, template: "@if (formFieldName) {\r\n<div class=\"form-group row form-field-wrapper\"\r\n     [ngClass]=\"{ \r\n       'read-mode': readMode, \r\n       'row': !labelAbove, \r\n       'me-3': inline, \r\n       'no-label': noLabel \r\n      }\">\r\n  @if (!noLabel) {\r\n  <label for=\"{{formFieldName}}\"\r\n         [ngClass]=\"labelClass\"\r\n         class=\"col-form-label form-field-label\">\r\n    <ng-content select=\"[trv-form-field-label-prepend]\"></ng-content>\r\n    {{label}}\r\n    <ng-content select=\"[trv-form-field-label-append]\"></ng-content>\r\n  </label>\r\n  }\r\n  <div [ngClass]=\"{'max-width': labelAbove, 'control-padding': controlPadding}\"\r\n       class=\"form-field-content {{colClass}}\">\r\n    <ng-content></ng-content>\r\n    @if (!readMode) {\r\n    <div class=\"validation-messages-wrapper\">\r\n      <trv-form-field-validation-messages [control]=\"$any(control)\"\r\n                                          [formFieldName]=\"formFieldName\"\r\n                                          [validationErrors]=\"validationErrors\"\r\n                                          [serverErrorMessage]=\"serverErrorMessage\"\r\n                                          [showAllValidationErrors]=\"showAllValidationErrors\">\r\n      </trv-form-field-validation-messages>\r\n    </div>\r\n    }\r\n  </div>\r\n</div>\r\n}", styles: [":root{--bs-border-color: #ced4da}.form-group{margin-bottom:.25rem!important}.form-group .validation-messages-wrapper{min-height:25px}.form-group.read-mode{margin-bottom:1rem}.form-group.read-mode .form-field-content{word-break:break-word}.form-group .col-form-label{font-weight:600}.form-group .no-label{margin-bottom:0}.form-group .max-width{max-width:none}.form-group .control-padding{padding-top:calc(.375rem + var(--bs-border-width))}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: TrvFormFieldValidationMessagesComponent, selector: "trv-form-field-validation-messages", inputs: ["control", "formFieldName", "validationErrors", "serverErrorMessage", "blockAlert", "showAllValidationErrors"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFormFieldComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-form-field', changeDetection: ChangeDetectionStrategy.OnPush, imports: [NgClass, TrvFormFieldValidationMessagesComponent], template: "@if (formFieldName) {\r\n<div class=\"form-group row form-field-wrapper\"\r\n     [ngClass]=\"{ \r\n       'read-mode': readMode, \r\n       'row': !labelAbove, \r\n       'me-3': inline, \r\n       'no-label': noLabel \r\n      }\">\r\n  @if (!noLabel) {\r\n  <label for=\"{{formFieldName}}\"\r\n         [ngClass]=\"labelClass\"\r\n         class=\"col-form-label form-field-label\">\r\n    <ng-content select=\"[trv-form-field-label-prepend]\"></ng-content>\r\n    {{label}}\r\n    <ng-content select=\"[trv-form-field-label-append]\"></ng-content>\r\n  </label>\r\n  }\r\n  <div [ngClass]=\"{'max-width': labelAbove, 'control-padding': controlPadding}\"\r\n       class=\"form-field-content {{colClass}}\">\r\n    <ng-content></ng-content>\r\n    @if (!readMode) {\r\n    <div class=\"validation-messages-wrapper\">\r\n      <trv-form-field-validation-messages [control]=\"$any(control)\"\r\n                                          [formFieldName]=\"formFieldName\"\r\n                                          [validationErrors]=\"validationErrors\"\r\n                                          [serverErrorMessage]=\"serverErrorMessage\"\r\n                                          [showAllValidationErrors]=\"showAllValidationErrors\">\r\n      </trv-form-field-validation-messages>\r\n    </div>\r\n    }\r\n  </div>\r\n</div>\r\n}", styles: [":root{--bs-border-color: #ced4da}.form-group{margin-bottom:.25rem!important}.form-group .validation-messages-wrapper{min-height:25px}.form-group.read-mode{margin-bottom:1rem}.form-group.read-mode .form-field-content{word-break:break-word}.form-group .col-form-label{font-weight:600}.form-group .no-label{margin-bottom:0}.form-group .max-width{max-width:none}.form-group .control-padding{padding-top:calc(.375rem + var(--bs-border-width))}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: TrvFormComponent, decorators: [{
                    type: Optional
                }] }, { type: TrvFormFieldGroupComponent, decorators: [{
                    type: Optional
                }] }], propDecorators: { control: [{
                type: Input
            }], label: [{
                type: Input
            }], labelAbove: [{
                type: Input
            }], fieldId: [{
                type: Input
            }], validationErrors: [{
                type: Input
            }], showAllValidationErrors: [{
                type: Input
            }], labelGridColumns: [{
                type: Input
            }], inputGridColumns: [{
                type: Input
            }], readMode: [{
                type: Input
            }], noLabel: [{
                type: Input
            }], inline: [{
                type: Input
            }], controlPadding: [{
                type: Input
            }], serverErrorMessage: [{
                type: Input
            }] } });

const TrvFormBundle = [
    TrvFormComponent,
    TrvFormFieldGroupComponent,
    TrvFormFieldComponent,
    FormFieldSwitchGroupComponent,
    TrvFormFieldValidationMessagesComponent
];

/*
    This is a service to add and display global messages for the user.
    The message will be displayed for X seconds or untill the dialog is closed manually.
*/
class TrvGlobalMessagesService {
    dateService;
    messages = {
        danger: [],
        success: [],
        warning: [],
        info: [],
        component: []
    };
    timeouts = [];
    defaultMessageTime = 8000;
    dangerMessageTime = 12000;
    messagesFlat;
    messageAdded$ = new Subject();
    constructor(dateService) {
        this.dateService = dateService;
    }
    configureMessageTime(defaultMessageTime, dangerMessageTime) {
        this.defaultMessageTime = defaultMessageTime;
        this.dangerMessageTime = dangerMessageTime;
    }
    getMessages() {
        return this.messages;
    }
    success(message) {
        this.add('success', message);
    }
    error(message, url) {
        for (let i = this.messages.danger.length - 1; i >= 0; i--) {
            //remove previous validationerrors from the same url
            //checking a timediff of 100ms in order to add multiple errors from same response
            if (this.messages.danger[i].url && this.messages.danger[i].url === url &&
                this.messages.danger[i].time < this.dateService.now().minus({ milliseconds: 100 })) {
                this.messages.danger.splice(i, 1);
                this.flattenMessages();
            }
        }
        this.add('danger', message, url);
    }
    warning(message) {
        this.add('warning', message);
    }
    info(message) {
        this.add('info', message);
    }
    component(component, inputs, time) {
        const messageObject = {
            type: 'component',
            time: this.dateService.now(),
            component: component,
            inputs: { ...inputs, isCloseButtonShow: inputs.isCloseButtonShow },
            countdown: time || this.defaultMessageTime
        };
        this.messages['component'].splice(0, 0, messageObject);
        this.flattenMessages();
        this.addTimeout('component', messageObject, time || this.defaultMessageTime);
    }
    clearType(type) {
        this.messages[type] = [];
    }
    closeMessage(message) {
        const index = this.messages[message.type].indexOf(message);
        this.messages[message.type].splice(index, 1);
        this.flattenMessages();
    }
    clearEndPointErrors(url) {
        for (let i = this.messages.danger.length - 1; i >= 0; i--) {
            if (this.messages.danger[i].url && this.messages.danger[i].url === url) {
                this.messages.danger.splice(i, 1);
                this.flattenMessages();
            }
        }
    }
    add(type, message, url) {
        const messageTime = type === 'danger' ? this.dangerMessageTime : this.defaultMessageTime;
        const messageObject = {
            type: type,
            time: this.dateService.now(),
            mess: typeof message === 'string' ? message : null,
            messageList: Array.isArray(message) ? message : null,
            countdown: messageTime
        };
        if (url) {
            messageObject.url = url;
        }
        this.messages[type].splice(0, 0, messageObject);
        this.flattenMessages();
        this.addTimeout(type, messageObject, messageTime);
    }
    addTimeout(type, object, time) {
        const timeoutIndex = this.timeouts.length;
        this.messageAdded$.next();
        this.timeouts.push(window.setTimeout(() => {
            const index = this.messages[type].indexOf(object);
            this.messages[type].splice(index, 1);
            this.flattenMessages();
            this.timeouts.splice(timeoutIndex, 1);
        }, time));
    }
    flattenMessages() {
        const flattened = [];
        for (const prop in this.messages) {
            if (this.messages.hasOwnProperty(prop)) {
                const typeMessages = this.messages[prop];
                typeMessages.forEach((m) => flattened.push(m));
            }
        }
        this.messagesFlat = flattened.sort((a, b) => a.time.valueOf() - b.time.valueOf());
        this.messageAdded$.next();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvGlobalMessagesService, deps: [{ token: TrvDateService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvGlobalMessagesService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvGlobalMessagesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: TrvDateService }] });

class TrvLuxonToFormatPipe {
    transform(dateTime, format) {
        if (!dateTime) {
            return undefined;
        }
        if (!format) {
            return dateTime.toISO({ suppressMilliseconds: true });
        }
        return dateTime.toFormat(format);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvLuxonToFormatPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.1", ngImport: i0, type: TrvLuxonToFormatPipe, isStandalone: true, name: "trvLuxonToFormat" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvLuxonToFormatPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'trvLuxonToFormat',
                    standalone: true
                }]
        }] });

class TrvProgressBarComponent {
    _max = 100;
    value;
    showValue;
    height;
    type;
    set max(max) {
        this._max = !Number.isInteger(max) || max <= 0 ? 100 : max;
    }
    get max() { return this._max; }
    constructor() { }
    getPercentValue() {
        return 100 * this.value / this._max;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvProgressBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvProgressBarComponent, isStandalone: true, selector: "trv-progressbar", inputs: { value: "value", showValue: "showValue", height: "height", type: "type", max: "max" }, ngImport: i0, template: "<div class=\"progress\"\r\n     [style.height]=\"height\">\r\n  <div class=\"progress-bar{{type ? ' bg-' + type : ''}}\"\r\n       role=\"progressbar\"\r\n       [style.width.%]=\"getPercentValue()\"\r\n       [attr.aria-valuenow]=\"value\"\r\n       aria-valuemin=\"0\"\r\n       [attr.aria-valuemax]=\"max\">\r\n    @if (showValue) {\r\n    <span>{{getPercentValue()}}%</span>\r\n    }\r\n  </div>\r\n</div>", changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvProgressBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-progressbar', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<div class=\"progress\"\r\n     [style.height]=\"height\">\r\n  <div class=\"progress-bar{{type ? ' bg-' + type : ''}}\"\r\n       role=\"progressbar\"\r\n       [style.width.%]=\"getPercentValue()\"\r\n       [attr.aria-valuenow]=\"value\"\r\n       aria-valuemin=\"0\"\r\n       [attr.aria-valuemax]=\"max\">\r\n    @if (showValue) {\r\n    <span>{{getPercentValue()}}%</span>\r\n    }\r\n  </div>\r\n</div>" }]
        }], ctorParameters: () => [], propDecorators: { value: [{
                type: Input
            }], showValue: [{
                type: Input
            }], height: [{
                type: Input
            }], type: [{
                type: Input
            }], max: [{
                type: Input
            }] } });

class CompontentContainerDirective {
    vcRef;
    constructor(vcRef) {
        this.vcRef = vcRef;
    }
    set compontentContainer(componentMessage) {
        this.vcRef.clear();
        const c = this.vcRef.createComponent(componentMessage.component);
        if (componentMessage.inputs) {
            for (const prop in componentMessage.inputs) {
                if (componentMessage.inputs.hasOwnProperty(prop)) {
                    c.instance[prop] = componentMessage.inputs[prop];
                }
            }
        }
    }
    ngOnDestroy() {
        this.vcRef.clear();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: CompontentContainerDirective, deps: [{ token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: CompontentContainerDirective, isStandalone: true, selector: "[trvCompontentContainer]", inputs: { compontentContainer: "compontentContainer" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: CompontentContainerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[trvCompontentContainer]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ViewContainerRef }], propDecorators: { compontentContainer: [{
                type: Input
            }] } });

class TrvGlobalMessageComponent {
    globalMessagesService;
    cdr;
    message;
    faTimes = faTimes;
    typeIcons = {
        success: faCheck,
        danger: faExclamationTriangle,
        warning: faExclamationCircle,
        info: faInfoCircle
    };
    interval;
    progressValue = 100 - 4;
    constructor(globalMessagesService, cdr) {
        this.globalMessagesService = globalMessagesService;
        this.cdr = cdr;
    }
    ngOnInit() {
        const intervalTime = this.message.countdown / 25;
        this.interval = window.setInterval(() => {
            this.progressValue -= 4;
            this.cdr.detectChanges();
        }, intervalTime);
    }
    ngOnDestroy() {
        window.clearInterval(this.interval);
    }
    closeMessage() {
        this.globalMessagesService.closeMessage(this.message);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvGlobalMessageComponent, deps: [{ token: TrvGlobalMessagesService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvGlobalMessageComponent, isStandalone: true, selector: "trv-global-message", inputs: { message: "message" }, ngImport: i0, template: "<div class=\"global-message global-message--{{message.type}}\"\r\n     role=\"alert\">\r\n  @if (message.type !== 'component') {\r\n  <div class=\"global-message-content\">\r\n    <div class=\"icon-container\">\r\n      <fa-icon [icon]=\"typeIcons[message.type]\"></fa-icon>\r\n    </div>\r\n    <div class=\"message-container\">\r\n      <time>{{message.time | trvLuxonToFormat: 'HH:mm:ss' }}</time>\r\n      @if (message.mess) {\r\n      <p>{{message.mess}}</p>\r\n      }\r\n      @if (message.messageList) {\r\n      <ul>\r\n        @for (mess of message.messageList; track mess) {\r\n        <li>\r\n          <span>{{mess}}</span>\r\n        </li>\r\n        }\r\n      </ul>\r\n      }\r\n    </div>\r\n    <button type=\"button\"\r\n            class=\"btn btn-rounded\"\r\n            (click)=\"closeMessage()\">\r\n      <fa-icon [icon]=\"faTimes\"></fa-icon>\r\n    </button>\r\n  </div>\r\n  }\r\n  @if (message.type === 'component') {\r\n    @if(message.inputs.isCloseButtonShow) {\r\n      <div class=\"global-message-content\" >\r\n        <ng-container\r\n          trvCompontentContainer\r\n          [compontentContainer]=\"message\"\r\n        ></ng-container>\r\n        <button type=\"button\" class=\"btn btn-rounded\" (click)=\"closeMessage()\">\r\n          <fa-icon [icon]=\"faTimes\"></fa-icon>\r\n        </button>\r\n      </div>\r\n    }\r\n    @else {\r\n      <ng-container\r\n          trvCompontentContainer\r\n          [compontentContainer]=\"message\"\r\n        ></ng-container>\r\n    }\r\n  }\r\n  <trv-progressbar class=\"global-message-timer\"\r\n                   [type]=\"message.type\"\r\n                   [value]=\"progressValue\"></trv-progressbar>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}:host{display:block}.global-message{margin:15px 0 0;padding:0;display:flex;flex-direction:column;color:#fff;box-shadow:0 .2rem .5rem #000000b3}.global-message .global-message-content{display:flex;flex-direction:row}.global-message .global-message-content .icon-container{display:flex;align-items:flex-start;justify-content:center;flex-shrink:0;width:26px;padding:.25rem .35rem}.global-message .global-message-content .message-container{display:flex;flex-direction:column;align-items:flex-start;width:100%;padding:.5rem}.global-message .global-message-content .message-container time{font-size:.875rem}.global-message .global-message-content .message-container p{margin:0;word-break:break-word}.global-message .global-message-content .message-container ul{margin:0 0 0 -6px;padding:0}.global-message .global-message-content .message-container ul li{margin:0;padding:0}.global-message .global-message-content .message-container ul li span{position:relative;left:6px}.global-message .global-message-content .btn-rounded{color:#fff;font-size:1rem;margin:0 0 0 auto;padding:.25rem .5rem;align-self:flex-start}.global-message .global-message-content .btn-rounded:hover,.global-message .global-message-content .btn-rounded:focus,.global-message .global-message-content .btn-rounded:active{color:#ccc;background-color:transparent;border:none}.global-message .global-message-timer ::ng-deep .progress{height:.5rem;border-radius:0;background-color:#fff3}.global-message .global-message-timer ::ng-deep .progress-bar{background-color:#fff6!important}.global-message--success{background:#005f37b3}.global-message--success .icon-container{background:#005f37e6}.global-message--danger{background:#870000b3}.global-message--danger .icon-container{background:#870000e6}.global-message--warning{background:#b38a11b3}.global-message--warning .icon-container{background:#b38a11e6}.global-message--info,.global-message--component{background:#00375fb3}.global-message--info .icon-container,.global-message--component .icon-container{background:#00375fe6}.global-message--component{color:inherit}\n"], dependencies: [{ kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "directive", type: CompontentContainerDirective, selector: "[trvCompontentContainer]", inputs: ["compontentContainer"] }, { kind: "component", type: TrvProgressBarComponent, selector: "trv-progressbar", inputs: ["value", "showValue", "height", "type", "max"] }, { kind: "pipe", type: TrvLuxonToFormatPipe, name: "trvLuxonToFormat" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvGlobalMessageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-global-message', changeDetection: ChangeDetectionStrategy.OnPush, imports: [FaIconComponent, CompontentContainerDirective, TrvProgressBarComponent, TrvLuxonToFormatPipe], template: "<div class=\"global-message global-message--{{message.type}}\"\r\n     role=\"alert\">\r\n  @if (message.type !== 'component') {\r\n  <div class=\"global-message-content\">\r\n    <div class=\"icon-container\">\r\n      <fa-icon [icon]=\"typeIcons[message.type]\"></fa-icon>\r\n    </div>\r\n    <div class=\"message-container\">\r\n      <time>{{message.time | trvLuxonToFormat: 'HH:mm:ss' }}</time>\r\n      @if (message.mess) {\r\n      <p>{{message.mess}}</p>\r\n      }\r\n      @if (message.messageList) {\r\n      <ul>\r\n        @for (mess of message.messageList; track mess) {\r\n        <li>\r\n          <span>{{mess}}</span>\r\n        </li>\r\n        }\r\n      </ul>\r\n      }\r\n    </div>\r\n    <button type=\"button\"\r\n            class=\"btn btn-rounded\"\r\n            (click)=\"closeMessage()\">\r\n      <fa-icon [icon]=\"faTimes\"></fa-icon>\r\n    </button>\r\n  </div>\r\n  }\r\n  @if (message.type === 'component') {\r\n    @if(message.inputs.isCloseButtonShow) {\r\n      <div class=\"global-message-content\" >\r\n        <ng-container\r\n          trvCompontentContainer\r\n          [compontentContainer]=\"message\"\r\n        ></ng-container>\r\n        <button type=\"button\" class=\"btn btn-rounded\" (click)=\"closeMessage()\">\r\n          <fa-icon [icon]=\"faTimes\"></fa-icon>\r\n        </button>\r\n      </div>\r\n    }\r\n    @else {\r\n      <ng-container\r\n          trvCompontentContainer\r\n          [compontentContainer]=\"message\"\r\n        ></ng-container>\r\n    }\r\n  }\r\n  <trv-progressbar class=\"global-message-timer\"\r\n                   [type]=\"message.type\"\r\n                   [value]=\"progressValue\"></trv-progressbar>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}:host{display:block}.global-message{margin:15px 0 0;padding:0;display:flex;flex-direction:column;color:#fff;box-shadow:0 .2rem .5rem #000000b3}.global-message .global-message-content{display:flex;flex-direction:row}.global-message .global-message-content .icon-container{display:flex;align-items:flex-start;justify-content:center;flex-shrink:0;width:26px;padding:.25rem .35rem}.global-message .global-message-content .message-container{display:flex;flex-direction:column;align-items:flex-start;width:100%;padding:.5rem}.global-message .global-message-content .message-container time{font-size:.875rem}.global-message .global-message-content .message-container p{margin:0;word-break:break-word}.global-message .global-message-content .message-container ul{margin:0 0 0 -6px;padding:0}.global-message .global-message-content .message-container ul li{margin:0;padding:0}.global-message .global-message-content .message-container ul li span{position:relative;left:6px}.global-message .global-message-content .btn-rounded{color:#fff;font-size:1rem;margin:0 0 0 auto;padding:.25rem .5rem;align-self:flex-start}.global-message .global-message-content .btn-rounded:hover,.global-message .global-message-content .btn-rounded:focus,.global-message .global-message-content .btn-rounded:active{color:#ccc;background-color:transparent;border:none}.global-message .global-message-timer ::ng-deep .progress{height:.5rem;border-radius:0;background-color:#fff3}.global-message .global-message-timer ::ng-deep .progress-bar{background-color:#fff6!important}.global-message--success{background:#005f37b3}.global-message--success .icon-container{background:#005f37e6}.global-message--danger{background:#870000b3}.global-message--danger .icon-container{background:#870000e6}.global-message--warning{background:#b38a11b3}.global-message--warning .icon-container{background:#b38a11e6}.global-message--info,.global-message--component{background:#00375fb3}.global-message--info .icon-container,.global-message--component .icon-container{background:#00375fe6}.global-message--component{color:inherit}\n"] }]
        }], ctorParameters: () => [{ type: TrvGlobalMessagesService }, { type: i0.ChangeDetectorRef }], propDecorators: { message: [{
                type: Input
            }] } });

class TrvGlobalMessagesComponent extends trvUnsubscribeMixin() {
    globalMessagesService;
    cdr;
    constructor(globalMessagesService, cdr) {
        super();
        this.globalMessagesService = globalMessagesService;
        this.cdr = cdr;
        globalMessagesService.messageAdded$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => this.cdr.detectChanges());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvGlobalMessagesComponent, deps: [{ token: TrvGlobalMessagesService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvGlobalMessagesComponent, isStandalone: true, selector: "trv-global-messages", usesInheritance: true, ngImport: i0, template: "<div class=\"messages-container\">\r\n  @for (message of globalMessagesService.messagesFlat; track message; let i = $index) {\r\n  <trv-global-message [message]=\"message\"\r\n                      @trvFadeAnimation>\r\n  </trv-global-message>\r\n  }\r\n</div>", styles: [".messages-container{position:fixed;z-index:9999;bottom:15px;right:15px;width:350px;background:transparent}\n"], dependencies: [{ kind: "component", type: TrvGlobalMessageComponent, selector: "trv-global-message", inputs: ["message"] }], animations: [trvFadeAnimation], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvGlobalMessagesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-global-messages', changeDetection: ChangeDetectionStrategy.OnPush, animations: [trvFadeAnimation], imports: [TrvGlobalMessageComponent], template: "<div class=\"messages-container\">\r\n  @for (message of globalMessagesService.messagesFlat; track message; let i = $index) {\r\n  <trv-global-message [message]=\"message\"\r\n                      @trvFadeAnimation>\r\n  </trv-global-message>\r\n  }\r\n</div>", styles: [".messages-container{position:fixed;z-index:9999;bottom:15px;right:15px;width:350px;background:transparent}\n"] }]
        }], ctorParameters: () => [{ type: TrvGlobalMessagesService }, { type: i0.ChangeDetectorRef }] });

class TrvJumbotronComponent {
    header;
    lead;
    backgroundImage;
    constructor() { }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvJumbotronComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvJumbotronComponent, isStandalone: true, selector: "trv-jumbotron", inputs: { header: "header", lead: "lead", backgroundImage: "backgroundImage" }, ngImport: i0, template: "<section class=\"jumbotron text-center has-bg-image\"\r\n         [ngStyle]=\"{ 'background-image': backgroundImage ? 'url(' + backgroundImage + ')' : 'none'}\">\r\n  <div class=\"container\">\r\n    <h1 class=\"jumbotron-heading\">{{header}}</h1>\r\n    <p class=\"lead\">{{lead}}</p>\r\n    <div class=\"cta\">\r\n      <ng-content></ng-content>\r\n    </div>\r\n  </div>\r\n</section>", styles: [":root{--bs-border-color: #ced4da}.jumbotron.has-bg-image{background-repeat:no-repeat;height:300px;background-size:cover;position:relative}.jumbotron.has-bg-image:after{content:\"\";position:absolute;width:100%;height:100%;background:#00000096;left:0;right:0;top:0}.jumbotron.has-bg-image .container{color:#fff;position:relative;z-index:1}.jumbotron .cta .btn{margin:.5rem;border:solid 1px #fff;color:#fff;border-radius:0}:root{--jumbotron-padding-y: 3rem}.jumbotron{padding-top:var(--jumbotron-padding-y);padding-bottom:var(--jumbotron-padding-y);margin-bottom:0;background-color:#fff}@media (min-width: 768px){.jumbotron{padding-top:calc(var(--jumbotron-padding-y) * 2);padding-bottom:calc(var(--jumbotron-padding-y) * 2)}}.jumbotron p:last-child{margin-bottom:0}.jumbotron-heading{font-weight:300}.jumbotron .container{max-width:40rem}footer{padding-top:3rem;padding-bottom:3rem}footer p{margin-bottom:.25rem}.jumbotron{padding:1rem .5rem;margin-bottom:1rem;background-color:#d3d3d3;border-radius:.5rem}@media (min-width: 576px){.jumbotron{padding:2rem 1rem}}.jumbotron-fluid{padding-right:0;padding-left:0;border-radius:0}\n"], dependencies: [{ kind: "directive", type: NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvJumbotronComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-jumbotron', imports: [NgStyle], template: "<section class=\"jumbotron text-center has-bg-image\"\r\n         [ngStyle]=\"{ 'background-image': backgroundImage ? 'url(' + backgroundImage + ')' : 'none'}\">\r\n  <div class=\"container\">\r\n    <h1 class=\"jumbotron-heading\">{{header}}</h1>\r\n    <p class=\"lead\">{{lead}}</p>\r\n    <div class=\"cta\">\r\n      <ng-content></ng-content>\r\n    </div>\r\n  </div>\r\n</section>", styles: [":root{--bs-border-color: #ced4da}.jumbotron.has-bg-image{background-repeat:no-repeat;height:300px;background-size:cover;position:relative}.jumbotron.has-bg-image:after{content:\"\";position:absolute;width:100%;height:100%;background:#00000096;left:0;right:0;top:0}.jumbotron.has-bg-image .container{color:#fff;position:relative;z-index:1}.jumbotron .cta .btn{margin:.5rem;border:solid 1px #fff;color:#fff;border-radius:0}:root{--jumbotron-padding-y: 3rem}.jumbotron{padding-top:var(--jumbotron-padding-y);padding-bottom:var(--jumbotron-padding-y);margin-bottom:0;background-color:#fff}@media (min-width: 768px){.jumbotron{padding-top:calc(var(--jumbotron-padding-y) * 2);padding-bottom:calc(var(--jumbotron-padding-y) * 2)}}.jumbotron p:last-child{margin-bottom:0}.jumbotron-heading{font-weight:300}.jumbotron .container{max-width:40rem}footer{padding-top:3rem;padding-bottom:3rem}footer p{margin-bottom:.25rem}.jumbotron{padding:1rem .5rem;margin-bottom:1rem;background-color:#d3d3d3;border-radius:.5rem}@media (min-width: 576px){.jumbotron{padding:2rem 1rem}}.jumbotron-fluid{padding-right:0;padding-left:0;border-radius:0}\n"] }]
        }], ctorParameters: () => [], propDecorators: { header: [{
                type: Input
            }], lead: [{
                type: Input
            }], backgroundImage: [{
                type: Input
            }] } });

class TrvFullscreenLoaderService {
    fullscreenLoadingChanged = new EventEmitter();
    loadingCounter = 0;
    loadingAppCounter = 0;
    fullscreenLoading = false;
    appLoaderText = '';
    stopLoadingApp = false;
    constructor() { }
    addLoadTask(text) {
        this.loadingCounter++;
        this.appLoaderText = text;
        this.fullscreenLoading = true;
        this.fullscreenLoadingChanged.emit(this.fullscreenLoading);
    }
    removeLoadTask() {
        this.loadingCounter--;
        if (this.loadingCounter <= 0) {
            this.fullscreenLoading = false;
            this.appLoaderText = '';
            this.fullscreenLoadingChanged.emit(this.fullscreenLoading);
        }
    }
    shouldShowFullscreenLoader() {
        return this.fullscreenLoading;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFullscreenLoaderService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFullscreenLoaderService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFullscreenLoaderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [], propDecorators: { fullscreenLoadingChanged: [{
                type: Output
            }] } });

class TrvFullscreenLoaderComponent {
    fullscreenLoaderService;
    changeDetectorRef;
    utilityService;
    hideAfterDelay = 2000;
    loaderVisible = false;
    appLoaderText;
    constructor(fullscreenLoaderService, changeDetectorRef, utilityService) {
        this.fullscreenLoaderService = fullscreenLoaderService;
        this.changeDetectorRef = changeDetectorRef;
        this.utilityService = utilityService;
    }
    ngOnInit() {
        setTimeout(() => {
            const appLoader = document.querySelector('.trv-app-loader');
            if (!appLoader) {
                return;
            }
            appLoader.classList.add('fade-out');
            setTimeout(() => {
                document.body.removeChild(appLoader);
            }, 1000);
        }, this.hideAfterDelay);
        this.fullscreenLoaderService.fullscreenLoadingChanged.subscribe((load) => {
            this.loaderVisible = load;
            this.appLoaderText = this.fullscreenLoaderService.appLoaderText;
            this.utilityService.detectChanges(this.changeDetectorRef);
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFullscreenLoaderComponent, deps: [{ token: TrvFullscreenLoaderService }, { token: i0.ChangeDetectorRef }, { token: TrvUtilityService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvFullscreenLoaderComponent, isStandalone: true, selector: "trv-fullscreen-loader", inputs: { hideAfterDelay: "hideAfterDelay" }, ngImport: i0, template: "@if (loaderVisible) {\r\n<div class=\"trv-fullscreen-loader loading loading-visible\"\r\n     @trvFadeAnimation>\r\n  @if (appLoaderText) {\r\n  <div class=\"trv-loader-text\">\r\n    {{appLoaderText}}\r\n  </div>\r\n  }\r\n</div>\r\n}", styles: [""], animations: [trvFadeAnimation], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFullscreenLoaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-fullscreen-loader', changeDetection: ChangeDetectionStrategy.OnPush, animations: [trvFadeAnimation], standalone: true, template: "@if (loaderVisible) {\r\n<div class=\"trv-fullscreen-loader loading loading-visible\"\r\n     @trvFadeAnimation>\r\n  @if (appLoaderText) {\r\n  <div class=\"trv-loader-text\">\r\n    {{appLoaderText}}\r\n  </div>\r\n  }\r\n</div>\r\n}" }]
        }], ctorParameters: () => [{ type: TrvFullscreenLoaderService }, { type: i0.ChangeDetectorRef }, { type: TrvUtilityService }], propDecorators: { hideAfterDelay: [{
                type: Input
            }] } });

const TrvLoadingBundle = [TrvLoadingDirective, TrvFullscreenLoaderComponent];

// @dynamic
class TrvPaginationComponent {
    defaults;
    get pageSize() { return this._pageSize; }
    set pageSize(pageSize) {
        this._pageSize = pageSize;
        this.pageSizeOptionsControl?.setValue(this.pageSize, { emitEvent: false });
        this.calculateNumberOfPages();
    }
    _pageSize = null;
    get collectionSize() { return this._collectionSize; }
    set collectionSize(totalItemCount) {
        this._collectionSize = totalItemCount;
        this.calculateNumberOfPages();
    }
    _collectionSize = null;
    page = 1;
    currentPageSize;
    pageSizeOptions = null;
    disabled;
    hidePageSizeOptions = null;
    customStrings;
    pageChanged = new EventEmitter();
    faChevronLeft = faChevronLeft;
    faChevronRight = faChevronRight;
    numberOfPages = null;
    pageSizeOptionsControl;
    ngUnsubscribe = new Subject();
    constructor(defaults) {
        this.defaults = defaults;
    }
    ngOnInit() {
        this.pageSize = this.pageSize ?? this.defaults.pageSize ?? DEFAULT_PAGE_SIZE;
        this.pageSizeOptions = this.pageSizeOptions ?? this.defaults.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;
        this.hidePageSizeOptions = this.hidePageSizeOptions ?? this.defaults.hidePageSizeOptions ?? DEFAULT_HIDE_PAGE_SIZE_OPTIONS;
        if (!this.pageSize) {
            throw Error('pageSize must be specified.');
        }
        if (this.collectionSize && this.currentPageSize) {
            throw Error('Use either collectionSize or currentPageSize, not both.');
        }
        if (!this.pageSizeOptions.includes(this.pageSize) && !this.hidePageSizeOptions) {
            this.pageSizeOptions.push(this.pageSize);
            this.pageSizeOptions.sort((a, b) => a - b);
            console.warn('Specified pageSize not in pageSizeOptions. Adding to pageSizeOptions');
        }
        this.pageSizeOptionsControl = new FormControl(this.pageSize);
        this.pageSizeOptionsControl.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
            this.page = 1;
            this.emitPageChange();
        });
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
    }
    goToNextPage() {
        this.page++;
        this.emitPageChange();
    }
    goToPreviousPage() {
        this.page--;
        this.emitPageChange();
    }
    calculateNumberOfPages() {
        if (!this.collectionSize || !this.pageSize) {
            this.numberOfPages = null;
            return;
        }
        this.numberOfPages = Math.ceil(this.collectionSize / this.pageSize);
    }
    emitPageChange() {
        this.pageChanged.emit({ page: this.page, pageSize: this.pageSizeOptionsControl.value });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvPaginationComponent, deps: [{ token: TRV_PAGINATION_DEFAULT_OPTIONS, optional: true }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvPaginationComponent, isStandalone: true, selector: "trv-pagination", inputs: { pageSize: "pageSize", collectionSize: "collectionSize", page: "page", currentPageSize: "currentPageSize", pageSizeOptions: "pageSizeOptions", disabled: "disabled", hidePageSizeOptions: "hidePageSizeOptions", customStrings: "customStrings" }, outputs: { pageChanged: "pageChanged" }, ngImport: i0, template: "<div class=\"d-flex\">\r\n  <div class=\"d-flex align-items-center font-weight-normal\">\r\n    @if (!hidePageSizeOptions) {\r\n    <span class=\"text-nowrap me-2\">{{customStrings?.rowsPerPage || 'Rader per sida'}} </span>\r\n    <select class=\"form-select me-3\"\r\n            id=\"page-size-options-select\"\r\n            aria-label=\"Page size selection\"\r\n            [formControl]=\"pageSizeOptionsControl\">\r\n      @for (pageSize of pageSizeOptions; track pageSize) {\r\n      <option [value]=\"pageSize\">\r\n        {{pageSize}}\r\n      </option>\r\n      }\r\n    </select>\r\n    }\r\n    <span class=\" text-nowrap me-3\">\r\n      @if (collectionSize) {\r\n      {{(page - 1) * pageSize + 1}}-{{page === numberOfPages ? collectionSize : page * pageSize}} {{customStrings?.of || 'av'}} {{collectionSize}}\r\n      }\r\n      @if (currentPageSize) {\r\n      {{(page - 1) * pageSize + 1}}-{{((page - 1) * pageSize) + currentPageSize}}\r\n      }\r\n    </span>\r\n  </div>\r\n  <nav aria-label=\"Search results pages\">\r\n    <ul class=\"pagination mb-0\">\r\n      <li class=\"page-item me-2\">\r\n        <button class=\"page-link\"\r\n                type=\"button\"\r\n                [disabled]=\"page === 1 || disabled\"\r\n                (click)=\"goToPreviousPage()\"\r\n                aria-label=\"Previous page\">\r\n          <span aria-hidden=\"true\">\r\n            <fa-icon [icon]=\"faChevronLeft\"></fa-icon>\r\n          </span>\r\n        </button>\r\n      </li>\r\n      <li class=\"page-item\">\r\n        <button class=\"page-link\"\r\n                type=\"button\"\r\n                [disabled]=\"currentPageSize < pageSize || numberOfPages === page || disabled\"\r\n                (click)=\"goToNextPage()\"\r\n                aria-label=\"Next page\">\r\n          <span aria-hidden=\"true\">\r\n            <fa-icon [icon]=\"faChevronRight\"></fa-icon>\r\n          </span>\r\n        </button>\r\n      </li>\r\n    </ul>\r\n  </nav>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.page-link{line-height:1.5rem;font-weight:400;border-radius:0!important;padding:.25rem .4rem}.page-link:disabled{color:#c1c1c1}.page-link:disabled:hover{color:#c1c1c1;background-color:#fff}.page-link fa-icon{border-radius:100%;width:24px;height:24px;display:-ms-inline-flexbox;display:inline-flex;-ms-flex-align:center;align-items:center;-ms-flex-pack:center;justify-content:center}.form-select{border-radius:0}\n"], dependencies: [{ kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1$2.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i1$2.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i1$2.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvPaginationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-pagination', changeDetection: ChangeDetectionStrategy.OnPush, imports: [ReactiveFormsModule, FaIconComponent], template: "<div class=\"d-flex\">\r\n  <div class=\"d-flex align-items-center font-weight-normal\">\r\n    @if (!hidePageSizeOptions) {\r\n    <span class=\"text-nowrap me-2\">{{customStrings?.rowsPerPage || 'Rader per sida'}} </span>\r\n    <select class=\"form-select me-3\"\r\n            id=\"page-size-options-select\"\r\n            aria-label=\"Page size selection\"\r\n            [formControl]=\"pageSizeOptionsControl\">\r\n      @for (pageSize of pageSizeOptions; track pageSize) {\r\n      <option [value]=\"pageSize\">\r\n        {{pageSize}}\r\n      </option>\r\n      }\r\n    </select>\r\n    }\r\n    <span class=\" text-nowrap me-3\">\r\n      @if (collectionSize) {\r\n      {{(page - 1) * pageSize + 1}}-{{page === numberOfPages ? collectionSize : page * pageSize}} {{customStrings?.of || 'av'}} {{collectionSize}}\r\n      }\r\n      @if (currentPageSize) {\r\n      {{(page - 1) * pageSize + 1}}-{{((page - 1) * pageSize) + currentPageSize}}\r\n      }\r\n    </span>\r\n  </div>\r\n  <nav aria-label=\"Search results pages\">\r\n    <ul class=\"pagination mb-0\">\r\n      <li class=\"page-item me-2\">\r\n        <button class=\"page-link\"\r\n                type=\"button\"\r\n                [disabled]=\"page === 1 || disabled\"\r\n                (click)=\"goToPreviousPage()\"\r\n                aria-label=\"Previous page\">\r\n          <span aria-hidden=\"true\">\r\n            <fa-icon [icon]=\"faChevronLeft\"></fa-icon>\r\n          </span>\r\n        </button>\r\n      </li>\r\n      <li class=\"page-item\">\r\n        <button class=\"page-link\"\r\n                type=\"button\"\r\n                [disabled]=\"currentPageSize < pageSize || numberOfPages === page || disabled\"\r\n                (click)=\"goToNextPage()\"\r\n                aria-label=\"Next page\">\r\n          <span aria-hidden=\"true\">\r\n            <fa-icon [icon]=\"faChevronRight\"></fa-icon>\r\n          </span>\r\n        </button>\r\n      </li>\r\n    </ul>\r\n  </nav>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.page-link{line-height:1.5rem;font-weight:400;border-radius:0!important;padding:.25rem .4rem}.page-link:disabled{color:#c1c1c1}.page-link:disabled:hover{color:#c1c1c1;background-color:#fff}.page-link fa-icon{border-radius:100%;width:24px;height:24px;display:-ms-inline-flexbox;display:inline-flex;-ms-flex-align:center;align-items:center;-ms-flex-pack:center;justify-content:center}.form-select{border-radius:0}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [TRV_PAGINATION_DEFAULT_OPTIONS]
                }] }], propDecorators: { pageSize: [{
                type: Input
            }], collectionSize: [{
                type: Input
            }], page: [{
                type: Input
            }], currentPageSize: [{
                type: Input
            }], pageSizeOptions: [{
                type: Input
            }], disabled: [{
                type: Input
            }], hidePageSizeOptions: [{
                type: Input
            }], customStrings: [{
                type: Input
            }], pageChanged: [{
                type: Output
            }] } });
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_HIDE_PAGE_SIZE_OPTIONS = false;
/** Injection token to be used to override the default options */
const TRV_PAGINATION_DEFAULT_OPTIONS = new InjectionToken('trv-pagination-default-options', {
    providedIn: 'root',
    factory: () => ({ pageSize: DEFAULT_PAGE_SIZE, pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS, hidePageSizeOptions: DEFAULT_HIDE_PAGE_SIZE_OPTIONS })
});

class TrvTableRowClick {
    constructor() { }
    trvTableRowClick = new EventEmitter();
    tabindex = '0';
    onClick($event) {
        if (this.shouldPreventTableRowClick($event)) {
            return;
        }
        this.trvTableRowClick.emit();
    }
    //Förhindra att space scrollar sidan.
    preventDefault($event) {
        if (!this.shouldPreventTableRowClick($event)) {
            $event.preventDefault();
        }
    }
    shouldPreventTableRowClick($event) {
        const target = $event.target;
        return target.closest('.trv-prevent-table-row-click') != null;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableRowClick, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvTableRowClick, isStandalone: true, selector: "[trvTableRowClick]", outputs: { trvTableRowClick: "trvTableRowClick" }, host: { listeners: { "click": "onClick($event)", "keyup.space": "onClick($event)", "keyup.enter": "onClick($event)", "keydown.space": "preventDefault($event)" }, properties: { "tabindex": "this.tabindex" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableRowClick, decorators: [{
            type: Directive,
            args: [{
                    selector: '[trvTableRowClick]',
                    standalone: true
                }]
        }], ctorParameters: () => [], propDecorators: { trvTableRowClick: [{
                type: Output
            }], tabindex: [{
                type: HostBinding,
                args: ['tabindex']
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }, {
                type: HostListener,
                args: ['keyup.space', ['$event']]
            }, {
                type: HostListener,
                args: ['keyup.enter', ['$event']]
            }], preventDefault: [{
                type: HostListener,
                args: ['keydown.space', ['$event']]
            }] } });

class TrvTableRowLinkService {
    router;
    location;
    constructor(router, location) {
        this.router = router;
        this.location = location;
    }
    openLink(button, target, newWindow) {
        if (button.clickFunction) {
            button.clickFunction();
            return;
        }
        if (!target) {
            this.router.navigateByUrl(button.routerLink);
        }
        else {
            const baseUrl = window.location.href.replace(this.location.path(true), '');
            const url = baseUrl + button.routerLink;
            const features = newWindow ? `height=${window.outerHeight / 2},width=${window.outerWidth / 2},popup=yes,alwaysRaised=yes` : null;
            window.open(url, target, features);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableRowLinkService, deps: [{ token: i2.Router }, { token: i2$2.Location }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableRowLinkService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableRowLinkService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i2.Router }, { type: i2$2.Location }] });

class TrvTableRowLinkMenuComponent extends BasePortalOutlet {
    trvTableRowLinkService;
    _portalOutlet;
    _focusTrap;
    autoFocus = true;
    animationStateChanged = new Subject();
    visibility = 'visible';
    button;
    constructor(trvTableRowLinkService) {
        super();
        this.trvTableRowLinkService = trvTableRowLinkService;
    }
    attachComponentPortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            throw new Error('Content already attached');
        }
        return this._portalOutlet.attachComponentPortal(portal);
    }
    attachTemplatePortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            throw new Error('Content already attached');
        }
        return this._portalOutlet.attachTemplatePortal(portal);
    }
    onAnimationDone(event) {
        this.animationStateChanged.next(event);
        if (this.visibility === 'visible') {
            this._focusTrap.focusTrap.focusFirstTabbableElement();
        }
    }
    startExitAnimation() {
        this.visibility = 'hidden';
    }
    open(target, newWindow) {
        this.trvTableRowLinkService.openLink(this.button, target, newWindow);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableRowLinkMenuComponent, deps: [{ token: TrvTableRowLinkService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvTableRowLinkMenuComponent, isStandalone: true, selector: "ng-component", inputs: { button: "button" }, host: { listeners: { "@state.done": "onAnimationDone($event)" }, properties: { "@state": "visibility" }, classAttribute: "popover-container" }, viewQueries: [{ propertyName: "_portalOutlet", first: true, predicate: CdkPortalOutlet, descendants: true, static: true }, { propertyName: "_focusTrap", first: true, predicate: CdkTrapFocus, descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"popover-container\">\r\n  <div class=\"popover-content\"\r\n       [cdkTrapFocus]=\"autoFocus\"\r\n       [cdkTrapFocusAutoCapture]=\"autoFocus\"\r\n       #focusTrap=\"cdkTrapFocus\">\r\n    <div>\r\n      <button class=\"dropdown-item menu-button\"\r\n              (click)=\"open()\">\r\n        <span>\u00D6ppna</span>\r\n        <span class=\"shortcut-label\">\r\n          klick\r\n        </span>\r\n      </button>\r\n      <button class=\"dropdown-item menu-button\"\r\n              (click)=\"open('_blank')\">\r\n        <span>\u00D6ppna i ny flik</span>\r\n        <span class=\"shortcut-label\">\r\n          ctrl+klick\r\n        </span>\r\n      </button>\r\n      <button class=\"dropdown-item menu-button\"\r\n              (click)=\"open('_blank', true)\">\r\n        <span>\u00D6ppna i nytt f\u00F6nster</span>\r\n        <span class=\"shortcut-label\">\r\n          shift+klick\r\n        </span>\r\n      </button>\r\n    </div>\r\n  </div>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.popover-container{margin:10px}.popover-container:focus{outline:none}.popover-container .popover-content{min-width:150px;font-size:.975rem;background-color:#fff;border:1px solid #dee2e6;box-shadow:0 2px 4px #0000002e}.popover-container button{display:flex;justify-content:space-between;align-items:center}.popover-container button:focus{outline:none}.popover-container button .shortcut-label{font-size:.75rem;margin-left:2rem}\n"], dependencies: [{ kind: "ngmodule", type: A11yModule }, { kind: "directive", type: i2$1.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }], animations: [
            trigger('state', [
                transition('void => visible', [
                    style({ opacity: 0, transform: 'scale(0)' }),
                    animate('100ms cubic-bezier(0, 0, 0.2, 1)', style({ transform: 'none', opacity: 1 }))
                ]),
                transition('visible => hidden', [
                    animate('0ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 0, transform: 'scale(0)' }))
                ])
            ])
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableRowLinkMenuComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, animations: [
                        trigger('state', [
                            transition('void => visible', [
                                style({ opacity: 0, transform: 'scale(0)' }),
                                animate('100ms cubic-bezier(0, 0, 0.2, 1)', style({ transform: 'none', opacity: 1 }))
                            ]),
                            transition('visible => hidden', [
                                animate('0ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 0, transform: 'scale(0)' }))
                            ])
                        ])
                    ], host: {
                        '[@state]': 'visibility',
                        '(@state.done)': 'onAnimationDone($event)',
                        class: 'popover-container'
                    }, imports: [A11yModule], template: "<div class=\"popover-container\">\r\n  <div class=\"popover-content\"\r\n       [cdkTrapFocus]=\"autoFocus\"\r\n       [cdkTrapFocusAutoCapture]=\"autoFocus\"\r\n       #focusTrap=\"cdkTrapFocus\">\r\n    <div>\r\n      <button class=\"dropdown-item menu-button\"\r\n              (click)=\"open()\">\r\n        <span>\u00D6ppna</span>\r\n        <span class=\"shortcut-label\">\r\n          klick\r\n        </span>\r\n      </button>\r\n      <button class=\"dropdown-item menu-button\"\r\n              (click)=\"open('_blank')\">\r\n        <span>\u00D6ppna i ny flik</span>\r\n        <span class=\"shortcut-label\">\r\n          ctrl+klick\r\n        </span>\r\n      </button>\r\n      <button class=\"dropdown-item menu-button\"\r\n              (click)=\"open('_blank', true)\">\r\n        <span>\u00D6ppna i nytt f\u00F6nster</span>\r\n        <span class=\"shortcut-label\">\r\n          shift+klick\r\n        </span>\r\n      </button>\r\n    </div>\r\n  </div>\r\n</div>", styles: [":root{--bs-border-color: #ced4da}.popover-container{margin:10px}.popover-container:focus{outline:none}.popover-container .popover-content{min-width:150px;font-size:.975rem;background-color:#fff;border:1px solid #dee2e6;box-shadow:0 2px 4px #0000002e}.popover-container button{display:flex;justify-content:space-between;align-items:center}.popover-container button:focus{outline:none}.popover-container button .shortcut-label{font-size:.75rem;margin-left:2rem}\n"] }]
        }], ctorParameters: () => [{ type: TrvTableRowLinkService }], propDecorators: { _portalOutlet: [{
                type: ViewChild,
                args: [CdkPortalOutlet, { static: true }]
            }], _focusTrap: [{
                type: ViewChild,
                args: [CdkTrapFocus, { static: true }]
            }], button: [{
                type: Input
            }] } });

class TrvTableRowLink {
    overlay;
    viewContainerRef;
    injector;
    trvTableRowLinkService;
    overlayRef;
    clickSub;
    trvTableRowLink;
    tabindex = '0';
    constructor(overlay, viewContainerRef, injector, trvTableRowLinkService) {
        this.overlay = overlay;
        this.viewContainerRef = viewContainerRef;
        this.injector = injector;
        this.trvTableRowLinkService = trvTableRowLinkService;
    }
    onClick($event) {
        if (this.shouldPreventTableRowClick($event)) {
            return;
        }
        if ($event.ctrlKey && $event.shiftKey) {
            this.trvTableRowLinkService.openLink(this.trvTableRowLink, '_blank');
        }
        else if ($event.ctrlKey) {
            this.trvTableRowLinkService.openLink(this.trvTableRowLink, '_blank');
        }
        else if ($event.shiftKey) {
            this.trvTableRowLinkService.openLink(this.trvTableRowLink, '_blank', true);
        }
        else {
            this.trvTableRowLinkService.openLink(this.trvTableRowLink);
        }
        // this.trvTableRowClick.emit();
    }
    onMiddleMouseUp($event) {
        if (this.shouldPreventTableRowClick($event)) {
            return;
        }
        if ($event.button === 1) { // middle-mouse - samma hantering som ctrl+klick
            this.trvTableRowLinkService.openLink(this.trvTableRowLink, '_blank');
        }
    }
    onMiddleMouseDown($event) {
        if (!this.trvTableRowLink) {
            $event.preventDefault();
            return;
        }
        if ($event.button !== 1) {
            return;
        }
        $event.preventDefault();
    }
    onContextMenu($event) {
        if (this.shouldPreventTableRowClick($event)) {
            return;
        }
        $event.preventDefault();
        this.openMenu($event);
    }
    //Förhindra att space scrollar sidan.
    preventDefault($event) {
        if (!this.shouldPreventTableRowClick($event)) {
            $event.preventDefault();
        }
    }
    openMenu({ x, y }) {
        this.closeMenu();
        if (!x || !y || !this.trvTableRowLink.routerLink) {
            return;
        }
        this.clickSub = fromEvent(document, 'mouseup')
            .pipe(take(1)).subscribe(() => setTimeout(() => this.closeMenu()));
        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo({ x, y })
            .withPositions([
            {
                originX: 'start',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'top',
            }
        ]);
        const config = {
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.close()
        };
        this.overlayRef = this.overlay.create(config);
        const portalInjector = new PortalInjector(this.injector, new WeakMap([[OverlayConfig, config], [OverlayRef, this.overlayRef]]));
        const containerRef = this.overlayRef.attach(new ComponentPortal(TrvTableRowLinkMenuComponent, this.viewContainerRef, portalInjector));
        containerRef.instance.button = this.trvTableRowLink;
    }
    closeMenu() {
        this.clickSub?.unsubscribe();
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }
    shouldPreventTableRowClick($event) {
        const target = $event.target;
        return target.closest('.trv-prevent-table-row-click') != null;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableRowLink, deps: [{ token: i1.Overlay }, { token: i0.ViewContainerRef }, { token: i0.Injector }, { token: TrvTableRowLinkService }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvTableRowLink, isStandalone: true, selector: "[trvTableRowLink]", inputs: { trvTableRowLink: "trvTableRowLink" }, host: { listeners: { "click": "onClick($event)", "keyup.space": "onClick($event)", "keyup.enter": "onClick($event)", "mouseup": "onMiddleMouseUp($event)", "mousedown": "onMiddleMouseDown($event)", "contextmenu": "onContextMenu($event)", "keydown.space": "preventDefault($event)" }, properties: { "tabindex": "this.tabindex" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableRowLink, decorators: [{
            type: Directive,
            args: [{
                    selector: '[trvTableRowLink]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i1.Overlay }, { type: i0.ViewContainerRef }, { type: i0.Injector }, { type: TrvTableRowLinkService }], propDecorators: { trvTableRowLink: [{
                type: Input
            }], tabindex: [{
                type: HostBinding,
                args: ['tabindex']
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }, {
                type: HostListener,
                args: ['keyup.space', ['$event']]
            }, {
                type: HostListener,
                args: ['keyup.enter', ['$event']]
            }], onMiddleMouseUp: [{
                type: HostListener,
                args: ['mouseup', ['$event']]
            }], onMiddleMouseDown: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }], onContextMenu: [{
                type: HostListener,
                args: ['contextmenu', ['$event']]
            }], preventDefault: [{
                type: HostListener,
                args: ['keydown.space', ['$event']]
            }] } });

class TrvDateFullPipe {
    dateService;
    constructor(dateService) {
        this.dateService = dateService;
    }
    transform(date, prettyNearTerm) {
        if (!date) {
            return undefined;
        }
        const dateTime = this.dateService.fromISO(date);
        if (prettyNearTerm) {
            if (this.dateService.isToday(dateTime)) {
                return 'Idag ' + dateTime.toFormat('HH:mm');
            }
            else if (this.dateService.isYesterday(dateTime)) {
                return 'Igår ' + dateTime.toFormat('HH:mm');
            }
        }
        return dateTime.toFormat('yyyy-MM-dd HH:mm');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateFullPipe, deps: [{ token: TrvDateService }], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.1", ngImport: i0, type: TrvDateFullPipe, isStandalone: true, name: "trvDateFull" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateFullPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'trvDateFull',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: TrvDateService }] });

class TrvTableToggleColumnComponent {
    ngUnsubscribe = new Subject();
    faColumns = faColumns;
    columns;
    disabled;
    visibleColumnsChanged = new EventEmitter();
    columnsContainer;
    constructor() { }
    ngOnInit() {
        if (!this.columns || !this.columns.length) {
            throw new Error('toggle-table-column component: columns missing');
        }
    }
    onPopoverShown() {
        // TODO: ViewChild har ändrat beteende i Angular 8. columnsContainer är undefined i detta läge.
        // Ignorerar keyboard-stöd ett tag för att se om det dyker upp lösningar på problemet längre fram...
        if (!this.columnsContainer) {
            return;
        }
        // setTimeout(() => {
        // 	this.columnsContainer.nativeElement.focus();
        // });
    }
    toggleColumnVisible(column, event) {
        column.visible = event.target.checked;
        this.visibleColumnsChanged.emit(this.columns);
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableToggleColumnComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvTableToggleColumnComponent, isStandalone: true, selector: "trv-table-toggle-column", inputs: { columns: "columns", disabled: "disabled" }, outputs: { visibleColumnsChanged: "visibleColumnsChanged" }, viewQueries: [{ propertyName: "columnsContainer", first: true, predicate: ["columnsContainer"], descendants: true }], ngImport: i0, template: "<div [trvPopover]=\"popoverTemplate\"\r\n\t\t [position]=\"['bottom-end', 'top-end', 'left', 'right']\"\r\n\t\t popoverClass=\"popover-table-item-menu\"\r\n\t\t (shown)=\"onPopoverShown()\">\r\n\t<button type=\"button\"\r\n\t\t\t\t\tclass=\"btn btn-link\"\r\n\t\t\t\t\t[disabled]=\"disabled\"\r\n\t\t\t\t\t#toggleButton>\r\n\t\t<fa-icon [icon]=\"faColumns\"></fa-icon>\r\n\t</button>\r\n</div>\r\n\r\n<ng-template #popoverTemplate>\r\n\t<ul class=\"toggle-column-list\"\r\n\t\t\t#columnsContainer>\r\n\t\t@for (col of columns; track col; let i = $index) {\r\n\t\t@if (i > 0) {\r\n\t\t<li class=\"toggle-column-item\">\r\n\t\t\t<div class=\"form-check\">\r\n\t\t\t\t<input type=\"checkbox\"\r\n\t\t\t\t\t\t\t class=\"form-check-input\"\r\n\t\t\t\t\t\t\t id=\"customCheck{{i}}\"\r\n\t\t\t\t\t\t\t [checked]=\"col.visible\"\r\n\t\t\t\t\t\t\t [disabled]=\"col.disabled\"\r\n\t\t\t\t\t\t\t (change)=\"toggleColumnVisible(col, $event)\">\r\n\t\t\t\t<label class=\"form-check-label\"\r\n\t\t\t\t\t\t\t for=\"customCheck{{i}}\">{{col.label}}</label>\r\n\t\t\t</div>\r\n\t\t</li>\r\n\t\t}\r\n\t\t}\r\n\t</ul>\r\n</ng-template>", styles: [".toggle-column-list{list-style-type:none;padding:0;margin:0;min-width:200px}.toggle-column-list .toggle-column-item{height:42px;cursor:pointer;color:#1e5c88;display:flex;align-items:center}.toggle-column-list .toggle-column-item:hover{background:#c6dce5}.toggle-column-list .toggle-column-item .form-check{flex:1;padding:.75rem .75rem .75rem 2rem;position:relative}.toggle-column-list .toggle-column-item .form-check .form-check-label{white-space:nowrap;cursor:pointer;width:100%}.toggle-column-list .toggle-column-item .form-check .form-check-label:after{content:\"\";position:absolute;inset:0}.columns-button{cursor:pointer}.columns-button .ng-fa-icon{color:#1e5c88;margin-right:5px}.btn-link{font-size:14px;padding:0;vertical-align:inherit}\n"], dependencies: [{ kind: "directive", type: TrvPopoverDirective, selector: "[trvPopover]", inputs: ["trvPopover", "manualToggle", "position", "popoverClass", "allowMultiple", "disablePopover", "autoFocus"], outputs: ["shown", "hidden", "clickOutside", "afterClose", "afterCloseWithType"], exportAs: ["trvPopover"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableToggleColumnComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-table-toggle-column', changeDetection: ChangeDetectionStrategy.OnPush, imports: [TrvPopoverDirective, FaIconComponent], template: "<div [trvPopover]=\"popoverTemplate\"\r\n\t\t [position]=\"['bottom-end', 'top-end', 'left', 'right']\"\r\n\t\t popoverClass=\"popover-table-item-menu\"\r\n\t\t (shown)=\"onPopoverShown()\">\r\n\t<button type=\"button\"\r\n\t\t\t\t\tclass=\"btn btn-link\"\r\n\t\t\t\t\t[disabled]=\"disabled\"\r\n\t\t\t\t\t#toggleButton>\r\n\t\t<fa-icon [icon]=\"faColumns\"></fa-icon>\r\n\t</button>\r\n</div>\r\n\r\n<ng-template #popoverTemplate>\r\n\t<ul class=\"toggle-column-list\"\r\n\t\t\t#columnsContainer>\r\n\t\t@for (col of columns; track col; let i = $index) {\r\n\t\t@if (i > 0) {\r\n\t\t<li class=\"toggle-column-item\">\r\n\t\t\t<div class=\"form-check\">\r\n\t\t\t\t<input type=\"checkbox\"\r\n\t\t\t\t\t\t\t class=\"form-check-input\"\r\n\t\t\t\t\t\t\t id=\"customCheck{{i}}\"\r\n\t\t\t\t\t\t\t [checked]=\"col.visible\"\r\n\t\t\t\t\t\t\t [disabled]=\"col.disabled\"\r\n\t\t\t\t\t\t\t (change)=\"toggleColumnVisible(col, $event)\">\r\n\t\t\t\t<label class=\"form-check-label\"\r\n\t\t\t\t\t\t\t for=\"customCheck{{i}}\">{{col.label}}</label>\r\n\t\t\t</div>\r\n\t\t</li>\r\n\t\t}\r\n\t\t}\r\n\t</ul>\r\n</ng-template>", styles: [".toggle-column-list{list-style-type:none;padding:0;margin:0;min-width:200px}.toggle-column-list .toggle-column-item{height:42px;cursor:pointer;color:#1e5c88;display:flex;align-items:center}.toggle-column-list .toggle-column-item:hover{background:#c6dce5}.toggle-column-list .toggle-column-item .form-check{flex:1;padding:.75rem .75rem .75rem 2rem;position:relative}.toggle-column-list .toggle-column-item .form-check .form-check-label{white-space:nowrap;cursor:pointer;width:100%}.toggle-column-list .toggle-column-item .form-check .form-check-label:after{content:\"\";position:absolute;inset:0}.columns-button{cursor:pointer}.columns-button .ng-fa-icon{color:#1e5c88;margin-right:5px}.btn-link{font-size:14px;padding:0;vertical-align:inherit}\n"] }]
        }], ctorParameters: () => [], propDecorators: { columns: [{
                type: Input
            }], disabled: [{
                type: Input
            }], visibleColumnsChanged: [{
                type: Output
            }], columnsContainer: [{
                type: ViewChild,
                args: ['columnsContainer']
            }] } });

class TrvTableActionBarComponent {
    tableColumns;
    timestamp;
    disabled;
    startFilterOpened = false;
    hideAdd;
    hideRefresh;
    hideColumnToggle;
    hideFilterToggle;
    refresh = new EventEmitter();
    add = new EventEmitter();
    toggleFilter = new EventEmitter();
    visibleColumnsChanged = new EventEmitter();
    faPlus = faPlus;
    faSyncAlt = faSyncAlt;
    faFilter = faFilter;
    refreshOutputSet;
    filterOutputSet;
    addOutputSet;
    visibleColumnsChangedOutputSet;
    filterVisible = false;
    constructor() { }
    ngOnInit() {
        this.refreshOutputSet = this.refresh.observers.length > 0;
        this.addOutputSet = this.add.observers.length > 0;
        this.filterOutputSet = this.toggleFilter.observers.length > 0;
        this.visibleColumnsChangedOutputSet = this.visibleColumnsChanged.observers.length > 0;
        this.filterVisible = this.startFilterOpened;
    }
    onFilterToggle() {
        this.filterVisible = !this.filterVisible;
        this.toggleFilter.emit(this.filterVisible);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableActionBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvTableActionBarComponent, isStandalone: true, selector: "trv-table-action-bar", inputs: { tableColumns: "tableColumns", timestamp: "timestamp", disabled: "disabled", startFilterOpened: "startFilterOpened", hideAdd: "hideAdd", hideRefresh: "hideRefresh", hideColumnToggle: "hideColumnToggle", hideFilterToggle: "hideFilterToggle" }, outputs: { refresh: "refresh", add: "add", toggleFilter: "toggleFilter", visibleColumnsChanged: "visibleColumnsChanged" }, ngImport: i0, template: "<div class=\"table-actions\">\r\n\t<ng-content select=\"[table-action-bar-prepend]\"></ng-content>\r\n\t<ul class=\"list-inline\">\r\n\t\t@if (addOutputSet && !hideAdd) {\r\n\t\t<li class=\"list-inline-item\">\r\n\t\t\t<button type=\"button\"\r\n\t\t\t\t\t\t\tid=\"btnAddItem\"\r\n\t\t\t\t\t\t\tclass=\"btn btn-link\"\r\n\t\t\t\t\t\t\t[disabled]=\"disabled\"\r\n\t\t\t\t\t\t\t(click)=\"add.emit()\">\r\n\t\t\t\t<fa-icon [icon]=\"faPlus\"\r\n\t\t\t\t\t\t\t\t class=\"me-2\"></fa-icon>Skapa\r\n\t\t\t</button>\r\n\t\t</li>\r\n\t\t}\r\n\t\t@if (refreshOutputSet && !hideRefresh) {\r\n\t\t<li class=\"list-inline-item\">\r\n\t\t\t<button type=\"button\"\r\n\t\t\t\t\t\t\tclass=\"btn btn-link\"\r\n\t\t\t\t\t\t\t[disabled]=\"disabled\"\r\n\t\t\t\t\t\t\t(click)=\"refresh.emit()\">\r\n\t\t\t\t<fa-icon [icon]=\"faSyncAlt\"\r\n\t\t\t\t\t\t\t\t class=\"me-2\"></fa-icon>Uppdatera\r\n\t\t\t</button>\r\n\t\t\t@if (timestamp) {\r\n\t\t\t<time class=\"text-muted font-weight-normal ms-2\">{{timestamp | trvDateFull:true}}</time>\r\n\t\t\t}\r\n\t\t</li>\r\n\t\t}\r\n\t</ul>\r\n\t<ng-content select=\"[table-action-bar-append]\"></ng-content>\r\n\t<ng-content></ng-content>\r\n\t<div class=\"d-flex align-items-center ms-auto\">\r\n\t\t<ng-content select=\"trv-pagination\"></ng-content>\r\n\t\t<ng-content select=\"[table-action-bar-prepend-right]\"></ng-content>\r\n\t\t<ul class=\"list-inline ms-3\">\r\n\t\t\t@if (visibleColumnsChangedOutputSet && !hideColumnToggle) {\r\n\t\t\t<li class=\"list-inline-item me-0\">\r\n\t\t\t\t<trv-table-toggle-column [columns]=\"tableColumns\"\r\n\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t [disabled]=\"disabled\"\r\n\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t (visibleColumnsChanged)=\"visibleColumnsChanged.emit($event)\">\r\n\t\t\t\t</trv-table-toggle-column>\r\n\t\t\t</li>\r\n\t\t\t}\r\n\t\t\t@if (filterOutputSet && !hideFilterToggle) {\r\n\t\t\t<li class=\"list-inline-item\">\r\n\t\t\t\t<button type=\"button\"\r\n\t\t\t\t\t\t\t\tclass=\"btn btn-link px-2 btn-toggle-filter\"\r\n\t\t\t\t\t\t\t\t[disabled]=\"disabled\"\r\n\t\t\t\t\t\t\t\t[ngClass]=\"{'filter-active': filterVisible === true}\"\r\n\t\t\t\t\t\t\t\t(click)=\"onFilterToggle()\"\r\n\t\t\t\t\t\t\t\t#toggleButton>\r\n\t\t\t\t\t<fa-icon [icon]=\"faFilter\"\r\n\t\t\t\t\t\t\t\t\t class=\"me-2\"></fa-icon>Filter\r\n\t\t\t\t</button>\r\n\t\t\t</li>\r\n\t\t\t}\r\n\t\t</ul>\r\n\t\t<ng-content select=\"[table-action-bar-append-right]\"></ng-content>\r\n\t</div>\r\n</div>", styles: [""], dependencies: [{ kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "component", type: TrvTableToggleColumnComponent, selector: "trv-table-toggle-column", inputs: ["columns", "disabled"], outputs: ["visibleColumnsChanged"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "pipe", type: TrvDateFullPipe, name: "trvDateFull" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableActionBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-table-action-bar', changeDetection: ChangeDetectionStrategy.OnPush, imports: [FaIconComponent, TrvTableToggleColumnComponent, NgClass, TrvDateFullPipe], template: "<div class=\"table-actions\">\r\n\t<ng-content select=\"[table-action-bar-prepend]\"></ng-content>\r\n\t<ul class=\"list-inline\">\r\n\t\t@if (addOutputSet && !hideAdd) {\r\n\t\t<li class=\"list-inline-item\">\r\n\t\t\t<button type=\"button\"\r\n\t\t\t\t\t\t\tid=\"btnAddItem\"\r\n\t\t\t\t\t\t\tclass=\"btn btn-link\"\r\n\t\t\t\t\t\t\t[disabled]=\"disabled\"\r\n\t\t\t\t\t\t\t(click)=\"add.emit()\">\r\n\t\t\t\t<fa-icon [icon]=\"faPlus\"\r\n\t\t\t\t\t\t\t\t class=\"me-2\"></fa-icon>Skapa\r\n\t\t\t</button>\r\n\t\t</li>\r\n\t\t}\r\n\t\t@if (refreshOutputSet && !hideRefresh) {\r\n\t\t<li class=\"list-inline-item\">\r\n\t\t\t<button type=\"button\"\r\n\t\t\t\t\t\t\tclass=\"btn btn-link\"\r\n\t\t\t\t\t\t\t[disabled]=\"disabled\"\r\n\t\t\t\t\t\t\t(click)=\"refresh.emit()\">\r\n\t\t\t\t<fa-icon [icon]=\"faSyncAlt\"\r\n\t\t\t\t\t\t\t\t class=\"me-2\"></fa-icon>Uppdatera\r\n\t\t\t</button>\r\n\t\t\t@if (timestamp) {\r\n\t\t\t<time class=\"text-muted font-weight-normal ms-2\">{{timestamp | trvDateFull:true}}</time>\r\n\t\t\t}\r\n\t\t</li>\r\n\t\t}\r\n\t</ul>\r\n\t<ng-content select=\"[table-action-bar-append]\"></ng-content>\r\n\t<ng-content></ng-content>\r\n\t<div class=\"d-flex align-items-center ms-auto\">\r\n\t\t<ng-content select=\"trv-pagination\"></ng-content>\r\n\t\t<ng-content select=\"[table-action-bar-prepend-right]\"></ng-content>\r\n\t\t<ul class=\"list-inline ms-3\">\r\n\t\t\t@if (visibleColumnsChangedOutputSet && !hideColumnToggle) {\r\n\t\t\t<li class=\"list-inline-item me-0\">\r\n\t\t\t\t<trv-table-toggle-column [columns]=\"tableColumns\"\r\n\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t [disabled]=\"disabled\"\r\n\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t (visibleColumnsChanged)=\"visibleColumnsChanged.emit($event)\">\r\n\t\t\t\t</trv-table-toggle-column>\r\n\t\t\t</li>\r\n\t\t\t}\r\n\t\t\t@if (filterOutputSet && !hideFilterToggle) {\r\n\t\t\t<li class=\"list-inline-item\">\r\n\t\t\t\t<button type=\"button\"\r\n\t\t\t\t\t\t\t\tclass=\"btn btn-link px-2 btn-toggle-filter\"\r\n\t\t\t\t\t\t\t\t[disabled]=\"disabled\"\r\n\t\t\t\t\t\t\t\t[ngClass]=\"{'filter-active': filterVisible === true}\"\r\n\t\t\t\t\t\t\t\t(click)=\"onFilterToggle()\"\r\n\t\t\t\t\t\t\t\t#toggleButton>\r\n\t\t\t\t\t<fa-icon [icon]=\"faFilter\"\r\n\t\t\t\t\t\t\t\t\t class=\"me-2\"></fa-icon>Filter\r\n\t\t\t\t</button>\r\n\t\t\t</li>\r\n\t\t\t}\r\n\t\t</ul>\r\n\t\t<ng-content select=\"[table-action-bar-append-right]\"></ng-content>\r\n\t</div>\r\n</div>" }]
        }], ctorParameters: () => [], propDecorators: { tableColumns: [{
                type: Input
            }], timestamp: [{
                type: Input
            }], disabled: [{
                type: Input
            }], startFilterOpened: [{
                type: Input
            }], hideAdd: [{
                type: Input
            }], hideRefresh: [{
                type: Input
            }], hideColumnToggle: [{
                type: Input
            }], hideFilterToggle: [{
                type: Input
            }], refresh: [{
                type: Output
            }], add: [{
                type: Output
            }], toggleFilter: [{
                type: Output
            }], visibleColumnsChanged: [{
                type: Output
            }] } });

class TrvTableRowMenuComponent {
    disabled;
    menuButtonTabIndex = 0;
    faEllipsisV = faEllipsisV;
    constructor() { }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableRowMenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvTableRowMenuComponent, isStandalone: true, selector: "trv-table-row-menu", inputs: { disabled: "disabled", menuButtonTabIndex: "menuButtonTabIndex" }, ngImport: i0, template: "<div #popover=\"trvPopover\"\r\n     [trvPopover]=\"popoverTemplate\"\r\n     [disablePopover]=\"disabled\"\r\n     [position]=\"['right-start', 'right-end', 'left-start']\"\r\n     popoverClass=\"popover-table-item-menu\"\r\n     class=\"table-item-menu trv-prevent-table-row-click\">\r\n    <button class=\"btn btn-rounded\"\r\n            #toggleButton\r\n            [disabled]=\"disabled\"\r\n            [tabIndex]=\"menuButtonTabIndex\">\r\n        <fa-icon [icon]=\"faEllipsisV\"\r\n                 class=\"icon\"></fa-icon>\r\n    </button>\r\n</div>\r\n\r\n<ng-template #popoverTemplate>\r\n    <div (click)=\"popover.hide()\"\r\n         (keyup.space)=\"popover.hide()\">\r\n        <ng-content></ng-content>\r\n    </div>\r\n</ng-template>", styles: [":root{--bs-border-color: #ced4da}.dropdown-toggle:after{content:none}.btn.btn-rounded:hover:enabled,.btn.btn-rounded:focus:enabled{background-color:#a4cfe1}.btn.btn-rounded:hover:disabled{background-color:initial;color:#1e5c88}\n"], dependencies: [{ kind: "directive", type: TrvPopoverDirective, selector: "[trvPopover]", inputs: ["trvPopover", "manualToggle", "position", "popoverClass", "allowMultiple", "disablePopover", "autoFocus"], outputs: ["shown", "hidden", "clickOutside", "afterClose", "afterCloseWithType"], exportAs: ["trvPopover"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTableRowMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-table-row-menu', changeDetection: ChangeDetectionStrategy.OnPush, imports: [TrvPopoverDirective, FaIconComponent], template: "<div #popover=\"trvPopover\"\r\n     [trvPopover]=\"popoverTemplate\"\r\n     [disablePopover]=\"disabled\"\r\n     [position]=\"['right-start', 'right-end', 'left-start']\"\r\n     popoverClass=\"popover-table-item-menu\"\r\n     class=\"table-item-menu trv-prevent-table-row-click\">\r\n    <button class=\"btn btn-rounded\"\r\n            #toggleButton\r\n            [disabled]=\"disabled\"\r\n            [tabIndex]=\"menuButtonTabIndex\">\r\n        <fa-icon [icon]=\"faEllipsisV\"\r\n                 class=\"icon\"></fa-icon>\r\n    </button>\r\n</div>\r\n\r\n<ng-template #popoverTemplate>\r\n    <div (click)=\"popover.hide()\"\r\n         (keyup.space)=\"popover.hide()\">\r\n        <ng-content></ng-content>\r\n    </div>\r\n</ng-template>", styles: [":root{--bs-border-color: #ced4da}.dropdown-toggle:after{content:none}.btn.btn-rounded:hover:enabled,.btn.btn-rounded:focus:enabled{background-color:#a4cfe1}.btn.btn-rounded:hover:disabled{background-color:initial;color:#1e5c88}\n"] }]
        }], ctorParameters: () => [], propDecorators: { disabled: [{
                type: Input
            }], menuButtonTabIndex: [{
                type: Input
            }] } });

const TrvTableBundle = [
    TrvTableActionBarComponent,
    TrvTableToggleColumnComponent,
    TrvTableRowMenuComponent,
    TrvTableRowClick,
    TrvTableRowLink
];

//Används för att dekorera <ng-template> i en trv-tab. Innehållet i templaten lazy-loadas.
class TrvTabContent {
    constructor() { }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTabContent, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvTabContent, isStandalone: true, selector: "[trvTabContent]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTabContent, decorators: [{
            type: Directive,
            args: [{
                    selector: '[trvTabContent]',
                    standalone: true
                }]
        }], ctorParameters: () => [] });

class LayoutService {
    breakpointObserver$ = new BehaviorSubject('xxxl');
    constructor(breakpointObserver) {
        breakpointObserver.observe([
            '(max-width: 0)',
            '(max-width: 576px)',
            '(max-width: 768px)',
            '(max-width: 992px)',
            '(max-width: 1200px)',
            '(max-width: 1440px)',
            '(max-width: 1600px)'
        ])
            .subscribe(result => {
            if (result.matches) {
                for (const [key, value] of Object.entries(result.breakpoints)) {
                    if (value === true) {
                        this.breakpointObserver$.next(this.getBreakpointName(key));
                        break;
                    }
                }
            }
            else {
                return 'xxxl+';
            }
        });
    }
    breakpointIsEqualOrSmallerThan(breakpointToCompare, triggeredBreakpoint) {
        return this.getBreakpointNumeringValue(triggeredBreakpoint) <= this.getBreakpointNumeringValue(breakpointToCompare);
    }
    getBreakpointNumeringValue(breakpoint) {
        switch (breakpoint) {
            case 'xs':
                return 1;
            case 'sm':
                return 2;
            case 'md':
                return 3;
            case 'lg':
                return 4;
            case 'xl':
                return 5;
            case 'xxl':
                return 6;
            case 'xxxl':
                return 7;
            case 'xxxl+':
                return 8;
            default:
                return null;
        }
    }
    getBreakpointName(key) {
        switch (key) {
            case '(max-width: 0)':
                return 'xs';
            case '(max-width: 576px)':
                return 'sm';
            case '(max-width: 768px)':
                return 'md';
            case '(max-width: 992px)':
                return 'lg';
            case '(max-width: 1200px)':
                return 'xl';
            case '(max-width: 1440px)':
                return 'xxl';
            case '(max-width: 1600px)':
                return 'xxxl';
            default:
                return null;
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: LayoutService, deps: [{ token: i1$3.BreakpointObserver }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: LayoutService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: LayoutService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$3.BreakpointObserver }] });

//Ansvarar för att rita ut tab-innehållet mha. PortalOutlet.
class TrvTabContentBodyComponent {
    injector;
    appRef;
    content;
    shouldRender;
    outletElement;
    _portalHost;
    viewInitiated = false;
    constructor(injector, appRef) {
        this.injector = injector;
        this.appRef = appRef;
    }
    ngAfterViewInit() {
        this._portalHost = this.createDomPortalHost();
        if (this.shouldRender && this.content) {
            this._portalHost.attachTemplatePortal(this.content);
        }
        this.viewInitiated = true;
    }
    ngOnChanges(changes) {
        if (changes.shouldRender != null) {
            if (!this.viewInitiated) {
                return;
            }
            if (this.shouldRender === true && this.content) {
                this._portalHost.attachTemplatePortal(this.content);
            }
            else {
                this._portalHost.detach();
            }
        }
    }
    createDomPortalHost() {
        return new DomPortalOutlet(this.outletElement.nativeElement, null, this.appRef, this.injector);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTabContentBodyComponent, deps: [{ token: i0.Injector }, { token: i0.ApplicationRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvTabContentBodyComponent, isStandalone: true, selector: "trv-tab-content-body", inputs: { content: "content", shouldRender: "shouldRender" }, viewQueries: [{ propertyName: "outletElement", first: true, predicate: ["outletElement"], descendants: true, read: ElementRef, static: true }], usesOnChanges: true, ngImport: i0, template: "<div #outletElement></div>", changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTabContentBodyComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-tab-content-body', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<div #outletElement></div>" }]
        }], ctorParameters: () => [{ type: i0.Injector }, { type: i0.ApplicationRef }], propDecorators: { content: [{
                type: Input
            }], shouldRender: [{
                type: Input
            }], outletElement: [{
                type: ViewChild,
                args: ['outletElement', { read: ElementRef, static: true }]
            }] } });

class TrvTabsetComponent extends trvUnsubscribeMixin() {
    cdr;
    trvUtilityService;
    layoutService;
    constructor(cdr, trvUtilityService, layoutService) {
        super();
        this.cdr = cdr;
        this.trvUtilityService = trvUtilityService;
        this.layoutService = layoutService;
    }
    justification = 'start';
    dropdownMenuText;
    dropdownPrependIcon;
    dropdownAppendIcon;
    showDropdownOnBreakpoint;
    showAddButton;
    activeTab;
    tabChanged = new EventEmitter();
    addTabPressed = new EventEmitter();
    popover;
    faCaretDown = faCaretDown;
    faExclamationCircle = faExclamationCircle;
    faPlus = faPlus;
    tabs = [];
    showTabsAsDropdown;
    ngOnInit() {
        if (this.showDropdownOnBreakpoint != null) {
            this.layoutService.breakpointObserver$
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(breakpoint => {
                this.showTabsAsDropdown = this.layoutService.breakpointIsEqualOrSmallerThan(this.showDropdownOnBreakpoint, breakpoint);
                this.cdr.markForCheck();
            });
        }
    }
    ngOnChanges(changes) {
        if (changes?.activeTab?.currentValue && !changes.activeTab.isFirstChange()) {
            const activeTabId = changes.activeTab.currentValue;
            const activeTab = this.tabs.find(t => t.id === activeTabId);
            if (!activeTab.isActive) {
                this.selectTab(activeTab);
            }
        }
    }
    addTab(tab) {
        if (this.tabs.length === 0) {
            tab.isActive = true;
            tab.activeOnce = true;
            tab.animationTrigger = 'visible';
        }
        this.tabs.push(tab);
    }
    selectTab(selectedTab, event) {
        if (event) {
            event.preventDefault();
        }
        if (selectedTab.disabled || selectedTab.isActive) {
            return;
        }
        const tabToSelect = this.tabs.find(tab => tab === selectedTab);
        const tabToDeselect = this.tabs.find(tab => tab.isActive === true);
        if (!tabToSelect || !tabToDeselect) {
            throw new Error('Could not find tabs to select and deselect');
        }
        tabToSelect.setActive(true);
        tabToDeselect.setActive(false);
        this.tabChanged.emit(selectedTab.id || 'Tab-id not set');
        if (this.popover) {
            this.popover.hide();
        }
    }
    //Tab som markerats med startActive="true"
    preselectTab(preselectedTab) {
        const tabToSelect = this.tabs.find(tab => tab === preselectedTab);
        const tabToDeselect = this.tabs.find(tab => tab.isActive === true);
        tabToDeselect.isActive = false;
        tabToDeselect.animationTrigger = 'hidden';
        tabToSelect.activeOnce = true;
        tabToSelect.isActive = true;
        tabToSelect.animationTrigger = 'visible';
    }
    emitAddTabClick() {
        this.addTabPressed.emit();
    }
    detectChanges() {
        this.trvUtilityService.detectChanges(this.cdr);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTabsetComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: TrvUtilityService }, { token: LayoutService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.1", type: TrvTabsetComponent, isStandalone: true, selector: "trv-tabset", inputs: { justification: "justification", dropdownMenuText: "dropdownMenuText", dropdownPrependIcon: "dropdownPrependIcon", dropdownAppendIcon: "dropdownAppendIcon", showDropdownOnBreakpoint: "showDropdownOnBreakpoint", showAddButton: "showAddButton", activeTab: "activeTab" }, outputs: { tabChanged: "tabChanged", addTabPressed: "addTabPressed" }, viewQueries: [{ propertyName: "popover", first: true, predicate: ["popover"], descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div class=\"tabset\">\r\n  <!-- Unlimited width (visa som tabbar) -->\r\n  @if (!showTabsAsDropdown) {\r\n  <ul class=\"navigation-tabs\"\r\n      role=\"tablist\"\r\n      [ngClass]=\"{\r\n                  'justify-content-start': justification === 'start',\r\n                  'justify-content-end': justification === 'end',\r\n                  'justify-content-center': justification === 'center',\r\n                  'justify-content-between': justification === 'between',\r\n                  'justify-content-around': justification === 'around'}\">\r\n    @for (tab of tabs; track tab) {\r\n    <li class=\"navigation-item\"\r\n        [ngClass]=\"tab.tabClasses\"\r\n        [attr.id]=\"tab.id\">\r\n      <ng-container [ngTemplateOutlet]=\"tabTemplate\"\r\n                    [ngTemplateOutletContext]=\"{ tab: tab, dropdown: false}\">\r\n      </ng-container>\r\n    </li>\r\n    }\r\n    @if (showAddButton) {\r\n    <button (click)=\"emitAddTabClick()\"\r\n            class=\"btn btn-primary\">\r\n      <fa-icon [icon]=\"faPlus\"></fa-icon>\r\n      L\u00E4gg till\r\n    </button>\r\n    }\r\n  </ul>\r\n  }\r\n\r\n  <!-- Limited width (visa dropdown knapp) -->\r\n  @if (showTabsAsDropdown) {\r\n  <div class=\"dropdown-button\">\r\n    <button\u00A0[trvPopover]=\"popoverTemplate\" \u00A0\r\n                                           #popover=\"trvPopover\"\r\n                                           [position]=\"['bottom-start']\"\r\n                                           popoverClass=\"trv-popover-no-container-margin\"\r\n                                           type=\"button\"\r\n                                           class=\"btn\u00A0btn-primary\">\r\n      @if (dropdownPrependIcon) {\r\n      <fa-icon [icon]=\"dropdownPrependIcon\"\r\n               class=\"me-2\"></fa-icon>\r\n      }\r\n      {{dropdownMenuText\u00A0||\u00A0'Meny'}} <fa-icon\u00A0[icon]=\"dropdownAppendIcon\u00A0||\u00A0faCaretDown\" class=\"ms-2\"></fa-icon>\r\n    </button>\r\n  </div>\r\n  }\r\n\r\n  <!-- Inneh\u00E5llet i tabben -->\r\n  <div class=\"tabs-container\">\r\n    @for (tab of tabs; track tab) {\r\n    <trv-tab-content-body [@tabContent]=\"tab.animationTrigger\"\r\n                          [class.tab-content-active]=\"tab.isActive\"\r\n                          [content]=\"tab.content\"\r\n                          [shouldRender]=\"tab.activeOnce\"\r\n                          role=\"tabpanel\">\r\n    </trv-tab-content-body>\r\n    }\r\n  </div>\r\n</div>\r\n\r\n<!-- Dropdown template -->\r\n<ng-template #popoverTemplate>\r\n  <div class=\"dropdown\">\r\n    @for (tab of tabs; track tab) {\r\n    <ng-container [ngTemplateOutlet]=\"tabTemplate\"\r\n                  [ngTemplateOutletContext]=\"{ tab: tab, dropdown: true}\">\r\n    </ng-container>\r\n    }\r\n  </div>\r\n</ng-template>\r\n\r\n<!-- En tab (b\u00E5de tab och dropdown item) -->\r\n<ng-template #tabTemplate\r\n             let-tab=\"tab\"\r\n             let-dropdown=\"dropdown\">\r\n  @if (tab.showDotIndicator) {\r\n  <div class=\"dot-indicator\"></div>\r\n  }\r\n  <a href\r\n     [attr.tabindex]=\"tab.disabled ? '-1' : undefined\"\r\n     [ngClass]=\"{ \r\n       'dropdown-item': dropdown,\r\n       'navigation-link': !dropdown,\r\n       'active': tab.isActive, \r\n       'disabled': tab.disabled\r\n      }\"\r\n     (click)=\"selectTab(tab, $event)\"\r\n     (keyup.space)=\"selectTab(tab)\"\r\n     (keyup.enter)=\"selectTab(tab)\"\r\n     (keydown.space)=\"$event.preventDefault()\">\r\n    @if (tab.icon) {\r\n    <fa-icon [icon]=\"tab.icon\"\r\n             class=\"me-2\">\r\n    </fa-icon>\r\n    }\r\n    {{tab.header}}\r\n    @if (tab.badge) {\r\n    <span class=\"tab-badge\">\r\n      {{tab.badge}}\r\n    </span>\r\n    }\r\n    @if (tab.secondaryBadge !== null && tab.secondaryBadge !== undefined) {\r\n    <span class=\"tab-secondary-badge\">\r\n      {{tab.secondaryBadge}}\r\n    </span>\r\n    }\r\n    @if (tab.hasValidationError) {\r\n    <fa-icon [icon]=\"faExclamationCircle\"\r\n             class=\"validation-error-icon\">\r\n    </fa-icon>\r\n    }\r\n  </a>\r\n</ng-template>", styles: [":root{--bs-border-color: #ced4da}.navigation-tabs{border:none;margin-bottom:20px;position:relative;display:flex;flex-wrap:wrap;list-style:none;padding-left:0}.navigation-tabs:after{content:\"\";position:absolute;z-index:100;left:0;bottom:-4px;background:#98bfda;height:2px;right:0;border-radius:2px;z-index:90}.navigation-tabs .navigation-item{margin-bottom:-1px;position:relative}.navigation-tabs .navigation-item .navigation-link{display:block;padding:.5rem 1rem;cursor:pointer;position:relative;border:none;background:transparent;color:#495057}.navigation-tabs .navigation-item .navigation-link.disabled{color:#ababab;pointer-events:none;cursor:default}.navigation-tabs .navigation-item .navigation-link:before{content:\"\";position:absolute;z-index:100;left:0;right:100%;bottom:-4px;background:#1e5c88;height:4px;border-radius:4px;transition-property:right;transition-duration:.1s;transition-timing-function:ease-out}.navigation-tabs .navigation-item .navigation-link:hover{text-decoration:none;color:#1e5c88}.navigation-tabs .navigation-item .navigation-link:hover:before{opacity:.6;right:0}.navigation-tabs .navigation-item .navigation-link:focus{background:#dee2e6;transition:background .15s linear;transition-delay:50ms;outline:none}.navigation-tabs .navigation-item .navigation-link.active{color:#1e5c88}.navigation-tabs .navigation-item .navigation-link.active:before{opacity:1;right:0;background:#1e5c88}.navigation-tabs .navigation-item .navigation-link.active:focus{background:transparent}.navigation-tabs .navigation-item .navigation-link .tab-badge{display:inline-block;background-color:#000;color:#fff;border-radius:3px;padding:0 4px;margin-left:3px;font-size:11px;font-weight:600}.navigation-tabs .navigation-item .navigation-link .tab-secondary-badge{background-color:#1e5c88;color:#fff;border-radius:3px;padding:0 4px;font-size:8px;font-weight:600;position:absolute;right:0;top:0;z-index:1}.navigation-tabs .navigation-item .validation-error-icon{color:#870000;margin-left:.25rem}.navigation-tabs .navigation-item .dot-indicator{height:10px;width:10px;background-color:#1e5c88;border-radius:50%;position:absolute;right:0;top:0;z-index:1}.dropdown{display:flex;flex-direction:column}.dropdown .dropdown-item{color:#1e5c88;cursor:pointer;padding:.75rem;min-width:100px;display:flex}.dropdown .dropdown-item>span{margin-right:.5rem}.dropdown .dropdown-item fa-icon{text-align:center;width:18px;margin-right:.5rem}.dropdown .dropdown-item.active{color:#fff!important}.dropdown .dropdown-item.active:hover,.dropdown .dropdown-item.active:focus{background:#1e5c88}.dropdown .dropdown-item:focus{background-color:#fff}.dropdown .dropdown-item:hover{background-color:#dee2e6;color:#212529}.tabset{display:flex;flex-direction:column;position:relative}@media (max-width: 575.98px){.tabset ul{flex-wrap:nowrap;white-space:nowrap;padding-bottom:10px;overflow-x:auto;overflow-y:visible}.tabset ul:after{bottom:6px}.tabset ul li.navigation-item a:after{content:\"\";position:absolute;z-index:10;left:0;right:100%;bottom:-3px;background:#98bfda;height:2px;border-radius:4px;width:100%}}.tabset ul{position:relative}.tabset .tabs-container{position:relative;display:flex}.tabset .tabs-container trv-tab-content-body{position:absolute;display:block;inset:0;flex-basis:100%}.tabset .tabs-container trv-tab-content-body.tab-content-active{position:relative}.tabset .tabs-container trv-tab-content-body:not(.tab-content-active){overflow:hidden}.tabset .tabs-container trv-tab-content-body[style*=\"visibility: hidden\"]{display:none}.tabset-dropdown{padding:.5rem 0}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "directive", type: TrvPopoverDirective, selector: "[trvPopover]", inputs: ["trvPopover", "manualToggle", "position", "popoverClass", "allowMultiple", "disablePopover", "autoFocus"], outputs: ["shown", "hidden", "clickOutside", "afterClose", "afterCloseWithType"], exportAs: ["trvPopover"] }, { kind: "component", type: TrvTabContentBodyComponent, selector: "trv-tab-content-body", inputs: ["content", "shouldRender"] }], animations: [
            trigger('tabContent', [
                state('visible', style({ visibility: 'visible', opacity: 1 })),
                state('hidden', style({ visibility: 'hidden', opacity: 0 })),
                transition('visible => hidden', animate('120ms cubic-bezier(0.42, 0.0, 1, 1)')),
                transition('hidden => visible', animate('175ms cubic-bezier(0.42, 0.0, 1, 1)'))
            ])
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTabsetComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-tabset', changeDetection: ChangeDetectionStrategy.OnPush, animations: [
                        trigger('tabContent', [
                            state('visible', style({ visibility: 'visible', opacity: 1 })),
                            state('hidden', style({ visibility: 'hidden', opacity: 0 })),
                            transition('visible => hidden', animate('120ms cubic-bezier(0.42, 0.0, 1, 1)')),
                            transition('hidden => visible', animate('175ms cubic-bezier(0.42, 0.0, 1, 1)'))
                        ])
                    ], imports: [NgClass, NgTemplateOutlet, FaIconComponent, TrvPopoverDirective, TrvTabContentBodyComponent], template: "<div class=\"tabset\">\r\n  <!-- Unlimited width (visa som tabbar) -->\r\n  @if (!showTabsAsDropdown) {\r\n  <ul class=\"navigation-tabs\"\r\n      role=\"tablist\"\r\n      [ngClass]=\"{\r\n                  'justify-content-start': justification === 'start',\r\n                  'justify-content-end': justification === 'end',\r\n                  'justify-content-center': justification === 'center',\r\n                  'justify-content-between': justification === 'between',\r\n                  'justify-content-around': justification === 'around'}\">\r\n    @for (tab of tabs; track tab) {\r\n    <li class=\"navigation-item\"\r\n        [ngClass]=\"tab.tabClasses\"\r\n        [attr.id]=\"tab.id\">\r\n      <ng-container [ngTemplateOutlet]=\"tabTemplate\"\r\n                    [ngTemplateOutletContext]=\"{ tab: tab, dropdown: false}\">\r\n      </ng-container>\r\n    </li>\r\n    }\r\n    @if (showAddButton) {\r\n    <button (click)=\"emitAddTabClick()\"\r\n            class=\"btn btn-primary\">\r\n      <fa-icon [icon]=\"faPlus\"></fa-icon>\r\n      L\u00E4gg till\r\n    </button>\r\n    }\r\n  </ul>\r\n  }\r\n\r\n  <!-- Limited width (visa dropdown knapp) -->\r\n  @if (showTabsAsDropdown) {\r\n  <div class=\"dropdown-button\">\r\n    <button\u00A0[trvPopover]=\"popoverTemplate\" \u00A0\r\n                                           #popover=\"trvPopover\"\r\n                                           [position]=\"['bottom-start']\"\r\n                                           popoverClass=\"trv-popover-no-container-margin\"\r\n                                           type=\"button\"\r\n                                           class=\"btn\u00A0btn-primary\">\r\n      @if (dropdownPrependIcon) {\r\n      <fa-icon [icon]=\"dropdownPrependIcon\"\r\n               class=\"me-2\"></fa-icon>\r\n      }\r\n      {{dropdownMenuText\u00A0||\u00A0'Meny'}} <fa-icon\u00A0[icon]=\"dropdownAppendIcon\u00A0||\u00A0faCaretDown\" class=\"ms-2\"></fa-icon>\r\n    </button>\r\n  </div>\r\n  }\r\n\r\n  <!-- Inneh\u00E5llet i tabben -->\r\n  <div class=\"tabs-container\">\r\n    @for (tab of tabs; track tab) {\r\n    <trv-tab-content-body [@tabContent]=\"tab.animationTrigger\"\r\n                          [class.tab-content-active]=\"tab.isActive\"\r\n                          [content]=\"tab.content\"\r\n                          [shouldRender]=\"tab.activeOnce\"\r\n                          role=\"tabpanel\">\r\n    </trv-tab-content-body>\r\n    }\r\n  </div>\r\n</div>\r\n\r\n<!-- Dropdown template -->\r\n<ng-template #popoverTemplate>\r\n  <div class=\"dropdown\">\r\n    @for (tab of tabs; track tab) {\r\n    <ng-container [ngTemplateOutlet]=\"tabTemplate\"\r\n                  [ngTemplateOutletContext]=\"{ tab: tab, dropdown: true}\">\r\n    </ng-container>\r\n    }\r\n  </div>\r\n</ng-template>\r\n\r\n<!-- En tab (b\u00E5de tab och dropdown item) -->\r\n<ng-template #tabTemplate\r\n             let-tab=\"tab\"\r\n             let-dropdown=\"dropdown\">\r\n  @if (tab.showDotIndicator) {\r\n  <div class=\"dot-indicator\"></div>\r\n  }\r\n  <a href\r\n     [attr.tabindex]=\"tab.disabled ? '-1' : undefined\"\r\n     [ngClass]=\"{ \r\n       'dropdown-item': dropdown,\r\n       'navigation-link': !dropdown,\r\n       'active': tab.isActive, \r\n       'disabled': tab.disabled\r\n      }\"\r\n     (click)=\"selectTab(tab, $event)\"\r\n     (keyup.space)=\"selectTab(tab)\"\r\n     (keyup.enter)=\"selectTab(tab)\"\r\n     (keydown.space)=\"$event.preventDefault()\">\r\n    @if (tab.icon) {\r\n    <fa-icon [icon]=\"tab.icon\"\r\n             class=\"me-2\">\r\n    </fa-icon>\r\n    }\r\n    {{tab.header}}\r\n    @if (tab.badge) {\r\n    <span class=\"tab-badge\">\r\n      {{tab.badge}}\r\n    </span>\r\n    }\r\n    @if (tab.secondaryBadge !== null && tab.secondaryBadge !== undefined) {\r\n    <span class=\"tab-secondary-badge\">\r\n      {{tab.secondaryBadge}}\r\n    </span>\r\n    }\r\n    @if (tab.hasValidationError) {\r\n    <fa-icon [icon]=\"faExclamationCircle\"\r\n             class=\"validation-error-icon\">\r\n    </fa-icon>\r\n    }\r\n  </a>\r\n</ng-template>", styles: [":root{--bs-border-color: #ced4da}.navigation-tabs{border:none;margin-bottom:20px;position:relative;display:flex;flex-wrap:wrap;list-style:none;padding-left:0}.navigation-tabs:after{content:\"\";position:absolute;z-index:100;left:0;bottom:-4px;background:#98bfda;height:2px;right:0;border-radius:2px;z-index:90}.navigation-tabs .navigation-item{margin-bottom:-1px;position:relative}.navigation-tabs .navigation-item .navigation-link{display:block;padding:.5rem 1rem;cursor:pointer;position:relative;border:none;background:transparent;color:#495057}.navigation-tabs .navigation-item .navigation-link.disabled{color:#ababab;pointer-events:none;cursor:default}.navigation-tabs .navigation-item .navigation-link:before{content:\"\";position:absolute;z-index:100;left:0;right:100%;bottom:-4px;background:#1e5c88;height:4px;border-radius:4px;transition-property:right;transition-duration:.1s;transition-timing-function:ease-out}.navigation-tabs .navigation-item .navigation-link:hover{text-decoration:none;color:#1e5c88}.navigation-tabs .navigation-item .navigation-link:hover:before{opacity:.6;right:0}.navigation-tabs .navigation-item .navigation-link:focus{background:#dee2e6;transition:background .15s linear;transition-delay:50ms;outline:none}.navigation-tabs .navigation-item .navigation-link.active{color:#1e5c88}.navigation-tabs .navigation-item .navigation-link.active:before{opacity:1;right:0;background:#1e5c88}.navigation-tabs .navigation-item .navigation-link.active:focus{background:transparent}.navigation-tabs .navigation-item .navigation-link .tab-badge{display:inline-block;background-color:#000;color:#fff;border-radius:3px;padding:0 4px;margin-left:3px;font-size:11px;font-weight:600}.navigation-tabs .navigation-item .navigation-link .tab-secondary-badge{background-color:#1e5c88;color:#fff;border-radius:3px;padding:0 4px;font-size:8px;font-weight:600;position:absolute;right:0;top:0;z-index:1}.navigation-tabs .navigation-item .validation-error-icon{color:#870000;margin-left:.25rem}.navigation-tabs .navigation-item .dot-indicator{height:10px;width:10px;background-color:#1e5c88;border-radius:50%;position:absolute;right:0;top:0;z-index:1}.dropdown{display:flex;flex-direction:column}.dropdown .dropdown-item{color:#1e5c88;cursor:pointer;padding:.75rem;min-width:100px;display:flex}.dropdown .dropdown-item>span{margin-right:.5rem}.dropdown .dropdown-item fa-icon{text-align:center;width:18px;margin-right:.5rem}.dropdown .dropdown-item.active{color:#fff!important}.dropdown .dropdown-item.active:hover,.dropdown .dropdown-item.active:focus{background:#1e5c88}.dropdown .dropdown-item:focus{background-color:#fff}.dropdown .dropdown-item:hover{background-color:#dee2e6;color:#212529}.tabset{display:flex;flex-direction:column;position:relative}@media (max-width: 575.98px){.tabset ul{flex-wrap:nowrap;white-space:nowrap;padding-bottom:10px;overflow-x:auto;overflow-y:visible}.tabset ul:after{bottom:6px}.tabset ul li.navigation-item a:after{content:\"\";position:absolute;z-index:10;left:0;right:100%;bottom:-3px;background:#98bfda;height:2px;border-radius:4px;width:100%}}.tabset ul{position:relative}.tabset .tabs-container{position:relative;display:flex}.tabset .tabs-container trv-tab-content-body{position:absolute;display:block;inset:0;flex-basis:100%}.tabset .tabs-container trv-tab-content-body.tab-content-active{position:relative}.tabset .tabs-container trv-tab-content-body:not(.tab-content-active){overflow:hidden}.tabset .tabs-container trv-tab-content-body[style*=\"visibility: hidden\"]{display:none}.tabset-dropdown{padding:.5rem 0}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: TrvUtilityService }, { type: LayoutService }], propDecorators: { justification: [{
                type: Input
            }], dropdownMenuText: [{
                type: Input
            }], dropdownPrependIcon: [{
                type: Input
            }], dropdownAppendIcon: [{
                type: Input
            }], showDropdownOnBreakpoint: [{
                type: Input
            }], showAddButton: [{
                type: Input
            }], activeTab: [{
                type: Input
            }], tabChanged: [{
                type: Output
            }], addTabPressed: [{
                type: Output
            }], popover: [{
                type: ViewChild,
                args: ['popover']
            }] } });

class TrvTabComponent {
    viewContainerRef;
    tabset;
    cdr;
    trvUtilityService;
    id;
    header;
    badge;
    secondaryBadge;
    icon;
    disabled;
    startActive;
    hasValidationError;
    showDotIndicator;
    //Template i trv-tab som har dekorerats med TrvTabContent-direktivet. Tex. <trv-tab><ng-template trvTabContent>Hej!</ng-template></trv-tab>
    //Används i första hand istället för implicitContent nedan.
    explicitContent;
    //Innehåll som ligger direkt i trv-tab. Tex. <trv-tab>Hej!</trv-tab>
    implicitContent;
    _contentPortal = null;
    isActive = false;
    tabClasses;
    //Används bara för att trigga animationen vid rätt tillfälle
    //Sätta till false vid init och direkt då man byter tabb.
    //Sätts till true först då fade out-animationen är klar.
    animationTrigger = 'hidden';
    activeOnce = false;
    constructor(viewContainerRef, tabset, cdr, trvUtilityService, elementRef) {
        this.viewContainerRef = viewContainerRef;
        this.tabset = tabset;
        this.cdr = cdr;
        this.trvUtilityService = trvUtilityService;
        tabset.addTab(this);
        const nativeElement = elementRef.nativeElement;
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.attributeName == 'class') {
                    const classes = [];
                    for (const cls of mutation.target.classList) {
                        if (cls) {
                            classes.push(cls);
                        }
                    }
                    this.tabClasses = classes;
                    tabset.detectChanges();
                }
            });
        });
        observer.observe(nativeElement, { attributes: true });
    }
    ngOnInit() {
        this._contentPortal = new TemplatePortal(this.explicitContent || this.implicitContent, this.viewContainerRef);
        if (this.startActive) {
            this.tabset.preselectTab(this);
        }
        this.tabset.detectChanges();
    }
    ngOnChanges(changes) {
        for (const key of Object.keys(changes)) {
            if (!changes[key].firstChange) {
                this.tabset.detectChanges();
            }
        }
    }
    setActive(active) {
        this.isActive = active;
        if (!active) {
            this.animationTrigger = 'hidden';
            return;
        }
        this.activeOnce = true;
        this.animationTrigger = 'visible';
    }
    get content() {
        return this._contentPortal;
    }
    detectsChanges() {
        this.trvUtilityService.detectChanges(this.cdr);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTabComponent, deps: [{ token: i0.ViewContainerRef }, { token: TrvTabsetComponent }, { token: i0.ChangeDetectorRef }, { token: TrvUtilityService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.1", type: TrvTabComponent, isStandalone: true, selector: "trv-tab", inputs: { id: "id", header: "header", badge: "badge", secondaryBadge: "secondaryBadge", icon: "icon", disabled: "disabled", startActive: "startActive", hasValidationError: "hasValidationError", showDotIndicator: "showDotIndicator" }, queries: [{ propertyName: "explicitContent", first: true, predicate: TrvTabContent, descendants: true, read: TemplateRef, static: true }], viewQueries: [{ propertyName: "implicitContent", first: true, predicate: TemplateRef, descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<ng-template><ng-content></ng-content></ng-template>", styles: [""], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvTabComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trv-tab', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<ng-template><ng-content></ng-content></ng-template>" }]
        }], ctorParameters: () => [{ type: i0.ViewContainerRef }, { type: TrvTabsetComponent }, { type: i0.ChangeDetectorRef }, { type: TrvUtilityService }, { type: i0.ElementRef }], propDecorators: { id: [{
                type: Input
            }], header: [{
                type: Input
            }], badge: [{
                type: Input
            }], secondaryBadge: [{
                type: Input
            }], icon: [{
                type: Input
            }], disabled: [{
                type: Input
            }], startActive: [{
                type: Input
            }], hasValidationError: [{
                type: Input
            }], showDotIndicator: [{
                type: Input
            }], explicitContent: [{
                type: ContentChild,
                args: [TrvTabContent, { read: TemplateRef, static: true }]
            }], implicitContent: [{
                type: ViewChild,
                args: [TemplateRef, { static: true }]
            }] } });

const TrvTabsetBundle = [TrvTabsetComponent, TrvTabComponent, TrvTabContent];

class TrvDebounceDirective {
    renderer;
    element;
    ngUnsubscribe = new Subject();
    onChange;
    onTouched;
    trvDebounce;
    debounce = 250;
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    constructor(renderer, element) {
        this.renderer = renderer;
        this.element = element;
    }
    ngOnInit() {
        this.debounce = this.trvDebounce || this.debounce;
        fromEvent(this.element.nativeElement, 'input')
            .pipe(map((e) => ({ value: e.target['value'] })), debounceTime(this.debounce), takeUntil(this.ngUnsubscribe))
            .subscribe(event => {
            this.onChange(event.value);
        });
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
    }
    writeValue(value) {
        const normalizedValue = value == null ? '' : value;
        this.renderer.setProperty(this.element.nativeElement, 'value', normalizedValue);
    }
    setDisabledState(isDisabled) {
        this.renderer.setProperty(this.element.nativeElement, 'disabled', isDisabled);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDebounceDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvDebounceDirective, isStandalone: true, selector: "input[formControlName][trvDebounce],input[formControl][trvDebounce],input[ngModel][trvDebounce]", inputs: { trvDebounce: "trvDebounce" }, providers: [{
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TrvDebounceDirective),
                multi: true
            }], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDebounceDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[formControlName][trvDebounce],input[formControl][trvDebounce],input[ngModel][trvDebounce]',
                    providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TrvDebounceDirective),
                            multi: true
                        }],
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: i0.ElementRef }], propDecorators: { trvDebounce: [{
                type: Input
            }] } });

class TrvAsFormControlPipe {
    transform(abstractControl) {
        if (abstractControl instanceof UntypedFormControl) {
            return abstractControl;
        }
        else {
            const unknownObject = abstractControl;
            throw new TypeError(`Expected FormControl, found ${unknownObject?.constructor?.name || 'Unknown object'}.`);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvAsFormControlPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.1", ngImport: i0, type: TrvAsFormControlPipe, isStandalone: true, name: "trvAsFormControl" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvAsFormControlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'trvAsFormControl',
                    standalone: true
                }]
        }] });

class TrvDateRangeFullPipe {
    dateService;
    constructor(dateService) {
        this.dateService = dateService;
    }
    transform(dateRange) {
        if (!dateRange) {
            return undefined;
        }
        const dr = dateRange;
        const f = dr.from || dr.beginDate || dr.start;
        const t = dr.to || dr.endDate || dr.end;
        const from = f ? this.dateService.fromISO(f).toFormat('yyyy-MM-dd HH:mm') : 'Obestämd';
        const to = t ? this.dateService.fromISO(t).toFormat('yyyy-MM-dd HH:mm') : 'Tillsvidare';
        return from + ' – ' + to;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateRangeFullPipe, deps: [{ token: TrvDateService }], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.1", ngImport: i0, type: TrvDateRangeFullPipe, isStandalone: true, name: "trvDateRangeFull" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateRangeFullPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'trvDateRangeFull',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: TrvDateService }] });

class TrvDateShortPipe {
    dateService;
    constructor(dateService) {
        this.dateService = dateService;
    }
    transform(date, prettyNearTerm) {
        if (!date) {
            return undefined;
        }
        const dateTime = this.dateService.fromISO(date);
        if (prettyNearTerm) {
            if (this.dateService.isToday(dateTime)) {
                return 'Idag';
            }
            else if (this.dateService.isYesterday(dateTime)) {
                return 'Igår';
            }
        }
        return dateTime.toFormat('yyyy-MM-dd');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateShortPipe, deps: [{ token: TrvDateService }], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.1", ngImport: i0, type: TrvDateShortPipe, isStandalone: true, name: "trvDateShort" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvDateShortPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'trvDateShort',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: TrvDateService }] });

const TrvCompleteBundle = [
    ...TrvAccordionBundle,
    ...TrvDashboardBundle,
    ...TrvTabsetBundle,
    ...TrvFormBundle,
    ...TrvTableBundle,
    ...TrvLoadingBundle,
    TrvAsFormControlPipe,
    TrvCalendarComponent,
    TrvFullscreenLoaderComponent,
    TrvGlobalMessagesComponent,
    TrvLoadingDirective,
    TrvPopoverDirective,
    TrvDatePickerComponent,
    TrvDateRangeComponent,
    TrvFileUploadComponent,
    TrvFilterSummaryComponent,
    TrvJumbotronComponent,
    TrvSelectComponent,
    TrvShouldFocusDirective,
    TrvPaginationComponent,
    TrvProgressBarComponent,
    TrvTextCutterDirective,
    TrvDateRangeFullPipe,
    TrvDateRangeShortPipe,
    TrvDateFullPipe,
    TrvDateShortPipe,
    TrvToggleSwitchComponent,
    TrvTooltipDirective,
    TrvCapitalizePipe,
    TrvLuxonToFormatPipe,
    TrvSafeHtmlPipe,
    TrvStringReplacePipe,
    TrvDebounceDirective
];

class TrvFloatingLabelDirective extends trvUnsubscribeMixin() {
    renderer;
    element;
    control;
    label;
    labelText;
    labelElement;
    restingClass = 'label-resting';
    floatingClass = 'label-floating';
    parentElement;
    constructor(renderer, element, control) {
        super();
        this.renderer = renderer;
        this.element = element;
        this.control = control;
    }
    ngOnInit() {
        this.labelText = this.renderer.createText(this.label);
        this.labelElement = this.renderer.createElement('label');
        this.renderer.appendChild(this.labelElement, this.labelText);
        this.renderer.addClass(this.labelElement, 'trv-floating-label');
    }
    ngAfterViewInit() {
        const hostElement = this.element.nativeElement;
        const formControlElement = this.getFormControlElement(hostElement);
        this.parentElement = formControlElement.parentNode;
        if (!this.parentElement) {
            throw new Error('the element with [trvFloatingLabel] needs a parent container');
        }
        this.renderer.removeAttribute(formControlElement, 'placeholder');
        this.renderer.setAttribute(this.labelElement, 'for', formControlElement.getAttribute('id'), null);
        if (this.shouldFloat(this.control.value)) {
            this.renderer.addClass(this.parentElement, this.floatingClass);
        }
        else {
            this.renderer.addClass(this.parentElement, this.restingClass);
        }
        this.renderer.addClass(this.parentElement, 'trv-floating-label-container');
        if (this.parentElement.getElementsByClassName('input-group-prepend')?.length > 0) {
            this.renderer.addClass(this.parentElement, 'has-prepend');
        }
        this.renderer.appendChild(this.parentElement, this.labelElement);
        merge(this.control.valueChanges, fromEvent(formControlElement, 'input'))
            .pipe(map(event => event?.target?.value != null ? event.target.value : event), takeUntil(this.ngUnsubscribe))
            .subscribe(value => this.shouldFloat(value) ? this.float() : this.reset());
    }
    reset() {
        this.renderer.addClass(this.parentElement, this.restingClass);
        this.renderer.removeClass(this.parentElement, this.floatingClass);
    }
    float() {
        this.renderer.addClass(this.parentElement, this.floatingClass);
        this.renderer.removeClass(this.parentElement, this.restingClass);
    }
    shouldFloat = (value) => {
        return value != null && (value.length == null || value.length > 0);
    };
    isFormControlElement(element) {
        const formTags = ['input', 'select', 'textarea'];
        return element && (element.classList.contains('form-control') ||
            formTags.some((t) => t === element.tagName));
    }
    getFormControlElement(host) {
        if (this.isFormControlElement(host)) {
            return host;
        }
        const elements = host.querySelectorAll('.form-control, input, select, textarea');
        if (elements.length > 0) {
            return elements[0];
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFloatingLabelDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i1$2.NgControl }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvFloatingLabelDirective, isStandalone: true, selector: "[trvFloatingLabel]", inputs: { label: ["trvFloatingLabel", "label"] }, exportAs: ["trvFloatingLabel"], usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvFloatingLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[trvFloatingLabel]',
                    exportAs: 'trvFloatingLabel',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i1$2.NgControl }], propDecorators: { label: [{
                type: Input,
                args: ['trvFloatingLabel']
            }] } });

class TrvScrollHeightEventsDirective {
    elementRef;
    scrollCloseToBottom = new EventEmitter;
    constructor(elementRef) {
        this.elementRef = elementRef;
    }
    ngOnInit() {
        const element = this.elementRef.nativeElement;
        let currentScrollHeight;
        element.addEventListener('scroll', () => {
            if (this.isScrollCloseToBottom(element, 2) && element.scrollHeight !== currentScrollHeight) {
                currentScrollHeight = element.scrollHeight;
                this.scrollCloseToBottom.emit();
            }
        });
    }
    // sant om scroll är x gånger elementets synliga längd från botten
    isScrollCloseToBottom(element, elementHeightMultiplier) {
        return element.scrollTop + element.clientHeight >=
            (element.scrollHeight - (element.clientHeight * elementHeightMultiplier));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvScrollHeightEventsDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: TrvScrollHeightEventsDirective, isStandalone: true, selector: "[trvScrollHeightEvents]", outputs: { scrollCloseToBottom: "scrollCloseToBottom" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvScrollHeightEventsDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[trvScrollHeightEvents]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { scrollCloseToBottom: [{
                type: Output
            }] } });

class TrvAsFormArrayPipe {
    transform(abstractControl) {
        if (abstractControl instanceof UntypedFormArray) {
            return abstractControl;
        }
        else {
            const unknownObject = abstractControl;
            throw new TypeError(`Expected FormArray, found ${unknownObject?.constructor?.name || 'Unknown object'}.`);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvAsFormArrayPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.1", ngImport: i0, type: TrvAsFormArrayPipe, isStandalone: true, name: "trvAsFormArray" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvAsFormArrayPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'trvAsFormArray',
                    standalone: true
                }]
        }] });

class TrvAsFormGroupPipe {
    transform(abstractControl) {
        if (abstractControl instanceof UntypedFormGroup) {
            return abstractControl;
        }
        else {
            const unknownObject = abstractControl;
            throw new TypeError(`Expected FormGroup, found ${unknownObject?.constructor?.name || 'Unknown object'}.`);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvAsFormGroupPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.1", ngImport: i0, type: TrvAsFormGroupPipe, isStandalone: true, name: "trvAsFormGroup" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TrvAsFormGroupPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'trvAsFormGroup',
                    standalone: true
                }]
        }] });

const trvIndicateLoading = (indicator, cdr) => (source) => source.pipe(prepare(() => {
    indicator.next(true);
    markForCheck(cdr);
}), finalize(() => {
    indicator.next(false);
    markForCheck(cdr);
}));
const prepare = (callback) => (source) => defer(() => {
    callback();
    return source;
});
const markForCheck = (cdr) => {
    if (cdr && !cdr.destroyed) {
        cdr.markForCheck();
    }
};

/**
 * Generated bundle index. Do not edit.
 */

export { ClickNavigationItem, DividerNavigationItem, ExternalNavigationItem, FormFieldSwitchGroupComponent, LayoutService, NavigationItem, RouterNavigationItem, SelectNavigationItem, TRV_LOADING_DEFAULT_OPTIONS, TRV_PAGINATION_DEFAULT_OPTIONS, TrvAccordionBundle, TrvAccordionComponent, TrvAccordionPanelComponent, TrvAsFormArrayPipe, TrvAsFormControlPipe, TrvAsFormGroupPipe, TrvCalendarComponent, TrvCalendarService, TrvCapitalizePipe, TrvCompleteBundle, TrvConfirmModalComponent, TrvDashboardBundle, TrvDashboardComponent, TrvDashboardContentComponent, TrvDashboardFooterComponent, TrvDashboardNavBlocksComponent, TrvDashboardNavPageComponent, TrvDashboardPageContentComponent, TrvDashboardPageHeaderComponent, TrvDashboardService, TrvDashboardSideNavComponent, TrvDashboardTopNavComponent, TrvDateFullPipe, TrvDatePickerComponent, TrvDateRangeComponent, TrvDateRangeFullPipe, TrvDateRangeShortPipe, TrvDateService, TrvDateShortPipe, TrvDateStringParserDirective, TrvDebounceDirective, TrvFileUploadComponent, TrvFilterSummaryComponent, TrvFloatingLabelDirective, TrvFormBundle, TrvFormComponent, TrvFormFieldComponent, TrvFormFieldGroupComponent, TrvFormFieldValidationMessagesComponent, TrvFormUtilityService, TrvFullscreenLoaderComponent, TrvFullscreenLoaderService, TrvGlobalMessagesComponent, TrvGlobalMessagesService, TrvJumbotronComponent, TrvLoadingBundle, TrvLoadingDirective, TrvLuxonToFormatPipe, TrvModalConfig, TrvModalContainerComponent, TrvModalRef, TrvModalService, TrvPaginationComponent, TrvPopoverDirective, TrvPopoverRef, TrvProgressBarComponent, TrvSafeHtmlPipe, TrvScrollHeightEventsDirective, TrvSelectComponent, TrvSelectDefaultInputs, TrvShouldFocusDirective, TrvStringReplacePipe, TrvTabComponent, TrvTabContent, TrvTableActionBarComponent, TrvTableBundle, TrvTableRowClick, TrvTableRowLink, TrvTableRowLinkMenuComponent, TrvTableRowLinkService, TrvTableRowMenuComponent, TrvTableToggleColumnComponent, TrvTabsetBundle, TrvTabsetComponent, TrvTextCutterDirective, TrvTimeStringParserDirective, TrvToggleSwitchComponent, TrvTooltipDirective, TrvUtilityService, trvCollapseStateAnimation, trvCollapseVoidAnimation, trvFadeAnimation, trvIndicateLoading, trvPopAltAnimation, trvPopAnimation, trvUnsubscribeMixin };
